import { useState, useRef, useEffect, useCallback } from 'react';
import { reportMessage } from '../lib/errorReporter';
import { isGroupCallBusy } from '../lib/groupCallBridge';
import { setCallActive } from '../lib/callActivity';
import { supabase } from '../supabaseClient';
import { leaveGroupCall, declineGroupCall, cancelGroupCallIfRinging } from '../lib/groupCallDb';
import { startRingtone, startRingbackTone, stopAllCallSounds } from '../utils/callSounds';
import * as callAudioGraph from '../utils/callAudioGraph';
import { getDisplayName } from '../utils/displayName';
import { getIceServers, getIceServersForRestart, clearIceServersCache, hasValidTurnCache, DEFAULT_ICE_SERVERS } from '../utils/iceServers';
import { closeCallNotificationBanner } from '../utils/swMessage';
import { startMicLevelMeter } from '../utils/micLevelMeter';
import { isWindows, isMac } from '../utils/browser';

/**
 * 무료 공개 Google STUN 서버 목록 (폴백 기본값)
 */
const ICE_SERVERS = DEFAULT_ICE_SERVERS;

// [F구역] 자기 탭 브로드캐스트 루프백 무시용 탭 식별자 (페이지 로드당 1회 생성)
const TAB_INSTANCE_ID = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random()}`;

/**
 * [선장 검증용 임시 스위치]
 * TURN 중계(relay) 강제 검증용 스위치입니다.
 * true로 변경 시 P2P 직결(host/srflx)을 차단하고 Cloudflare TURN relay 경로만 강제로 사용합니다.
 * 검증 완료 후에는 반드시 false로 되돌려야 합니다.
 */
const FORCE_TURN_RELAY = false;
// [CALL_DESIGN §5-2 설계값] TURN 중계 시 송신 화질 프로파일 (480p, 600kbps)
const RELAY_VIDEO_MAX_BITRATE = 600000;
const RELAY_VIDEO_TARGET_HEIGHT = 480;
const ICE_CANDIDATE_BUFFER_LIMIT = 200;
// 작업 2에서 실제 재시도 기준으로 쓸 예정, 실측 후 조정
const ICE_RESTART_JUDGE_MS = 3000;
// [결함 ④ 작업 2-A 보강 2] ICE restart 재연결 제한 시간 20초
// Supabase 재접속 시도가 1·3·8·18초째(1초, 2초, 5초, 10초 간격)에 일어날 수 있어 8초째를 놓쳐도 18초째에 붙도록 20초로 잡는다.
const ICE_RESTART_TIMEOUT_MS = 20000;
const ICE_RESTART_REPEAT_INTERVAL_MS = 2000; // 재연결 신호 2초 되풀이 전송 간격
const ICE_RESTART_SUCCESS_POLL_MS = 1000; // [결함 ④ 작업 2-A 재시험] 재연결 성공 보조 판정 주기 1초
// 통화 끊김 안내는 다시 걸 수 있도록 넉넉히 7.5초 표시
const UNSTABLE_ENDED_TOAST_MS = 7500;
const PRE_AUDIO_ROADBLOCK_GRACE_MS = 6000; // [결함 ④ 5-8] 소리 연결 전 막힘 확정 시 응답 후 P2P 수립 유예 6초
const AUDIO_CONNECT_TIMEOUT_MS = 8000; // [결함 ④ 5-9] 상대 응답 후 소리 연결 대기 한도

// [관측 ⑮-다] 통화 전 망 변경 안정화 후 조용한 1회 새로고침
const PRE_CALL_RELOAD_STABLE_MS = 10000; // 마지막 망 흔들림·화면 복귀 뒤 안정 대기
const PRE_CALL_RELOAD_QUIET_INPUT_MS = 20000; // 마지막 터치·키·휠 입력 뒤 조용함 기준
const PRE_CALL_RELOAD_COOLDOWN_MS = 60000; // 미리 새로고침 재실행 최소 간격
const PRE_CALL_RELOAD_STORAGE_KEY = 'moowoo_last_pre_call_reload'; // sessionStorage 키(마지막 실행 시각)

// [결함 ④ 5-9 보완] 통화 전 망 변경 딱지 새로고침 허용 사유 (망 원인만)
const NETWORK_FAILURE_REASONS = new Set([
  'reload_recovery_failed', // 5-8 소리 전 막힘 그물, 5-9 8초 감시, 망 변경 복구 실패
  'reload_not_scheduled', // 망 변경 안전장치 새로고침 예약 실패
  'reload_recovery_timeout', // 복구 1단계 20초 타임아웃
  'recover_self_timeout', // 복구 2단계 25초 안전 타이머 만료
  'peer_recover_timeout', // 상대 망 변경 복구 신호 대기 20초 타임아웃
  'net_switch_timeout', // 망 변경 후 통로 복구 30초 전체 제한 초과
  'connection_failed', // WebRTC peerConnection 상태 failed
  'connection_lost', // WebRTC disconnected 15초 유예 만료
  'ice_failed', // WebRTC iceConnectionState failed
  'heartbeat_timeout', // 15초 네트워크 생존 신호 두절
  'pc_rebuild_exhausted', // WebRTC PC 재연결 3회 한도 초과
  'pc_rebuild_timeout', // WebRTC PC 재구축 20초 타임아웃
  'ice_restart_timeout', // ICE 재시작 20초 타임아웃
  'ice_restart_offer_failed', // ICE restart offer 생성/전송/적용 실패
  'ice_restart_answer_failed', // ICE restart answer 생성/적용 실패
  'rebuild_exhausted', // 상대가 보낸 call:end 원인 (PC 재연결 한도 초과)
]);

// [결함 ⑥ 수리 1단계] 신호 통로 기초 방어 상수
const PEER_CHANNEL_SUBSCRIBE_TIMEOUT_MS = 2500;
const PEER_CHANNEL_REMOVE_TIMEOUT_MS = 1500;
const FLUSH_QUEUE_BEFORE_REMOVE_TIMEOUT_MS = 800;
const PEER_CHANNEL_IDLE_CLOSE_MS = 120000; // [C단계-6] 같은 상대 채널 2분 무사용 시 닫기
const EARLY_ICE_LIMIT_PER_CALL = ICE_CANDIDATE_BUFFER_LIMIT;
const EARLY_ICE_TTL_MS = 10000;
const IMPORTANT_SIGNALS = [
  'call:offer',
  'call:offer_resend',
  'call:answer',
  'call:end',
  'call:decline',
  'call:busy',
  'ice-restart-request',
  'ice-restart-offer',
  'ice-restart-answer',
  'call:rebuild-request',
  'call:rebuild-offer',
  'call:rebuild-answer',
  'call:recover',
  'call:recover-ready',
];
const TERMINATION_SIGNALS = [
  'call:end',
  'call:decline',
  'call:busy',
];

// [결함 ④ 작업 2-A 보강] 재연결 대기열 상한
const RESTART_CANDIDATE_BUFFER_LIMIT = 50;

/**
 * [선장 검증용 임시 스위치 판정]
 * FORCE_TURN_RELAY 상수가 true 이거나,
 * 개발 모드(DEV)에서 localStorage 'moowoo_debug_force_relay' === '1' 일 때 relay 강제 사용
 */
function isForceRelayEnabled() {
  if (FORCE_TURN_RELAY === true) return true;
  if (!import.meta.env.DEV) return false;
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem('moowoo_debug_force_relay') === '1';
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[useVoiceCall] isForceRelayEnabled localStorage 읽기 실패:', err);
    }
    return false;
  }
}

/**
 * 음성 통화 마이크 캡처 제약조건 (발신·수신·핫스왑 일원화)
 */
const CALL_AUDIO_CONSTRAINTS = Object.freeze({ audio: true, video: false });

/**
 * 마이크 교체 시 선호 제약 조건 조회 헬퍼
 * - 기본 장치(deviceId: 'default')가 목록에 있으면 exact: 'default' 지정
 * - 없거나 enumerateDevices 실패 시 기존 CALL_AUDIO_CONSTRAINTS 반환
 * - 장치 이름(label)은 보지 않음 (규칙 22 준수)
 */
async function getPreferredMicConstraints() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return CALL_AUDIO_CONSTRAINTS;
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasDefault = devices.some((d) => d.kind === 'audioinput' && d.deviceId === 'default');
    if (hasDefault) {
      return { audio: { deviceId: { exact: 'default' } }, video: false };
    }
  } catch {}
  return CALL_AUDIO_CONSTRAINTS;
}

/**
 * 통화 시작(발신/수신) 마이크 스트림 획득 헬퍼
 * - OS 기본 장치(default) 우선 시도 및 실패 시 기본 조건 재시도
 */
async function acquireCallStartMic() {
  const constraints = await getPreferredMicConstraints();
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (e) {
    if (constraints !== CALL_AUDIO_CONSTRAINTS &&
        (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
      if (import.meta.env.DEV) console.log('[마이크 잡음] default 지정 실패 → 기본 방식 재시도:', e.name);
      return await navigator.mediaDevices.getUserMedia(CALL_AUDIO_CONSTRAINTS);
    }
    throw e;
  }
}

/**
 * [선장 검증용 임시 로그 - 추후 제거 대상]
 * 통화가 connected 상태가 된 직후 getStats()로 선택된 candidate pair 타입을 1회 조회하여 콘솔에 1줄 출력
 */
const logSelectedCandidatePair = async (peerConnection, hasLoggedRef) => {
  if (!peerConnection || hasLoggedRef.current) return;
  const checkStats = async () => {
    if (!peerConnection || hasLoggedRef.current) return;
    try {
      const stats = await peerConnection.getStats();
      let selectedPair = null;
      stats.forEach((report) => {
        if (report.type === 'transport' && report.selectedCandidatePairId) {
          selectedPair = stats.get(report.selectedCandidatePairId);
        }
        if (!selectedPair && report.type === 'candidate-pair' && (report.selected || report.nominated)) {
          selectedPair = report;
        }
      });

      if (selectedPair) {
        const localCandidate = selectedPair.localCandidateId ? stats.get(selectedPair.localCandidateId) : null;
        const remoteCandidate = selectedPair.remoteCandidateId ? stats.get(selectedPair.remoteCandidateId) : null;
        const localType = localCandidate?.candidateType || localCandidate?.type || '-';
        const remoteType = remoteCandidate?.candidateType || remoteCandidate?.type || '-';
        const proto = (localCandidate?.protocol || remoteCandidate?.protocol || localCandidate?.relayProtocol || remoteCandidate?.relayProtocol || '-').toLowerCase();
        hasLoggedRef.current = true;
        console.log(`[통화 경로] ${localType}<->${remoteType}(${proto})`);
      }
    } catch (e) {}
  };

  await checkStats();
  if (!hasLoggedRef.current) {
    setTimeout(checkStats, 500);
  }
};

/**
 * [결함 ④ 진단] 개발 모드 전용 ICE 후보 종류 및 프로토콜 파싱 헬퍼
 * IP 주소 원문은 일체 출력하지 않고 type 및 protocol 만 추출 (RFC 5245/8445)
 */
function parseCandidateTypeAndProto(candidateObj) {
  let type = 'unknown';
  let proto = 'unknown';
  if (!candidateObj) return { type, proto };

  if (typeof candidateObj.type === 'string' && candidateObj.type) {
    type = candidateObj.type.toLowerCase();
  }
  if (typeof candidateObj.protocol === 'string' && candidateObj.protocol) {
    proto = candidateObj.protocol.toLowerCase();
  }

  const candStr = typeof candidateObj.candidate === 'string'
    ? candidateObj.candidate
    : (typeof candidateObj === 'string' ? candidateObj : '');

  if (type === 'unknown' && candStr) {
    const match = candStr.match(/typ\s+(\w+)/i);
    if (match) type = match[1].toLowerCase();
  }
  if (proto === 'unknown' && candStr) {
    const match = candStr.match(/\s(udp|tcp)\s/i);
    if (match) proto = match[1].toLowerCase();
  }
  return { type, proto };
}

/**
 * [결함 ④ 작업 2-A 보강] 후보 객체에서 ICE usernameFragment(ufrag) 표식 추출
 * @param {RTCIceCandidateInit|RTCIceCandidate|object} c
 * @returns {string|null}
 */
function getCandidateUfrag(c) {
  if (!c) return null;
  if (typeof c.usernameFragment === 'string' && c.usernameFragment) {
    return c.usernameFragment;
  }
  const candStr = typeof c.candidate === 'string'
    ? c.candidate
    : (typeof c === 'string' ? c : '');
  const match = candStr.match(/ ufrag (\S+)/);
  if (match && match[1]) {
    return match[1];
  }
  return null;
}

/**
 * [결함 ④ 작업 2-A 보강] RTCPeerConnection의 remoteDescription SDP에서 모든 'a=ice-ufrag:' 값을 Set으로 추출
 * @param {RTCPeerConnection|null} pc
 * @returns {Set<string>}
 */
function getRemoteUfrags(pc) {
  const set = new Set();
  const sdp = pc?.remoteDescription?.sdp;
  if (typeof sdp !== 'string' || !sdp) return set;
  const matches = sdp.matchAll(/^a=ice-ufrag:(\S+)/gmi);
  for (const m of matches) {
    if (m[1]) set.add(m[1].trim());
  }
  return set;
}

/**
 * [결함 ④ 작업 2-A 보강 2] SDP 문자열에서 모든 a=candidate 줄을 꺼내 m-섹션 mid와 mLineIndex를 포함한 후보 목록 생성
 * @param {string} sdpString
 * @returns {Array<{ candidate: string, sdpMid: string|null, sdpMLineIndex: number }>}
 */
function extractCandidatesFromSdp(sdpString) {
  if (!sdpString || typeof sdpString !== 'string') return [];
  const lines = sdpString.split(/\r?\n/);
  const candidates = [];
  let currentMLineIndex = -1;
  let currentMid = null;

  for (const line of lines) {
    if (line.startsWith('m=')) {
      currentMLineIndex += 1;
      currentMid = null;
    } else if (line.startsWith('a=mid:')) {
      currentMid = line.slice(6).trim();
    } else if (line.startsWith('a=candidate:')) {
      const candidateStr = line.slice(2); // 'candidate:...' 형태로 RTCIceCandidateInit 규격 준수
      candidates.push({
        candidate: candidateStr,
        sdpMid: currentMid,
        sdpMLineIndex: currentMLineIndex >= 0 ? currentMLineIndex : 0,
      });
    }
  }
  return candidates;
}

/**
 * [결함 ④ 작업 2-A 보강 5] SDP 텍스트 내 a=candidate 줄에서 후보 유형별 및 망 종류(network-cost) 개수 집계
 * @param {string|object|null} sdpInput
 * @returns {{ host: number, hostWifi: number, hostCell: number, hostOther: number, srflx: number, relay: number, prflx: number, total: number }}
 */
function countCandidatesInSdp(sdpInput) {
  const counts = { host: 0, hostWifi: 0, hostCell: 0, hostOther: 0, srflx: 0, relay: 0, prflx: 0, total: 0 };
  const sdpText = typeof sdpInput === 'string' ? sdpInput : sdpInput?.sdp;
  if (!sdpText || typeof sdpText !== 'string') return counts;

  const lines = sdpText.split(/\r?\n/);
  for (const line of lines) {
    if (line.startsWith('a=candidate:')) {
      counts.total += 1;
      const { type } = parseCandidateTypeAndProto(line);
      const costMatch = line.match(/network-cost\s+(\d+)/i);
      const cost = costMatch ? parseInt(costMatch[1], 10) : null;
      if (type === 'host') {
        counts.host += 1;
        if (cost === 10) counts.hostWifi += 1;
        else if (cost === 900) counts.hostCell += 1;
        else counts.hostOther += 1;
      } else if (type === 'srflx') {
        counts.srflx += 1;
      } else if (type === 'relay') {
        counts.relay += 1;
      } else if (type === 'prflx') {
        counts.prflx += 1;
      }
    }
  }
  return counts;
}

/**
 * [결함 ④ 작업 2-A 보강 5] 내 후보 로그 출력 문자열 포맷팅 (망 구분 포함)
 * 예: host 6(wifi 3, cell 3) / srflx 0 / relay 0
 */
function formatCandidateCounts(counts) {
  const hostDesc = (counts.hostWifi > 0 || counts.hostCell > 0 || counts.hostOther > 0)
    ? `${counts.host}(wifi ${counts.hostWifi}, cell ${counts.hostCell}${counts.hostOther > 0 ? `, 기타 ${counts.hostOther}` : ''})`
    : `${counts.host}`;
  return `host ${hostDesc} / srflx ${counts.srflx} / relay ${counts.relay}${counts.relay === 0 ? ' (relay 없음 경고)' : ''}`;
}

/**
 * [결함 ④ 작업 3 2단계] ICE 통화 길 막힘 순수 판정 함수
 * @param {object} params
 * @param {number} params.host
 * @param {number} params.srflx
 * @param {number} params.relay
 * @param {boolean} params.complete
 * @param {number} params.stunCount
 * @param {number} params.turnCount
 * @param {boolean} params.forceRelay
 * @param {boolean} params.online
 * @returns {{ status: '막힘' | '막힘의심' | '정상' | '판정제외', detail: string }}
 */
function judgeIceRoadblock({ host, srflx, relay, complete, stunCount, turnCount, forceRelay, online }) {
  void stunCount;
  if (online === false) {
    return { status: '판정제외', detail: '오프라인' };
  }
  if (forceRelay === true) {
    return { status: '판정제외', detail: 'relay 강제 모드' };
  }
  if (turnCount === 0) {
    return { status: '판정제외', detail: 'TURN 0개(폴백) — 공인 IP 망에서 srflx 생략 가능' };
  }
  if (host === 0) {
    return { status: '판정제외', detail: 'host 0' };
  }
  if (srflx === 0 && relay === 0) {
    return {
      status: complete ? '막힘' : '막힘의심',
      detail: complete ? '외부 후보 0개' : '4초 시간초과 — 외부 후보 미도착',
    };
  }
  return { status: '정상', detail: '정상' };
}

/**
 * [결함 ④ 작업 3 2단계] RTCPeerConnection 설정에서 STUN/TURN 주소 개수 및 relay 모드 확인 보조 함수
 * @param {RTCPeerConnection|null} pc
 * @returns {{ stunCount: number, turnCount: number, forceRelay: boolean }}
 */
function getIceConfigSummary(pc) {
  if (!pc || typeof pc.getConfiguration !== 'function') {
    return { stunCount: -1, turnCount: -1, forceRelay: false };
  }
  try {
    const config = pc.getConfiguration();
    const iceServers = config?.iceServers;
    let stunCount = 0;
    let turnCount = 0;
    if (Array.isArray(iceServers)) {
      iceServers.forEach((item) => {
        const urls = Array.isArray(item?.urls) ? item.urls : [item?.urls];
        urls.forEach((u) => {
          if (typeof u === 'string') {
            const lower = u.toLowerCase();
            if (lower.startsWith('turn:') || lower.startsWith('turns:')) {
              turnCount += 1;
            } else if (lower.startsWith('stun:') || lower.startsWith('stuns:')) {
              stunCount += 1;
            }
          }
        });
      });
    } else {
      stunCount = -1;
      turnCount = -1;
    }
    const forceRelay = config?.iceTransportPolicy === 'relay';
    return { stunCount, turnCount, forceRelay };
  } catch {
    return { stunCount: -1, turnCount: -1, forceRelay: false };
  }
}

// [PWA전용] 네이티브 대체: 앱 전환 후 7걸음 실기에서 막힘이 없으면 앱에서 끔(NATIVE_MIGRATION §4 1번)
const CALL_RECOVERY_STORAGE_KEY = 'moowoo_call_recovery';
const CALL_RECOVERY_USES_STORAGE_KEY = 'moowoo_call_recovery_uses';
const CALL_RECOVERY_VALID_MS = 20000;
const MAX_AUTO_RELOADS_PER_CALL = 2;

function getAutoReloadCount(uses) {
  if (!uses) return 0;
  if (typeof uses.auto_reload_count === 'number') return uses.auto_reload_count;
  return uses.auto_reload_used ? 1 : 0;
}

function writeCallRecoveryInfo(info) {
  let sessionSuccess = false;
  let localSuccess = false;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(CALL_RECOVERY_STORAGE_KEY, JSON.stringify(info));
      const verify = window.sessionStorage.getItem(CALL_RECOVERY_STORAGE_KEY);
      if (verify) sessionSuccess = true;
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[복구 준비] sessionStorage 저장 실패:', e);
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(CALL_RECOVERY_STORAGE_KEY, JSON.stringify(info));
      const verify = window.localStorage.getItem(CALL_RECOVERY_STORAGE_KEY);
      if (verify) localSuccess = true;
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[복구 준비] localStorage 저장 실패:', e);
  }
  return sessionSuccess || localSuccess;
}

function readValidCallRecoveryInfo(callId = null) {
  const parseAndValidate = (raw) => {
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (data.v !== 1) return null;
      if (callId && data.callId !== callId) return null;
      const age = Date.now() - (data.savedAt || 0);
      if (age < 0 || age > CALL_RECOVERY_VALID_MS) return null;
      return data;
    } catch {
      return null;
    }
  };

  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const fromSession = parseAndValidate(window.sessionStorage.getItem(CALL_RECOVERY_STORAGE_KEY));
      if (fromSession) return fromSession;
    }
  } catch {}

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const fromLocal = parseAndValidate(window.localStorage.getItem(CALL_RECOVERY_STORAGE_KEY));
      if (fromLocal) return fromLocal;
    }
  } catch {}

  return null;
}

/**
 * [결함 ④ 5-3 작업② 보정] 첫 렌더 전 동기 판정 전용 — 현재 탭의 sessionStorage만 판정 (새 탭/다른 탭 오작동 방지)
 */
function readSessionCallRecoveryInfo(callId = null) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(CALL_RECOVERY_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.v !== 1) return null;
    if (callId && data.callId !== callId) return null;
    const age = Date.now() - (data.savedAt || 0);
    if (age < 0 || age > CALL_RECOVERY_VALID_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function clearCallRecoveryInfo() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(CALL_RECOVERY_STORAGE_KEY);
    }
  } catch {}
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(CALL_RECOVERY_STORAGE_KEY);
    }
  } catch {}
}

function readCallRecoveryUses(callId = null) {
  const parseAndValidate = (raw) => {
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      if (callId && data.call_id !== callId) return null;
      return data;
    } catch {
      return null;
    }
  };

  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const fromSession = parseAndValidate(window.sessionStorage.getItem(CALL_RECOVERY_USES_STORAGE_KEY));
      if (fromSession) return fromSession;
    }
  } catch {}

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const fromLocal = parseAndValidate(window.localStorage.getItem(CALL_RECOVERY_USES_STORAGE_KEY));
      if (fromLocal) return fromLocal;
    }
  } catch {}

  return null;
}

function markAutoReloadUsed(callId) {
  const prev = readCallRecoveryUses(callId) || { call_id: callId, auto_reload_count: 0, auto_reload_used: false, button_used: false };
  const next = {
    ...prev,
    call_id: callId,
    auto_reload_count: getAutoReloadCount(prev) + 1,
    auto_reload_used: true,
  };
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem(CALL_RECOVERY_USES_STORAGE_KEY, JSON.stringify(next));
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[복구 준비] sessionStorage 횟수 저장 실패:', e);
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(CALL_RECOVERY_USES_STORAGE_KEY, JSON.stringify(next));
    }
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[복구 준비] localStorage 횟수 저장 실패:', e);
  }
}

function clearCallRecoveryUses() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(CALL_RECOVERY_USES_STORAGE_KEY);
    }
  } catch {}
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(CALL_RECOVERY_USES_STORAGE_KEY);
    }
  } catch {}
}

/**
 * [결함 ④ 작업 3 2단계] ICE 통화 길 막힘 감지 판정 및 로깅 통합 함수
 * @param {object} params
 * @param {RTCPeerConnection|null} params.pc
 * @param {{ host: number, srflx: number, relay: number }} params.counts
 * @param {boolean} params.complete
 * @param {string} params.reason
 * @param {string} params.place
 * @param {string} params.role
 * @param {string|number} [params.round]
 * @param {React.MutableRefObject|null} [params.roadblockConfirmedRef]
 * @param {Function} [params.onRoadblockConfirmed]
 * @returns {{ status: '막힘' | '막힘의심' | '정상' | '판정제외', detail: string }}
 */
function logIceRoadblockJudgement({ pc, counts, complete, reason, place, role, round, roadblockConfirmedRef, onRoadblockConfirmed, skipRecheck = false }) {
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
  const { stunCount, turnCount, forceRelay } = getIceConfigSummary(pc);
  const roadblock = judgeIceRoadblock({
    host: counts.host,
    srflx: counts.srflx,
    relay: counts.relay,
    complete,
    stunCount,
    turnCount,
    forceRelay,
    online: isOnline,
  });

  const roundPart = round !== undefined && round !== null ? `, 회차: ${round}` : '';
  console.log(
    `[길 막힘 감지] 판정: ${roadblock.status} — host ${counts.host} / srflx ${counts.srflx} / relay ${counts.relay}, 온라인: ${isOnline ? '예' : '아니오'}, 완료이유: ${reason}, STUN ${stunCount} / TURN ${turnCount}, 위치: ${place}, 역할: ${role}${roundPart} (${roadblock.detail})`
  );

  const executeRoadblockConfirmed = (logMessage) => {
    if (logMessage) console.log(logMessage);
    if (roadblockConfirmedRef) {
      roadblockConfirmedRef.current = { place, round: round ?? null, at: Date.now() };
    }
    if (typeof onRoadblockConfirmed === 'function') {
      try {
        onRoadblockConfirmed({ place, round: round ?? null });
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[복구 준비] onRoadblockConfirmed 콜백 예외:', e);
      }
    }
  };

  if (roadblock.status === '막힘') {
    executeRoadblockConfirmed();
  }

  if (roadblock.status === '막힘의심') {
    if (skipRecheck) {
      executeRoadblockConfirmed(
        `[길 막힘 감지] 판정: 막힘 확정 — 망 변경 복구 중 재집계 생략, 위치: ${place}${roundPart}`
      );
    } else {
      const targetPc = pc;
      setTimeout(() => {
        if (!targetPc || targetPc.signalingState === 'closed') return;
        const reCounts = countCandidatesInSdp(targetPc.localDescription);
        const hasOuter = (reCounts.srflx > 0 || reCounts.relay > 0);
        console.log(
          `[길 막힘 감지] 4초 뒤 재집계 — host ${reCounts.host} / srflx ${reCounts.srflx} / relay ${reCounts.relay}, 바깥길 생김: ${hasOuter ? '예' : '아니오'}, 위치: ${place}`
        );
        if (!hasOuter) {
          executeRoadblockConfirmed(
            `[길 막힘 감지] 판정: 막힘 확정 — 4초 재집계 후에도 외부 후보 0개, 위치: ${place}${roundPart}`
          );
        }
      }, 4000);
    }
  }

  return roadblock;
}

/**
 * [결함 ④ 작업 2-A 보강 5] icecandidateerror 이벤트의 url에서 출입증/IP를 제외하고 scheme:도메인:포트(전송방식)만 안전 추출
 */
function formatIceErrorTarget(url) {
  if (!url || typeof url !== 'string') return 'none';
  try {
    const protoMatch = url.match(/transport=(\w+)/i);
    const proto = protoMatch ? protoMatch[1].toLowerCase() : 'udp';
    const cleanUrl = url.replace(/^(turn|turns|stun):/, 'http://');
    const parsed = new URL(cleanUrl);
    const scheme = url.split(':')[0];
    const port = parsed.port || '3478';
    return `${scheme}:${parsed.hostname}:${port}(${proto})`;
  } catch {
    return url.split('?')[0].replace(/\/\/.*@/, '//');
  }
}

/**
 * [결함 ④ 작업 2-A 보강 6] 새 연결 성공 시 최종 선택된 candidate-pair 경로 1줄 출력 (운영 빌드 포함)
 * IP 주소 원문 출력 금지
 */
async function logRebuildSelectedPair(pc, elapsedMs) {
  if (!pc) return;
  const check = async () => {
    try {
      const stats = await pc.getStats();
      let selectedPair = null;
      stats.forEach((report) => {
        if (report.type === 'transport' && report.selectedCandidatePairId) {
          selectedPair = stats.get(report.selectedCandidatePairId);
        }
        if (!selectedPair && report.type === 'candidate-pair' && (report.selected || report.nominated)) {
          selectedPair = report;
        }
      });
      if (selectedPair) {
        const local = selectedPair.localCandidateId ? stats.get(selectedPair.localCandidateId) : null;
        const remote = selectedPair.remoteCandidateId ? stats.get(selectedPair.remoteCandidateId) : null;
        const localType = local?.candidateType || local?.type || '-';
        const remoteType = remote?.candidateType || remote?.type || '-';
        const proto = (local?.protocol || remote?.protocol || local?.relayProtocol || remote?.relayProtocol || '-').toLowerCase();
        console.log(`[재연결] 새 연결 성공 — 경로: ${localType}<->${remoteType}(${proto}), 소요: ${elapsedMs}ms`);
        return true;
      }
    } catch {
      /* 의도적 무시 */
    }
    return false;
  };
  const found = await check();
  if (!found) {
    setTimeout(check, 500);
  }
}

/**
 * [결함 ④ 작업 2-A 보강 4 / 5-3 C안] 새 연결 수립 시 ICE 후보 수집 완료 대기 헬퍼
 * (icegatheringstatechange -> complete 또는 null icecandidate 이벤트 감지 시 즉시 해결)
 * - 이전 세션의 잔류 'complete' 오판정 방지: 진입 시점의 complete는 즉시 완료로 인정하지 않음
 * - 완료 인정 조건:
 *   (가) 함수 진입 후 'gathering' 전이를 관측했고 그 뒤 'complete' 전이 관측 ('상태전이')
 *   (나) 함수 진입 후 onicecandidate의 null 후보(수집 끝 신호) 관측 ('null후보')
 *   (다) options.earlyFinishOnRelay 지정 시 첫 relay 후보 감지 후 300ms 조기 완료 ('relay조기완료')
 * @param {RTCPeerConnection} pc
 * @param {number} [timeoutMs=4000]
 * @param {object|null} [options=null]
 * @param {boolean} [options.earlyFinishOnRelay=false] - relay 후보 확보 시 300ms 후 조기 완료 여부
 * @returns {Promise<{ complete: boolean, waitedMs: number, reason: '상태전이' | 'null후보' | '시간초과' | 'relay조기완료' }>}
 */
function waitForIceGatheringComplete(pc, timeoutMs = 4000, options = null) {
  if (!pc) return Promise.resolve({ complete: false, waitedMs: 0, reason: '시간초과' });

  const startAt = Date.now();
  return new Promise((resolve) => {
    let timer = null;
    let relayWaitTimer = null;
    let isSettled = false;
    let sawGathering = pc.iceGatheringState === 'gathering';

    const finish = (complete, reason) => {
      if (isSettled) return;
      isSettled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (relayWaitTimer) {
        clearTimeout(relayWaitTimer);
        relayWaitTimer = null;
      }
      try {
        pc.removeEventListener('icegatheringstatechange', onGatheringChange);
        pc.removeEventListener('icecandidate', onIceCandidate);
      } catch {
        /* 의도적 무시 */
      }
      resolve({ complete, waitedMs: Date.now() - startAt, reason });
    };

    const onGatheringChange = () => {
      if (pc.iceGatheringState === 'gathering') {
        sawGathering = true;
      } else if (pc.iceGatheringState === 'complete') {
        if (sawGathering) {
          finish(true, '상태전이');
        }
      }
    };

    const onIceCandidate = (event) => {
      if (!event.candidate) {
        finish(true, 'null후보');
        return;
      }
      if (options?.earlyFinishOnRelay && event.candidate.type === 'relay') {
        // [결함 ④ 5-3 작업②] immediateRelay 지정 시 지연 없이 즉시 종료
        if (options?.immediateRelay) {
          const waited = Date.now() - startAt;
          console.log(`[재연결] 후보 수집 즉시 종료 — relay 확보 ${waited}ms`);
          finish(true, 'relay조기완료');
          return;
        }
        if (!relayWaitTimer) {
          relayWaitTimer = setTimeout(() => {
            const waited = Date.now() - startAt;
            console.log(`[재연결] 후보 수집 조기 종료 — relay 확보 ${waited}ms`);
            finish(true, 'relay조기완료');
          }, 300);
        }
      }
    };

    try {
      pc.addEventListener('icegatheringstatechange', onGatheringChange);
      pc.addEventListener('icecandidate', onIceCandidate);
    } catch {
      finish(false, '시간초과');
      return;
    }

    // [결함 ④ 5-3 작업②] 진입 시점에 이미 relay가 확보되어 있고 immediateRelay인 경우 즉시 완료
    if (options?.earlyFinishOnRelay && options?.immediateRelay && pc.localDescription) {
      const initCounts = countCandidatesInSdp(pc.localDescription);
      if (initCounts.relay >= 1) {
        const waited = Date.now() - startAt;
        console.log(`[재연결] 후보 수집 즉시 종료 — 기존 relay 존재 ${waited}ms`);
        finish(true, 'relay조기완료');
        return;
      }
    }

    timer = setTimeout(() => {
      finish(false, '시간초과');
    }, timeoutMs);
  });
}

/**
 * [결함 ④ 5-3 C안] 통화 중 새로고침 없는 빠른 연결 길 진단
 * 외부 후보(srflx 또는 relay)가 1개 생기면 즉시 '정상'으로 조기 종료, 없으면 최대 timeoutMs 뒤 '막힘' 판정.
 * @param {number} [timeoutMs=4000]
 * @returns {Promise<{ status: '정상' | '막힘', reason: string, waitedMs: number, counts: { host: number, srflx: number, relay: number } }>}
 */
function probeIceCandidatesQuick(timeoutMs = 4000, options = {}) {
  const startAt = Date.now();
  let testPc = null;
  let settled = false;
  let counts = { host: 0, srflx: 0, relay: 0 };

  return new Promise((resolve) => {
    let timer = null;

    const finish = (status, reason = '알수없음') => {
      if (settled) return;
      settled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (testPc) {
        try {
          testPc.removeEventListener('icegatheringstatechange', onGatheringChange);
          testPc.onicecandidate = null;
          testPc.close();
        } catch {}
      }
      resolve({ status, reason, waitedMs: Date.now() - startAt, counts });
    };

    const onGatheringChange = () => {
      if (!testPc) return;
      if (testPc.iceGatheringState === 'complete') {
        if (testPc.localDescription) {
          counts = countCandidatesInSdp(testPc.localDescription);
        }
        // [결함 ④ 5-3 작업②] 후보 수집 완료 시 외부 후보(srflx/relay) 없으면 4초 대기 없이 즉시 막힘 판정
        if (counts.srflx >= 1 || counts.relay >= 1) {
          finish('정상', '수집완료');
        } else {
          finish('막힘', '수집완료');
        }
      }
    };

    (async () => {
      try {
        const iceServers = await getIceServers();
        testPc = new RTCPeerConnection({ iceServers });
        testPc.addTransceiver('audio', { direction: 'recvonly' });

        testPc.addEventListener('icegatheringstatechange', onGatheringChange);

        testPc.onicecandidate = (event) => {
          if (event.candidate) {
            const candType = event.candidate.type;
            if (testPc.localDescription) {
              counts = countCandidatesInSdp(testPc.localDescription);
            } else if (candType) {
              counts[candType] = (counts[candType] || 0) + 1;
            }
            if (options.requireRelay) {
              if (counts.relay >= 1 || candType === 'relay') {
                finish('정상', '조기완료');
              }
            } else {
              if (candType === 'srflx' || candType === 'relay') {
                finish('정상', `${candType}조기완료`);
              }
            }
          } else {
            // null 후보 (수집 완료)
            if (testPc.localDescription) {
              counts = countCandidatesInSdp(testPc.localDescription);
            }
            // [결함 ④ 5-3 작업②] 수집 끝났는데 srflx 0 · relay 0이면 4초 재집계 없이 곧바로 새로고침 경로로
            if (counts.srflx >= 1 || counts.relay >= 1) {
              finish('정상', 'null후보');
            } else {
              finish('막힘', 'null후보');
            }
          }
        };

        const offer = await testPc.createOffer();
        await testPc.setLocalDescription(offer);

        counts = countCandidatesInSdp(testPc.localDescription);
        const hasEarlyCandidate = options.requireRelay
          ? counts.relay >= 1
          : (counts.srflx >= 1 || counts.relay >= 1);
        if (hasEarlyCandidate) {
          finish('정상', options.requireRelay ? '조기완료' : 'offer설정시외부후보');
          return;
        }

        // [결함 ④ 5-3 작업②] offer 설정 시점에 이미 complete인 경우 외부 후보 없으면 즉시 판정
        if (testPc.iceGatheringState === 'complete') {
          finish('막힘', 'offer설정시수집완료');
          return;
        }

        timer = setTimeout(() => {
          if (testPc.localDescription) {
            counts = countCandidatesInSdp(testPc.localDescription);
          }
          if (counts.srflx >= 1 || counts.relay >= 1) {
            finish('정상', '시간초과');
          } else {
            finish('막힘', '시간초과');
          }
        }, timeoutMs);
      } catch (err) {
        console.warn('[복구 C안] 진단 예외 발생 (막힘 처리):', err);
        finish('막힘', '예외발생');
      }
    })();
  });
}

/**
 * [결함 ④ 5-3 C안] 수신 오디오 packetsReceived 통계 조회
 * @param {RTCPeerConnection|null} pc
 * @returns {Promise<number>}
 */
async function getAudioPacketsReceived(pc) {
  if (!pc || typeof pc.getStats !== 'function') return 0;
  try {
    const stats = await pc.getStats();
    for (const report of stats.values()) {
      if (report.type === 'inbound-rtp' && (report.kind === 'audio' || report.mediaType === 'audio')) {
        return report.packetsReceived || 0;
      }
    }
  } catch {}
  return 0;
}

/**
 * [결함 ④ 작업 2-A] 재연결 타임아웃 시 candidate-pair 상태별 개수 집계 및 운영 진단 출력
 * @param {RTCPeerConnection|null} pc
 */
async function logRestartCandidatePairsDiagnostic(pc) {
  if (!pc || !pc.getStats) return;
  try {
    const stats = await pc.getStats();
    let total = 0;
    let succeeded = 0;
    let inProgress = 0;
    let failed = 0;
    let waiting = 0;

    stats.forEach((report) => {
      if (report.type === 'candidate-pair') {
        total += 1;
        const st = report.state;
        if (st === 'succeeded') succeeded += 1;
        else if (st === 'in-progress' || st === 'inprogress') inProgress += 1;
        else if (st === 'failed') failed += 1;
        else if (st === 'waiting') waiting += 1;
      }
    });

    console.log(`[재연결] ICE 쌍 진단 — 총 ${total}개 (succeeded: ${succeeded}, in-progress: ${inProgress}, failed: ${failed}, waiting: ${waiting})`);
  } catch {
    /* 진단 출력 실패 무시 */
  }
}

/**
 * [결함 ④ 작업 2-A 재시험] 재연결 성공 보조 판정을 위한 오디오 패킷 수 및 후보 쌍 통계 수집 헬퍼
 * @param {RTCPeerConnection|null} pc
 * @returns {Promise<{ audioPackets: number, selectedPairId: string|null, pairBytes: number, pairState: string|null }|null>}
 */
async function getAudioAndPairStats(pc) {
  if (!pc || !pc.getStats) return null;
  try {
    const stats = await pc.getStats();
    let audioPackets = 0;
    let selectedPairId = null;
    let pairBytes = 0;
    let pairBytesReceived = 0;
    let pairState = null;

    stats.forEach((report) => {
      if (report.type === 'inbound-rtp' && report.kind === 'audio') {
        audioPackets = report.packetsReceived || 0;
      }
      if (report.type === 'transport' && report.selectedCandidatePairId) {
        selectedPairId = report.selectedCandidatePairId;
      }
    });

    if (selectedPairId) {
      const pair = stats.get(selectedPairId);
      if (pair) {
        pairBytes = (pair.bytesReceived || 0) + (pair.bytesSent || 0);
        pairBytesReceived = pair.bytesReceived || 0;
        pairState = pair.state || null;
      }
    } else {
      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && (report.selected || report.nominated)) {
          selectedPairId = report.id;
          pairBytes = (report.bytesReceived || 0) + (report.bytesSent || 0);
          pairBytesReceived = report.bytesReceived || 0;
          pairState = report.state || null;
        }
      });
    }

    return { audioPackets, selectedPairId, pairBytes, pairBytesReceived, pairState };
  } catch {
    return null;
  }
}

function recordCandidateStat(statsRef, candidateObj) {
  if (!import.meta.env.DEV || !statsRef?.current) return;
  const { type, proto } = parseCandidateTypeAndProto(candidateObj);
  const validTypes = ['host', 'srflx', 'relay', 'prflx'];
  const targetType = validTypes.includes(type) ? type : 'other';

  if (!statsRef.current[targetType]) {
    statsRef.current[targetType] = { udp: 0, tcp: 0, total: 0 };
  }
  statsRef.current[targetType].total += 1;
  if (proto === 'udp') statsRef.current[targetType].udp += 1;
  else if (proto === 'tcp') statsRef.current[targetType].tcp += 1;
}

function formatCandidateSummary(stats) {
  if (!stats) return '';
  const parts = [];
  ['host', 'srflx', 'relay', 'prflx'].forEach((k) => {
    const item = stats[k] || { udp: 0, tcp: 0, total: 0 };
    const sub = [];
    if (item.udp > 0) sub.push(`udp ${item.udp}`);
    if (item.tcp > 0) sub.push(`tcp ${item.tcp}`);
    const subStr = sub.length > 0 ? ` (${sub.join(', ')})` : '';
    parts.push(`${k} ${item.total}${subStr}`);
  });
  if (stats.other?.total > 0) {
    parts.push(`other ${stats.other.total}`);
  }
  return parts.join(' / ');
}

async function dumpCandidatePairs(pc, reason, dumpCountRef, maxLimit = 2) {
  if (!import.meta.env.DEV || !pc || !dumpCountRef) return;
  if (dumpCountRef.current >= maxLimit) return;
  dumpCountRef.current += 1;

  try {
    const stats = await pc.getStats();
    const pairs = [];
    stats.forEach((report) => {
      if (report.type === 'candidate-pair') {
        const local = report.localCandidateId ? stats.get(report.localCandidateId) : null;
        const remote = report.remoteCandidateId ? stats.get(report.remoteCandidateId) : null;
        const localType = local?.candidateType || local?.type || 'unknown';
        const remoteType = remote?.candidateType || remote?.type || 'unknown';
        pairs.push({
          state: report.state,
          nominated: Boolean(report.nominated),
          localType,
          remoteType,
          requestsSent: report.requestsSent || 0,
          responsesReceived: report.responsesReceived || 0,
        });
      }
    });

    console.log(`[ICE 쌍 덤프] (${reason}, ${dumpCountRef.current}/${maxLimit}회) 총 ${pairs.length}개 쌍:`);
    if (pairs.length === 0) {
      console.log('  (생성된 candidate-pair 없음)');
    } else {
      pairs.forEach((p, idx) => {
        console.log(
          `  [${idx + 1}] ${p.localType} <-> ${p.remoteType} | 상태: ${p.state} | 지명: ${p.nominated} | 요청: ${p.requestsSent} / 응답: ${p.responsesReceived}`
        );
      });
    }
  } catch (err) {
    console.warn('[ICE 쌍 덤프] getStats 실패:', err);
  }
}

async function logCandidatePairCount(pc, elapsed) {
  if (!import.meta.env.DEV || !pc) return;
  try {
    const stats = await pc.getStats();
    let pairCount = 0;
    stats.forEach((report) => {
      if (report.type === 'candidate-pair') {
        pairCount += 1;
      }
    });
    console.log(`[ICE 쌍 수] 총 ${pairCount}개 | 연결까지 ${elapsed}ms`);
  } catch (err) {
    console.warn('[ICE 쌍 수] getStats 실패:', err);
  }
}

function logLocalCandidatesSummary(localCandidatesStatsRef, localCandidatesLoggedRef, excludedCountRef = null) {
  if (!import.meta.env.DEV || !localCandidatesStatsRef || !localCandidatesLoggedRef) return;
  if (localCandidatesLoggedRef.current) return;
  localCandidatesLoggedRef.current = true;
  const excludedText = ` (전송 제외 host tcp ${excludedCountRef?.current || 0})`;
  console.log('[ICE 내 후보]', formatCandidateSummary(localCandidatesStatsRef.current) + excludedText);
}

function logRemoteCandidatesSummary(remoteCandidatesStatsRef, remoteCandidatesLoggedRef, trigger, excludedCountRef = null) {
  if (!import.meta.env.DEV || !remoteCandidatesStatsRef || !remoteCandidatesLoggedRef) return;
  if (remoteCandidatesLoggedRef.current) return;
  remoteCandidatesLoggedRef.current = true;
  const excludedText = ` (수신 제외 host tcp ${excludedCountRef?.current || 0})`;
  console.log(`[ICE 상대 후보] (${trigger})`, formatCandidateSummary(remoteCandidatesStatsRef.current) + excludedText);
}

/**
 * 비디오 트랙의 실제 화면 출력(화소 변화 및 밝기)을 측정하여 정상 여부 판정 (B-2 단위 1-A)
 * @param {MediaStreamTrack} videoTrack 
 * @param {() => boolean} isAliveFn 
 * @returns {Promise<{ status: 'ok' | 'black' | 'frozen' | 'aborted', brightness: number, diff: number, retried: boolean }>}
 */
async function inspectVideoTrackLiveness(videoTrack, isAliveFn) {
  const checkShouldAbort = () => {
    if (!videoTrack || videoTrack.readyState !== 'live' || videoTrack.muted === true) return true;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return true;
    if (!isAliveFn || !isAliveFn()) return true;
    return false;
  };

  if (checkShouldAbort()) {
    return { status: 'aborted', brightness: 0, diff: 0, retried: false };
  }

  let tempVideo = null;
  try {
    tempVideo = document.createElement('video');
    tempVideo.muted = true;
    tempVideo.playsInline = true;
    tempVideo.srcObject = new MediaStream([videoTrack]);
    await tempVideo.play().catch(() => {});

    // 첫 프레임 수신 대기 (requestVideoFrameCallback 우선, 없으면 loadeddata, 최대 1500ms 타임아웃)
    await new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (!done) {
          done = true;
          resolve();
        }
      };
      if ('requestVideoFrameCallback' in tempVideo) {
        tempVideo.requestVideoFrameCallback(finish);
      } else {
        tempVideo.onloadeddata = finish;
      }
      setTimeout(finish, 1500);
    });

    if (checkShouldAbort()) {
      return { status: 'aborted', brightness: 0, diff: 0, retried: false };
    }

    // 첫 프레임 수신 후 1000ms 대기
    await new Promise((res) => setTimeout(res, 1000));
    if (checkShouldAbort()) {
      return { status: 'aborted', brightness: 0, diff: 0, retried: false };
    }

    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 24;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return { status: 'aborted', brightness: 0, diff: 0, retried: false };
    }

    const measureOnce = async () => {
      if (checkShouldAbort()) return null;
      ctx.drawImage(tempVideo, 0, 0, 32, 24);
      const img1 = ctx.getImageData(0, 0, 32, 24).data;
      const totalPixels = 32 * 24;
      const pix1 = new Float32Array(totalPixels);
      for (let i = 0, p = 0; i < img1.length; i += 4, p += 1) {
        pix1[p] = (img1[i] + img1[i + 1] + img1[i + 2]) / 3;
      }

      await new Promise((res) => setTimeout(res, 1000));
      if (checkShouldAbort()) return null;

      ctx.drawImage(tempVideo, 0, 0, 32, 24);
      const img2 = ctx.getImageData(0, 0, 32, 24).data;
      let sum2 = 0;
      let sumDiff = 0;
      for (let i = 0, p = 0; i < img2.length; i += 4, p += 1) {
        const lum = (img2[i] + img2[i + 1] + img2[i + 2]) / 3;
        sum2 += lum;
        sumDiff += Math.abs(lum - pix1[p]);
      }
      const b2 = sum2 / totalPixels;
      const diff = sumDiff / totalPixels;

      let st = 'ok';
      if (b2 < 5) st = 'black';
      else if (diff < 0.1) st = 'frozen';

      return { status: st, brightness: b2, diff };
    };

    const sample1 = await measureOnce();
    if (!sample1) {
      return { status: 'aborted', brightness: 0, diff: 0, retried: false };
    }

    if (sample1.status === 'ok') {
      return { status: 'ok', brightness: sample1.brightness, diff: sample1.diff, retried: false };
    }

    // 'black' 또는 'frozen'이면 1000ms 뒤 1회 재측정
    await new Promise((res) => setTimeout(res, 1000));
    if (checkShouldAbort()) {
      return { status: 'aborted', brightness: 0, diff: 0, retried: true };
    }

    const sample2 = await measureOnce();
    if (!sample2) {
      return { status: 'aborted', brightness: 0, diff: 0, retried: true };
    }

    return {
      status: sample2.status,
      brightness: sample2.brightness,
      diff: sample2.diff,
      retried: true,
    };
  } catch {
    return { status: 'aborted', brightness: 0, diff: 0, retried: false };
  } finally {
    if (tempVideo) {
      tempVideo.srcObject = null;
      tempVideo = null;
    }
  }
}

const PREFERRED_CAMERA_KEY = 'moowoo_preferred_camera_device_id';
const AUTO_SWITCH_MAX_CANDIDATES = 3;

function getStoredCameraDeviceId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return '';
    return window.localStorage.getItem(PREFERRED_CAMERA_KEY) || '';
  } catch {
    /* 의도적 무시: 저장소 사용 불가 환경 */
    return '';
  }
}

function setStoredCameraDeviceId(deviceId) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (deviceId) {
      window.localStorage.setItem(PREFERRED_CAMERA_KEY, deviceId);
    }
  } catch {
    /* 의도적 무시: 저장소 쓰기 실패 환경 */
  }
}

function clearStoredCameraDeviceId() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(PREFERRED_CAMERA_KEY);
  } catch {
    /* 의도적 무시: 저장소 삭제 실패 환경 */
  }
}

function isLaptopDevice() {
  return isWindows() || isMac();
}

function stopStreamQuietly(stream) {
  if (!stream) return;
  try {
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* 의도적 무시: 개별 트랙 정지 실패 */
      }
    });
  } catch {
    /* 의도적 무시: 스트림 트랙 열거 실패 */
  }
}

/**
 * 통화 유령 방지 및 연결 감시 관련 상수 (보수적 기준)
 */
// 1. WebRTC disconnected 상태 시 일시 회복 대기 유예 시간 (15초)
const DISCONNECT_GRACE_MS = 15000;

// 2. 통화 생존 신호(Heartbeat) 송출 주기 (5초)
const HEARTBEAT_INTERVAL_MS = 5000;

// 3. 통화 생존 신호 누락 타임아웃 판정 (15초)
const HEARTBEAT_TIMEOUT_MS = 15000;

// 4. 발신 전 잔여 ringing 유령 세션 정리 기준 (60초 = 1분)
const STALE_RINGING_TIMEOUT_MS = 60 * 1000;
const STALE_GHOST_CLEANUP_LIMIT = 50; // 받는 쪽 유령 정리 1회 상한(URL 길이 초과 방지)


// 6. 수신자 측 예비 무응답 자동 종료 타임아웃 (35초)
const INCOMING_AUTO_EXPIRE_MS = 35 * 1000;

/**
 * 1:1 음성 통화 엔진 훅 (전역 개인 채널 기반)
 * 
 * @param {Object} params
 * @param {string} params.userId - 현재 로그인 사용자 ID
 * @param {Object} params.myProfile - 내 프로필 정보
 * @param {Function} params.showToast - 토스트 띄우기 함수
 * @param {Function} params.t - 다국어 번역 함수
 * @param {Function} [params.onCallFinished] - 통화 종료 후 이력 갱신 콜백
 */
export function useVoiceCall({
  userId,
  myProfile,
  showToast,
  t,
  onCallFinished,
  onLocalTrackChanged,
  incomingChannelReadyPromiseRef,
}) {
  // [결함 ④ 5-3 작업② 보정] 새로고침 복구 시 첫 렌더 전 동기 복구 정보 확인 (현재 탭 sessionStorage 전용)
  const initialRecoveryInfo = typeof window !== 'undefined' ? readSessionCallRecoveryInfo(null) : null;
  const initialCallState = initialRecoveryInfo ? 'connected' : 'idle';

  const [callState, setCallState] = useState(initialCallState); // 'idle' | 'preparing' | 'calling' | 'incoming' | 'connected'
  const [incomingKind, setIncomingKind] = useState('audio'); // 'audio' | 'video'
  const incomingKindRef = useRef('audio');
  const wantVideoOnConnectRef = useRef(null); // { callId: string, done: boolean }
  const triggerAutoCameraOnConnectRef = useRef(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [callerInfo, setCallerInfo] = useState(null); // 수신 시 발신자 정보 { id, name, avatar, conversationId }
  const [currentPartner, setCurrentPartner] = useState(() => (initialRecoveryInfo?.partner || (initialRecoveryInfo?.peerUserId ? { id: initialRecoveryInfo.peerUserId } : null)));
  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    if (initialRecoveryInfo?.connectedAt) {
      return Math.max(0, Math.floor((Date.now() - initialRecoveryInfo.connectedAt) / 1000));
    }
    return 0;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [peerMuted, setPeerMuted] = useState(false);
  const [isMicLocked, setIsMicLocked] = useState(false);
  const [hasRecoveredOfferSdp, setHasRecoveredOfferSdp] = useState(true);
  const [micChangeNotice, setMicChangeNotice] = useState(null);
  const micNoticeSeqRef = useRef(0);
  const micGroupSnapshotRef = useRef('');
  const connectedAtRef = useRef(initialRecoveryInfo?.connectedAt || null); // [결함 ④ 5-2 2단계] 통화 연결 최초 시각
  const recoverSelfTimeoutRef = useRef(null); // [결함 ④ 5-2 2단계] 폰 복구 안전 타이머 (25초)
  const reloadRecoveryTimeoutRef = useRef(null); // [결함 ④ 5-3 작업②] 새로고침 복구 유효 시간 만료 타이머
  const recoveryDbPollTimerRef = useRef(null);

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const callStateRef = useRef(initialCallState);
  const groupCallRef = useRef(false); // [⑦ 3-1] 단체 통화 여부 — 3-3 전까지 true로 바꾸는 곳 없음
  const currentCallIdRef = useRef(initialRecoveryInfo?.callId || null);
  const currentChatIdRef = useRef(null);
  const currentPartnerRef = useRef(initialRecoveryInfo?.partner || (initialRecoveryInfo?.peerUserId ? { id: initialRecoveryInfo.peerUserId } : null));
  const currentPartnerStateRef = useRef(currentPartner);
  currentPartnerStateRef.current = currentPartner;
  const peerIdRef = useRef(initialRecoveryInfo?.peerUserId || null);
  const peerChannelRef = useRef(null);
  const peerChannelTargetRef = useRef(null); // [C단계-6] 피어 채널 대상 사용자 ID (재사용 판정용)
  const peerChannelIdleCloseTimerRef = useRef(null); // [C단계-6] 2분 무사용 시 채널 닫기 타이머
  const closePeerChannelRef = useRef(null); // [C단계-6] 언마운트 등에서 호출용 ref
  const glareAutoAcceptCallIdRef = useRef(null);
  const lastCallEndedAtRef = useRef(0); // [보안 묶음 2] 통화 종료 시각 (종료 후 2초 발신 연타 차단용)

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioSenderRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const videoSenderRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localVideoStreamRef = useRef(null);
  const cameraCandidateStreamRef = useRef(null);
  const remoteVideoTrackRef = useRef(null);
  const peerVideoCapableRef = useRef(false);
  const [peerVideoCapable, setPeerVideoCapable] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const cameraBusyRef = useRef(false);
  const currentFacingModeRef = useRef('user');
  const [cameraFacing, setCameraFacing] = useState('user');
  const pendingCameraResumeRef = useRef(null);
  const startCameraRef = useRef(null);
  const [localVideoStream, setLocalVideoStream] = useState(null);
  const [remoteVideoStream, setRemoteVideoStream] = useState(null);
  const [remoteVideoOn, setRemoteVideoOn] = useState(false);
  const [cameraDeviceCount, setCameraDeviceCount] = useState(0);

  // [F구역-1] 화면 켜짐 유지 (WakeLock) ref
  const wakeLockRef = useRef(null);
  const wakeLockRequestingRef = useRef(false);

  // [C-9] 마이크 회복 및 AudioContext 감시 ref
  const currentAudioTrackRef = useRef(null);
  const trackMuteHandlerRef = useRef(null);
  const trackUnmuteHandlerRef = useRef(null);
  const trackEndedHandlerRef = useRef(null);
  const replaceAudioTrackRef = useRef(null);

  // [C-11] TURN 중계 시 송신 화질 낮춤 ref
  const relayQualityAppliedRef = useRef({ sender: null, track: null });
  const relayQualityApplyingRef = useRef(false);

  // [C-11] 배포판 카메라 끊김 감지 ref
  const currentVideoTrackRef = useRef(null);
  const videoTrackEndedHandlerRef = useRef(null);
  const videoTrackMuteHandlerRef = useRef(null);
  const stopCameraRef = useRef(null);
  const notifyLocalVideoTrackChangedRef = useRef(null);

  // [B-2 단위 3 수리] 상대 카메라 신호·프레임 흐름 상태 ref (화면 표시 통합 판단)
  const peerVideoStateRef = useRef('unknown'); // 'unknown' | 'on' | 'off'
  const framesFlowingRef = useRef(false);
  const peerVideoOffAtRef = useRef(0);

  // [B-2 단위 3 수리] 상대 영상 화면 표시 단일 판단 관문
  const applyRemoteVideoVisibility = useCallback(() => {
    // 'on' 확인 시에만 표시 — 'unknown'은 화면 감시 루프가 실제 프레임 증가 확인 후 'on'으로 승격(재연결 깜빡임 방지)
    setRemoteVideoOn(peerVideoStateRef.current === 'on' && framesFlowingRef.current === true);
  }, []);

  // [B-2 단위 3] 카메라 꺼짐 시 화면 표시 스트림 정리 (stopCamera 는 무수정, 트랙 stop 금지)
  useEffect(() => {
    if (!cameraOn) {
      setLocalVideoStream(null);
    }
  }, [cameraOn]);
  const [cameraNotice, setCameraNotice] = useState(null);
  // [결함 ④ 5-3 C안 / 작업②] 망 전환 즉시 감지 및 복구 상태
  const [isNetworkReconnectingSelf, setIsNetworkReconnectingSelf] = useState(() => Boolean(initialRecoveryInfo));
  const [isNetworkReconnectingPeer, setIsNetworkReconnectingPeer] = useState(false);
  const [isPeerAudioUnstable, setIsPeerAudioUnstable] = useState(false); // [결함 ④ 5-3 2단계] 상대 무음 감지 상태
  const isPeerAudioUnstableRef = useRef(false);
  const peerAudioCheckIntervalRef = useRef(null);
  const lastPeerPacketsReceivedRef = useRef(null);
  const peerPacketsEverReceivedRef = useRef(false);
  const peerAudioStagnantCountRef = useRef(0);
  const lastMonitoredPcRef = useRef(null);
  const reloadScheduledRef = useRef(false); // [결함 ④ 5-3 2단계] 새로고침 예약 플래그
  const peerMutedRef = useRef(false);
  peerMutedRef.current = peerMuted;
  const [isReconnectedSuccess, setIsReconnectedSuccess] = useState(false);

  const isNetworkSwitchSuspectedRef = useRef(false);
  const isNetworkSwitchRecoveringRef = useRef(Boolean(initialRecoveryInfo));
  const networkRecoveryConfirmedAtRef = useRef(initialRecoveryInfo?.savedAt || 0);
  const networkRecoverySafetyTimerRef = useRef(null);
  const networkRecoveryDeadlineTimerRef = useRef(null); // [결함 ④ 5-3 작업① 보완 1-1] 30초 전체 제한 타이머
  const networkRecoveryOnlineHandlerRef = useRef(null); // [결함 ④ 5-3 작업① 보완 1-7] 오프라인 시 online 이벤트 1회 리스너 ref
  const triggerNetworkSwitchSuspectRef = useRef(null); // [결함 ④ 5-3 작업① 보완 1-3] 리스너 호출용 최신 함수 ref
  const confirmNetworkSwitchRef = useRef(null); // [결함 ④ 5-5] 망 변경 즉시 확정 함수 ref
  const needsReloadAfterNetworkSwitchRef = useRef(false); // 통화 중 망 변경 확정된 페이지 — 통화 종료 후 1회 새로고침 필요
  const lastUserSignalStatusRef = useRef(null); // [결함 ④ 5-3 작업① 보완 1-4] 수신 통로 최신 상태 기록 ref
  const lastHandledLiveRecoverIdRef = useRef(null); // [결함 ④ 5-3 작업① 보완 1-10] 처리된 live recover_id 기록 ref
  const activeLiveRecoverIdRef = useRef(null); // [결함 ④ 5-3 작업① 보완 2차] 현재 진행 중인 live 복구 회차 ref
  const reconnectedSuccessTimerRef = useRef(null);
  const peerRecoveryExtendedRef = useRef(false);
  const networkSwitchCheckTimerRef = useRef(null);
  const cameraNoticeSeqRef = useRef(0);
  const cameraNoticeGivenRef = useRef(false);
  const cameraVerifySeqRef = useRef(0);
  const queuedCandidatesRef = useRef([]);
  const pendingIceRef = useRef({ callId: null, candidates: [] });
  const sentIceCandidatesRef = useRef([]);
  const iceOverflowReportedRef = useRef({ pending: false, sent: false });
  const incomingOfferRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const timerStartedRef = useRef(Boolean(initialRecoveryInfo));
  const startTimerRef = useRef(null);
  const missedTimerRef = useRef(null);
  const incomingTimeoutRef = useRef(null);
  const offerResendTimeoutRef = useRef(null);
  const finalizedCallsRef = useRef(new Set());
  const recentlyEndedCallIdsRef = useRef(new Set());
  const processingOfferCallIdsRef = useRef(new Set());
  const earlyArrivalIceRef = useRef(new Map());
  const isAcceptingRef = useRef(false);
  const startAttemptRef = useRef(0);
  const preparingStartTimeRef = useRef(0);
  const acceptStartTimeRef = useRef(0);
  const peerChannelStatusRef = useRef('CLOSED');
  const peerChannelReadyPromiseRef = useRef(null);
  const peerChannelRemovingPromiseRef = useRef(null);
  const topicCleanupPromisesRef = useRef(new Map()); // [C-10] 토픽별 채널 정리 Promise 맵 (키: user-signal-${userId})

  /**
   * [C-10] 토픽별 채널 정리 Promise 등록 헬퍼 (완료 시 맵에서 안전하게 자기 Promise 제거)
   */
  const registerTopicCleanup = useCallback((topic, promise) => {
    if (!topic || !promise) return;
    topicCleanupPromisesRef.current.set(topic, promise);
    promise.finally(() => {
      if (topicCleanupPromisesRef.current.get(topic) === promise) {
        topicCleanupPromisesRef.current.delete(topic);
      }
    });
  }, []);
  const registerTopicCleanupRef = useRef(registerTopicCleanup);
  registerTopicCleanupRef.current = registerTopicCleanup;
  const signalQueueRef = useRef([]);
  const isFlushingQueueRef = useRef(false);

  // [결함 ④ 작업 2-A] ICE restart 비상 재연결 1회 안전망 상태 변수
  const iceRestartCountRef = useRef(0); // 통화당 재연결 시도 횟수 (최대 1)
  const isIceRestartingRef = useRef(false); // 재연결 진행 중 여부
  const iceRestartIdRef = useRef(0); // 회차 번호 (정수, 1부터 시작)
  const lastHandledRestartIdRef = useRef(0); // 마지막으로 처리한 회차 번호 (중복 수신 방지)
  const iceRestartTimeoutRef = useRef(null); // 10초 제한 시간 타이머
  const iceRestartStartTimeRef = useRef(0); // 재연결 시작 시각 (소요 시간 측정용)
  const isCallerRef = useRef(false); // 건 사람(Caller) 여부 (재연결 Offer 발행 주도권)
  // [결함 ④ 작업 2-A 보강] 재연결 후보(ufrag) 보관 및 원격 설명 적용 상태
  const pendingRestartCandidatesRef = useRef([]); // 아직 적용되지 않은 새 회차의 후보 (최대 50개 FIFO 버퍼)
  const staleRemoteUfragsRef = useRef(new Set()); // 지나간 원격 표식(ufrag) 집합
  const isRestartRemoteSdpAppliedRef = useRef(false); // 이번 회차 새 원격 설명(SDP) 적용 여부
  // [결함 ④ 작업 2-A 보강 2] 신호 되풀이 전송 타이머 및 answer 적용 상태
  const iceRestartRepeatTimerRef = useRef(null); // 2초 간격 신호 되풀이 타이머
  const restartAnswerAppliedRef = useRef(false); // 건 사람 측 이번 회차 answer 적용 완료 플래그 (중복 SDP 시 candidate만 추출)
  // [결함 ④ 작업 2-A 재시험] 재연결 성공 보조 판정 상태 ref
  const iceRestartSuccessPollRef = useRef(null); // 보조 판정 1초 타이머
  const iceRestartStatsBaselineRef = useRef({ audioPackets: 0, pairId: null, pairBytes: 0, pairBytesReceived: 0, captured: false }); // 재연결 시작 시점 기준값
  const iceRestartStatsHitRef = useRef(0); // 통계 연속 확인 횟수
  const iceRestartPollBusyRef = useRef(false); // getStats 겹침 방지 플래그
  const isRestartAnswerCreatedRef = useRef(false); // 받은 사람 측 answer setLocalDescription 완료 여부
  const restartSawNonConnectedRef = useRef(false); // 재연결 진행 중 connected/completed 외 상태 관측 여부
  const iceRestartRouteHitRef = useRef(0); // 경로 전환 보조 판정 연속 확인 횟수
  const iceRestartRecoveryHitRef = useRef(0); // 상태 회복 보조 판정 연속 확인 횟수
  const iceRestartLastPairBytesRef = useRef(-1); // 보조 판정 직전 1초 선택 pair의 bytesReceived
  const iceRestartLastPairIdRef = useRef(null); // 보조 판정 직전 1초 선택 pair id
  const iceRestartLatestPairIdRef = useRef(null); // 최신 관측된 선택 candidate-pair id
  const lastRestartOfferSentAtRef = useRef(0); // 건 사람 측 최신 offer 전송 시각 (1초 쿨다운용)
  const iceRestartLoggedErrorCodesRef = useRef(new Set()); // 재연결 중 출력한 ICE 후보 오류 코드 집합 (1회 기록용)

  // [결함 ④ 작업 2-A 보강 5] 재연결 실패 시 새 연결로 다시 잇기(RTCPeerConnection 재생성 1회) 상태 변수
  const isRebuildingPcRef = useRef(false); // 새 연결 진행 중 여부
  const initialRoadblockJudgedRef = useRef(false);
  // [결함 ④ 작업 3 2단계 보완] 통화 시작 시 4초 시간 기준 판정 타이머
  const initialRoadblockTimerRef = useRef(null);
  // [결함 ④ 작업 3 2단계 보완] 3단계 복구가 읽을 막힘 확정 결과 — 2단계에서는 보관만 함
  const roadblockConfirmedRef = useRef(null);
  // [결함 ④ 작업 3] 통화 중 상대(또는 본인)가 받기를 완료했는지 여부
  const peerAnsweredRef = useRef(false);
  const peerAnsweredAtRef = useRef(0); // [결함 ④ 5-8] 상대방 응답 시각(ms)
  // [결함 ④ 작업 3 3단계] 통화당 자동 복구 정보 저장 횟수 (최대 1회)
  const autoRecoverySaveCountRef = useRef(0);
  const pcRebuildCountRef = useRef(0); // 통화당 새 연결 시도 횟수 (최대 3)
  const pcRebuildTimeoutRef = useRef(null); // 20초 제한 시간 타이머
  const pcRebuildRepeatTimerRef = useRef(null); // 2초 간격 신호 되풀이 타이머
  const rebuildAnswerAppliedRef = useRef(false); // 건 사람 측 rebuild-answer 적용 완료 플래그
  const rebuildAnswerCreatedRef = useRef(false); // 받은 사람 측 rebuild-answer 생성 완료 플래그
  const lastRebuildOfferSentAtRef = useRef(0); // 건 사람 측 최신 rebuild-offer 전송 시각
  const pcRebuildStartTimeRef = useRef(0); // 새 연결 시작 시각
  const triggerPeerConnectionRebuildRef = useRef(null); // [결함 ④ 작업 2-A 보강 5] 재연결 실패 후 재생성 진입 함수 ref
  const currentRebuildIdRef = useRef(0); // [결함 ④ 작업 2-A 보강 6] 새 연결 회차 고유 번호
  const isRebuildAnswerCreatingRef = useRef(false); // [결함 ④ 작업 2-A 보강 6] 받은 사람 answer 생성 진행 중 플래그
  const rebuildFirstSrflxAtRef = useRef(0); // [결함 ④ 작업 2-A 보강 6] 새 연결 첫 srflx 도착 시각(ms)
  const rebuildFirstRelayAtRef = useRef(0); // [결함 ④ 작업 2-A 보강 6] 새 연결 첫 relay 도착 시각(ms)
  const sendSignalRef = useRef(null); // [결함 ④ 작업 3 4단계] 렉시컬 스코프 이전 호출용 ref
  const cleanupMediaRef = useRef(null);
  const ensurePeerChannelRef = useRef(null); // [결함 ④ 5-2 2단계] 렉시컬 스코프 이전 호출용 ref
  const terminateCallRef = useRef(null); // [결함 ④ 5-2 2단계] 렉시컬 스코프 이전 호출용 ref
  const checkCallEndedInDbRef = useRef(null);
  const startHeartbeatRef = useRef(null); // [결함 ④ 5-2 2단계] 렉시컬 스코프 이전 호출용 ref
  const handleCallAnswerRef = useRef(null); // [F구역 ⑥ 묶음 3 긴급수리] 렉시컬 스코프 이전 호출용 ref
  const peerRecoveryWaitRef = useRef(null); // [결함 ④ 작업 3 4단계] { callId, timeoutId, countdownIntervalId }
  const peerRecoveryWaitCountRef = useRef(0); // [결함 ④ 작업 3 4단계] 통화당 복구 대기 진입 횟수 (최대 2회)
  const peerRecoverNoticeRef = useRef(null); // [결함 B] { callId, receivedAt, rebuildDone: false } — 상대가 '복구할게'를 보낸 사실 기억
  const [peerRecoveryRemainingSec, setPeerRecoveryRemainingSec] = useState(null); // [결함 ④ 작업 3 4단계] 대기 화면 남은 초
  const pendingPreAudioRoadblockNetRef = useRef(null); // [결함 ④ 5-8] 건 쪽 울림 중 막힘 확정 시 상대 응답 후 재판정용 보류 기록 { callId, at }
  const preAudioRoadblockNetTimerRef = useRef(null); // [결함 ④ 5-8] 건 쪽 응답 후 6초 재판정 타이머 (동시에 1개만)
  const runPreAudioRoadblockNetRef = useRef(null); // [결함 ④ 5-8] 타이머 콜백용 ref
  const audioConnectWatchTimerRef = useRef(null); // [결함 ④ 5-9] 상대 응답 후 8초 소리 연결 감시 타이머
  const audioConnectWatchStartedAtRef = useRef(0); // [결함 ④ 5-9] 소리 연결 감시 시작 시각
  const startAudioConnectWatchRef = useRef(null); // [결함 ④ 5-9] 감시 함수 참조 ref
  const needsReloadPreCallRef = useRef(false); // [결함 ④ 5-9] 통화 외(idle/울림 중) 망 변경 감지 딱지
  // [관측 ⑮-다] 통화 전 망 변경 안정화 후 조용한 1회 새로고침 ref
  const preCallReloadArmedRef = useRef(false); // 오프라인 감지 시 장전 플래그
  const lastNetDisturbAtRef = useRef(0); // 마지막 망 흔들림 발생 시각
  const lastUserInputAtRef = useRef(Date.now()); // 마지막 터치·키·휠 조작 시각
  const lastVisibleAtRef = useRef(Date.now()); // 마지막 화면 표시 시작 시각
  const preCallReloadTimerRef = useRef(null); // 망 안정화 판정 지연 타이머
  const lastPreCallReloadDeferredReasonRef = useRef(''); // 직전 미룸 사유(중복 로그 방지)
  const recordNetworkDisturbanceRef = useRef(null); // 망 흔들림 기록 함수 참조 ref
  const [isAudioConnected, setIsAudioConnected] = useState(false); // [결함 ④ 5-9] 소리 연결 수립 여부 UI 상태
  const pendingEarlyAnswerRef = useRef(null); // [F구역 ⑥ 묶음 3 긴급수리] 오퍼전송 await 중 조기 도착한 answer 보관용 ref

  /**
   * [결함 ④ 5-8] 소리 연결 전 바깥 길 막힘 확정 시 0:00 방치 차단 그물 판정 함수
   */
  const runPreAudioRoadblockNet = useCallback((trigger) => {
    // ① currentCallIdRef 존재, callState !== idle, isTerminating !== true
    if (!currentCallIdRef.current || callStateRef.current === 'idle') {
      console.log(`[통화 시작 막힘 그물] 종료 안 함 — 사유: 통화없음/idle (계기: ${trigger})`);
      return;
    }
    if (isTerminatingRef.current) {
      console.log(`[통화 시작 막힘 그물] 종료 안 함 — 사유: 종료진행중 (계기: ${trigger})`);
      return;
    }
    // ② timerStartedRef === false (소리 연결 전)
    if (timerStartedRef.current) {
      console.log(`[통화 시작 막힘 그물] 종료 안 함 — 사유: 소리이미연결 (계기: ${trigger})`);
      return;
    }
    // ③ pcRef의 connectionState !== 'connected', iceConnectionState !== 'connected' && !== 'completed'
    const pc = pcRef.current;
    if (pc && (pc.connectionState === 'connected' || pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed')) {
      console.log(`[통화 시작 막힘 그물] 종료 안 함 — 사유: P2P이미연결/완료 (계기: ${trigger})`);
      return;
    }
    // ④ 상대 응답 완료 및 6초 유예 판정
    if (!peerAnsweredRef.current) {
      pendingPreAudioRoadblockNetRef.current = { callId: currentCallIdRef.current, at: Date.now() };
      console.log('[통화 시작 막힘 그물] 상대 응답 전 → 응답 후 판정 보류');
      return;
    }

    const now = Date.now();
    if (!peerAnsweredAtRef.current) {
      peerAnsweredAtRef.current = now;
    }
    const elapsedSinceAnswer = now - peerAnsweredAtRef.current;
    if (elapsedSinceAnswer < PRE_AUDIO_ROADBLOCK_GRACE_MS) {
      const remainingMs = Math.max(500, PRE_AUDIO_ROADBLOCK_GRACE_MS - elapsedSinceAnswer);
      if (!preAudioRoadblockNetTimerRef.current) {
        console.log(`[통화 시작 막힘 그물] 응답 후 ${elapsedSinceAnswer}ms — ${remainingMs}ms 뒤 재판정 예약 (계기: ${trigger})`);
        const targetCallId = currentCallIdRef.current;
        preAudioRoadblockNetTimerRef.current = setTimeout(() => {
          preAudioRoadblockNetTimerRef.current = null;
          pendingPreAudioRoadblockNetRef.current = null;
          if (currentCallIdRef.current === targetCallId) {
            runPreAudioRoadblockNetRef.current?.('grace_elapsed');
          }
        }, remainingMs);
      }
      return;
    }

    // 모두 만족 (응답 후 6초 이상 경과 & 미연결): 종료 후 새로고침
    const hadMark = needsReloadAfterNetworkSwitchRef.current;
    console.log(`[통화 시작 막힘 그물] 소리 연결 전 막힘 확정 → 종료 후 새로고침 (계기: ${trigger}, 딱지 기존: ${hadMark ? '예' : '아니오'})`);
    needsReloadAfterNetworkSwitchRef.current = true;
    terminateCallRef.current?.({
      status: 'failed',
      reason: 'reload_recovery_failed',
      toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
      toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
      notifyPeer: true,
    });
  }, [t]);
  runPreAudioRoadblockNetRef.current = runPreAudioRoadblockNet;

  /**
   * [결함 ④ 5-8] 상대 응답 시점에 보류된 막힘 판정이 있으면 6초 재판정 예약 공통 헬퍼
   */
  const schedulePreAudioRoadblockRecheckOnAnswer = useCallback((callId) => {
    if (pendingPreAudioRoadblockNetRef.current && pendingPreAudioRoadblockNetRef.current.callId === callId) {
      if (!preAudioRoadblockNetTimerRef.current) {
        console.log('[통화 시작 막힘 그물] 상대 응답 → 6초 후 재판정 예약');
        const targetCallId = callId;
        preAudioRoadblockNetTimerRef.current = setTimeout(() => {
          preAudioRoadblockNetTimerRef.current = null;
          pendingPreAudioRoadblockNetRef.current = null;
          if (currentCallIdRef.current === targetCallId) {
            runPreAudioRoadblockNetRef.current?.('after_answer');
          }
        }, PRE_AUDIO_ROADBLOCK_GRACE_MS);
      }
    }
  }, []);
  const schedulePreAudioRoadblockRecheckOnAnswerRef = useRef(null);
  schedulePreAudioRoadblockRecheckOnAnswerRef.current = schedulePreAudioRoadblockRecheckOnAnswer;

  /**
   * [결함 ④ 5-9] 상대 응답 후 8초 소리 연결 감시 함수
   */
  const startAudioConnectWatch = useCallback((callId, role) => {
    if (audioConnectWatchTimerRef.current) {
      clearTimeout(audioConnectWatchTimerRef.current);
      audioConnectWatchTimerRef.current = null;
    }
    if (timerStartedRef.current === true) return;

    audioConnectWatchStartedAtRef.current = Date.now();
    console.log(`[소리 연결 감시] 시작 — 역할: ${role}, 통화: ${callId?.slice?.(0, 8)}, 한도 ${AUDIO_CONNECT_TIMEOUT_MS}ms`);

    audioConnectWatchTimerRef.current = setTimeout(() => {
      audioConnectWatchTimerRef.current = null;
      const pc = pcRef.current;
      if (!currentCallIdRef.current || currentCallIdRef.current !== callId) {
        console.log(`[소리 연결 감시] 종료 안 함 — 사유: 통화 ID 불일치 (현재: ${currentCallIdRef.current?.slice?.(0, 8)}, 대상: ${callId?.slice?.(0, 8)})`);
        return;
      }
      if (callStateRef.current === 'idle') {
        console.log('[소리 연결 감시] 종료 안 함 — 사유: 통화 idle');
        return;
      }
      if (isTerminatingRef.current === true) {
        console.log('[소리 연결 감시] 종료 안 함 — 사유: 이미 종료 진행 중');
        return;
      }
      if (timerStartedRef.current === true) {
        console.log('[소리 연결 감시] 종료 안 함 — 사유: 소리 이미 연결됨');
        return;
      }
      if (pc && (pc.connectionState === 'connected' || pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed')) {
        console.log('[소리 연결 감시] 종료 안 함 — 사유: P2P 이미 연결/완료');
        return;
      }
      if (reloadScheduledRef.current === true) {
        console.log('[소리 연결 감시] 종료 안 함 — 사유: 새로고침 예약 중');
        return;
      }

      console.log(`[소리 연결 감시] 받은 뒤 ${AUDIO_CONNECT_TIMEOUT_MS}ms 미연결 → 종료(재연결 생략)`);
      terminateCallRef.current?.({
        status: 'failed',
        reason: 'reload_recovery_failed',
        toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
        toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
        notifyPeer: true,
      });
    }, AUDIO_CONNECT_TIMEOUT_MS);
  }, [t]);
  startAudioConnectWatchRef.current = startAudioConnectWatch;

  // [결함 ④ 작업 3 3단계] 막힘 확정 시 20초 유효 복구 정보 저장 (통화당 1회)
  const handleRoadblockConfirmed = useCallback(({ place, round }) => {
    const callId = currentCallIdRef.current;
    if (!callId) {
      console.log(`[복구 준비] 복구 정보 저장 건너뜀 — 통화 ID 없음, 위치: ${place}`);
      return false;
    }
    if (callStateRef.current === 'idle') {
      console.log(`[복구 준비] 복구 정보 저장 건너뜀 — callState idle, 위치: ${place}`);
      return false;
    }
    // [결함 ④ 5-3 2단계 보완 1-2] 이전 저장이 소리 연결 전이었고 아직 새로고침을 쓰지 않은 통화는 통과
    if (autoRecoverySaveCountRef.current >= 1) {
      const uses = readCallRecoveryUses(callId);
      const isBeforeAudio = !timerStartedRef.current;
      const alreadyAutoReloaded = getAutoReloadCount(uses) >= MAX_AUTO_RELOADS_PER_CALL;
      if (isBeforeAudio || alreadyAutoReloaded) {
        console.log(`[복구 준비] 복구 정보 저장 건너뜀 — 이미 저장됨(${autoRecoverySaveCountRef.current}), 소리연결전:${isBeforeAudio ? '예' : '아니오'}, 자동새로고침사용:${alreadyAutoReloaded ? '예' : '아니오'}, 위치: ${place}`);
        return false;
      }
      console.log(`[복구 준비] 이전 저장은 소리 연결 전 — 새로고침 위해 재저장 (위치: ${place})`);
    }

    const pSrc = currentPartnerRef.current || currentPartnerStateRef.current;
    const savedPartner = pSrc ? {
      id: pSrc.id || peerIdRef.current,
      name: pSrc.name || null,
      nickname: pSrc.nickname || null,
      avatar: pSrc.avatar || null,
      name_display_choice: pSrc.name_display_choice || null,
    } : null;

    const recoveryData = {
      v: 1,
      callId,
      peerUserId: peerIdRef.current,
      role: isCallerRef.current ? 'caller' : 'callee',
      isVideo: Boolean(localVideoTrackRef.current || remoteVideoTrackRef.current),
      peerVideoCapable: peerVideoCapableRef.current === true,
      cameraOn: localVideoTrackRef.current?.readyState === 'live',
      cameraFacing: currentFacingModeRef.current === 'environment' ? 'environment' : 'user',
      place,
      round: round ?? null,
      savedAt: Date.now(),
      connectedAt: connectedAtRef.current || null,
      partner: savedPartner,
    };

    const saved = writeCallRecoveryInfo(recoveryData);
    if (saved) {
      autoRecoverySaveCountRef.current += 1;
      const shortId = callId.slice(0, 8);
      console.log(`[복구 준비] 복구 정보 저장 — 통화: ${shortId}, 위치: ${place}, 유효 20초`);

      // [결함 ④ 작업 3 4단계] 소리 연결 이력(timerStartedRef)이 있는 통화만 call:recover 발송
      if (timerStartedRef.current) {
        const uses = readCallRecoveryUses(callId);
        if (getAutoReloadCount(uses) >= MAX_AUTO_RELOADS_PER_CALL) {
          console.log('[복구 준비] 자동 새로고침 2회 소진 → 생략 (버튼은 5-3)');
          return false;
        } else {
          markAutoReloadUsed(callId);
          // [결함 ④ 5-3 2단계] 새로고침 예약 시 10초 안전 타이머 해제 및 플래그 세움
          if (networkRecoverySafetyTimerRef.current) {
            clearTimeout(networkRecoverySafetyTimerRef.current);
            networkRecoverySafetyTimerRef.current = null;
          }
          reloadScheduledRef.current = true;
          // [결함 ④ 5-3 작업②] 새로고침 대기 플래그 세우고 통화창 유지
          isNetworkSwitchRecoveringRef.current = true;
          setIsNetworkReconnectingSelf(true);
          (async () => {
            let sendOk = false;
            try {
              const sendPromise = (async () => {
                await sendSignalRef.current?.('call:recover', {
                  call_id: callId,
                  round: round ?? null,
                });
                console.log(`[복구 대기] call:recover 전송 — 통화: ${shortId}`);
                return 'ok';
              })();
              const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve('timeout'), 1500));
              const res = await Promise.race([sendPromise, timeoutPromise]);
              sendOk = res === 'ok';
            } catch {
              sendOk = false;
            }
            console.log(`[복구 준비] 자동 새로고침 실행 — 통화: ${shortId}, 신호 결과: ${sendOk ? 'ok' : 'timeout'}`);
            if (typeof showToast === 'function') {
              showToast(t('call.self_recovering'), 2000);
            }
            // [결함 ④ 5-3 작업②] 미디어 트랙은 정리하되 callState는 유지하여 화면을 닫지 않음
            if (localStreamRef.current) {
              try {
                localStreamRef.current.getTracks().forEach((track) => track.stop());
              } catch {}
            }
            setTimeout(() => {
              // [결함 ④ 5-1] 크롬 길 막힘은 새로고침으로만 풀림 — NATIVE_MIGRATION §4 1번 우회의 연장
              window.location.reload();
            }, 500);
          })();
          return true;
        }
      } else {
        console.log('[복구 대기] 소리 연결 전 막힘 → call:recover 보내지 않음');
        runPreAudioRoadblockNet('roadblock_confirmed');
        return false;
      }
    } else {
      console.log(`[복구 준비] 복구 정보 저장 실패 — 스토리지 쓰기 실패, 위치: ${place}`);
      if (!timerStartedRef.current) {
        runPreAudioRoadblockNet('save_failed');
      }
      return false;
    }
  }, [showToast, t, runPreAudioRoadblockNet]);

  // [결함 ④ 작업 3 3단계] 앱 시작(훅 초기화) 시 복구 정보 점검
  useEffect(() => {
    let rawSession = null;
    let rawLocal = null;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        rawSession = window.sessionStorage.getItem(CALL_RECOVERY_STORAGE_KEY);
      }
    } catch {}
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        rawLocal = window.localStorage.getItem(CALL_RECOVERY_STORAGE_KEY);
      }
    } catch {}

    const raw = rawSession || rawLocal;
    if (raw) {
      const valid = readValidCallRecoveryInfo(null);
      if (valid) {
        const shortId = valid.callId ? valid.callId.slice(0, 8) : '알수없음';
        const remainingSec = Math.max(0, Math.round((CALL_RECOVERY_VALID_MS - (Date.now() - (valid.savedAt || 0))) / 1000));
        const uses = readCallRecoveryUses(valid.callId);
        const autoUsedStr = `${getAutoReloadCount(uses)}/2회`;
        console.log(`[복구 준비] 유효한 복구 정보 발견 — 통화: ${shortId}, 남은 ${remainingSec}초, 자동 새로고침 사용: ${autoUsedStr} — 다시 잇기는 5-2에서`);
      } else {
        clearCallRecoveryInfo();
        console.log('[복구 준비] 만료되었거나 손상된 복구 정보 정리');
      }
    }
  }, []);

  // [결함 ④ 5-2 2단계] 새로고침 후 복구 진단 및 recover-ready 신호 1회 실행 제어
  const hasRunRecoveryDiagnosticRef = useRef(false);
  useEffect(() => {
    if (!userId || hasRunRecoveryDiagnosticRef.current) return;

    const recoveryInfo = readValidCallRecoveryInfo(null);
    if (!recoveryInfo) return;
    hasRunRecoveryDiagnosticRef.current = true;

    // [결함 ④ 5-3 작업②] 유효 시간(20초) 만료 시 화면 즉시 닫고 실패 처리
    const age = Date.now() - (recoveryInfo.savedAt || 0);
    const remainingMs = Math.max(1000, CALL_RECOVERY_VALID_MS - age);
    if (reloadRecoveryTimeoutRef.current) clearTimeout(reloadRecoveryTimeoutRef.current);
    reloadRecoveryTimeoutRef.current = setTimeout(() => {
      reloadRecoveryTimeoutRef.current = null;
      if (callStateRef.current === 'connected' && isNetworkSwitchRecoveringRef.current) {
        console.log('[복구 2단계] 복구 유효 시간(20초) 초과 → 실패 종료');
        clearCallRecoveryInfo();
        setIsNetworkReconnectingSelf(false);
        if (isNetworkSwitchRecoveringRef.current === true && recoverSelfTimeoutRef.current !== null) {
          terminateCallRef.current?.({
            status: 'failed',
            reason: 'reload_recovery_failed',
            toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
            toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
            notifyPeer: true,
          });
        } else {
          terminateCallRef.current?.({
            status: 'failed',
            reason: 'reload_recovery_timeout',
            toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
            notifyPeer: false,
          });
        }
      }
    }, remainingMs);

    (async () => {
      const abortIfRecoveryEnded = async (trigger) => {
        if (await checkCallEndedInDbRef.current?.(recoveryInfo.callId) === 'ended') {
          if (isPeerEndedAbortedRef.current) return true;
          isPeerEndedAbortedRef.current = true;
          const st = dbStatusCheckRef.current?.rawStatus || 'ended';
          console.log(`[복구 2단계] 중단 — 사유: DB 통화 상태 ${st}, 계기: ${trigger}`);
          if (recoveryDbPollTimerRef.current) { clearInterval(recoveryDbPollTimerRef.current); recoveryDbPollTimerRef.current = null; }
          clearCallRecoveryInfo();
          terminateCallRef.current?.({ status: 'ended', reason: 'peer_ended_db', toastMessage: t('call.ended', '통화가 종료되었습니다.'), notifyPeer: false, skipDbUpdate: true });
          return true;
        }
        return false;
      };
      if (await abortIfRecoveryEnded('진입')) return;
      const shortId = recoveryInfo.callId ? recoveryInfo.callId.slice(0, 8) : '알수없음';
      const remainingSec = Math.max(0, Math.round((CALL_RECOVERY_VALID_MS - (Date.now() - (recoveryInfo.savedAt || 0))) / 1000));
      let diagCounts = { host: 0, srflx: 0, relay: 0 };
      let diagReason = '시간초과';
      let diagStun = 0;
      let diagTurn = 0;
      let diagStatus = '판정제외';
      let testPc = null;

      // ① 1단계 시험용 연결 길 진단 (로그 유지) — [결함 ④ 5-3 작업②] 최대 5초 유지, relay 확보 즉시 종료
      try {
        const iceServers = await getIceServers();
        testPc = new RTCPeerConnection({ iceServers });
        testPc.addTransceiver('audio', { direction: 'recvonly' });
        const offer = await testPc.createOffer();
        await testPc.setLocalDescription(offer);
        const gatherRes = await waitForIceGatheringComplete(testPc, 5000, { earlyFinishOnRelay: true, immediateRelay: true });
        diagReason = gatherRes.reason;
        diagCounts = countCandidatesInSdp(testPc.localDescription);
        const cfg = getIceConfigSummary(testPc);
        diagStun = cfg.stunCount;
        diagTurn = cfg.turnCount;
        const roadblock = judgeIceRoadblock({
          host: diagCounts.host,
          srflx: diagCounts.srflx,
          relay: diagCounts.relay,
          complete: gatherRes.complete,
          stunCount: diagStun,
          turnCount: diagTurn,
          forceRelay: cfg.forceRelay,
          online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
        });
        diagStatus = roadblock.status;
        console.log(
          `[복구 진단] 판정: ${diagStatus} — host ${diagCounts.host} / srflx ${diagCounts.srflx} / relay ${diagCounts.relay}, 완료이유: ${diagReason}, STUN/TURN: STUN ${diagStun} / TURN ${diagTurn}, 통화: ${shortId}, 남은 ${remainingSec}초`
        );
      } catch (diagErr) {
        console.warn('[복구 진단] 시험용 연결 실패 (무시):', diagErr);
      } finally {
        if (testPc) {
          try {
            testPc.close();
          } catch {}
        }
      }

      // ② acquireCallStartMic()으로 마이크 확보
      let stream = null;
      try {
        stream = await acquireCallStartMic();
      } catch {
        stream = null;
      }
      if (!stream) {
        console.log('[복구 2단계] 마이크 확보 실패');
        if (reloadRecoveryTimeoutRef.current) {
          clearTimeout(reloadRecoveryTimeoutRef.current);
          reloadRecoveryTimeoutRef.current = null;
        }
        clearCallRecoveryInfo();
        setIsNetworkReconnectingSelf(false);
        terminateCallRef.current?.({
          status: 'failed',
          reason: 'mic_failed',
          toastMessage: t('call.peer_recover_failed', '상대와 연결되지 않았어요. 다시 걸어 주세요.'),
          notifyPeer: true,
        });
        return;
      }
      localStreamRef.current = stream;
      console.log('[복구 2단계] 마이크 확보 성공');

      // partner 객체 복원 준비 (없으면 profiles 1회 조회)
      let resolvedPartner = recoveryInfo.partner;
      if (!resolvedPartner && recoveryInfo.peerUserId) {
        try {
          const { data: prof, error: profErr } = await supabase
            .from('profiles')
            .select('id, name, nickname, avatar, name_display_choice')
            .eq('id', recoveryInfo.peerUserId)
            .maybeSingle();
          if (profErr) {
            console.warn('[복구 2단계] 상대 프로필 보충 조회 실패:', profErr.message);
          }
          if (prof) {
            resolvedPartner = {
              id: prof.id,
              name: getDisplayName(prof, ''),
              avatar: prof.avatar || null,
              conversationId: recoveryInfo.conversationId || recoveryInfo.partner?.conversationId || null,
            };
          }
        } catch (e) {
          console.warn('[복구 2단계] 상대 프로필 보충 조회 예외 (무시):', e);
        }
      }
      if (!resolvedPartner) {
        resolvedPartner = { id: recoveryInfo.peerUserId };
      }

      // ④ startTimer용 resumeSeconds 계산
      let resumeSeconds = 0;
      if (recoveryInfo.connectedAt) {
        resumeSeconds = Math.max(0, Math.floor((Date.now() - recoveryInfo.connectedAt) / 1000));
      }

      // ③ 상태 복원 (sendSignal 전에 반드시 완료)
      currentCallIdRef.current = recoveryInfo.callId;
      peerIdRef.current = recoveryInfo.peerUserId;
      isCallerRef.current = recoveryInfo.role === 'caller';
      setCurrentPartner(resolvedPartner);
      currentPartnerRef.current = resolvedPartner;
      peerAnsweredRef.current = true;
      peerAnsweredAtRef.current = Date.now();
      schedulePreAudioRoadblockRecheckOnAnswerRef.current?.(recoveryInfo.callId);
      timerStartedRef.current = true;
      setIsAudioConnected(true);
      if (audioConnectWatchTimerRef.current) {
        clearTimeout(audioConnectWatchTimerRef.current);
        audioConnectWatchTimerRef.current = null;
      }
      connectedAtRef.current = recoveryInfo.connectedAt || (Date.now() - resumeSeconds * 1000);
      pcRebuildCountRef.current = 0;
      currentRebuildIdRef.current = 0;
      isRebuildingPcRef.current = false;
      updateCallState('connected');

      if (recoveryInfo.peerVideoCapable === true) {
        peerVideoCapableRef.current = true;
        setPeerVideoCapable(true);
        console.log('[복구 2단계] 상대 화상 가능 복원 — 출처: 저장');
      }

      if (recoveryInfo.cameraOn === true) {
        const facing = recoveryInfo.cameraFacing === 'environment' ? 'environment' : 'user';
        pendingCameraResumeRef.current = { callId: recoveryInfo.callId, facing };
        console.log(`[복구 2단계] 카메라 재가동 예약 — 렌즈: ${facing}`);
      }

      // earlyArrivalIceRef 병합
      if (earlyArrivalIceRef.current.has(recoveryInfo.callId)) {
        const earlyData = earlyArrivalIceRef.current.get(recoveryInfo.callId);
        earlyArrivalIceRef.current.delete(recoveryInfo.callId);
        if (earlyData && Array.isArray(earlyData.candidates) && earlyData.candidates.length > 0) {
          queuedCandidatesRef.current.push(...earlyData.candidates);
        }
      }

      // ④ startTimer 확장 호출 (connectedAt 기준 경과초)
      startTimerRef.current?.(resumeSeconds);
      console.log(`[복구 2단계] 상태 복원 — callId: ${shortId}, 역할: ${recoveryInfo.role}, 이어 셀 초: ${resumeSeconds}s`);

      // ⑤ 수신 통로 Promise 최대 3000ms 대기
      const waitChannelStart = Date.now();
      let channelReady = false;
      try {
        if (incomingChannelReadyPromiseRef?.current) {
          const channelPromise = incomingChannelReadyPromiseRef.current;
          const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve('timeout'), 3000));
          const waitResult = await Promise.race([channelPromise, timeoutPromise]);
          channelReady = waitResult === true;
        }
      } catch {
        channelReady = false;
      }
      const channelWaitMs = Date.now() - waitChannelStart;
      if (channelReady) {
        console.log(`[복구 2단계] 통로 준비 완료 (${channelWaitMs}ms)`);
      } else {
        console.warn(`[복구 2단계] 통로 준비 시간 초과 또는 실패 (${channelWaitMs}ms) — 진행 계속`);
      }

      // ⑥ call:recover-ready 1차 송신 (sendSignal)
      // 묶음 3 이후 상대 통로 구독 없음 — httpSend만 사용
      ensurePeerChannelRef.current?.(recoveryInfo.peerUserId);

      console.log(`[복구 2단계] call:recover-ready 1차 송신 — 통화: ${shortId}`);
      sendSignalRef.current?.('call:recover-ready', {
        diag: diagCounts,
        sent_at: Date.now(),
        video_capable: true,
      });

      // 2000ms 후 1회 재송신
      setTimeout(() => {
        if (!pcRef.current && !isRebuildingPcRef.current && callStateRef.current === 'connected') {
          console.log(`[복구 2단계] call:recover-ready 2차 재송신 — 통화: ${shortId}`);
          sendSignalRef.current?.('call:recover-ready', {
            diag: diagCounts,
            sent_at: Date.now(),
            is_retry: true,
            video_capable: true,
          });
        }
      }, 2000);

      if (recoveryDbPollTimerRef.current) clearInterval(recoveryDbPollTimerRef.current);
      recoveryDbPollTimerRef.current = setInterval(() => { void abortIfRecoveryEnded('대기 중'); }, 4000);

      // ⑦ 안전 타이머: 25초 안에 연결 성공이 없으면 terminateCall
      if (recoverSelfTimeoutRef.current) clearTimeout(recoverSelfTimeoutRef.current);
      recoverSelfTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current === 'connected' && (!pcRef.current || pcRef.current.connectionState !== 'connected')) {
          console.log('[복구 2단계] 안전 타이머 발동 — 25초 내 연결 성공 없음');
          if (isNetworkSwitchRecoveringRef.current === true) {
            terminateCallRef.current?.({
              status: 'failed',
              reason: 'reload_recovery_failed',
              toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
              toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
              notifyPeer: true,
            });
          } else {
            terminateCallRef.current?.({
              status: 'failed',
              reason: 'recover_self_timeout',
              notifyPeer: false,
              toastMessage: t('call.peer_recover_failed', '상대와 연결되지 않았어요. 다시 걸어 주세요.'),
            });
          }
        }
      }, 25000);
    })();
  }, [userId, showToast, t, incomingChannelReadyPromiseRef]);

  // [결함 ④ 5-1] 로그아웃 시 복구 정보 및 횟수 기록 삭제
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current && !userId) {
      clearCallRecoveryInfo();
      clearCallRecoveryUses();
      console.log('[복구 준비] 로그아웃 감지 → 복구 정보·횟수 기록 삭제');
    }
    prevUserIdRef.current = userId;
  }, [userId]);

  const onLocalTrackChangedRef = useRef(onLocalTrackChanged);
  useEffect(() => {
    onLocalTrackChangedRef.current = onLocalTrackChanged;
  }, [onLocalTrackChanged]);

  /**
   * 최근 종료된 통화 번호를 기록하고 60초 뒤 자동 삭제 (경합 방지)
   */
  const recordRecentlyEndedCall = useCallback((callId) => {
    if (!callId) return;
    recentlyEndedCallIdsRef.current.add(callId);
    setTimeout(() => {
      recentlyEndedCallIdsRef.current.delete(callId);
    }, 60000);
  }, []);

  // 2차 방어: 연결 끊김 유예 타이머 및 생존 신호(Heartbeat) refs
  const disconnectTimerRef = useRef(null);
  const heartbeatSendIntervalRef = useRef(null);
  const heartbeatCheckIntervalRef = useRef(null);
  const lastHeartbeatAtRef = useRef(0);
  const lastOfferResendAtRef = useRef(0);
  const isTerminatingRef = useRef(false);
  const hasLoggedRouteRef = useRef(false);
  const micMeterStopRef = useRef(null);
  const micTrackCheckIntervalRef = useRef(null);
  const isFirstMicCheckRef = useRef(true);
  const micLockConsecutiveStartRef = useRef(0);
  const isMutedRef = useRef(false);
  isMutedRef.current = isMuted;
  callStateRef.current = callState;
  const isReplacingAudioRef = useRef(false);
  const isSwappingAudioRef = useRef(false);
  const swapGraceTimerRef = useRef(null);
  const lastAudioInputFingerprintRef = useRef('');

  // [결함 ④ 진단] 개발 모드 전용 ICE 진단용 refs
  const callStartTimeRef = useRef(0);
  const localCandidatesStatsRef = useRef({ host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } });
  const remoteCandidatesStatsRef = useRef({ host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } });
  const localCandidatesLoggedRef = useRef(false);
  const remoteCandidatesLoggedRef = useRef(false);
  const iceDumpTimerRef = useRef(null);
  const iceDumpCountRef = useRef(0);

  // [결함 ④ 작업 1] 개발 모드 전용 ICE 재시도 판정 장치 refs
  const iceJudgeTimerRef = useRef(null);
  const iceJudgeStartRef = useRef(null);
  const iceJudgeLoggedRef = useRef(false);
  const iceJudgeDumpCountRef = useRef(0);

  // [결함 ④ 작업 2 1단계] host tcp 제외 카운터 refs (개발 모드 전용)
  const excludedLocalHostTcpCountRef = useRef(0);
  const excludedRemoteHostTcpCountRef = useRef(0);


  const updateCallState = (newState) => {
    callStateRef.current = newState;
    setCallState(newState);
    setCallActive(newState !== 'idle');
    if (newState === 'idle') {
      lastCallEndedAtRef.current = Date.now();
    }
  };

  /**
   * 상대방 개인 채널(user-signal-${targetUserId}) 획득 및 구독 헬퍼
   */
  const ensurePeerChannel = useCallback((targetUserId) => {
    if (!targetUserId) return null;

    // 무사용 닫기 타이머 해제
    if (peerChannelIdleCloseTimerRef.current) {
      clearTimeout(peerChannelIdleCloseTimerRef.current);
      peerChannelIdleCloseTimerRef.current = null;
    }

    const existingTarget = peerChannelTargetRef.current;
    const currentStatus = peerChannelStatusRef.current;
    const existingChannel = peerChannelRef.current;

    // 1. 기존 채널의 대상이 targetId와 같은 경우
    if (existingChannel && existingTarget === targetUserId) {
      const isStillInClient = supabase.getChannels().some((c) => c === existingChannel);
      if (isStillInClient) {
        console.log(`[신호 통로] 재사용(상태=${currentStatus})`);
        return existingChannel;
      }
      // 기존 객체가 client.channels 배열에서 이미 빠진 경우(CLOSED 후 _remove 완료): 옛 ref 정리 후 아래에서 새로 생성
      peerChannelRef.current = null;
      peerChannelTargetRef.current = null;
      peerChannelStatusRef.current = 'CLOSED';
      peerChannelReadyPromiseRef.current = null;
    }

    // 2. 상대가 다른 경우: 기존 채널 제거
    if (existingChannel && existingTarget !== targetUserId) {
      console.log('[신호 통로] 닫음(사유=상대변경)');
      const oldChannel = existingChannel;
      const oldTarget = existingTarget;
      peerChannelRef.current = null;
      peerChannelTargetRef.current = null;
      peerChannelStatusRef.current = 'CLOSED';
      peerChannelReadyPromiseRef.current = null;
      const removePromise = Promise.race([
        supabase.removeChannel(oldChannel),
        new Promise((resolve) => setTimeout(resolve, PEER_CHANNEL_REMOVE_TIMEOUT_MS)),
      ]).catch((err) => {
        if (import.meta.env.DEV) {
          console.warn('[채널] 피어 채널 해제 예외 (계속 진행):', err);
        }
      });
      peerChannelRemovingPromiseRef.current = removePromise;
      if (oldTarget) {
        registerTopicCleanupRef.current?.(`user-signal-${oldTarget}`, removePromise);
      }
    }

    // [C-10] 3. 같은 토픽이 아직 정리 중이면 생성 보류 (동기 유지: return null)
    const targetTopic = `user-signal-${targetUserId}`;
    if (topicCleanupPromisesRef.current.has(targetTopic)) {
      console.log(`[신호 통로] 같은 이름 채널 정리 중 — 생성 보류(상대=${String(targetUserId).slice(0, 8)})`);
      peerChannelRef.current = null;
      peerChannelTargetRef.current = null;
      peerChannelStatusRef.current = 'CLOSED';
      peerChannelReadyPromiseRef.current = null;
      return null;
    }

    // 4. 새 채널 생성 (상대 통로는 구독 없이 httpSend로만 전송, private 설정)
    void PEER_CHANNEL_SUBSCRIBE_TIMEOUT_MS;
    const openReason = existingTarget === targetUserId ? '배열제거후재생성' : '신규또는상대변경';
    console.log(`[신호 통로] 새로 엶(상대=${String(targetUserId).slice(0, 8)}, 사유=${openReason})`);
    peerChannelTargetRef.current = targetUserId;
    peerIdRef.current = targetUserId;
    const channel = supabase.channel(targetTopic, { config: { private: true } });
    peerChannelRef.current = channel;
    peerChannelStatusRef.current = 'SUBSCRIBED';
    peerChannelReadyPromiseRef.current = null;

    return channel;
  }, []);
  ensurePeerChannelRef.current = ensurePeerChannel;

  /**
   * [C단계-6] 피어 채널 실제 닫기 및 관련 ref 초기화 (2분 무사용 만료 / 상대 변경 / 언마운트 시 호출)
   */
  const closePeerChannel = useCallback((reason = '기본') => {
    if (peerChannelIdleCloseTimerRef.current) {
      clearTimeout(peerChannelIdleCloseTimerRef.current);
      peerChannelIdleCloseTimerRef.current = null;
    }
    if (peerChannelRef.current) {
      console.log(`[신호 통로] 닫음(사유=${reason})`);
      const oldChannel = peerChannelRef.current;
      const oldTarget = peerChannelTargetRef.current;
      peerChannelRef.current = null;
      peerChannelTargetRef.current = null;
      peerChannelStatusRef.current = 'CLOSED';
      peerChannelReadyPromiseRef.current = null;
      const removingPromise = (async () => {
        const startWait = Date.now();
        // 대기열에 종료 신호 등 잔여 신호가 있으면 비우기 완료 대기 (상한 800ms)
        if (signalQueueRef.current.length > 0) {
          const queueDeadline = Date.now() + FLUSH_QUEUE_BEFORE_REMOVE_TIMEOUT_MS;
          while (signalQueueRef.current.length > 0 && Date.now() < queueDeadline) {
            await new Promise((resolve) => setTimeout(resolve, 30));
          }
          if (import.meta.env.DEV) {
            const waitedMs = Date.now() - startWait;
            console.log(`[채널] 채널 해제 전 대기열 비우기 대기 완료 (${waitedMs}ms, 잔여신호: ${signalQueueRef.current.length}건)`);
          }
        }
        try {
          await Promise.race([
            supabase.removeChannel(oldChannel),
            new Promise((resolve) => setTimeout(resolve, PEER_CHANNEL_REMOVE_TIMEOUT_MS)),
          ]);
        } catch (err) {
          if (import.meta.env.DEV) {
            console.warn('[채널] 피어 채널 해제 예외 (계속 진행):', err);
          }
        }
      })();
      peerChannelRemovingPromiseRef.current = removingPromise;
      if (oldTarget) {
        const cleanupPromiseForMap = Promise.race([
          removingPromise,
          new Promise((resolve) => setTimeout(resolve, PEER_CHANNEL_REMOVE_TIMEOUT_MS)),
        ]);
        registerTopicCleanupRef.current?.(`user-signal-${oldTarget}`, cleanupPromiseForMap);
      }
    }
  }, []);
  closePeerChannelRef.current = closePeerChannel;

  /**
   * 중복 갱신 방지 DB 상태 종료 헬퍼
   */
  const markCallStatus = useCallback(async (callId, status, endedByUserId = null) => {
    if (!callId) return;
    if (finalizedCallsRef.current.has(callId)) return;
    finalizedCallsRef.current.add(callId);

    try {
      await supabase
        .from('calls')
        .update({
          status,
          ended_at: new Date().toISOString(),
          ended_by: endedByUserId,
        })
        .eq('id', callId)
        .in('status', ['ringing', 'connected']);
    } catch (e) {
      console.error(`[useVoiceCall] calls ${status} 상태 갱신 실패:`, e);
    }
  }, []);

  /**
   * 마이크 트랙 변경/해제 알림 헬퍼 (통역 레이어 등 외부 연동용 단일 지점)
   */
  const notifyLocalTrackChanged = useCallback((track) => {
    // [C-9] 이전 마이크 트랙 리스너 안전 해제
    const prevTrack = currentAudioTrackRef.current;
    if (prevTrack && prevTrack !== track) {
      try {
        if (trackMuteHandlerRef.current) prevTrack.removeEventListener('mute', trackMuteHandlerRef.current);
        if (trackUnmuteHandlerRef.current) prevTrack.removeEventListener('unmute', trackUnmuteHandlerRef.current);
        if (trackEndedHandlerRef.current) prevTrack.removeEventListener('ended', trackEndedHandlerRef.current);
      } catch {}
    }
    currentAudioTrackRef.current = track;

    if (track) {
      // [C-9] 새 마이크 트랙에 mute·unmute·ended 감시 등록
      const handleMute = () => {
        console.warn('[마이크 회복] 마이크 일시 멈춤(다른 앱 사용 의심)');
      };
      const handleUnmute = () => {
        console.info('[마이크 회복] 마이크 되돌아옴');
      };
      const handleEnded = () => {
        if (callStateRef.current === 'connected' && currentAudioTrackRef.current === track) {
          console.warn('[마이크 회복] 마이크 끊김 → 다시 잡기');
          replaceAudioTrackRef.current?.().catch((e) => console.warn('[마이크 회복] replaceAudioTrack 예외(무시):', e));
        }
      };

      trackMuteHandlerRef.current = handleMute;
      trackUnmuteHandlerRef.current = handleUnmute;
      trackEndedHandlerRef.current = handleEnded;

      try {
        track.addEventListener('mute', handleMute);
        track.addEventListener('unmute', handleUnmute);
        track.addEventListener('ended', handleEnded);
      } catch {}

      micGroupSnapshotRef.current = track.getSettings?.()?.groupId || '';
      if (import.meta.env.DEV) {
        getCurrentMicLabel().then((name) => {
          console.info('[마이크 잡음] ' + name);
        }).catch(() => {});
      }
    } else {
      micGroupSnapshotRef.current = '';
      trackMuteHandlerRef.current = null;
      trackUnmuteHandlerRef.current = null;
      trackEndedHandlerRef.current = null;
    }
    if (import.meta.env.DEV) console.info('[통역 자리] 마이크 트랙 변경:', track ? track.id : null);
    try {
      onLocalTrackChangedRef.current?.(track ?? null);
    } catch (e) {
      console.warn('[useVoiceCall] onLocalTrackChanged 콜백 오류(무시):', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 로컬 비디오 트랙 변경/해제 알림 헬퍼 (C-11 카메라 끊김 감지 단일 지점)
   */
  const notifyLocalVideoTrackChanged = useCallback((track) => {
    const prevTrack = currentVideoTrackRef.current;
    if (prevTrack && prevTrack !== track) {
      try {
        if (videoTrackMuteHandlerRef.current) prevTrack.removeEventListener('mute', videoTrackMuteHandlerRef.current);
        if (videoTrackEndedHandlerRef.current) prevTrack.removeEventListener('ended', videoTrackEndedHandlerRef.current);
      } catch {}
    }
    currentVideoTrackRef.current = track;

    if (track) {
      const handleMute = () => {
        console.warn('[화상] 로컬 영상 트랙 멈춤(onmute)');
      };
      const handleEnded = () => {
        if (currentVideoTrackRef.current !== track) return;
        if (callStateRef.current !== 'connected') {
          console.warn('[화상] 통화 비연결 중 카메라 끊김(onended) — 상태 정리 생략');
          return;
        }
        console.warn('[화상] 카메라가 끊겨 카메라 꺼짐으로 정리');
        notifyLocalVideoTrackChanged(null);
        stopCameraRef.current?.();
      };

      videoTrackMuteHandlerRef.current = handleMute;
      videoTrackEndedHandlerRef.current = handleEnded;

      try {
        track.addEventListener('mute', handleMute);
        track.addEventListener('ended', handleEnded);
      } catch {}
    } else {
      videoTrackMuteHandlerRef.current = null;
      videoTrackEndedHandlerRef.current = null;
    }
  }, []);
  notifyLocalVideoTrackChangedRef.current = notifyLocalVideoTrackChanged;

  /**
   * 미디어 스트림, 사운드, 진동, 타이머 및 WebRTC 연결 정리 안전장치
   */
  const cleanupMedia = useCallback(() => {
    // 1. 벨소리 및 진동 즉시 정지
    stopAllCallSounds();

    // 2. 30초 무응답 타이머 및 수신 35초 예비 타이머, 5초 offer 재전송 대기 타이머 해제
    if (missedTimerRef.current) {
      clearTimeout(missedTimerRef.current);
      missedTimerRef.current = null;
    }
    if (incomingTimeoutRef.current) {
      clearTimeout(incomingTimeoutRef.current);
      incomingTimeoutRef.current = null;
    }
    if (offerResendTimeoutRef.current) {
      clearTimeout(offerResendTimeoutRef.current);
      offerResendTimeoutRef.current = null;
    }

    // 2차 방어: 하트비트 및 연결 단절 유예 타이머 해제
    if (heartbeatSendIntervalRef.current) {
      clearInterval(heartbeatSendIntervalRef.current);
      heartbeatSendIntervalRef.current = null;
    }
    if (heartbeatCheckIntervalRef.current) {
      clearInterval(heartbeatCheckIntervalRef.current);
      heartbeatCheckIntervalRef.current = null;
    }
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    if (iceDumpTimerRef.current) {
      clearTimeout(iceDumpTimerRef.current);
      iceDumpTimerRef.current = null;
    }
    if (iceJudgeTimerRef.current) {
      clearTimeout(iceJudgeTimerRef.current);
      iceJudgeTimerRef.current = null;
    }
    if (recoverSelfTimeoutRef.current) {
      clearTimeout(recoverSelfTimeoutRef.current);
      recoverSelfTimeoutRef.current = null;
    }
    if (recoveryDbPollTimerRef.current) { clearInterval(recoveryDbPollTimerRef.current); recoveryDbPollTimerRef.current = null; }
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
      iceRestartTimeoutRef.current = null;
    }
    if (iceRestartRepeatTimerRef.current) {
      clearInterval(iceRestartRepeatTimerRef.current);
      iceRestartRepeatTimerRef.current = null;
    }
    if (iceRestartSuccessPollRef.current) {
      clearInterval(iceRestartSuccessPollRef.current);
      iceRestartSuccessPollRef.current = null;
    }
    iceRestartStatsHitRef.current = 0;
    iceRestartRouteHitRef.current = 0;
    iceRestartRecoveryHitRef.current = 0;
    iceRestartLastPairBytesRef.current = -1;
    iceRestartLastPairIdRef.current = null;
    iceRestartLatestPairIdRef.current = null;
    lastRestartOfferSentAtRef.current = 0;
    iceRestartPollBusyRef.current = false;
    iceRestartStatsBaselineRef.current = { audioPackets: 0, pairId: null, pairBytes: 0, pairBytesReceived: 0, captured: false };
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;
    iceRestartLoggedErrorCodesRef.current.clear();
    iceRestartCountRef.current = 0;
    isIceRestartingRef.current = false;
    iceRestartIdRef.current = 0;
    lastHandledRestartIdRef.current = 0;
    restartAnswerAppliedRef.current = false;
    iceRestartStartTimeRef.current = 0;
    isCallerRef.current = false;
    pendingRestartCandidatesRef.current = [];
    staleRemoteUfragsRef.current = new Set();
    isRestartRemoteSdpAppliedRef.current = false;
    iceJudgeStartRef.current = null;
    iceJudgeLoggedRef.current = false;
    iceJudgeDumpCountRef.current = 0;
    excludedLocalHostTcpCountRef.current = 0;
    excludedRemoteHostTcpCountRef.current = 0;
    lastHeartbeatAtRef.current = 0;
    lastOfferResendAtRef.current = 0;
    isPeerEndedAbortedRef.current = false;

    // [결함 ④ 작업 2-A 보강 5] 새 연결 상태 초기화
    if (pcRebuildTimeoutRef.current) {
      clearTimeout(pcRebuildTimeoutRef.current);
      pcRebuildTimeoutRef.current = null;
    }
    if (pcRebuildRepeatTimerRef.current) {
      clearInterval(pcRebuildRepeatTimerRef.current);
      pcRebuildRepeatTimerRef.current = null;
    }
    isRebuildingPcRef.current = false;
    pcRebuildCountRef.current = 0;
    rebuildAnswerAppliedRef.current = false;
    rebuildAnswerCreatedRef.current = false;
    lastRebuildOfferSentAtRef.current = 0;
    pcRebuildStartTimeRef.current = 0;
    currentRebuildIdRef.current = 0;
    isRebuildAnswerCreatingRef.current = false;
    rebuildFirstSrflxAtRef.current = 0;
    rebuildFirstRelayAtRef.current = 0;

    // [결함 ④ 작업 3 2단계 보완] 최초 길 막힘 판정 타이머 및 상태 초기화
    if (initialRoadblockTimerRef.current) {
      clearTimeout(initialRoadblockTimerRef.current);
      initialRoadblockTimerRef.current = null;
    }
    initialRoadblockJudgedRef.current = false;
    roadblockConfirmedRef.current = null;

    // [결함 ④ 5-8] 건 쪽 응답 후 막힘 재판정 타이머 및 보류 기록 정리
    if (preAudioRoadblockNetTimerRef.current) {
      clearTimeout(preAudioRoadblockNetTimerRef.current);
      preAudioRoadblockNetTimerRef.current = null;
    }
    pendingPreAudioRoadblockNetRef.current = null;
    peerAnsweredAtRef.current = 0;

    // [결함 ④ 5-9] 상대 응답 후 소리 연결 감시 타이머 정리
    if (audioConnectWatchTimerRef.current) {
      clearTimeout(audioConnectWatchTimerRef.current);
      audioConnectWatchTimerRef.current = null;
    }
    audioConnectWatchStartedAtRef.current = 0;
    setIsAudioConnected(false);

    // [결함 ④ 작업 3 4단계] 상대 복구 대기 타이머 및 상태 정리
    if (peerRecoveryWaitRef.current) {
      if (peerRecoveryWaitRef.current.timeoutId) {
        clearTimeout(peerRecoveryWaitRef.current.timeoutId);
      }
      if (peerRecoveryWaitRef.current.countdownIntervalId) {
        clearInterval(peerRecoveryWaitRef.current.countdownIntervalId);
      }
      peerRecoveryWaitRef.current = null;
    }
    peerRecoverNoticeRef.current = null;
    setPeerRecoveryRemainingSec(null);
    connectedAtRef.current = null;

    // [결함 ④ 5-3 C안] 망 전환 복구 타이머 및 상태 정리
    if (networkRecoverySafetyTimerRef.current) {
      clearTimeout(networkRecoverySafetyTimerRef.current);
      networkRecoverySafetyTimerRef.current = null;
    }
    if (reconnectedSuccessTimerRef.current) {
      clearTimeout(reconnectedSuccessTimerRef.current);
      reconnectedSuccessTimerRef.current = null;
    }
    if (networkSwitchCheckTimerRef.current) {
      clearInterval(networkSwitchCheckTimerRef.current);
      networkSwitchCheckTimerRef.current = null;
    }
    if (networkRecoveryDeadlineTimerRef.current) {
      clearTimeout(networkRecoveryDeadlineTimerRef.current);
      networkRecoveryDeadlineTimerRef.current = null;
    }
    if (networkRecoveryOnlineHandlerRef.current) {
      window.removeEventListener('online', networkRecoveryOnlineHandlerRef.current);
      networkRecoveryOnlineHandlerRef.current = null;
    }
    if (reloadRecoveryTimeoutRef.current) {
      clearTimeout(reloadRecoveryTimeoutRef.current);
      reloadRecoveryTimeoutRef.current = null;
    }
    lastHandledLiveRecoverIdRef.current = null;
    activeLiveRecoverIdRef.current = null;
    isNetworkSwitchSuspectedRef.current = false;
    isNetworkSwitchRecoveringRef.current = false;
    networkRecoveryConfirmedAtRef.current = 0;
    peerRecoveryExtendedRef.current = false;
    setIsNetworkReconnectingSelf(false);
    setIsNetworkReconnectingPeer(false);
    setIsReconnectedSuccess(false);
    // [결함 ④ 5-3 2단계] 새로고침 예약 플래그 및 상대 무음 감지 정리
    reloadScheduledRef.current = false;
    if (peerAudioCheckIntervalRef.current) {
      clearInterval(peerAudioCheckIntervalRef.current);
      peerAudioCheckIntervalRef.current = null;
    }
    peerAudioStagnantCountRef.current = 0;
    lastPeerPacketsReceivedRef.current = null;
    peerPacketsEverReceivedRef.current = false;
    lastMonitoredPcRef.current = null;
    if (isPeerAudioUnstableRef.current) {
      isPeerAudioUnstableRef.current = false;
      setIsPeerAudioUnstable(false);
    }

    // 3. 마이크 트랙 정지 (필수 안전장치 - §2-65 및 고아 스트림 방어)
    if (localStreamRef.current) {
      try {
        localStreamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {}
        });
      } catch (e) {
        console.error('[useVoiceCall] 마이크 트랙 정지 실패:', e);
      }
      localStreamRef.current = null;
      notifyLocalTrackChanged(null);
    }

    // 3-0. 영상 트랙 및 스트림 정지 (트랙 분리 보장 - §F B-1 단위 2)
    notifyLocalVideoTrackChangedRef.current?.(null);
    relayQualityAppliedRef.current = { sender: null, track: null };
    relayQualityApplyingRef.current = false;
    pendingCameraResumeRef.current = null;
    if (localVideoTrackRef.current) {
      try {
        localVideoTrackRef.current.stop();
      } catch {}
      localVideoTrackRef.current = null;
    }
    if (localVideoStreamRef.current) {
      try {
        localVideoStreamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {}
        });
      } catch {}
      localVideoStreamRef.current = null;
    }
    stopStreamQuietly(cameraCandidateStreamRef.current);
    cameraCandidateStreamRef.current = null;
    videoSenderRef.current = null;
    remoteVideoTrackRef.current = null;
    peerVideoCapableRef.current = false;
    setPeerVideoCapable(false);
    currentFacingModeRef.current = 'user';
    setCameraFacing('user');
    setLocalVideoStream(null);
    setRemoteVideoStream(null);
    setRemoteVideoOn(false);
    setCameraDeviceCount(0);
    peerVideoStateRef.current = 'unknown';
    framesFlowingRef.current = false;
    peerVideoOffAtRef.current = 0;

    // 3-1. 원격 오디오 스트림 및 오디오 그래프 연결 해제
    callAudioGraph.cleanup(remoteAudioRef.current);

    // 4. PeerConnection 닫기
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (e) {
        console.error('[useVoiceCall] PeerConnection 닫기 실패:', e);
      }
      pcRef.current = null;
    }
    audioSenderRef.current = null;

    // 5. 통화 시간 타이머 해제
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    timerStartedRef.current = false;
    connectedAtRef.current = null;
    peerAnsweredRef.current = false;
    groupCallRef.current = false;
    autoRecoverySaveCountRef.current = 0;
    setElapsedSeconds(0);

    // 6. 상대방 채널 정리: 같은 상대면 채널 유지, 2분 무사용 시 닫기 타이머 가동
    if (peerChannelIdleCloseTimerRef.current) {
      clearTimeout(peerChannelIdleCloseTimerRef.current);
      peerChannelIdleCloseTimerRef.current = null;
    }
    if (peerChannelRef.current) {
      peerChannelIdleCloseTimerRef.current = setTimeout(() => {
        if (callStateRef.current === 'idle' && signalQueueRef.current.length === 0) {
          closePeerChannelRef.current?.('무사용');
        }
      }, PEER_CHANNEL_IDLE_CLOSE_MS);
    }
    peerIdRef.current = null;
    isAcceptingRef.current = false;
    currentChatIdRef.current = null;
    currentPartnerRef.current = null;

    queuedCandidatesRef.current = [];
    pendingIceRef.current = { callId: null, candidates: [] };
    sentIceCandidatesRef.current = [];
    iceOverflowReportedRef.current = { pending: false, sent: false };
    incomingOfferRef.current = null;
    pendingEarlyAnswerRef.current = null; // [F구역 ⑥ 묶음 3 긴급수리]
    currentCallIdRef.current = null;
    setCurrentPartner(null);
    setElapsedSeconds(0);
    setCallerInfo(null);
    setHasRecoveredOfferSdp(true);
    hasLoggedRouteRef.current = false;
    // ★ 통화 종료 시 음소거 상태 반드시 초기화 (다음 통화 오작동 방지)
    setIsMuted(false);
    setPeerMuted(false);
    setCameraOn(false);
    setMicChangeNotice(null);
    cameraVerifySeqRef.current++;
    setCameraNotice(null);
    cameraNoticeGivenRef.current = false;
    wantVideoOnConnectRef.current = null;
    setIncomingKind('audio');
    incomingKindRef.current = 'audio';

    // ★ 실시간 마이크 측정기 및 잠김 판정 정리
    if (micMeterStopRef.current) {
      try {
        micMeterStopRef.current();
      } catch (e) {}
      micMeterStopRef.current = null;
    }
    if (micTrackCheckIntervalRef.current) {
      clearInterval(micTrackCheckIntervalRef.current);
      micTrackCheckIntervalRef.current = null;
    }
    setIsMicLocked(false);
    isFirstMicCheckRef.current = true;
    micLockConsecutiveStartRef.current = 0;

    // ★ 오디오 핫스왑 잠금 및 유예 타이머 정리
    if (swapGraceTimerRef.current) {
      clearTimeout(swapGraceTimerRef.current);
      swapGraceTimerRef.current = null;
    }
    isSwappingAudioRef.current = false;
    isReplacingAudioRef.current = false;
    lastAudioInputFingerprintRef.current = '';
  }, [notifyLocalTrackChanged]);
  cleanupMediaRef.current = cleanupMedia;

  /**
   * 상대 통로로 httpSend 단일 전송 헬퍼 (3초 실패 타임아웃 · 1초 초과 지연 시 console.warn)
   * 묶음 3 이후 상대 통로 구독 없음 — httpSend만 사용
   */
  const sendHttpWithTimeout = async (targetChan, ev, pl, timeoutMs = 3000) => {
    const start = Date.now();
    let result = 'error';
    let res = null;
    try {
      res = await Promise.race([
        targetChan.httpSend(ev, pl),
        new Promise((_, reject) => setTimeout(() => reject(new Error('httpSend_timeout')), timeoutMs)),
      ]);
      result = res?.success ? 'ok' : 'error';
    } catch (err) {
      result = err?.message || 'exception';
    }
    const elapsed = Date.now() - start;
    if (result !== 'ok') {
      console.warn(`[신호 송신] 실패 — 종류: ${ev}, 소요: ${elapsed}ms, 결과: ${result}`);
    } else if (elapsed > 1000) {
      console.warn(`[신호 송신] 지연 — 종류: ${ev}, 소요: ${elapsed}ms, 결과: ${result}`);
    }
    return { result, elapsed, success: result === 'ok' };
  };

  /**
   * [긴급 수리] 대상 상대 통로로 신호 실제 발송 (채널 확보 · 3초 타임아웃 · 실패 확실 시 1회 재전송)
   * 묶음 3 이후 상대 통로 구독 없음 — httpSend만 사용
   */
  const executeSend = useCallback(async (event, payload, targetId) => {
    let channel = peerChannelRef.current;
    if ((!channel || peerIdRef.current !== targetId) && targetId) {
      channel = ensurePeerChannel(targetId);
    }
    if (!channel) {
      console.warn('[useVoiceCall] 대상 개인 채널이 준비되지 않아 신호를 전송할 수 없습니다:', event, targetId);
      return 'error';
    }

    // 1차 전송 (3초 상한)
    const firstAttempt = await sendHttpWithTimeout(channel, event, payload, 3000);
    let result = firstAttempt.result;
    let isRetried = false;
    let retryResult = null;

    // 실제 실패(오류 응답 또는 3000ms 초과)일 때만 중요 신호에 한해 1회 재전송 (3초 상한)
    if (!firstAttempt.success && IMPORTANT_SIGNALS.includes(event)) {
      isRetried = true;
      if (import.meta.env.DEV) {
        console.warn(`[신호 통로] httpSend 실패(${firstAttempt.result}) — 1회 재전송: ${event}`);
      }
      const retryAttempt = await sendHttpWithTimeout(channel, event, payload, 3000);
      retryResult = retryAttempt.result;
      result = retryResult;
    }

    // [결함 ④ 작업 2-A 보강 2] 재연결 신호 3종 전송 결과 한 줄 기록 (되풀이 n번째 표기 포함)
    // 묶음 3 이후 상대 통로 구독 없음 — httpSend만 사용
    if (['ice-restart-request', 'ice-restart-offer', 'ice-restart-answer'].includes(event)) {
      const restartId = payload.restart_id ?? iceRestartIdRef.current ?? '-';
      const retryStr = isRetried ? `, 재전송 결과: ${retryResult}` : '';
      const repeatPrefix = payload.repeat_seq ? `되풀이 ${payload.repeat_seq}번째 ` : '';
      console.log(`[재연결] ${repeatPrefix}신호 전송 — 종류: ${event}, 회차: ${restartId}, 1차 결과: ${firstAttempt.result}${retryStr}, 보내기 방식: httpSend`);
    }

    return result;
  }, [ensurePeerChannel]);

  /**
   * 상대방 개인 채널로 시그널링 대기열 순차 전송 실행기 (일반 신호 직렬 처리)
   */
  const flushSignalQueue = useCallback(async () => {
    if (isFlushingQueueRef.current) return;
    isFlushingQueueRef.current = true;

    try {
      while (signalQueueRef.current.length > 0) {
        const item = signalQueueRef.current[0];
        const targetId = item.targetUserId || peerIdRef.current;

        // [지시 1 보완] 대기열 item의 call_id가 현재 통화와 다르고 종료 계열 신호가 아니면 버림
        const isCurrentCall = Boolean(currentCallIdRef.current && item.callId === currentCallIdRef.current);
        const isTermination = TERMINATION_SIGNALS.includes(item.event);
        if (!isCurrentCall && !isTermination) {
          if (import.meta.env.DEV) {
            console.warn(`[신호 송신] 이전 통화 만료 신호 폐기 (사유: call_id 불일치, event: ${item.event}, 신호call_id: ${item.callId}, 현재: ${currentCallIdRef.current})`);
          }
          signalQueueRef.current.shift();
          continue;
        }

        const result = await executeSend(item.event, item.payload, targetId, item.callId);

        if (import.meta.env.DEV) {
          const waitMs = Date.now() - item.queuedAt;
          const remainingCount = signalQueueRef.current.length - 1;
          console.log(`[신호 송신] ${item.event} | 결과: ${result} | 대기: ${waitMs}ms | 남은대기: ${remainingCount}`);
        }

        signalQueueRef.current.shift();
      }
    } finally {
      isFlushingQueueRef.current = false;
    }
  }, [executeSend]);

  /**
   * 상대방 개인 채널로 시그널링 브로드캐스트 전송
   */
  const sendSignal = useCallback(async (event, payload = {}, targetUserId = null, options = {}) => {
    const targetId = targetUserId || peerIdRef.current;
    const resolvedCallId = payload.call_id || currentCallIdRef.current;
    const resolvedPayload = {
      ...payload,
      call_id: resolvedCallId,
      sender_id: userId,
    };

    // [긴급 수리] 1. ICE 후보는 await로 줄 세우지 말고 즉시 비동기(fire-and-forget)로 내보냄 (사전관문 G3)
    if (event === 'call:ice') {
      void executeSend(event, resolvedPayload, targetId, resolvedCallId);
      return;
    }

    // [긴급 수리] 2. 제3자 일회 송신 (oneShot) 처리: 비동기(fire-and-forget) 송신 (본 통화 신호 막힘 차단)
    // 묶음 3 이후 상대 통로 구독 없음 — httpSend만 사용
    if (options.oneShot) {
      void (async () => {
        const sendStart = Date.now();
        let isSuccess = false;
        try {
          if (targetId) {
            const targetTopicName = `user-signal-${targetId}`;
            const existingChannel = supabase.getChannels().find(
              (c) => c.topic === `realtime:${targetTopicName}` || c.topic === targetTopicName || c.subTopic === targetTopicName
            );
            const channelToSend = existingChannel || supabase.channel(targetTopicName, { config: { private: true } });

            const attempt = await sendHttpWithTimeout(channelToSend, event, resolvedPayload, 3000);
            isSuccess = attempt.success;

            if (!existingChannel && peerChannelRef.current !== channelToSend && targetId !== userIdRef.current) {
              try {
                await supabase.removeChannel(channelToSend);
              } catch {}
            }
          }
        } catch {
          isSuccess = false;
        } finally {
          console.log(`[신호 통로] 제3자 일회 송신 ${event} → ${String(targetId).slice(0, 8)} (${Date.now() - sendStart}ms, ${isSuccess ? '성공' : '실패'})`);
        }
      })();
      return;
    }

    // [긴급 수리] 3. offer/answer/end/rebuild 등 중요 신호는 다른 신호를 기다리지 않고 즉시 httpSend로 보냄
    if (IMPORTANT_SIGNALS.includes(event)) {
      await executeSend(event, resolvedPayload, targetId, resolvedCallId);
      return;
    }

    // 4. 그 외 일반 신호(ping, mute 등): 대기열에 넣어 순차 전송
    signalQueueRef.current.push({
      event,
      payload: resolvedPayload,
      callId: resolvedCallId,
      targetUserId: targetId,
      queuedAt: Date.now(),
      oneShot: false,
    });
    await flushSignalQueue();
  }, [executeSend, flushSignalQueue, userId]);
  sendSignalRef.current = sendSignal;

  // [B-2 단위 3 수리] 카메라 켬/끔 상태 변경 시 call:video_state 신호 단일 송신 경로
  const prevCameraOnRef = useRef(cameraOn);
  useEffect(() => {
    if (prevCameraOnRef.current !== cameraOn) {
      prevCameraOnRef.current = cameraOn;
      if (callStateRef.current === 'connected') {
        sendSignal('call:video_state', { on: cameraOn });
      }
    }
  }, [cameraOn, sendSignal]);

  /**
   * 동일 계정의 다른 탭/기기로 상태 동기화 브로드캐스트 전송 (다중 탭 충돌 방어)
   */
  const broadcastToOwnDevices = useCallback(async (event, payload = {}) => {
    if (!userId) return;
    try {
      const ownChannel = supabase.channel(`user-signal-${userId}`, { config: { private: true } });
      const resolvedPayload = {
        ...payload,
        call_id: payload.call_id || currentCallIdRef.current,
        sender_id: userId,
        sender_tab_id: TAB_INSTANCE_ID,
      };
      const res = await ownChannel.httpSend(event, resolvedPayload);
      if (import.meta.env.DEV) {
        if (res && res.success) {
          console.log(`[탭 알림] httpSend 성공: ${event}`);
        } else {
          console.warn('[탭 알림] httpSend 실패 응답:', res);
        }
      }
    } catch (e) {
      console.warn('[useVoiceCall] 자가 기기 브로드캐스트(httpSend) 실패:', e);
    }
  }, [userId]);

  /**
   * 통화 경과 시간 타이머 시작
   */
  const startTimer = (initialSeconds = 0) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setElapsedSeconds(initialSeconds);
    timerIntervalRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  };
  startTimerRef.current = startTimer;

  /**
   * [결함 ④ 작업 3] 받았지만 실제 소리 연결(connected)이 0회인 통화는 'ended' 대신 'failed'로 보정
   */
  const resolveFinalCallStatus = (rawStatus, answered, connected) => {
    if (rawStatus === 'ended' && answered && !connected) {
      console.log("[통화 기록] 받았지만 소리 연결 0회 → 'failed'로 기록 (원래: ended)");
      return 'failed';
    }
    return rawStatus;
  };

  /**
   * 통합 통화 마감 함수 (멱등성 보장: 중복 마감·이중 토스트 원천 차단)
   */
  const terminateCall = useCallback(async ({
    status = 'ended',       // 'ended' | 'declined' | 'missed' | 'busy' | 'failed'
    reason = null,
    toastMessage = null,
    toastDurationMs = 3000,
    notifyPeer = true,
    endedByUserId = userId,
    skipDbUpdate = false,
  } = {}) => {
    const callId = currentCallIdRef.current;
    const everConnected = timerStartedRef.current;
    const peerAnswered = peerAnsweredRef.current;
    if (callId) {
      recordRecentlyEndedCall(callId);
    }
    if (!callId && callStateRef.current === 'idle') return;
    if (isTerminatingRef.current) return;
    isTerminatingRef.current = true;

    // [결함 ④ 5-9 마무리] 종료 상태 판정 1회 수행 (DB 갱신 및 새로고침 판정에서 재사용)
    const effectiveStatus = resolveFinalCallStatus(status, peerAnswered, everConnected);

    // [결함 ④ 5-9] 종료 시작 시 소리 연결 감시 타이머 해제
    if (audioConnectWatchTimerRef.current) {
      clearTimeout(audioConnectWatchTimerRef.current);
      audioConnectWatchTimerRef.current = null;
    }

    if (import.meta.env.DEV) {
      if (iceDumpTimerRef.current) {
        clearTimeout(iceDumpTimerRef.current);
        iceDumpTimerRef.current = null;
      }
      if (iceJudgeTimerRef.current) {
        clearTimeout(iceJudgeTimerRef.current);
        iceJudgeTimerRef.current = null;
      }
      logRemoteCandidatesSummary(remoteCandidatesStatsRef, remoteCandidatesLoggedRef, `종료:${status}`, excludedRemoteHostTcpCountRef);
    }

    // 1. 상대방에게 통화 종료 신호 전파 (대상 확정 후 대기열에 즉시 등록, await 없이 발송)
    const capturedCallId = callId;
    const capturedStatus = effectiveStatus;
    const capturedEndedBy = endedByUserId;
    const capturedIsGroup = groupCallRef.current;

    if (notifyPeer && capturedCallId) {
      console.log(`[call:end 송신] 출처=terminateCall reason=${reason ?? 'null'} call=${capturedCallId.slice(0, 8)} status=${status}`);
    }
    const signalPromise = (notifyPeer && capturedCallId)
      ? sendSignal('call:end', { call_id: capturedCallId, reason }).catch((sigErr) => {
          console.warn('[useVoiceCall] call:end 신호 전송 실패 (정리 계속 진행):', sigErr);
        })
      : Promise.resolve();

    // 2. DB calls 테이블 상태 갱신 선출발 (화면 정리 시작 전 백그라운드 출발, await 금지)
    const dbStart = Date.now();
    const dbPromise = (capturedCallId && !skipDbUpdate)
      ? (async () => {
          try {
            if (capturedIsGroup) {
              await leaveGroupCall(capturedCallId, userId);
              await cancelGroupCallIfRinging(capturedCallId, capturedStatus, capturedEndedBy);
              console.log(`[통화 종료] 서버 기록(뒤에서) 완료 — ${Date.now() - dbStart}ms`);
              return;
            }
            await markCallStatus(capturedCallId, capturedStatus, capturedEndedBy);
            console.log(`[통화 종료] 서버 기록(뒤에서) 완료 — ${Date.now() - dbStart}ms`);
          } catch (err) {
            console.error('[useVoiceCall] terminateCall 서버 기록 오류:', err);
          }
        })()
      : Promise.resolve();

    const termUiStart = Date.now();
    try {
      if (capturedCallId) {
        clearCallRecoveryInfo();
        clearCallRecoveryUses();
      }

      const hasReloadMark = needsReloadAfterNetworkSwitchRef.current;
      const reloadAlreadyScheduled = reloadScheduledRef.current === true;
      let reloadBlockedBy10s = false;
      try {
        const lastReload = Number(sessionStorage.getItem('moowoo_last_post_call_reload') || 0);
        reloadBlockedBy10s = (Date.now() - lastReload) < 10000;
      } catch {
        reloadBlockedBy10s = false;
      }

      // [결함 ④ 5-9 보완] 통화 전 망 변경 딱지는 망 관련 실패 종료일 때만 새로고침 참여 (화이트리스트 + 소리 0회 종료)
      const isAnsweredNoAudio = peerAnswered === true && everConnected === false && capturedStatus === 'failed';
      const isNetworkRelatedFailure = capturedStatus === 'failed'
        && (NETWORK_FAILURE_REASONS.has(reason) || isAnsweredNoAudio);
      const hasPreCallReloadMark = needsReloadPreCallRef.current && !everConnected && isNetworkRelatedFailure;

      if (hasPreCallReloadMark) {
        console.log(`[통화 전 망 변경] 새로고침 판정 — 사유: ${reason ?? '없음'}, 근거: ${NETWORK_FAILURE_REASONS.has(reason) ? '망 사유' : '받았으나 소리 0회'}`);
      } else if (needsReloadPreCallRef.current) {
        console.log(`[통화 전 망 변경] 새로고침 안 함 — 사유: ${reason ?? '없음'}, 상태: ${capturedStatus}, 딱지 유지`);
      }

      const willReload = (hasReloadMark || hasPreCallReloadMark) && !reloadAlreadyScheduled && !reloadBlockedBy10s;

      // 3. 미디어 스트림, 사운드, 진동, 타이머, 피어 연결 전면 정리 (무조건 실행 보장)
      cleanupMedia();

      // 4. UI 상태를 idle 로 복원
      updateCallState('idle');

      // 5. 토스트 안내 (지정된 경우)
      let effectiveToastMessage = toastMessage;
      let effectiveToastDurationMs = toastDurationMs;
      if (isAnsweredNoAudio && (reason == null || reason === 'normal_hangup')) {
        effectiveToastMessage = t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.');
        effectiveToastDurationMs = UNSTABLE_ENDED_TOAST_MS;
      }

      if (effectiveToastMessage) {
        if (willReload) {
          try {
            sessionStorage.setItem('moowoo_pending_call_toast', JSON.stringify({
              message: effectiveToastMessage,
              duration: effectiveToastDurationMs,
              savedAt: Date.now(),
            }));
          } catch {
            console.log(`[통화 안내] 안내 표시 — ${effectiveToastDurationMs}ms, 문구: ${String(effectiveToastMessage).slice(0, 20)}`);
            showToast(effectiveToastMessage, effectiveToastDurationMs);
          }
        } else {
          console.log(`[통화 안내] 안내 표시 — ${effectiveToastDurationMs}ms, 문구: ${String(effectiveToastMessage).slice(0, 20)}`);
          showToast(effectiveToastMessage, effectiveToastDurationMs);
        }
      }

      // 6. 통화 이력 갱신 콜백 호출
      onCallFinished?.();

      if (willReload) {
        const reloadReasonSource = hasReloadMark ? '통화중망변경' : '통화전망변경_미연결실패';
        console.log(`[망 변경] 통화 종료 후 새로고침 실행 (0.2초 후, 출처: ${reloadReasonSource})`);
        needsReloadAfterNetworkSwitchRef.current = false;
        needsReloadPreCallRef.current = false;
        clearCallRecoveryInfo();
        clearCallRecoveryUses();
        try {
          sessionStorage.setItem('moowoo_last_post_call_reload', String(Date.now()));
        } catch {}
        const reloadWaitStart = Date.now();
        void Promise.race([
          Promise.allSettled([signalPromise, dbPromise]),
          new Promise((r) => setTimeout(r, 3000)),
        ]).then(() => {
          console.log(`[망 변경] 새로고침 전 신호·기록 대기 ${Date.now() - reloadWaitStart}ms`);
          setTimeout(() => {
            if (callStateRef.current !== 'idle' || processingOfferCallIdsRef.current.size > 0) {
              console.warn('[새로고침] 취소 — 새 통화 진행 중, 상태=' + callStateRef.current);
              return;
            }
            window.location.reload();
          }, 200);
        });
      } else if ((hasReloadMark || hasPreCallReloadMark) && reloadAlreadyScheduled) {
        console.log('[망 변경] 복구 새로고침 이미 예약됨 — 종료 후 새로고침 생략');
      } else if ((hasReloadMark || hasPreCallReloadMark) && reloadBlockedBy10s) {
        console.log('[망 변경] 새로고침 반복 방지(10초) — 이번엔 건너뜀, 딱지 유지');
      }
    } catch (err) {
      console.error('[useVoiceCall] terminateCall 처리 중 오류 (정리 보장):', err);
    } finally {
      isTerminatingRef.current = false;
      console.log(`[통화 종료] 화면 정리 ${Date.now() - termUiStart}ms`);
    }
  }, [userId, sendSignal, markCallStatus, cleanupMedia, showToast, onCallFinished, recordRecentlyEndedCall, t]);
  terminateCallRef.current = terminateCall;

  /**
   * 통화 정상 종료 처리 (통화 중 종료 or 발신 취소)
   */
  const endCall = useCallback(async (opts) => {
    const autoReason = (opts && typeof opts.autoReason === 'string') ? opts.autoReason : null;
    const isPreparing = callStateRef.current === 'preparing';
    const isCallingBeforeAnswer = callStateRef.current === 'calling' || isPreparing;
    if (isPreparing) {
      startAttemptRef.current = 0;
    }
    await terminateCall({
      status: autoReason ? 'failed' : 'ended',
      reason: autoReason || (isCallingBeforeAnswer ? 'canceled' : 'normal_hangup'),
      toastMessage: autoReason
        ? null
        : (isCallingBeforeAnswer
            ? t('call.canceled', '통화가 취소되었습니다.')
            : t('call.ended', '통화가 종료되었습니다.')),
      notifyPeer: !isPreparing,
      endedByUserId: userId,
    });
  }, [terminateCall, t, userId]);

  /**
   * 1차 방어: 브라우저 탭 닫기, 새로고침, 창 닫기 시 이탈 직전 종료 신호 발신
   */
  useEffect(() => {
    const handleUnload = () => {
      if (callStateRef.current === 'idle' || !currentCallIdRef.current) return;
      const callId = currentCallIdRef.current;
      const targetPeerId = peerIdRef.current;

      const recoveryInfo = readValidCallRecoveryInfo(callId);
      const skipEndForRecovery = !!recoveryInfo;
      if (skipEndForRecovery) {
        const remainingSec = Math.max(0, Math.round((CALL_RECOVERY_VALID_MS - (Date.now() - recoveryInfo.savedAt)) / 1000));
        console.log(`[복구 준비] 복구 정보 유효(남은 ${remainingSec}초) → call:end·DB 종료 건너뜀(마이크는 정지)`);
      }

      // ① broadcast call:end 최우선 즉시 발송 시도
      if (!skipEndForRecovery) {
        try {
          if (targetPeerId) {
            console.log(`[call:end 송신] 출처=handleUnload reason=unload call=${callId.slice(0, 8)}`);
            const peerChan = supabase.channel(`user-signal-${targetPeerId}`, { config: { private: true } });
            peerChan.httpSend('call:end', {
              call_id: callId,
              sender_id: userId,
              reason: 'unload',
            }).catch(() => {});
          }
        } catch (e) {
          console.warn('[useVoiceCall] 언로드 브로드캐스트 발신 실패:', e);
        }
      }

      // ② 로컬 미디어 트랙 정지 (마이크 끄기 - §2-65)
      if (localStreamRef.current) {
        try {
          localStreamRef.current.getTracks().forEach((track) => track.stop());
        } catch (e) {
          console.warn('[useVoiceCall] 언로드 마이크 트랙 정지 실패:', e);
        }
        localStreamRef.current = null;
      }
      notifyLocalVideoTrackChangedRef.current?.(null);
      if (localVideoTrackRef.current) {
        try {
          localVideoTrackRef.current.stop();
        } catch {}
        localVideoTrackRef.current = null;
      }
      if (localVideoStreamRef.current) {
        try {
          localVideoStreamRef.current.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch {}
          });
        } catch {}
        localVideoStreamRef.current = null;
      }
      stopStreamQuietly(cameraCandidateStreamRef.current);
      cameraCandidateStreamRef.current = null;
      videoSenderRef.current = null;
      remoteVideoTrackRef.current = null;
      peerVideoCapableRef.current = false;
      setPeerVideoCapable(false);
      setCameraDeviceCount(0);
      setCameraFacing('user');
      setLocalVideoStream(null);
      setRemoteVideoStream(null);
      setRemoteVideoOn(false);
      peerVideoStateRef.current = 'unknown';
      framesFlowingRef.current = false;
      peerVideoOffAtRef.current = 0;

      // ③ DB calls 행 마감 시도 (언로드 시점 비동기 미보장이므로 보너스 취급)
      if (!skipEndForRecovery) {
        try {
          const unloadEverConnected = timerStartedRef.current;
          const unloadPeerAnswered = peerAnsweredRef.current;
          const unloadStatus = resolveFinalCallStatus('ended', unloadPeerAnswered, unloadEverConnected);
          supabase
            .from('calls')
            .update({
              status: unloadStatus,
              ended_at: new Date().toISOString(),
              ended_by: userId,
            })
            .eq('id', callId)
            .in('status', ['ringing', 'connected'])
            .then(() => {});
        } catch (e) {
          // 무시
        }
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [userId]);

  // [결함 ④ 작업 1] 재시도 판정 장치 (개발 모드 전용, 통화 동작 무변경)
  const startIceRestartJudgeTimer = useCallback(() => {
    if (!import.meta.env.DEV) return;
    if (iceJudgeTimerRef.current) clearTimeout(iceJudgeTimerRef.current);
    iceJudgeStartRef.current = Date.now();
    iceJudgeLoggedRef.current = false;
    iceJudgeDumpCountRef.current = 0;

    iceJudgeTimerRef.current = setTimeout(() => {
      iceJudgeTimerRef.current = null;
      if (iceJudgeLoggedRef.current) return;
      const currentPc = pcRef.current;
      if (!currentPc) return;
      const iceState = currentPc.iceConnectionState;
      if (iceState === 'connected' || iceState === 'completed') {
        const elapsed = iceJudgeStartRef.current ? Date.now() - iceJudgeStartRef.current : 0;
        iceJudgeLoggedRef.current = true;
        console.log(`[ICE 재시도 판정] 비대상 — 연결까지 ${elapsed}ms`);
        return;
      }
      iceJudgeLoggedRef.current = true;
      console.log(
        `[ICE 재시도 판정] 대상 — ${ICE_RESTART_JUDGE_MS}ms 시점 미연결 (상태: ${iceState}) | 지금 버전이었다면 relay 재시도`
      );
      dumpCandidatePairs(currentPc, '3초 판정', iceJudgeDumpCountRef, 1);
    }, ICE_RESTART_JUDGE_MS);
  }, []);

  const handleIceRestartJudgeConnected = useCallback(() => {
    if (!import.meta.env.DEV) return;
    if (iceJudgeTimerRef.current) {
      clearTimeout(iceJudgeTimerRef.current);
      iceJudgeTimerRef.current = null;
    }
    if (!iceJudgeLoggedRef.current && iceJudgeStartRef.current) {
      iceJudgeLoggedRef.current = true;
      const elapsed = Date.now() - iceJudgeStartRef.current;
      console.log(`[ICE 재시도 판정] 비대상 — 연결까지 ${elapsed}ms`);
      logCandidatePairCount(pcRef.current, elapsed);
    }
  }, []);

  const handleIceRestartJudgeFailed = useCallback(() => {
    if (!import.meta.env.DEV) return;
    if (iceJudgeTimerRef.current) {
      clearTimeout(iceJudgeTimerRef.current);
      iceJudgeTimerRef.current = null;
    }
    if (!iceJudgeLoggedRef.current && iceJudgeStartRef.current) {
      iceJudgeLoggedRef.current = true;
      const elapsed = Date.now() - iceJudgeStartRef.current;
      console.log(
        `[ICE 재시도 판정] 대상 — failed 도달 (${elapsed}ms) | 지금 버전이었다면 relay 재시도`
      );
    }
  }, []);

  /**
   * [결함 ④ 작업 2-A 보강 2] 재연결 신호 되풀이 타이머 정리 헬퍼 함수
   */
  const stopIceRestartRepeatTimer = useCallback((reason) => {
    if (iceRestartRepeatTimerRef.current) {
      clearInterval(iceRestartRepeatTimerRef.current);
      iceRestartRepeatTimerRef.current = null;
      if (reason) {
        console.log(`[재연결] 되풀이 타이머 정지 — 사유: ${reason}`);
      }
    }
  }, []);

  /**
   * [결함 ④ 작업 2-A 재시험] 재연결 SDP 교환 완료 여부 판별 헬퍼
   */
  const isRestartSdpExchangeDone = useCallback(() => {
    return isCallerRef.current
      ? restartAnswerAppliedRef.current === true
      : isRestartAnswerCreatedRef.current === true;
  }, []);

  /**
   * [결함 ④ 작업 2-A 재시험] 재연결 성공 보조 판정 타이머 및 ref 정리 헬퍼
   */
  const stopIceRestartSuccessPoll = useCallback(() => {
    if (iceRestartSuccessPollRef.current) {
      clearInterval(iceRestartSuccessPollRef.current);
      iceRestartSuccessPollRef.current = null;
    }
    iceRestartStatsHitRef.current = 0;
    iceRestartRouteHitRef.current = 0;
    iceRestartRecoveryHitRef.current = 0;
    iceRestartLastPairBytesRef.current = -1;
    iceRestartLastPairIdRef.current = null;
    iceRestartLatestPairIdRef.current = null;
    lastRestartOfferSentAtRef.current = 0;
    iceRestartPollBusyRef.current = false;
    iceRestartStatsBaselineRef.current = { audioPackets: 0, pairId: null, pairBytes: 0, pairBytesReceived: 0, captured: false };
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;
  }, []);

  /**
   * [결함 ④ 작업 2-A] ICE restart 재연결 성공 처리
   * @param {string} path - 성공 판정 경로
   * @param {boolean|null} [pairChangedOverride] - candidate-pair 변경 여부 강제 지정 (null이면 baseline 비교)
   */
  const handleIceRestartSuccess = useCallback((path = '이벤트', pairChangedOverride = null) => {
    if (!isIceRestartingRef.current) return;
    isIceRestartingRef.current = false;
    isRestartRemoteSdpAppliedRef.current = false;
    // 성공 시 풀지 않음 — 같은 회차 늦은 answer는 후보만 추가(L2530), 새 회차 시작 시 초기화
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;
    stopIceRestartRepeatTimer('재연결 성공');
    stopIceRestartSuccessPoll();
    lastHeartbeatAtRef.current = Date.now(); // [결함 ④ 작업 2-A 보강] 재연결 성공 시 생존 신호 기준 시각 갱신
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
      iceRestartTimeoutRef.current = null;
    }
    const elapsed = iceRestartStartTimeRef.current ? (Date.now() - iceRestartStartTimeRef.current) : 0;

    let isPairChanged = false;
    if (typeof pairChangedOverride === 'boolean') {
      isPairChanged = pairChangedOverride;
    } else {
      const base = iceRestartStatsBaselineRef.current;
      const latestPairId = iceRestartLatestPairIdRef.current;
      isPairChanged = Boolean(base.captured && base.pairId && latestPairId && latestPairId !== base.pairId);
    }

    console.log(`[재연결] 성공 — 판정 경로: ${path}, 회차: ${iceRestartIdRef.current}, 소요: ${elapsed}ms, pair 변경: ${isPairChanged ? '예' : '아니오'}`);
  }, [stopIceRestartRepeatTimer, stopIceRestartSuccessPoll]);

  /**
   * [결함 ④ 작업 2-A 재시험] 재연결 성공 1초 보조 판정 타이머 시작
   * @param {RTCPeerConnection} pc
   */
  const startIceRestartSuccessPoll = useCallback((pc) => {
    if (!pc) return;
    stopIceRestartSuccessPoll();
    const targetPc = pc;

    // 2-3. 시작 시 기준값 비동기 수집
    getAudioAndPairStats(targetPc).then((base) => {
      if (base) {
        iceRestartStatsBaselineRef.current = {
          audioPackets: base.audioPackets,
          pairId: base.selectedPairId,
          pairBytes: base.pairBytes,
          pairBytesReceived: base.pairBytesReceived,
          captured: true,
        };
        iceRestartLatestPairIdRef.current = base.selectedPairId;
        iceRestartLastPairIdRef.current = base.selectedPairId;
        iceRestartLastPairBytesRef.current = base.pairBytesReceived;
      }
    });

    let pollTick = 0;
    iceRestartSuccessPollRef.current = setInterval(async () => {
      // 2-2. 인스턴스 변경 또는 재연결 종료 시 즉시 해제
      if (targetPc !== pcRef.current || !isIceRestartingRef.current || callStateRef.current !== 'connected') {
        stopIceRestartSuccessPoll();
        return;
      }

      // getStats 겹침 방지
      if (iceRestartPollBusyRef.current) return;
      iceRestartPollBusyRef.current = true;

      try {
        const cState = targetPc.connectionState;
        const iceState = targetPc.iceConnectionState;

        // 1-2. 재연결 진행 중 connected/completed 외의 상태가 되면 restartSawNonConnectedRef를 true로 설정
        if (cState !== 'connected' || (iceState !== 'connected' && iceState !== 'completed')) {
          restartSawNonConnectedRef.current = true;
        }

        const sdpDone = isRestartSdpExchangeDone();

        const curr = await getAudioAndPairStats(targetPc);
        const base = iceRestartStatsBaselineRef.current;

        if (curr?.selectedPairId) {
          iceRestartLatestPairIdRef.current = curr.selectedPairId;
        }

        // 1-3 (나) 보조(경로 전환): 선택 candidate-pair id가 재연결 시작 기준값과 다르고, state === 'succeeded'이며,
        // 그 pair의 bytesReceived가 직전 1초 대비 증가 — 2회 연속
        // (기준 pair id가 null이었다면 이 경로는 쓰지 말고 (가)·(다)로만 판정)
        let routeConditionMet = false;
        if (base.captured && base.pairId !== null && curr?.selectedPairId && curr.selectedPairId !== base.pairId) {
          const isPairSucceeded = curr.pairState === 'succeeded';
          const samePairAsLastSec = (curr.selectedPairId === iceRestartLastPairIdRef.current);
          const isBytesIncreased = samePairAsLastSec && (curr.pairBytesReceived > iceRestartLastPairBytesRef.current);

          if (isPairSucceeded && isBytesIncreased) {
            routeConditionMet = true;
          }
        }

        // 직전 초 데이터 저장 (다음 초 비교용)
        if (curr?.selectedPairId) {
          iceRestartLastPairIdRef.current = curr.selectedPairId;
          iceRestartLastPairBytesRef.current = curr.pairBytesReceived;
        } else {
          iceRestartLastPairIdRef.current = null;
          iceRestartLastPairBytesRef.current = -1;
        }

        if (routeConditionMet) {
          iceRestartRouteHitRef.current += 1;
        } else {
          iceRestartRouteHitRef.current = 0;
        }

        // 1-3 (다) 보조(상태 회복): restartSawNonConnectedRef === true이고,
        // 현재 connectionState === 'connected' 또는 iceConnectionState가 connected/completed — 2회 연속
        const isStateConnected = (cState === 'connected' || iceState === 'connected' || iceState === 'completed');
        if (restartSawNonConnectedRef.current && isStateConnected) {
          iceRestartRecoveryHitRef.current += 1;
        } else {
          iceRestartRecoveryHitRef.current = 0;
        }

        // 1-3. 성공 판정: sdpDone === true이고 아래 중 하나 만족 시
        if (sdpDone) {
          if (iceRestartRouteHitRef.current >= 2) {
            iceRestartPollBusyRef.current = false;
            handleIceRestartSuccess('보조(경로 전환)', true);
            return;
          }
          if (iceRestartRecoveryHitRef.current >= 2) {
            iceRestartPollBusyRef.current = false;
            const isPairChanged = Boolean(base.captured && base.pairId && curr?.selectedPairId && curr.selectedPairId !== base.pairId);
            handleIceRestartSuccess('보조(상태 회복)', isPairChanged);
            return;
          }
        }

        // 1-7. 개발 모드 2초에 1회 로그
        pollTick += 1;
        if (import.meta.env.DEV && pollTick % 2 === 0) {
          const isPairChanged = Boolean(base.captured && base.pairId && curr?.selectedPairId && curr.selectedPairId !== base.pairId);
          console.log(
            `[재연결] 보조 판정 — connectionState: ${cState} / iceConnectionState: ${iceState} / pair: ${curr?.pairState || '기타'} / SDP교환: ${sdpDone ? '완료' : '미완료'} / pair 변경: ${isPairChanged ? '예' : '아니오'} / 끊김 관측: ${restartSawNonConnectedRef.current ? '예' : '아니오'} / 연속(전환/회복): ${iceRestartRouteHitRef.current}/${iceRestartRecoveryHitRef.current}`
          );
        }
      } catch (pollErr) {
        if (import.meta.env.DEV) {
          console.warn('[재연결] 보조 판정 수행 예외:', pollErr);
        }
      } finally {
        iceRestartPollBusyRef.current = false;
      }
    }, ICE_RESTART_SUCCESS_POLL_MS);
  }, [stopIceRestartSuccessPoll, handleIceRestartSuccess, isRestartSdpExchangeDone]);

  /**
   * [결함 ④ 작업 2-A] 건 사람(Caller) 주도 ICE restart 실행
   */
  const startIceRestartAsCaller = useCallback(async (reason, customRestartId = null) => {
    const pc = pcRef.current;
    if (!pc || callStateRef.current !== 'connected') return;
    if (isIceRestartingRef.current) return;

    isIceRestartingRef.current = true;
    isRestartRemoteSdpAppliedRef.current = false;
    restartAnswerAppliedRef.current = false;
    restartSawNonConnectedRef.current = false;
    iceRestartLoggedErrorCodesRef.current.clear();
    startIceRestartSuccessPoll(pc);
    iceRestartCountRef.current += 1;
    const restartId = customRestartId || (iceRestartIdRef.current + 1);
    iceRestartIdRef.current = restartId;
    lastHandledRestartIdRef.current = restartId;
    iceRestartStartTimeRef.current = Date.now();

    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }

    // ⑤ 제한 시간 20초 타이머
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
    }
    iceRestartTimeoutRef.current = setTimeout(async () => {
      iceRestartTimeoutRef.current = null;
      if (peerRecoveryWaitRef.current?.callId) {
        console.log('[복구 대기] 기다림 중 → restart 타임아웃 보류');
        return;
      }
      if (isIceRestartingRef.current) {
        isIceRestartingRef.current = false;
        stopIceRestartRepeatTimer('20초 제한 시간 초과');
        stopIceRestartSuccessPoll();
        console.log(`[재연결] 실패 — 20초 제한 시간 초과 (회차: ${restartId})`);
        await logRestartCandidatePairsDiagnostic(pcRef.current);
        if (pcRebuildCountRef.current < 3) {
          triggerPeerConnectionRebuildRef.current?.('caller_restart_timeout', 'caller');
          return;
        }
        terminateCall({
          status: 'failed',
          reason: 'ice_restart_timeout',
          toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
          notifyPeer: false,
        });
      }
    }, ICE_RESTART_TIMEOUT_MS);

    // ② 출입증 확인 및 pc.setConfiguration
    let refreshed = false;
    try {
      const res = await getIceServersForRestart();
      refreshed = res.refreshed;
      try {
        const currentConfig = pc.getConfiguration ? pc.getConfiguration() : {};
        pc.setConfiguration({
          ...currentConfig,
          iceServers: res.iceServers,
        });
      } catch (cfgErr) {
        console.warn('[재연결] setConfiguration 실패, 기존 설정 유지:', cfgErr?.message || cfgErr);
      }
    } catch (tokenErr) {
      console.warn('[재연결] 출입증 확인 예외, 기존 설정 유지:', tokenErr?.message || tokenErr);
    }

    console.log(`[재연결] 발동 — 이유: ${reason}, 회차 번호: ${restartId}, 출입증 갱신: ${refreshed ? '새로 받음' : '기존 재사용'}`);

    // ③ createOffer({ iceRestart: true }) → setLocalDescription → waitForIceGatheringComplete → 'ice-restart-offer' 전송
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);

      // [결함 ④ 작업 2-A] 후보 수집 완료 대기 (최대 4초)
      const gatherRes = await waitForIceGatheringComplete(pc, 4000);

      // 중단 검사
      if (pc !== pcRef.current || !isIceRestartingRef.current || callStateRef.current !== 'connected') {
        return;
      }

      console.log(`[재연결] 후보 수집 — 완료: ${gatherRes.complete ? '예' : '시간초과'}, 대기: ${gatherRes.waitedMs}ms, 근거: ${gatherRes.reason}, 역할: 건 사람`);
      const myCounts = countCandidatesInSdp(pc.localDescription);
      console.log(`[재연결] 내 후보 — ${formatCandidateCounts(myCounts)}`);

      // [결함 ④ 작업 3 2단계] 길 막힘 감지 판정
      logIceRoadblockJudgement({ pc, counts: myCounts, complete: gatherRes.complete, reason: gatherRes.reason, place: '재연결 건 쪽', role: '건 사람', round: restartId, roadblockConfirmedRef, onRoadblockConfirmed: handleRoadblockConfirmed });

      lastRestartOfferSentAtRef.current = Date.now();
      await sendSignal('ice-restart-offer', {
        call_id: currentCallIdRef.current,
        restart_id: restartId,
        sdp: pc.localDescription,
      });

      // [결함 ④ 작업 2-A 보강 2] 건 사람: 최신 offer 2초 간격 되풀이 전송 (새 offer 생성 없이 최신 localDescription 전송)
      stopIceRestartRepeatTimer('새 offer 되풀이 타이머 시작 전 초기화');
      let offerRepeatSeq = 1;
      iceRestartRepeatTimerRef.current = setInterval(() => {
        if (!isIceRestartingRef.current || callStateRef.current !== 'connected' || restartAnswerAppliedRef.current) {
          stopIceRestartRepeatTimer('재연결 종료 또는 answer 적용 완료');
          return;
        }
        const currentPc = pcRef.current;
        if (!currentPc) return;

        offerRepeatSeq += 1;
        // 5. 큐 넘침 방지: 이미 신호 큐에 같은 종류·같은 회차 신호가 대기 중이면 건너뜀
        const isAlreadyQueued = signalQueueRef.current.some(
          item => item.event === 'ice-restart-offer' && item.payload?.restart_id === restartId
        );
        if (isAlreadyQueued) {
          console.log(`[재연결] 되풀이 ${offerRepeatSeq}번째 전송 건너뜀 — 큐에 동일 신호 대기 중 (종류: ice-restart-offer, 회차: ${restartId})`);
          return;
        }

        const latestLocalSdp = currentPc.localDescription || offer;
        lastRestartOfferSentAtRef.current = Date.now();
        sendSignal('ice-restart-offer', {
          call_id: currentCallIdRef.current,
          restart_id: restartId,
          sdp: latestLocalSdp,
          repeat_seq: offerRepeatSeq,
        });
      }, ICE_RESTART_REPEAT_INTERVAL_MS);
    } catch (offerErr) {
      console.error('[재연결] Offer 생성 또는 전송 실패:', offerErr);
      stopIceRestartRepeatTimer('Offer 생성 또는 전송 실패');
      stopIceRestartSuccessPoll();
      if (iceRestartTimeoutRef.current) {
        clearTimeout(iceRestartTimeoutRef.current);
        iceRestartTimeoutRef.current = null;
      }
      isIceRestartingRef.current = false;
      terminateCall({
        status: 'failed',
        reason: 'ice_restart_offer_failed',
        toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
        notifyPeer: false,
      });
    }
  }, [sendSignal, terminateCall, t, stopIceRestartRepeatTimer, startIceRestartSuccessPoll, stopIceRestartSuccessPoll, handleRoadblockConfirmed]);

  const dbStatusCheckRef = useRef({ callId: null, result: null, rawStatus: null, checkedAt: 0, promise: null });
  const isPeerEndedAbortedRef = useRef(false);

  const checkCallEndedInDb = useCallback(async (callId) => {
    if (!callId) return 'unknown';
    const now = Date.now();
    const cache = dbStatusCheckRef.current;
    if (cache.callId === callId && now - cache.checkedAt < 4000 && cache.result) return cache.result;
    if (cache.callId === callId && cache.promise) return cache.promise;
    const p = (async () => {
      try {
        const { data, error } = await supabase.from('calls').select('status').eq('id', callId).maybeSingle();
        if (error || !data?.status) return 'unknown';
        const st = data.status;
        const res = (st === 'ringing' || st === 'connected') ? 'alive' : 'ended';
        if (dbStatusCheckRef.current.callId === callId) {
          dbStatusCheckRef.current.rawStatus = st;
          dbStatusCheckRef.current.result = res;
          dbStatusCheckRef.current.checkedAt = Date.now();
        }
        return res;
      } catch {
        return 'unknown';
      } finally {
        if (dbStatusCheckRef.current.callId === callId) dbStatusCheckRef.current.promise = null;
      }
    })();
    dbStatusCheckRef.current = { callId, result: null, rawStatus: null, checkedAt: now, promise: p };
    return p;
  }, []);
  checkCallEndedInDbRef.current = checkCallEndedInDb;

  const abortIfPeerEndedInDb = useCallback(async (callId) => {
    if (!callId || callId !== currentCallIdRef.current) return false;
    const res = await checkCallEndedInDb(callId);
    if (callId !== currentCallIdRef.current) return false;
    if (res === 'ended') {
      if (isPeerEndedAbortedRef.current) return true;
      isPeerEndedAbortedRef.current = true;
      const st = dbStatusCheckRef.current.rawStatus || 'ended';
      console.log(`[재연결] 중단 — 사유: DB 통화 상태 ${st}, 통화: ${String(callId).slice(0, 8)}`);
      void terminateCall({
        status: 'ended',
        reason: 'peer_ended_db',
        toastMessage: t('call.ended', '통화가 종료되었습니다.'),
        notifyPeer: false,
        skipDbUpdate: true,
      });
      return true;
    }
    return false;
  }, [checkCallEndedInDb, terminateCall, t]);

  /**
   * [결함 ④ 작업 2-A] 비상 재연결 발동 트리거 (A: failed, B: disconnected 5초 유예 만료)
   */
  const triggerIceRestart = useCallback((reason) => {
    if (peerRecoveryWaitRef.current?.callId || isNetworkSwitchRecoveringRef.current) {
      console.log('[복구 대기] 기다림 또는 망 전환 복구 중 → triggerIceRestart 보류');
      return;
    }
    if (isIceRestartingRef.current || iceRestartCountRef.current >= 1) return;
    const pc = pcRef.current;
    if (!pc || callStateRef.current !== 'connected') return;

    if (isCallerRef.current) {
      startIceRestartAsCaller(reason);
    } else {
      const nextRestartId = iceRestartIdRef.current + 1;
      iceRestartIdRef.current = nextRestartId;
      isIceRestartingRef.current = true;
      isRestartRemoteSdpAppliedRef.current = false;
      restartAnswerAppliedRef.current = false;
      iceRestartLoggedErrorCodesRef.current.clear();
      iceRestartCountRef.current += 1;
      iceRestartStartTimeRef.current = Date.now();

      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }

      if (iceRestartTimeoutRef.current) {
        clearTimeout(iceRestartTimeoutRef.current);
      }
      iceRestartTimeoutRef.current = setTimeout(async () => {
        iceRestartTimeoutRef.current = null;
        if (isIceRestartingRef.current) {
          isIceRestartingRef.current = false;
          stopIceRestartRepeatTimer('20초 제한 시간 초과');
          stopIceRestartSuccessPoll();
          console.log(`[재연결] 실패 — 20초 제한 시간 초과 (회차: ${nextRestartId})`);
          await logRestartCandidatePairsDiagnostic(pcRef.current);
          if (await abortIfPeerEndedInDb(currentCallIdRef.current)) return;
          if (pcRebuildCountRef.current < 3) {
            triggerPeerConnectionRebuildRef.current?.('callee_restart_timeout', 'callee');
            return;
          }
          terminateCall({
            status: 'failed',
            reason: 'ice_restart_timeout',
            toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
            notifyPeer: false,
          });
        }
      }, ICE_RESTART_TIMEOUT_MS);

      console.log(`[재연결] 받은 사람 request 발송 — 이유: ${reason}, 회차 번호: ${nextRestartId}`);
      sendSignal('ice-restart-request', {
        call_id: currentCallIdRef.current,
        restart_id: nextRestartId,
        reason,
      });

      // [결함 ④ 작업 2-A 보강 2] 받은 사람: request 2초 간격 되풀이 전송
      stopIceRestartRepeatTimer('새 request 되풀이 타이머 시작 전 초기화');
      let reqRepeatSeq = 1;
      iceRestartRepeatTimerRef.current = setInterval(async () => {
        if (!isIceRestartingRef.current || callStateRef.current !== 'connected') {
          stopIceRestartRepeatTimer('재연결 종료 또는 통화 단절');
          return;
        }
        if (await abortIfPeerEndedInDb(currentCallIdRef.current)) return;

        reqRepeatSeq += 1;
        // 5. 큐 넘침 방지: 이미 신호 큐에 같은 종류·같은 회차 신호가 대기 중이면 건너뜀
        const isAlreadyQueued = signalQueueRef.current.some(
          item => item.event === 'ice-restart-request' && item.payload?.restart_id === nextRestartId
        );
        if (isAlreadyQueued) {
          console.log(`[재연결] 되풀이 ${reqRepeatSeq}번째 전송 건너뜀 — 큐에 동일 신호 대기 중 (종류: ice-restart-request, 회차: ${nextRestartId})`);
          return;
        }

        sendSignal('ice-restart-request', {
          call_id: currentCallIdRef.current,
          restart_id: nextRestartId,
          reason,
          repeat_seq: reqRepeatSeq,
        });
      }, ICE_RESTART_REPEAT_INTERVAL_MS);
    }
  }, [startIceRestartAsCaller, sendSignal, terminateCall, t, stopIceRestartRepeatTimer, stopIceRestartSuccessPoll, abortIfPeerEndedInDb]);

  /**
   * [결함 ④ 작업 2-A] 수신된 ice-restart-request 처리 (건 사람 측)
   */
  const handleIceRestartRequest = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'connected') return;
    if (isNetworkSwitchRecoveringRef.current) return;

    if (!isCallerRef.current) {
      console.log('[재연결] 무시한 신호: 받은 사람이 request 수신하여 무시');
      return;
    }

    // [결함 ④ 작업 2-A 보강 2 / 재시험] 재연결 진행 중이고 같은 회차 request인 경우: 최신 offer 즉시 1회 재전송 (1초 쿨다운 적용, createOffer 재호출 금지)
    if (isIceRestartingRef.current && payload.restart_id === iceRestartIdRef.current) {
      const now = Date.now();
      if (now - lastRestartOfferSentAtRef.current < 1000) {
        if (import.meta.env.DEV) {
          console.log(`[재연결] 진행 중 request 수신 — 1초 이내 전송 이력으로 재전송 건너뜀 (회차: ${payload.restart_id})`);
        }
        return;
      }
      const pc = pcRef.current;
      if (pc && pc.localDescription) {
        lastRestartOfferSentAtRef.current = now;
        console.log(`[재연결] 진행 중 request 수신에 따른 최신 offer 즉시 재전송 — 회차: ${payload.restart_id}`);
        sendSignal('ice-restart-offer', {
          call_id: currentCallIdRef.current,
          restart_id: payload.restart_id,
          sdp: pc.localDescription,
        });
        return;
      }
    }

    // 재연결 중이 아니고 횟수도 남아 있으면 기존처럼 재연결 시작
    if (!isIceRestartingRef.current && iceRestartCountRef.current < 1) {
      startIceRestartAsCaller(payload.reason || 'callee_request', payload.restart_id);
      return;
    }

    // 그 밖의 경우는 무시하고 기록
    console.log(`[재연결] 무시한 신호: 재연결 조건 미충족 request 무시 (진행중: ${isIceRestartingRef.current}, 횟수: ${iceRestartCountRef.current}, answer적용: ${restartAnswerAppliedRef.current})`);
  }, [userId, startIceRestartAsCaller, sendSignal]);

  /**
   * [결함 ④ 작업 2-A 보강] 재연결 새 원격 설명(SDP) 적용 후 보관된 후보 일괄 처리 공통 함수
   * @param {RTCPeerConnection} pc
   * @param {Set<string>} prevRemoteUfrags - setRemoteDescription 직전 원격 표식 집합
   */
  const drainRestartCandidatesAfterRemoteSdp = useCallback(async (pc, prevRemoteUfrags) => {
    if (!pc) return;

    // ① 적용 직전 표식 중 새 집합에 없는 것만 staleRemoteUfragsRef에 추가
    const newRemoteUfrags = getRemoteUfrags(pc);
    if (prevRemoteUfrags && prevRemoteUfrags.size > 0) {
      for (const oldU of prevRemoteUfrags) {
        if (!newRemoteUfrags.has(oldU)) {
          staleRemoteUfragsRef.current.add(oldU);
        }
      }
    }

    // ② 이번 회차 새 원격 설명 적용됨 표시 켬
    isRestartRemoteSdpAppliedRef.current = true;

    // ③ pendingRestartCandidatesRef 비움: 새 집합과 맞거나 null인 것은 addIceCandidate, 그 외엔 폐기
    let appliedCount = 0;
    let discardedCount = 0;
    const pendingList = pendingRestartCandidatesRef.current;
    pendingRestartCandidatesRef.current = [];

    for (const cand of pendingList) {
      const ufrag = getCandidateUfrag(cand);
      if (!ufrag || newRemoteUfrags.has(ufrag)) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
          appliedCount += 1;
        } catch (e) {
          console.log(`[재연결] 지난 후보 무시(적용 실패: ${ufrag || 'none'}):`, e?.message || e);
          discardedCount += 1;
        }
      } else {
        console.log(`[재연결] 지난 후보 무시(옛 표식 폐기: ${ufrag})`);
        discardedCount += 1;
      }
    }

    // ④ 기존 queuedCandidatesRef 비우기도 그대로 둠
    if (queuedCandidatesRef.current.length > 0) {
      for (const cand of queuedCandidatesRef.current) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
        } catch (e) {
          console.log('[재연결] 지난 후보 무시:', e?.message || e);
        }
      }
      queuedCandidatesRef.current = [];
    }

    // ⑤ 적용 개수와 버린 개수 기록
    console.log(`[재연결] 보관 후보 적용 ${appliedCount}개 / 폐기 ${discardedCount}개`);
  }, []);

  /**
   * [결함 ④ 작업 2-A] 수신된 ice-restart-offer 처리 (받은 사람 측)
   * 순서: 회차 검사 → setRemoteDescription → 1-4 공통 처리 → 출입증 확인/setConfiguration → createAnswer → setLocalDescription → answer 전송
   */
  const handleIceRestartOffer = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'connected') return;
    if (isNetworkSwitchRecoveringRef.current) return;

    // 회차 번호 검문: 마지막으로 처리한 번호보다 작으면 무시 (지난 회차 버림)
    if (payload.restart_id < lastHandledRestartIdRef.current) {
      console.log(`[재연결] 무시한 신호: 이미 처리된 회차 offer 무시 (수신: ${payload.restart_id}, 마지막: ${lastHandledRestartIdRef.current})`);
      return;
    }

    const pc = pcRef.current;
    if (!pc) return;

    // [결함 ④ 작업 2-A 보강 2 / 결함 ⑦-1 수리] 같은 회차 offer가 다시 왔을 때: 상태에 따라 세 갈래 처리
    if (payload.restart_id === lastHandledRestartIdRef.current) {
      stopIceRestartRepeatTimer('중복 offer 수신');
      const sdpText = typeof payload.sdp === 'string' ? payload.sdp : payload.sdp?.sdp;
      const extracted = extractCandidatesFromSdp(sdpText);

      // (가) 새 원격 설명 적용 전: 후보를 즉시 적용하지 않고 보관, answer 재전송 보류
      if (!isRestartRemoteSdpAppliedRef.current) {
        let queuedCount = 0;
        for (const cand of extracted) {
          const { type, proto } = parseCandidateTypeAndProto(cand.candidate);
          if (type === 'host' && proto === 'tcp') continue;
          const ufrag = getCandidateUfrag(cand.candidate);
          if (ufrag && staleRemoteUfragsRef.current.has(ufrag)) {
            console.log(`[재연결] 지난 후보 무시(옛 표식: ${ufrag})`);
            continue;
          }
          if (pendingRestartCandidatesRef.current.length >= RESTART_CANDIDATE_BUFFER_LIMIT) {
            const dropped = pendingRestartCandidatesRef.current.shift();
            const droppedUfrag = getCandidateUfrag(dropped);
            console.warn(`[재연결] 새 회차 후보 대기열 초과(50개)로 오래된 후보 버림(표식: ${droppedUfrag || 'none'})`);
          }
          pendingRestartCandidatesRef.current.push(cand);
          queuedCount += 1;
        }
        console.log(`[재연결] 중복 offer 수신 — 새 설명 적용 전: 후보 ${queuedCount}개 보관, answer 재전송 보류 (회차: ${payload.restart_id})`);
        return;
      }

      // 새 원격 설명 적용 완료 후: 후보 즉시 적용
      let appliedCount = 0;
      for (const cand of extracted) {
        const { type, proto } = parseCandidateTypeAndProto(cand.candidate);
        if (type === 'host' && proto === 'tcp') continue;
        const ufrag = getCandidateUfrag(cand.candidate);
        if (ufrag && staleRemoteUfragsRef.current.has(ufrag)) {
          console.log(`[재연결] 지난 후보 무시(옛 표식: ${ufrag})`);
          continue;
        }
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
          appliedCount += 1;
        } catch (e) {
          console.log(`[재연결] 중복 offer 후보 적용 실패(표식: ${ufrag || 'none'}):`, e?.message || e);
        }
      }

      // (나) 적용은 끝났지만 answer 생성·수집 중: answer 재전송 보류
      if (!isRestartAnswerCreatedRef.current) {
        console.log(`[재연결] 중복 offer 수신 — answer 생성 중: 후보 적용 ${appliedCount}개, answer 재전송 보류(완성 후 자동 전송) (회차: ${payload.restart_id})`);
        return;
      }

      // (다) answer 생성 완료: 최신 localDescription 재전송
      console.log(`[재연결] 중복 offer 수신 — 회차: ${payload.restart_id}, 꺼낸 후보: ${extracted.length}개(적용: ${appliedCount}개)`);

      if (pc.localDescription) {
        console.log(`[재연결] 중복 offer 수신에 따른 최신 answer 재전송 — 회차: ${payload.restart_id}`);
        sendSignal('ice-restart-answer', {
          call_id: currentCallIdRef.current,
          restart_id: payload.restart_id,
          sdp: pc.localDescription,
        });
      }
      return;
    }

    // 첫 offer 도착 시 받은 사람의 request 되풀이 타이머 즉시 정지
    stopIceRestartRepeatTimer('첫 offer 수신 처리');

    lastHandledRestartIdRef.current = payload.restart_id;
    iceRestartIdRef.current = payload.restart_id;
    isIceRestartingRef.current = true;
    isRestartRemoteSdpAppliedRef.current = false;
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;
    iceRestartLoggedErrorCodesRef.current.clear();
    startIceRestartSuccessPoll(pc);
    iceRestartCountRef.current = Math.max(iceRestartCountRef.current, 1);
    if (!iceRestartStartTimeRef.current) {
      iceRestartStartTimeRef.current = Date.now();
    }

    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }

    // 20초 제한 시간 타이머 (받은 사람도 동일 적용)
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
    }
    iceRestartTimeoutRef.current = setTimeout(async () => {
      iceRestartTimeoutRef.current = null;
      if (peerRecoveryWaitRef.current?.callId) {
        console.log('[복구 대기] 기다림 중 → restart 타임아웃 보류');
        return;
      }
      if (isIceRestartingRef.current) {
        isIceRestartingRef.current = false;
        isRestartRemoteSdpAppliedRef.current = false;
        stopIceRestartRepeatTimer('20초 제한 시간 초과');
        stopIceRestartSuccessPoll();
        console.log(`[재연결] 실패 — 20초 제한 시간 초과 (회차: ${payload.restart_id})`);
        await logRestartCandidatePairsDiagnostic(pcRef.current);
        if (await abortIfPeerEndedInDb(currentCallIdRef.current)) return;
        if (pcRebuildCountRef.current < 3) {
          triggerPeerConnectionRebuildRef.current?.('callee_restart_timeout', 'callee');
          return;
        }
        terminateCall({
          status: 'failed',
          reason: 'ice_restart_timeout',
          toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
          notifyPeer: false,
        });
      }
    }, ICE_RESTART_TIMEOUT_MS);

    // [결함 ④ 작업 2-A 보강] 새 원격 설명(Offer) 적용 및 보관 후보 처리를 출입증 조회 전으로 앞당김
    try {
      const prevRemoteUfrags = getRemoteUfrags(pc);
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));

      // 2-2. 상대 후보 유형별 개수 (운영 진단)
      const remoteCounts = countCandidatesInSdp(payload.sdp);
      console.log(`[재연결] 상대 후보 — host ${remoteCounts.host} / srflx ${remoteCounts.srflx} / relay ${remoteCounts.relay}`);

      await drainRestartCandidatesAfterRemoteSdp(pc, prevRemoteUfrags);
    } catch (offerErr) {
      console.error('[재연결] Offer setRemoteDescription 실패:', offerErr);
      stopIceRestartRepeatTimer('Offer setRemoteDescription 실패');
      stopIceRestartSuccessPoll();
      if (iceRestartTimeoutRef.current) {
        clearTimeout(iceRestartTimeoutRef.current);
        iceRestartTimeoutRef.current = null;
      }
      isIceRestartingRef.current = false;
      isRestartRemoteSdpAppliedRef.current = false;
      terminateCall({
        status: 'failed',
        reason: 'ice_restart_offer_failed',
        toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
        notifyPeer: false,
      });
      return;
    }

    // 출입증 확인 및 setConfiguration (기존 try/catch 유지)
    let refreshed = false;
    try {
      const res = await getIceServersForRestart();
      refreshed = res.refreshed;
      try {
        const currentConfig = pc.getConfiguration ? pc.getConfiguration() : {};
        pc.setConfiguration({
          ...currentConfig,
          iceServers: res.iceServers,
        });
      } catch (cfgErr) {
        console.warn('[재연결] setConfiguration 실패, 기존 설정 유지:', cfgErr?.message || cfgErr);
      }
    } catch (tokenErr) {
      console.warn('[재연결] 출입증 확인 예외, 기존 설정 유지:', tokenErr?.message || tokenErr);
    }

    console.log(`[재연결] Offer 수신 처리 — 회차 번호: ${payload.restart_id}, 출입증 갱신: ${refreshed ? '새로 받음' : '기존 재사용'}`);

    try {
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      // [결함 ④ 작업 2-A] 후보 수집 완료 대기 (최대 4초)
      const gatherRes = await waitForIceGatheringComplete(pc, 4000);

      // 중단 검사
      if (pc !== pcRef.current || !isIceRestartingRef.current || callStateRef.current !== 'connected') {
        return;
      }

      console.log(`[재연결] 후보 수집 — 완료: ${gatherRes.complete ? '예' : '시간초과'}, 대기: ${gatherRes.waitedMs}ms, 근거: ${gatherRes.reason}, 역할: 받은 사람`);
      const myCounts = countCandidatesInSdp(pc.localDescription);
      console.log(`[재연결] 내 후보 — ${formatCandidateCounts(myCounts)}`);

      // [결함 ④ 작업 3 2단계] 길 막힘 감지 판정
      logIceRoadblockJudgement({ pc, counts: myCounts, complete: gatherRes.complete, reason: gatherRes.reason, place: '재연결 받는 쪽', role: '받은 사람', round: payload.restart_id, roadblockConfirmedRef, onRoadblockConfirmed: handleRoadblockConfirmed });

      isRestartAnswerCreatedRef.current = true; // [결함 ④ 작업 2-A 재시험] 받은 사람 측 SDP 교환 완료
      stopIceRestartRepeatTimer('answer 생성 및 적용 완료 후 request 되풀이 정지');

      await sendSignal('ice-restart-answer', {
        call_id: currentCallIdRef.current,
        restart_id: payload.restart_id,
        sdp: pc.localDescription,
      });
    } catch (err) {
      console.error('[재연결] Answer 생성 또는 적용 실패:', err);
      stopIceRestartRepeatTimer('Answer 생성 또는 적용 실패');
      stopIceRestartSuccessPoll();
      if (iceRestartTimeoutRef.current) {
        clearTimeout(iceRestartTimeoutRef.current);
        iceRestartTimeoutRef.current = null;
      }
      isIceRestartingRef.current = false;
      isRestartRemoteSdpAppliedRef.current = false;
      terminateCall({
        status: 'failed',
        reason: 'ice_restart_answer_failed',
        toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
        notifyPeer: false,
      });
    }
  }, [userId, sendSignal, terminateCall, t, drainRestartCandidatesAfterRemoteSdp, stopIceRestartRepeatTimer, startIceRestartSuccessPoll, stopIceRestartSuccessPoll, handleRoadblockConfirmed, abortIfPeerEndedInDb]);

  /**
   * [결함 ④ 작업 2-A] 수신된 ice-restart-answer 처리 (건 사람 측)
   */
  const handleIceRestartAnswer = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'connected') return;
    if (isNetworkSwitchRecoveringRef.current) return;

    // 회차 번호 검문: 지금 진행 중인 번호와 같을 때만 처리, 다르면 버림
    if (payload.restart_id !== iceRestartIdRef.current) {
      console.log(`[재연결] 무시한 신호: 진행 중 회차와 불일치하는 answer 무시 (수신: ${payload.restart_id}, 진행중: ${iceRestartIdRef.current})`);
      return;
    }

    const pc = pcRef.current;
    if (!pc) return;

    // 건 사람 쪽 되풀이 타이머 정지 (answer 도착)
    stopIceRestartRepeatTimer('answer 수신 처리');

    // [결함 ④ 작업 2-A 보강 2] 같은 회차의 answer를 이미 적용한 경우 (두 번째부터는 setRemoteDescription 생략하고 candidate만 추가)
    if (restartAnswerAppliedRef.current) {
      const sdpText = typeof payload.sdp === 'string' ? payload.sdp : payload.sdp?.sdp;
      const extracted = extractCandidatesFromSdp(sdpText);
      let appliedCount = 0;
      for (const cand of extracted) {
        const { type, proto } = parseCandidateTypeAndProto(cand.candidate);
        if (type === 'host' && proto === 'tcp') continue;
        const ufrag = getCandidateUfrag(cand.candidate);
        if (ufrag && staleRemoteUfragsRef.current.has(ufrag)) {
          console.log(`[재연결] 지난 후보 무시(옛 표식: ${ufrag})`);
          continue;
        }
        try {
          await pc.addIceCandidate(new RTCIceCandidate(cand));
          appliedCount += 1;
        } catch (e) {
          console.log(`[재연결] 중복 answer 후보 적용 실패(표식: ${ufrag || 'none'}):`, e?.message || e);
        }
      }
      console.log(`[재연결] 중복 answer 수신 — 회차: ${payload.restart_id}, 꺼낸 후보: ${extracted.length}개(적용: ${appliedCount}개)`);
      return;
    }

    restartAnswerAppliedRef.current = true;
    lastHandledRestartIdRef.current = payload.restart_id;

    try {
      if (pc.signalingState !== 'have-local-offer') {
        console.log(`[재연결] 늦은 answer — 설명 적용 생략 (상태: ${pc.signalingState})`);
        return;
      }
      const prevRemoteUfrags = getRemoteUfrags(pc);
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));

      // 2-2. 상대 후보 유형별 개수 (운영 진단)
      const remoteCounts = countCandidatesInSdp(payload.sdp);
      console.log(`[재연결] 상대 후보 — host ${remoteCounts.host} / srflx ${remoteCounts.srflx} / relay ${remoteCounts.relay}`);

      console.log(`[재연결] Answer 수신 및 remoteDescription 설정 완료 — 회차 번호: ${payload.restart_id}`);

      // [결함 ④ 작업 2-A 보강] 대기 중인 보관 후보 일괄 적용 (공통 함수 호출)
      await drainRestartCandidatesAfterRemoteSdp(pc, prevRemoteUfrags);
    } catch (err) {
      console.error('[재연결] Answer remoteDescription 설정 실패:', err);
    }
  }, [userId, drainRestartCandidatesAfterRemoteSdp, stopIceRestartRepeatTimer]);

  /**
   * [결함 ④ 5-2 2단계 보강] 새 연결(rebuild) 성공 시 마무리 공통 함수
   * connectionState 또는 iceConnectionState 중 어느 쪽이 먼저 성공 판정을 내리더라도 1회 이상 안전하게 수행된다.
   */
  const finishRebuildSuccess = useCallback((rebuildElapsed) => {
    console.log(`[복구 2단계] 연결 성공 — rebuild 소요: ${rebuildElapsed}ms`);

    // 하트비트 재가동 (양쪽 공통)
    if (heartbeatSendIntervalRef.current) {
      clearInterval(heartbeatSendIntervalRef.current);
      heartbeatSendIntervalRef.current = null;
    }
    if (heartbeatCheckIntervalRef.current) {
      clearInterval(heartbeatCheckIntervalRef.current);
      heartbeatCheckIntervalRef.current = null;
    }
    startHeartbeatRef.current?.();
    console.log('[복구 2단계] 하트비트 재가동');

    // [결함 ④ 5-3 C안] 망 전환 복구 성공 처리
    if (networkRecoveryConfirmedAtRef.current) {
      const elapsed = Date.now() - networkRecoveryConfirmedAtRef.current;
      console.log(`[복구 C안] 성공 — 확정부터 소요 ${elapsed}ms`);
      networkRecoveryConfirmedAtRef.current = 0;
    }
    if (networkRecoverySafetyTimerRef.current) {
      clearTimeout(networkRecoverySafetyTimerRef.current);
      networkRecoverySafetyTimerRef.current = null;
    }
    if (networkRecoveryDeadlineTimerRef.current) {
      clearTimeout(networkRecoveryDeadlineTimerRef.current);
      networkRecoveryDeadlineTimerRef.current = null;
    }
    if (networkRecoveryOnlineHandlerRef.current) {
      window.removeEventListener('online', networkRecoveryOnlineHandlerRef.current);
      networkRecoveryOnlineHandlerRef.current = null;
    }
    if (reloadRecoveryTimeoutRef.current) {
      clearTimeout(reloadRecoveryTimeoutRef.current);
      reloadRecoveryTimeoutRef.current = null;
    }
    if (peerRecoveryWaitRef.current) {
      if (peerRecoveryWaitRef.current.timeoutId) clearTimeout(peerRecoveryWaitRef.current.timeoutId);
      if (peerRecoveryWaitRef.current.countdownIntervalId) clearInterval(peerRecoveryWaitRef.current.countdownIntervalId);
      peerRecoveryWaitRef.current = null;
    }
    peerRecoveryExtendedRef.current = false;
    setPeerRecoveryRemainingSec(null);
    activeLiveRecoverIdRef.current = null;
    isNetworkSwitchRecoveringRef.current = false;
    isNetworkSwitchSuspectedRef.current = false;
    setIsNetworkReconnectingSelf(false);
    // [결함 ④ 5-3 2단계] 새 연결 성공 시 상대 무음 안내 해제
    peerAudioStagnantCountRef.current = 0;
    if (isPeerAudioUnstableRef.current) {
      isPeerAudioUnstableRef.current = false;
      setIsPeerAudioUnstable(false);
      console.log('[상대 무음 감지] 안내 해제 — 사유: 새 연결 성공');
    }
    setIsNetworkReconnectingPeer(false);
    setIsReconnectedSuccess(true);
    if (reconnectedSuccessTimerRef.current) clearTimeout(reconnectedSuccessTimerRef.current);
    reconnectedSuccessTimerRef.current = setTimeout(() => {
      setIsReconnectedSuccess(false);
      reconnectedSuccessTimerRef.current = null;
    }, 2000);

    // 폰: clearCallRecoveryInfo()를 호출하고 ⑦ 안전 타이머를 clear한다.
    if (recoverSelfTimeoutRef.current) {
      clearTimeout(recoverSelfTimeoutRef.current);
      recoverSelfTimeoutRef.current = null;
    }
    if (recoveryDbPollTimerRef.current) { clearInterval(recoveryDbPollTimerRef.current); recoveryDbPollTimerRef.current = null; }

    const pendingResume = pendingCameraResumeRef.current;
    pendingCameraResumeRef.current = null;
    if (pendingResume) {
      if (pendingResume.callId !== currentCallIdRef.current) {
        console.log('[복구 2단계] 카메라 자동 재가동 건너뜀 — 사유: 통화 불일치');
      } else if (localVideoTrackRef.current && localVideoTrackRef.current.readyState === 'live') {
        console.log('[복구 2단계] 카메라 자동 재가동 건너뜀 — 사유: 카메라 이미 켜짐');
      } else {
        const resumePromise = startCameraRef.current?.(pendingResume.facing)
          || Promise.resolve({ ok: false, reason: 'not_ready' });
        resumePromise
          .then((res) => {
            if (currentCallIdRef.current !== pendingResume.callId || callStateRef.current !== 'connected') {
              return;
            }
            if (res && res.ok) {
              console.log('[복구 2단계] 카메라 자동 재가동 — 결과: 성공');
            } else {
              console.log(`[복구 2단계] 카메라 자동 재가동 — 결과: 실패(${res?.reason || '알수없음'})`);
            }
          })
          .catch((err) => {
            console.log(`[복구 2단계] 카메라 자동 재가동 — 결과: 실패(${err?.message || err})`);
          });
      }
    }

    clearCallRecoveryInfo();
  }, []);

  /**
   * 2차 방어 (가): WebRTC 연결 상태 감시 핸들러
   */
  const handlePeerConnectionStateChange = useCallback((state, eventPc = null) => {
    if (eventPc && eventPc !== pcRef.current) return;
    if (import.meta.env.DEV) {
      const elapsed = callStartTimeRef.current ? `${Date.now() - callStartTimeRef.current}ms` : '0ms';
      console.log(`[ICE 전이] connectionState: ${state} (${elapsed})`);
    }
    if (callStateRef.current !== 'connected') return;

    // 2-4. 재연결 진행 중 connectionState 변화 (운영 진단)
    if (isIceRestartingRef.current) {
      console.log(`[재연결] connectionState: ${state}`);
    }
    if (isRebuildingPcRef.current) {
      console.log(`[재연결] 새 연결 connectionState: ${state}`);
    }

    // [결함 ④ 작업 2-A 재시험] 재연결 진행 중 connected 외 상태 관측 기록
    if (isIceRestartingRef.current && state !== 'connected') {
      restartSawNonConnectedRef.current = true;
    }

    if (state === 'failed') {
      if (import.meta.env.DEV) {
        logRemoteCandidatesSummary(remoteCandidatesStatsRef, remoteCandidatesLoggedRef, 'failed', excludedRemoteHostTcpCountRef);
        dumpCandidatePairs(pcRef.current, 'connectionState failed', iceDumpCountRef);
        handleIceRestartJudgeFailed();
      }
      if (isNetworkSwitchRecoveringRef.current === true && recoverSelfTimeoutRef.current !== null) {
        console.log('[복구 2단계] 새 연결 실패 → 재시도 없이 종료');
        terminateCall({
          status: 'failed',
          reason: 'reload_recovery_failed',
          toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
          toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
          notifyPeer: true,
        });
        return;
      }
      if (peerRecoveryWaitRef.current?.callId) {
        console.log('[복구 대기] 기다림 중 → connectionState failed 보류');
        return;
      }
      if (isRebuildingPcRef.current) {
        if (import.meta.env.DEV) {
          console.log('[재연결] connectionState failed 발생했으나 새 연결 진행 중이므로 무시');
        }
        return;
      }
      if (isIceRestartingRef.current || isNetworkSwitchRecoveringRef.current) {
        if (import.meta.env.DEV) {
          console.log('[재연결] connectionState failed 발생했으나 재연결/망전환 진행 중이므로 무시');
        }
        return;
      }
      if (isNetworkSwitchSuspectedRef.current) {
        console.log(`[망 변경] 의심 중 connectionState failed → 감시 건너뛰고 즉시 확정 (iceRestartCount: ${iceRestartCountRef.current})`);
        confirmNetworkSwitchRef.current?.('webrtc_failed');
        return;
      }
      if (iceRestartCountRef.current < 1) {
        triggerIceRestart('A_connection_failed');
        return;
      }
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      console.log(`[연결 실패 종료] 출처=connectionState, 상태=failed, iceRestartCount=${iceRestartCountRef.current}, 의심=${isNetworkSwitchSuspectedRef.current}, 복구중=${isNetworkSwitchRecoveringRef.current}, 상대복구대기=${peerRecoveryWaitRef.current?.callId ? '있음' : '없음'}, 역할=${isCallerRef.current ? 'caller' : 'callee'}`);
      terminateCall({
        status: 'failed',
        reason: 'connection_failed',
        toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
        notifyPeer: true,
      });
    } else if (state === 'disconnected') {
      if (isRebuildingPcRef.current || isIceRestartingRef.current || isNetworkSwitchRecoveringRef.current) {
        return;
      }
      if (!disconnectTimerRef.current) {
        disconnectTimerRef.current = setTimeout(() => {
          disconnectTimerRef.current = null;
          if (peerRecoveryWaitRef.current?.callId || isNetworkSwitchRecoveringRef.current) {
            console.log('[복구 대기] 기다림 또는 망 전환 복구 중 → 단절 유예 만료 보류');
            return;
          }
          if (callStateRef.current === 'connected') {
            if (!isIceRestartingRef.current && !isRebuildingPcRef.current && iceRestartCountRef.current < 1) {
              triggerIceRestart('B_disconnected_timeout');
              return;
            }
            terminateCall({
              status: 'failed',
              reason: 'connection_lost',
              toastMessage: t('call.connection_lost', '네트워크 연결이 끊겨 통화가 종료되었습니다.'),
              notifyPeer: false,
            });
          }
        }, DISCONNECT_GRACE_MS);
      }
    } else if (state === 'connected') {
      // [결함 ④ 작업 3 4단계] 연결 회복 관측 시 대기 해제
      if (peerRecoveryWaitRef.current) {
        console.log('[복구 대기] 연결 회복 관측 → 기다림 해제');
        if (peerRecoveryWaitRef.current.timeoutId) clearTimeout(peerRecoveryWaitRef.current.timeoutId);
        if (peerRecoveryWaitRef.current.countdownIntervalId) clearInterval(peerRecoveryWaitRef.current.countdownIntervalId);
        peerRecoveryWaitRef.current = null;
        setPeerRecoveryRemainingSec(null);
      }

      // [결함 ④ 작업 3] 실제 연결 시점에 1회만 통화 시간 측정 시작
      if (!timerStartedRef.current) {
        timerStartedRef.current = true;
        setIsAudioConnected(true);
        if (audioConnectWatchTimerRef.current) {
          clearTimeout(audioConnectWatchTimerRef.current);
          audioConnectWatchTimerRef.current = null;
          const elapsedFromAnswer = audioConnectWatchStartedAtRef.current > 0 ? (Date.now() - audioConnectWatchStartedAtRef.current) : 0;
          console.log(`[소리 연결 감시] 소리 연결 — 받은 뒤 ${elapsedFromAnswer}ms`);
        }
        if (needsReloadPreCallRef.current) {
          needsReloadPreCallRef.current = false;
          console.log('[통화 전 망 변경] 소리 연결 성공 → 딱지 해제');
        }
        connectedAtRef.current = Date.now();
        startTimerRef.current?.();
      }

      if (isRebuildingPcRef.current) {
        isRebuildingPcRef.current = false;
        if (pcRebuildTimeoutRef.current) {
          clearTimeout(pcRebuildTimeoutRef.current);
          pcRebuildTimeoutRef.current = null;
        }
        if (pcRebuildRepeatTimerRef.current) {
          clearInterval(pcRebuildRepeatTimerRef.current);
          pcRebuildRepeatTimerRef.current = null;
        }
        lastHeartbeatAtRef.current = Date.now();
        const rebuildElapsed = pcRebuildStartTimeRef.current ? Date.now() - pcRebuildStartTimeRef.current : 0;
        console.log(`[재연결] 새 연결 성공 — 소요: ${rebuildElapsed}ms, 판정 경로: 이벤트(connectionState)`);
        logRebuildSelectedPair(pcRef.current, rebuildElapsed);
        finishRebuildSuccess(rebuildElapsed);
      } else if (isIceRestartingRef.current) {
        if (!isRestartSdpExchangeDone()) {
          if (import.meta.env.DEV) {
            console.log('[재연결] 이른 connected 무시 — 경로: 이벤트(connectionState), SDP 교환 미완료');
          }
        } else {
          handleIceRestartSuccess('이벤트(connectionState)');
        }
      }
      if (import.meta.env.DEV) {
        logRemoteCandidatesSummary(remoteCandidatesStatsRef, remoteCandidatesLoggedRef, 'connected', excludedRemoteHostTcpCountRef);
        handleIceRestartJudgeConnected();
      }
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      if (isNetworkSwitchRecoveringRef.current || isNetworkSwitchSuspectedRef.current) {
        isNetworkSwitchRecoveringRef.current = false;
        isNetworkSwitchSuspectedRef.current = false;
        setIsNetworkReconnectingSelf(false);
        setIsNetworkReconnectingPeer(false);
      }
      logSelectedCandidatePair(pcRef.current, hasLoggedRouteRef);
    }
  }, [finishRebuildSuccess, terminateCall, t, handleIceRestartJudgeConnected, handleIceRestartJudgeFailed, triggerIceRestart, handleIceRestartSuccess, isRestartSdpExchangeDone]);

  const handleIceConnectionStateChange = useCallback((iceState, eventPc = null) => {
    if (eventPc && eventPc !== pcRef.current) return;
    if (import.meta.env.DEV) {
      const elapsed = callStartTimeRef.current ? `${Date.now() - callStartTimeRef.current}ms` : '0ms';
      console.log(`[ICE 전이] iceConnectionState: ${iceState} (${elapsed})`);
    }
    if (callStateRef.current !== 'connected') return;

    // 2-4. 재연결 진행 중 iceConnectionState 변화 (운영 진단)
    if (isIceRestartingRef.current) {
      console.log(`[재연결] iceConnectionState: ${iceState}`);
    }
    if (isRebuildingPcRef.current) {
      console.log(`[재연결] 새 연결 iceConnectionState: ${iceState}`);
    }

    // [결함 ④ 작업 2-A 재시험] 재연결 진행 중 connected/completed 외 상태 관측 기록
    if (isIceRestartingRef.current && iceState !== 'connected' && iceState !== 'completed') {
      restartSawNonConnectedRef.current = true;
    }

    if (iceState === 'connected' || iceState === 'completed') {
      if (isRebuildingPcRef.current) {
        isRebuildingPcRef.current = false;
        if (pcRebuildTimeoutRef.current) {
          clearTimeout(pcRebuildTimeoutRef.current);
          pcRebuildTimeoutRef.current = null;
        }
        if (pcRebuildRepeatTimerRef.current) {
          clearInterval(pcRebuildRepeatTimerRef.current);
          pcRebuildRepeatTimerRef.current = null;
        }
        lastHeartbeatAtRef.current = Date.now();
        const rebuildElapsed = pcRebuildStartTimeRef.current ? Date.now() - pcRebuildStartTimeRef.current : 0;
        console.log(`[재연결] 새 연결 성공 — 소요: ${rebuildElapsed}ms, 판정 경로: 이벤트(iceConnectionState)`);
        logSelectedCandidatePair(pcRef.current, hasLoggedRouteRef);
        finishRebuildSuccess(rebuildElapsed);
      } else if (isIceRestartingRef.current) {
        if (!isRestartSdpExchangeDone()) {
          if (import.meta.env.DEV) {
            console.log('[재연결] 이른 connected 무시 — 경로: 이벤트(iceConnectionState), SDP 교환 미완료');
          }
        } else {
          handleIceRestartSuccess('이벤트(iceConnectionState)');
        }
      }
      if (import.meta.env.DEV) {
        logRemoteCandidatesSummary(remoteCandidatesStatsRef, remoteCandidatesLoggedRef, 'connected', excludedRemoteHostTcpCountRef);
        handleIceRestartJudgeConnected();
      }
      logSelectedCandidatePair(pcRef.current, hasLoggedRouteRef);
    }

    if (iceState === 'failed') {
      if (import.meta.env.DEV) {
        logRemoteCandidatesSummary(remoteCandidatesStatsRef, remoteCandidatesLoggedRef, 'failed', excludedRemoteHostTcpCountRef);
        dumpCandidatePairs(pcRef.current, 'iceConnectionState failed', iceDumpCountRef);
        handleIceRestartJudgeFailed();
      }
      if (isNetworkSwitchRecoveringRef.current === true && recoverSelfTimeoutRef.current !== null) {
        console.log('[복구 2단계] 새 연결 실패 → 재시도 없이 종료');
        terminateCall({
          status: 'failed',
          reason: 'reload_recovery_failed',
          toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
          toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
          notifyPeer: true,
        });
        return;
      }
      if (peerRecoveryWaitRef.current?.callId || isNetworkSwitchRecoveringRef.current) {
        console.log('[복구 대기] 기다림 또는 망 전환 복구 중 → iceConnectionState failed 보류');
        return;
      }
      if (isRebuildingPcRef.current) {
        if (import.meta.env.DEV) {
          console.log('[재연결] iceConnectionState failed 발생했으나 새 연결 진행 중이므로 무시');
        }
        return;
      }
      if (isIceRestartingRef.current) {
        if (import.meta.env.DEV) {
          console.log('[재연결] iceConnectionState failed 발생했으나 재연결 진행 중이므로 무시');
        }
        return;
      }
      if (isNetworkSwitchSuspectedRef.current) {
        console.log(`[망 변경] 의심 중 iceConnectionState failed → 감시 건너뛰고 즉시 확정 (iceRestartCount: ${iceRestartCountRef.current})`);
        confirmNetworkSwitchRef.current?.('webrtc_failed');
        return;
      }
      if (iceRestartCountRef.current < 1) {
        triggerIceRestart('A_ice_failed');
        return;
      }
      console.log(`[연결 실패 종료] 출처=iceConnectionState, 상태=failed, iceRestartCount=${iceRestartCountRef.current}, 의심=${isNetworkSwitchSuspectedRef.current}, 복구중=${isNetworkSwitchRecoveringRef.current}, 상대복구대기=${peerRecoveryWaitRef.current?.callId ? '있음' : '없음'}, 역할=${isCallerRef.current ? 'caller' : 'callee'}`);
      terminateCall({
        status: 'failed',
        reason: 'ice_failed',
        toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
        notifyPeer: true,
      });
    }
  }, [finishRebuildSuccess, terminateCall, t, handleIceRestartJudgeConnected, handleIceRestartJudgeFailed, triggerIceRestart, handleIceRestartSuccess, isRestartSdpExchangeDone]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 구 연결(oldPc) 정리 전용 함수
   * 마이크·카메라 트랙은 정지하지 않고 보존하며, 구 PC 이벤트와 타이머만 깨끗이 닫는다.
   */
  const closePeerConnectionOnly = useCallback((oldPc) => {
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
      iceRestartTimeoutRef.current = null;
    }
    stopIceRestartRepeatTimer('새 연결 전환으로 ICE restart 되풀이 정지');
    stopIceRestartSuccessPoll();
    isIceRestartingRef.current = false;
    iceRestartCountRef.current = 1; // 새 연결에서 ICE restart는 다시 하지 않음

    // 후보 대기열 및 통계 리셋
    queuedCandidatesRef.current = [];
    sentIceCandidatesRef.current = [];
    pendingRestartCandidatesRef.current = [];
    staleRemoteUfragsRef.current = new Set();
    isRestartRemoteSdpAppliedRef.current = false;
    restartAnswerAppliedRef.current = false;
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;
    iceRestartLoggedErrorCodesRef.current.clear();
    localCandidatesStatsRef.current = { host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } };
    remoteCandidatesStatsRef.current = { host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } };
    localCandidatesLoggedRef.current = false;
    remoteCandidatesLoggedRef.current = false;
    iceDumpCountRef.current = 0;
    excludedLocalHostTcpCountRef.current = 0;
    excludedRemoteHostTcpCountRef.current = 0;

    if (oldPc) {
      try {
        oldPc.onconnectionstatechange = null;
        oldPc.oniceconnectionstatechange = null;
        oldPc.onicegatheringstatechange = null;
        oldPc.onicecandidateerror = null;
        oldPc.onicecandidate = null;
        oldPc.ontrack = null;
        oldPc.close();
      } catch (err) {
        console.warn('[재연결] 구 PeerConnection close 오류(무시):', err);
      }
    }
  }, [stopIceRestartRepeatTimer, stopIceRestartSuccessPoll]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 새 연결 신호 되풀이 타이머 정지
   */
  const stopRebuildRepeatTimer = useCallback((reason = '기본') => {
    if (pcRebuildRepeatTimerRef.current) {
      clearInterval(pcRebuildRepeatTimerRef.current);
      pcRebuildRepeatTimerRef.current = null;
      if (import.meta.env.DEV) {
        console.log(`[재연결] 새 연결 되풀이 타이머 정지 — 사유: ${reason}`);
      }
    }
  }, []);

  /**
   * [결함 ④ 5-3 작업① 보완 2차] 옛 재연결 타이머 7개 항목 정리 함수
   */
  const clearLegacyRecoveryTimers = useCallback((reason = '기본') => {
    if (heartbeatCheckIntervalRef.current) {
      clearInterval(heartbeatCheckIntervalRef.current);
      heartbeatCheckIntervalRef.current = null;
    }
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
      iceRestartTimeoutRef.current = null;
    }
    stopIceRestartRepeatTimer(`${reason}로 ICE restart 타이머 정지`);
    stopIceRestartSuccessPoll();
    if (pcRebuildTimeoutRef.current) {
      clearTimeout(pcRebuildTimeoutRef.current);
      pcRebuildTimeoutRef.current = null;
    }
    stopRebuildRepeatTimer(`${reason}로 rebuild 타이머 정지`);
  }, [stopIceRestartRepeatTimer, stopIceRestartSuccessPoll, stopRebuildRepeatTimer]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 대기열에 쌓인 후보 일괄 적용
   */
  const drainQueuedCandidates = useCallback(async (pc) => {
    if (!pc || queuedCandidatesRef.current.length === 0) return { applied: 0, failed: 0 };
    let applied = 0;
    let failed = 0;
    const candidates = [...queuedCandidatesRef.current];
    queuedCandidatesRef.current = [];
    for (const cand of candidates) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(cand));
        applied += 1;
      } catch (e) {
        failed += 1;
        console.warn('[재연결] 대기 ICE 후보 적용 실패(무시):', e?.message || e);
      }
    }
    return { applied, failed };
  }, []);

  /**
   * [B-3a 고칠 목록 ⑧] 카메라 전환과 재연결 겹침 방어 — 현재 videoSender에 현재 카메라 트랙 동기화
   */
  const syncVideoSenderTrack = useCallback(async (reason) => {
    const track = localVideoTrackRef.current;
    if (!track || track.readyState !== 'live') return;
    const sender = videoSenderRef.current;
    if (!sender) return;
    if (sender.track === track) return;

    try {
      await sender.replaceTrack(track);
      console.info('[화상] 영상 다시 끼움 — 계기: ' + reason);
    } catch (err) {
      console.warn('[화상] 영상 다시 끼움 실패 — 계기: ' + reason, err);
    }
  }, []);

  /**
   * [결함 ④ 작업 2-A 보강 5] 재생성 전용 새 RTCPeerConnection 인스턴스 구축
   */
  const setupRebuiltPeerConnection = useCallback(async (role) => {
    closePeerConnectionOnly(pcRef.current);

    const { iceServers } = await getIceServersForRestart();
    const forceRelay = isForceRelayEnabled();
    const rtcConfig = {
      iceServers,
      ...(forceRelay ? { iceTransportPolicy: 'relay' } : {}),
    };

    const newPc = new RTCPeerConnection(rtcConfig);
    pcRef.current = newPc;

    newPc.onconnectionstatechange = () => handlePeerConnectionStateChange(newPc.connectionState, newPc);
    newPc.oniceconnectionstatechange = () => handleIceConnectionStateChange(newPc.iceConnectionState, newPc);
    newPc.onicegatheringstatechange = () => {
      if (import.meta.env.DEV) {
        console.log(`[ICE 전이] iceGatheringState: ${newPc.iceGatheringState} (새 연결)`);
      }
    };
    newPc.onicecandidateerror = (event) => {
      if (isRebuildingPcRef.current && event) {
        const code = event.errorCode;
        if (code && !iceRestartLoggedErrorCodesRef.current.has(code)) {
          iceRestartLoggedErrorCodesRef.current.add(code);
          const target = formatIceErrorTarget(event.url);
          console.log(`[재연결] ICE 후보 오류 — 코드: ${code}, 대상: ${target}, 사유: ${event.errorText || 'none'}`);
        }
      }
    };

    newPc.onicecandidate = (event) => {
      if (event.candidate) {
        if (isRebuildingPcRef.current) {
          const elapsed = pcRebuildStartTimeRef.current ? Date.now() - pcRebuildStartTimeRef.current : 0;
          const { type: candType } = parseCandidateTypeAndProto(event.candidate);
          if (candType === 'srflx' && !rebuildFirstSrflxAtRef.current) {
            rebuildFirstSrflxAtRef.current = elapsed;
          }
          if (candType === 'relay' && !rebuildFirstRelayAtRef.current) {
            rebuildFirstRelayAtRef.current = elapsed;
          }
        }
        const { type, proto } = parseCandidateTypeAndProto(event.candidate);
        if (type === 'host' && proto === 'tcp') return;
        const candJson = event.candidate.toJSON();
        sendSignal('call:ice', {
          candidate: candJson,
          call_id: currentCallIdRef.current,
        });
      }
    };

    newPc.ontrack = (event) => {
      if (event.track.kind === 'audio') {
        if (remoteAudioRef.current && event.streams[0]) {
          callAudioGraph.attachRemoteStream(event.streams[0], remoteAudioRef.current);
        }
      } else if (event.track.kind === 'video') {
        remoteVideoTrackRef.current = event.track;
        setRemoteVideoStream(new MediaStream([event.track]));
        framesFlowingRef.current = (event.track.muted === false);
        applyRemoteVideoVisibility();
        event.track.onunmute = () => {
          framesFlowingRef.current = true;
          applyRemoteVideoVisibility();
        };
        event.track.onmute = () => {
          framesFlowingRef.current = false;
          applyRemoteVideoVisibility();
        };
      }
    };

    // 로컬 마이크 트랙 장착
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((track) => {
        const sender = newPc.addTrack(track, localStreamRef.current);
        audioSenderRef.current = sender;
      });
    }

    // 비디오 트랜시버 장착 (건 사람인 경우 sendrecv 사전 생성)
    if (role === 'caller') {
      const videoTransceiver = newPc.addTransceiver('video', { direction: 'sendrecv' });
      videoSenderRef.current = videoTransceiver.sender;
      if (localVideoTrackRef.current && localVideoTrackRef.current.readyState === 'live') {
        videoTransceiver.sender.replaceTrack(localVideoTrackRef.current).catch(() => {});
      }
    }

    return newPc;
  }, [closePeerConnectionOnly, handlePeerConnectionStateChange, handleIceConnectionStateChange, sendSignal, applyRemoteVideoVisibility]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 건 사람 측 새 연결 offer 생성 및 전송
   */
  const startPeerConnectionRebuildAsCaller = useCallback(async (reason) => {
    const rebuildId = Date.now();
    currentRebuildIdRef.current = rebuildId;
    console.log(`[재연결] 새 연결 시작 — 이유: ${reason}, 역할: 건 사람, 회차: ${rebuildId}`);
    try {
      const newPc = await setupRebuiltPeerConnection('caller');
      const offer = await newPc.createOffer();
      await newPc.setLocalDescription(offer);

      const gatherRes = await waitForIceGatheringComplete(newPc, 4000, { earlyFinishOnRelay: true });
      if (newPc !== pcRef.current || !isRebuildingPcRef.current || callStateRef.current !== 'connected') {
        return;
      }
      console.log(`[재연결] 후보 수집 — 완료: ${gatherRes.complete ? '예' : '시간초과'}, 대기: ${gatherRes.waitedMs}ms, 근거: ${gatherRes.reason}, 역할: 건 사람(새 연결)`);
      const myCounts = countCandidatesInSdp(newPc.localDescription);
      const srflxMsText = rebuildFirstSrflxAtRef.current ? `${rebuildFirstSrflxAtRef.current}ms` : '없음';
      const relayMsText = rebuildFirstRelayAtRef.current ? `${rebuildFirstRelayAtRef.current}ms` : '없음';
      console.log(`[재연결] 내 후보(새 연결) — ${formatCandidateCounts(myCounts)} (첫 srflx: ${srflxMsText}, 첫 relay: ${relayMsText})`);

      // [결함 ④ 작업 3 2단계 / 5-3 2단계] 길 막힘 감지 판정 (망 변경 복구 중 재집계 생략)
      const shouldSkipRecheck =
        isNetworkSwitchRecoveringRef.current === true &&
        callStateRef.current === 'connected' &&
        getAutoReloadCount(readCallRecoveryUses(currentCallIdRef.current)) < MAX_AUTO_RELOADS_PER_CALL;

      logIceRoadblockJudgement({
        pc: newPc,
        counts: myCounts,
        complete: gatherRes.complete,
        reason: gatherRes.reason,
        place: 'rebuild 건 쪽',
        role: '건 사람(새 연결)',
        round: rebuildId,
        roadblockConfirmedRef,
        onRoadblockConfirmed: handleRoadblockConfirmed,
        skipRecheck: shouldSkipRecheck,
      });

      lastRebuildOfferSentAtRef.current = Date.now();
      const sendRes = await sendSignal('call:rebuild-offer', {
        call_id: currentCallIdRef.current,
        rebuild_id: rebuildId,
        rebuild_seq: 1,
        sdp: newPc.localDescription,
        video_capable: true,
      });
      console.log(`[재연결] 신호 전송 — 종류: call:rebuild-offer, 1차 결과: ${sendRes?.result || 'ok'}`);

      stopRebuildRepeatTimer('새 rebuild-offer 되풀이 타이머 시작 전 초기화');
      pcRebuildRepeatTimerRef.current = setInterval(() => {
        if (!isRebuildingPcRef.current || callStateRef.current !== 'connected' || rebuildAnswerAppliedRef.current) {
          stopRebuildRepeatTimer('새 연결 종료 또는 answer 적용 완료');
          return;
        }
        sendSignal('call:rebuild-offer', {
          call_id: currentCallIdRef.current,
          rebuild_id: rebuildId,
          rebuild_seq: 1,
          sdp: pcRef.current?.localDescription,
          video_capable: true,
        });
      }, 2000);
    } catch (err) {
      console.error('[재연결] 건 사람 새 연결 offer 생성 실패:', err);
    }
  }, [setupRebuiltPeerConnection, sendSignal, stopRebuildRepeatTimer, handleRoadblockConfirmed]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 받은 사람 측 새 연결 request 전송
   */
  const startPeerConnectionRebuildAsCallee = useCallback(async (reason) => {
    console.log(`[재연결] 새 연결 시작 — 이유: ${reason}, 역할: 받은 사람`);
    const sendRes = await sendSignal('call:rebuild-request', {
      call_id: currentCallIdRef.current,
      rebuild_seq: 1,
      reason,
      video_capable: true,
    });
    console.log(`[재연결] 신호 전송 — 종류: call:rebuild-request, 1차 결과: ${sendRes?.result || 'ok'}`);

    stopRebuildRepeatTimer('새 rebuild-request 되풀이 타이머 시작 전 초기화');
    pcRebuildRepeatTimerRef.current = setInterval(() => {
      if (!isRebuildingPcRef.current || callStateRef.current !== 'connected' || rebuildAnswerCreatedRef.current) {
        stopRebuildRepeatTimer('rebuild-offer 수신 또는 종료');
        return;
      }
      sendSignal('call:rebuild-request', {
        call_id: currentCallIdRef.current,
        rebuild_seq: 1,
        reason,
        video_capable: true,
      });
    }, 2000);
  }, [sendSignal, stopRebuildRepeatTimer]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 새 연결 진입 관문 (1회 한정, 20초 타이머)
   */
  const triggerPeerConnectionRebuild = useCallback((reason, role) => {
    if (peerRecoveryWaitRef.current?.callId) {
      console.log('[복구 대기] 기다림 중 → triggerPeerConnectionRebuild 보류');
      return;
    }
    if (pcRebuildCountRef.current >= 3) {
      console.log(`[재연결] 새 연결 3회 제한 도달 — 추가 재연결 중단 (사유: ${reason})`);
      terminateCall({
        status: 'failed',
        reason: 'pc_rebuild_exhausted',
        toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
        notifyPeer: false,
      });
      return;
    }

    pcRebuildCountRef.current += 1;
    isRebuildingPcRef.current = true;
    rebuildAnswerAppliedRef.current = false;
    rebuildAnswerCreatedRef.current = false;
    pcRebuildStartTimeRef.current = Date.now();
    rebuildFirstSrflxAtRef.current = 0;
    rebuildFirstRelayAtRef.current = 0;

    if (pcRebuildTimeoutRef.current) clearTimeout(pcRebuildTimeoutRef.current);
    pcRebuildTimeoutRef.current = setTimeout(async () => {
      pcRebuildTimeoutRef.current = null;
      if (peerRecoveryWaitRef.current?.callId) {
        console.log('[복구 대기] 기다림 중 → rebuild 타임아웃 보류');
        return;
      }
      if (isRebuildingPcRef.current) {
        if (await abortIfPeerEndedInDb(currentCallIdRef.current)) return;
        isRebuildingPcRef.current = false;
        stopRebuildRepeatTimer('20초 제한 시간 초과');
        console.log('[재연결] 새 연결 실패 — 20초 제한 시간 초과');
        if (isNetworkSwitchRecoveringRef.current === true && recoverSelfTimeoutRef.current !== null) {
          terminateCall({
            status: 'failed',
            reason: 'reload_recovery_failed',
            toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
            toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
            notifyPeer: true,
          });
        } else {
          terminateCall({
            status: 'failed',
            reason: 'pc_rebuild_timeout',
            toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
            notifyPeer: false,
          });
        }
      }
    }, ICE_RESTART_TIMEOUT_MS);

    if (role === 'caller') {
      startPeerConnectionRebuildAsCaller(reason);
    } else {
      startPeerConnectionRebuildAsCallee(reason);
    }
  }, [terminateCall, t, stopRebuildRepeatTimer, startPeerConnectionRebuildAsCaller, startPeerConnectionRebuildAsCallee, abortIfPeerEndedInDb]);
  triggerPeerConnectionRebuildRef.current = triggerPeerConnectionRebuild;

  /**
   * [결함 ④ 작업 2-A 보강 5] 건 사람 측 call:rebuild-request 수신 처리
   */
  const handlePeerConnectionRebuildRequest = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    console.log(`[재연결] rebuild-request 수신 진입 — call: ${payload?.call_id?.slice(0, 8)}, sender: ${payload?.sender_id?.slice(0, 8)}`);
    if (!payload || payload.sender_id === userId) {
      console.log(`[재연결] rebuild-request 무시 — 사유: ${!payload ? '페이로드없음' : '자신이송신'}`);
      return;
    }
    if (payload.call_id !== currentCallIdRef.current) {
      console.log(`[재연결] rebuild-request 무시 — 사유: 통화불일치 (수신: ${payload.call_id?.slice(0, 8)}, 현재: ${currentCallIdRef.current?.slice(0, 8)})`);
      return;
    }
    if (peerIdRef.current && payload.sender_id !== peerIdRef.current) {
      console.log(`[재연결] rebuild-request 무시 — 사유: 발신자불일치 (보낸이: ${payload.sender_id?.slice(0, 8)}, 상대: ${peerIdRef.current?.slice(0, 8)})`);
      return;
    }
    if (callStateRef.current !== 'connected') {
      console.log(`[재연결] rebuild-request 무시 — 사유: 통화비연결 (현재: ${callStateRef.current})`);
      return;
    }

    if (payload.video_capable === true && peerVideoCapableRef.current !== true) {
      peerVideoCapableRef.current = true;
      setPeerVideoCapable(true);
      console.log('[복구 신호] 상대 화상 가능 확인 — 출처: call:rebuild-request');
    }

    if (isIceRestartingRef.current) {
      isIceRestartingRef.current = false;
      stopIceRestartRepeatTimer('rebuild-request 수신으로 ice-restart 정지');
      stopIceRestartSuccessPoll();
      if (iceRestartTimeoutRef.current) {
        clearTimeout(iceRestartTimeoutRef.current);
        iceRestartTimeoutRef.current = null;
      }
    }

    if (isRebuildingPcRef.current) {
      if (Date.now() - lastRebuildOfferSentAtRef.current > 1000 && pcRef.current?.localDescription) {
        lastRebuildOfferSentAtRef.current = Date.now();
        sendSignal('call:rebuild-offer', {
          call_id: currentCallIdRef.current,
          rebuild_id: currentRebuildIdRef.current,
          rebuild_seq: 1,
          sdp: pcRef.current.localDescription,
          video_capable: true,
        });
      }
      return;
    }

    if (pcRebuildCountRef.current < 3) {
      triggerPeerConnectionRebuild('callee_requested', 'caller');
    } else {
      console.log(`[재연결] rebuild-request 무시 — 사유: 3회제한초과 (현재횟수: ${pcRebuildCountRef.current})`);
    }
  }, [userId, stopIceRestartRepeatTimer, stopIceRestartSuccessPoll, sendSignal, triggerPeerConnectionRebuild]);

  /**
   * [결함 ④ 작업 2-A 보강 5] 받은 사람 측 call:rebuild-offer 수신 처리
   */
  const handlePeerConnectionRebuildOffer = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    console.log(`[재연결] rebuild-offer 수신 진입 — call: ${payload?.call_id?.slice(0, 8)}, sender: ${payload?.sender_id?.slice(0, 8)}, 회차: ${payload?.rebuild_id}`);
    if (!payload || payload.sender_id === userId) {
      console.log(`[재연결] rebuild-offer 무시 — 사유: ${!payload ? '페이로드없음' : '자신이송신'}`);
      return;
    }
    if (payload.call_id !== currentCallIdRef.current) {
      console.log(`[재연결] rebuild-offer 무시 — 사유: 통화불일치 (수신: ${payload.call_id?.slice(0, 8)}, 현재: ${currentCallIdRef.current?.slice(0, 8)})`);
      return;
    }
    if (callStateRef.current !== 'connected') {
      console.log(`[재연결] rebuild-offer 무시 — 사유: 통화비연결 (현재: ${callStateRef.current})`);
      return;
    }

    if (payload.video_capable === true && peerVideoCapableRef.current !== true) {
      peerVideoCapableRef.current = true;
      setPeerVideoCapable(true);
      console.log('[복구 신호] 상대 화상 가능 확인 — 출처: call:rebuild-offer');
    }

    if (isIceRestartingRef.current) {
      isIceRestartingRef.current = false;
      stopIceRestartRepeatTimer('rebuild-offer 수신으로 ice-restart 정지');
      stopIceRestartSuccessPoll();
      if (iceRestartTimeoutRef.current) {
        clearTimeout(iceRestartTimeoutRef.current);
        iceRestartTimeoutRef.current = null;
      }
    }

    stopRebuildRepeatTimer('rebuild-offer 수신으로 request 되풀이 정지');

    const offerRebuildId = payload.rebuild_id || 1;

    // [결함 ④ 작업 2-A 보강 6] 1-2. 같은 회차 중복 offer 검사:
    // 이미 answer를 생성했으면 최신 localDescription 재전송만 하고, 생성 중이면 무시
    if (isRebuildingPcRef.current && currentRebuildIdRef.current === offerRebuildId) {
      if (rebuildAnswerCreatedRef.current) {
        console.log('[재연결] 새 연결 중복 offer — 처리: answer 재전송');
        if (pcRef.current?.localDescription) {
          sendSignal('call:rebuild-answer', {
            call_id: currentCallIdRef.current,
            rebuild_id: offerRebuildId,
            rebuild_seq: 1,
            sdp: pcRef.current.localDescription,
            complete: true,
            video_capable: true,
          });
        }
      } else {
        console.log('[재연결] 새 연결 중복 offer — 처리: 처리 중 무시');
      }
      return;
    }

    // [결함 ⑦-2 수리] 받는 사람(callee)이 rebuild-request를 먼저 보낸 뒤 상대의 rebuild-offer를 기다리는 상태 판별
    const isCalleeAwaitingOffer =
      !isCallerRef.current &&
      isRebuildingPcRef.current === true &&
      rebuildAnswerCreatedRef.current === false &&
      currentRebuildIdRef.current === 0;

    if (isCalleeAwaitingOffer) {
      // (가) 1회 제한 차단 건너뜀
      // (나) pcRebuildCountRef += 1 건너뜀 (요청할 때 이미 1로 증가됨)
      // (다) 첫 await 앞에서 currentRebuildIdRef.current 설정
      currentRebuildIdRef.current = offerRebuildId;
      // (라) 받는 쪽 request 되풀이 타이머 정지
      stopRebuildRepeatTimer('내가 요청한 새 연결의 응답 offer 수신');
      console.log(`[재연결] 새 연결 Offer 수용 — 내가 요청한 새 연결의 응답 (회차: ${offerRebuildId}, 횟수 유지: ${pcRebuildCountRef.current})`);
    } else {
      // 통화당 3회 제한: 이미 다른 회차의 새 연결을 진행했거나 완료한 경우 차단
      if (pcRebuildCountRef.current >= 3 && currentRebuildIdRef.current !== offerRebuildId) {
        console.log(`[재연결] 새 연결 Offer 무시 — 3회 제한 (내 회차: ${currentRebuildIdRef.current}, 받은 회차: ${offerRebuildId}, 역할: ${isCallerRef.current ? '건 사람' : '받은 사람'})`);
        return;
      }
      pcRebuildCountRef.current += 1;
      currentRebuildIdRef.current = offerRebuildId;
    }
    // [결함 ⑦-2 보강] 두 갈래 공통 — 새 연결 진행 표지판(3fb11e6에서 누락된 줄 복원)
    isRebuildingPcRef.current = true;
    rebuildAnswerCreatedRef.current = false;
    isRebuildAnswerCreatingRef.current = true;
    pcRebuildStartTimeRef.current = Date.now();
    rebuildFirstSrflxAtRef.current = 0;
    rebuildFirstRelayAtRef.current = 0;

    if (pcRebuildTimeoutRef.current) clearTimeout(pcRebuildTimeoutRef.current);
    pcRebuildTimeoutRef.current = setTimeout(async () => {
      pcRebuildTimeoutRef.current = null;
      if (peerRecoveryWaitRef.current?.callId) {
        console.log('[복구 대기] 기다림 중 → rebuild 타임아웃 보류');
        return;
      }
      if (isRebuildingPcRef.current) {
        if (await abortIfPeerEndedInDb(currentCallIdRef.current)) return;
        isRebuildingPcRef.current = false;
        stopRebuildRepeatTimer('20초 제한 시간 초과');
        if (isNetworkSwitchRecoveringRef.current === true && recoverSelfTimeoutRef.current !== null) {
          terminateCall({
            status: 'failed',
            reason: 'reload_recovery_failed',
            toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
            toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
            notifyPeer: true,
          });
        } else {
          terminateCall({
            status: 'failed',
            reason: 'pc_rebuild_timeout',
            toastMessage: t('call.connection_failed', '통화 연결에 실패했습니다.'),
            notifyPeer: false,
          });
        }
      }
    }, ICE_RESTART_TIMEOUT_MS);

    console.log(`[재연결] 새 연결 Offer 수신 처리 — 역할: 받은 사람, 회차: ${offerRebuildId}`);
    try {
      const newPc = await setupRebuiltPeerConnection('callee');
      await newPc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
      await drainQueuedCandidates(newPc);

      const videoTransceiver = newPc.getTransceivers().find(
        (t) => t.receiver?.track?.kind === 'video' && t.direction !== 'stopped'
      );
      if (videoTransceiver) {
        videoTransceiver.direction = 'sendrecv';
        videoSenderRef.current = videoTransceiver.sender;
        if (localVideoTrackRef.current && localVideoTrackRef.current.readyState === 'live') {
          videoTransceiver.sender.replaceTrack(localVideoTrackRef.current).catch(() => {});
        }
      }

      const answer = await newPc.createAnswer();
      await newPc.setLocalDescription(answer);

      // [결함 ④ 작업 2-A 보강 6] 2-1. setLocalDescription(answer) 직후 후보 수집 완료를 기다리지 않고 바로 answer 즉시 전송
      rebuildAnswerCreatedRef.current = true;
      isRebuildAnswerCreatingRef.current = false;

      const sendResImmediate = await sendSignal('call:rebuild-answer', {
        call_id: currentCallIdRef.current,
        rebuild_id: offerRebuildId,
        rebuild_seq: 1,
        sdp: newPc.localDescription,
        complete: false,
        video_capable: true,
      });
      console.log(`[재연결] 신호 전송 — 종류: call:rebuild-answer, 구분: 즉시, 1차 결과: ${sendResImmediate?.result || 'ok'}`);
      void syncVideoSenderTrack('재연결 완료');

      // [결함 ④ 작업 2-A 보강 6] 2-2. 후보 수집 완료(또는 4초 시간초과) 후 완성본 SDP로 1회 더 전송(보강용)
      const gatherRes = await waitForIceGatheringComplete(newPc, 4000);
      if (newPc !== pcRef.current || !isRebuildingPcRef.current || callStateRef.current !== 'connected') {
        return;
      }
      console.log(`[재연결] 후보 수집 — 완료: ${gatherRes.complete ? '예' : '시간초과'}, 대기: ${gatherRes.waitedMs}ms, 근거: ${gatherRes.reason}, 역할: 받은 사람(새 연결)`);
      const myCounts = countCandidatesInSdp(newPc.localDescription);
      const srflxMsText = rebuildFirstSrflxAtRef.current ? `${rebuildFirstSrflxAtRef.current}ms` : '없음';
      const relayMsText = rebuildFirstRelayAtRef.current ? `${rebuildFirstRelayAtRef.current}ms` : '없음';
      console.log(`[재연결] 내 후보(새 연결) — ${formatCandidateCounts(myCounts)} (첫 srflx: ${srflxMsText}, 첫 relay: ${relayMsText})`);

      // [결함 ④ 작업 3 2단계 / 5-3 2단계] 길 막힘 감지 판정 (망 변경 복구 중 재집계 생략)
      const shouldSkipRecheck =
        isNetworkSwitchRecoveringRef.current === true &&
        callStateRef.current === 'connected' &&
        getAutoReloadCount(readCallRecoveryUses(currentCallIdRef.current)) < MAX_AUTO_RELOADS_PER_CALL;

      logIceRoadblockJudgement({
        pc: newPc,
        counts: myCounts,
        complete: gatherRes.complete,
        reason: gatherRes.reason,
        place: 'rebuild 받는 쪽',
        role: '받은 사람(새 연결)',
        round: offerRebuildId,
        roadblockConfirmedRef,
        onRoadblockConfirmed: handleRoadblockConfirmed,
        skipRecheck: shouldSkipRecheck,
      });

      const sendResComplete = await sendSignal('call:rebuild-answer', {
        call_id: currentCallIdRef.current,
        rebuild_id: offerRebuildId,
        rebuild_seq: 1,
        sdp: newPc.localDescription,
        complete: true,
        video_capable: true,
      });
      console.log(`[재연결] 신호 전송 — 종류: call:rebuild-answer, 구분: 완성본, 1차 결과: ${sendResComplete?.result || 'ok'}`);
    } catch (err) {
      isRebuildAnswerCreatingRef.current = false;
      console.error('[재연결] 받은 사람 새 연결 Answer 처리 실패:', err);
    }
  }, [userId, stopIceRestartRepeatTimer, stopIceRestartSuccessPoll, stopRebuildRepeatTimer, terminateCall, t, setupRebuiltPeerConnection, drainQueuedCandidates, sendSignal, handleRoadblockConfirmed, syncVideoSenderTrack, abortIfPeerEndedInDb]);

  /**
   * [결함 ④ 작업 2-A 보강 5/6] 건 사람 측 call:rebuild-answer 수신 처리
   */
  const handlePeerConnectionRebuildAnswer = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'connected' || !isRebuildingPcRef.current) return;

    if (payload.video_capable === true && peerVideoCapableRef.current !== true) {
      peerVideoCapableRef.current = true;
      setPeerVideoCapable(true);
      console.log('[복구 신호] 상대 화상 가능 확인 — 출처: call:rebuild-answer');
    }

    const pc = pcRef.current;
    if (!pc) return;

    // [결함 ④ 작업 2-A 보강 6] 3-1. 첫 rebuild-answer: setRemoteDescription -> queuedCandidatesRef 일괄 적용 -> 되풀이 타이머 정지
    if (!rebuildAnswerAppliedRef.current) {
      rebuildAnswerAppliedRef.current = true;
      stopRebuildRepeatTimer('rebuild-answer 수신으로 offer 되풀이 정지');

      console.log(`[재연결] 새 연결 Answer 수신 처리 — 역할: 건 사람`);
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        const drainResult = await drainQueuedCandidates(pc);
        console.log(`[재연결] 새 연결 answer 수신 — 적용 후보 ${drainResult?.applied ?? 0}개 / 실패 ${drainResult?.failed ?? 0}개`);
        void syncVideoSenderTrack('재연결 완료');
      } catch (err) {
        console.error('[재연결] 건 사람 새 연결 Answer 적용 실패:', err);
      }
      return;
    }

    // [결함 ④ 작업 2-A 보강 6] 3-2. 두 번째 이후(완성본 포함): setRemoteDescription 재실행 금지, a=candidate만 추출하여 addIceCandidate
    const sdpText = typeof payload.sdp === 'string' ? payload.sdp : payload.sdp?.sdp;
    const extracted = extractCandidatesFromSdp(sdpText);
    let appliedCount = 0;
    for (const cand of extracted) {
      const { type, proto } = parseCandidateTypeAndProto(cand.candidate);
      if (type === 'host' && proto === 'tcp') continue;
      try {
        await pc.addIceCandidate(new RTCIceCandidate(cand));
        appliedCount += 1;
      } catch {
        /* 중복 후보 등 예외 무시 */
      }
    }
    console.log(`[재연결] 새 연결 중복 answer — 꺼낸 후보 ${extracted.length}개(적용 ${appliedCount}개)`);
  }, [userId, stopRebuildRepeatTimer, drainQueuedCandidates, syncVideoSenderTrack]);

  /**
   * [결함 ④ 작업 3 4단계] 상대방의 call:recover 신호 수신 처리 (기다리는 쪽)
   */
  const handleCallRecover = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    const callId = payload.call_id;

    // 가드 1: call_id가 현재 통화와 다를 때
    if (!callId || callId !== currentCallIdRef.current) {
      console.log(`[복구 대기] call:recover 무시 — 통화 ID 불일치 (수신: ${callId?.slice(0, 8)}, 현재: ${currentCallIdRef.current?.slice(0, 8)})`);
      return;
    }
    // 가드 2: 내 쪽도 소리 연결된 적이 없을 때 (timerStartedRef false)
    if (!timerStartedRef.current) {
      console.log('[복구 대기] call:recover 무시 — 소리 연결 이력 없음');
      return;
    }
    // [결함 ④ 5-3 2단계] call:recover 수신 시 상대 무음 안내 해제
    peerAudioStagnantCountRef.current = 0;
    if (isPeerAudioUnstableRef.current) {
      isPeerAudioUnstableRef.current = false;
      setIsPeerAudioUnstable(false);
      console.log('[상대 무음 감지] 안내 해제 — 사유: call:recover 수신');
    }
    // [결함 ④ 5-3 C안 보완] 동시 감지 처리:
    // (a) 내가 받은 사람(!isCallerRef.current)이고 망 변경 의심 또는 복구 중이면 내가 복구를 주도하므로 상대 recover 무시
    if (!isCallerRef.current && (isNetworkSwitchSuspectedRef.current || isNetworkSwitchRecoveringRef.current)) {
      console.log('[망 변경] 동시 감지 — 받은 사람이 복구 주도, 상대 신호 무시');
      return;
    }

    // (b) 내가 건 사람(isCallerRef.current)이고 망 변경 의심 또는 복구 중이면 내 복구를 멈추고 상대 대기 모드로 전환
    if (isCallerRef.current && (isNetworkSwitchSuspectedRef.current || isNetworkSwitchRecoveringRef.current)) {
      console.log('[망 변경] 동시 감지 — 받은 사람이 복구 주도');
      activeLiveRecoverIdRef.current = null;
      isNetworkSwitchRecoveringRef.current = false;
      isNetworkSwitchSuspectedRef.current = false;
      setIsNetworkReconnectingSelf(false);
      if (networkRecoverySafetyTimerRef.current) {
        clearTimeout(networkRecoverySafetyTimerRef.current);
        networkRecoverySafetyTimerRef.current = null;
      }
      if (networkRecoveryDeadlineTimerRef.current) {
        clearTimeout(networkRecoveryDeadlineTimerRef.current);
        networkRecoveryDeadlineTimerRef.current = null;
      }
      if (networkRecoveryOnlineHandlerRef.current) {
        window.removeEventListener('online', networkRecoveryOnlineHandlerRef.current);
        networkRecoveryOnlineHandlerRef.current = null;
      }
      if (networkSwitchCheckTimerRef.current) {
        clearInterval(networkSwitchCheckTimerRef.current);
        networkSwitchCheckTimerRef.current = null;
      }
    }

    // 가드 3: 이미 같은 통화를 기다리는 중일 때 (1회 한정 20초 대기 다시 시작)
    if (peerRecoveryWaitRef.current?.callId === callId) {
      if (!peerRecoveryExtendedRef.current) {
        peerRecoveryExtendedRef.current = true;
        console.log('[복구 대기] call:recover 재수신 — 20초 대기 1회 다시 시작');
        peerRecoverNoticeRef.current = { callId, receivedAt: Date.now(), rebuildDone: false };
        if (peerRecoveryWaitRef.current.timeoutId) {
          clearTimeout(peerRecoveryWaitRef.current.timeoutId);
        }
        peerRecoveryWaitRef.current.timeoutId = setTimeout(() => {
          console.log('[복구 대기] 20초 초과 → 연결 실패로 종료');
          if (peerRecoveryWaitRef.current?.countdownIntervalId) {
            clearInterval(peerRecoveryWaitRef.current.countdownIntervalId);
          }
          peerRecoveryWaitRef.current = null;
          setPeerRecoveryRemainingSec(null);
          setIsNetworkReconnectingPeer(false);

          terminateCall({
            status: 'failed',
            reason: 'peer_recover_timeout',
            toastMessage: t('call.peer_recover_failed', '상대와 연결되지 않았어요. 다시 걸어 주세요.'),
            notifyPeer: false,
          });
        }, CALL_RECOVERY_VALID_MS);
        setIsNetworkReconnectingPeer(true);
        return;
      }
      console.log('[복구 대기] call:recover 무시 — 이미 대기 중인 통화 (되풀이 신호)');
      return;
    }
    // 가드 4: 이 통화에서 이미 2번 기다렸을 때 (자동 1회 + 버튼 1회)
    if (peerRecoveryWaitCountRef.current >= 2) {
      console.log(`[복구 대기] call:recover 무시 — 최대 대기 횟수(2회) 초과 (${peerRecoveryWaitCountRef.current}회)`);
      return;
    }

    peerRecoveryWaitCountRef.current += 1;
    const waitRound = peerRecoveryWaitCountRef.current;
    console.log(`[복구 대기] 상대 복구 중 — 최대 20초 기다림 (회차 ${waitRound}/2)`);
    peerRecoverNoticeRef.current = { callId, receivedAt: Date.now(), rebuildDone: false };

    // 1-d 표의 모든 타이머 정지
    clearLegacyRecoveryTimers('상대 복구 대기');

    // 20초 남은 초 카운트다운 시작
    setPeerRecoveryRemainingSec(20);
    const countdownIntervalId = setInterval(() => {
      setPeerRecoveryRemainingSec((prev) => {
        if (prev === null || prev <= 1) return null;
        return prev - 1;
      });
    }, 1000);

    // 20초 기다림 타이머
    const timeoutId = setTimeout(() => {
      console.log('[복구 대기] 20초 초과 → 연결 실패로 종료');
      if (peerRecoveryWaitRef.current?.countdownIntervalId) {
        clearInterval(peerRecoveryWaitRef.current.countdownIntervalId);
      }
      peerRecoveryWaitRef.current = null;
      setPeerRecoveryRemainingSec(null);
      setIsNetworkReconnectingPeer(false);

      // 1-e 실패 종료: 'failed' 기록 + "다시 걸어 주세요" 안내
      terminateCall({
        status: 'failed',
        reason: 'peer_recover_timeout',
        toastMessage: t('call.peer_recover_failed', '상대와 연결되지 않았어요. 다시 걸어 주세요.'),
        notifyPeer: false,
      });
    }, CALL_RECOVERY_VALID_MS);

    peerRecoveryWaitRef.current = {
      callId,
      timeoutId,
      countdownIntervalId,
    };
    setIsNetworkReconnectingPeer(true);
  }, [userId, t, clearLegacyRecoveryTimers, terminateCall]);

  /**
   * [결함 ④ 5-2 2단계] 상대방의 call:recover-ready 신호 수신 처리
   */
  const handleCallRecoverReady = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload) return;

    const callIdMatches = Boolean(payload.call_id && payload.call_id === currentCallIdRef.current);
    const senderMatches = Boolean(payload.sender_id && payload.sender_id === peerIdRef.current);
    const isWaiting = Boolean(peerRecoveryWaitRef.current?.callId && payload.call_id && peerRecoveryWaitRef.current.callId === payload.call_id);
    const remainingSec = peerRecoveryRemainingSec !== null ? peerRecoveryRemainingSec : '알수없음';
    const diagStr = payload.diag
      ? `host ${payload.diag.host ?? 0} / srflx ${payload.diag.srflx ?? 0} / relay ${payload.diag.relay ?? 0}`
      : '없음';
    const delayMs = payload.sent_at ? Date.now() - payload.sent_at : '알수없음';

    console.log(
      `[복구 신호] call:recover-ready 수신 — 통화 일치: ${callIdMatches ? '예' : '아니오'}, 보낸이 일치: ${senderMatches ? '예' : '아니오'}, 대기 중: ${isWaiting ? '예' : '아니오'}, 남은 대기 ${remainingSec}초, 상대 진단 ${diagStr}, 지연 ${delayMs} ms(기기 시계 차이 포함 참고값)`
    );

    if (callIdMatches && senderMatches && payload.video_capable === true && peerVideoCapableRef.current !== true) {
      peerVideoCapableRef.current = true;
      setPeerVideoCapable(true);
      console.log('[복구 신호] 상대 화상 가능 확인 — 출처: call:recover-ready');
    }

    // 1-10-4. isWaiting이 false일 때: payload.path === 'live' 분기 수용 검사
    if (!isWaiting) {
      if (payload.path === 'live') {
        let rejectReason = null;
        if (!callIdMatches || !senderMatches) {
          rejectReason = '통화/보낸이 불일치';
        } else if (callStateRef.current !== 'connected') {
          rejectReason = '통화 비연결';
        } else if (isNetworkSwitchSuspectedRef.current || isNetworkSwitchRecoveringRef.current) {
          rejectReason = '내가복구중';
        } else if (isRebuildingPcRef.current) {
          rejectReason = 'rebuild중';
        } else if (!payload.recover_id || payload.recover_id === lastHandledLiveRecoverIdRef.current) {
          rejectReason = '중복번호';
        } else if (pcRebuildCountRef.current >= 3) {
          rejectReason = '횟수초과';
        }

        if (rejectReason) {
          console.log(`[복구 C안] recover-ready 무시 — 사유: ${rejectReason}`);
          return;
        }

        // 1-10-5. 새 경로로 받아들일 때 수행:
        // ① lastHandledLiveRecoverIdRef에 recover_id 기록
        lastHandledLiveRecoverIdRef.current = payload.recover_id;

        // ② 0-3에서 확인한 옛 재연결 정리 7개 항목 동일 정리
        clearLegacyRecoveryTimers('대기 없는 recover-ready 수신');
        isIceRestartingRef.current = false;
        isRestartRemoteSdpAppliedRef.current = false;
        restartAnswerAppliedRef.current = false;
        isRestartAnswerCreatedRef.current = false;
        restartSawNonConnectedRef.current = false;

        pcRebuildCountRef.current = 0;
        currentRebuildIdRef.current = 0;

        // ③ 안내 표시
        setIsNetworkReconnectingPeer(true);

        // ④ 기존 경로와 같은 역할 판정으로 triggerPeerConnectionRebuild 호출
        const role = isCallerRef.current ? 'caller' : 'callee';
        console.log(`[복구 C안] recover-ready 수용 — 대기 없이 받음 (번호: ${payload.recover_id}, 역할: ${role})`);
        triggerPeerConnectionRebuildRef.current?.('peer_recovered', role);
        return;
      }

      // [결함 B] 대기 해제 후 도착한 새로고침 복구(path 없는 경우) 수용 검사
      const notice = peerRecoverNoticeRef.current;
      if (notice && notice.callId === payload.call_id) {
        let rejectReason = null;
        if (notice.rebuildDone) {
          rejectReason = '이미rebuild';
        } else if (Date.now() - notice.receivedAt > 30000) {
          rejectReason = '시간초과';
        } else if (!callIdMatches || !senderMatches || callStateRef.current !== 'connected') {
          rejectReason = '상태불일치';
        } else if (isRebuildingPcRef.current) {
          rejectReason = 'rebuild진행중';
        }

        if (!rejectReason) {
          notice.rebuildDone = true;
          console.log(`[복구 2단계] 대기 해제 후 도착한 recover-ready 수용 → rebuild 실행 — 통화: ${payload.call_id?.slice(0, 8)}`);
          clearLegacyRecoveryTimers('대기 해제 후 recover-ready 수용');
          const role = isCallerRef.current ? 'caller' : 'callee';
          triggerPeerConnectionRebuildRef.current?.('peer_recovered', role);
          return;
        } else {
          console.log(`[복구 2단계] recover-ready 무시 — 사유: ${rejectReason}`);
          return;
        }
      }

      // 새로고침 복구 경로(path 없는 경우)는 기존과 동일하게 무시
      return;
    }

    if (!callIdMatches || !senderMatches) {
      return;
    }

    // 1-10-3. 기존 "대기 중" 경로: payload.recover_id가 있으면 기록
    if (payload.recover_id) {
      lastHandledLiveRecoverIdRef.current = payload.recover_id;
    }

    // ① 복구 대기 타이머 2개를 clear하고 대기 구조를 null로, 남은 초를 null로 만든다.
    if (peerRecoveryWaitRef.current?.timeoutId) {
      clearTimeout(peerRecoveryWaitRef.current.timeoutId);
    }
    if (peerRecoveryWaitRef.current?.countdownIntervalId) {
      clearInterval(peerRecoveryWaitRef.current.countdownIntervalId);
    }
    peerRecoveryWaitRef.current = null;
    setPeerRecoveryRemainingSec(null);
    setIsNetworkReconnectingPeer(false);

    // ② ICE 재연결 타이머와 플래그 정리
    if (iceRestartTimeoutRef.current) {
      clearTimeout(iceRestartTimeoutRef.current);
      iceRestartTimeoutRef.current = null;
    }
    stopIceRestartRepeatTimer('recover-ready 수신으로 ICE restart 되풀이 정지');
    stopIceRestartSuccessPoll();
    isIceRestartingRef.current = false;
    isRestartRemoteSdpAppliedRef.current = false;
    restartAnswerAppliedRef.current = false;
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;

    // ③ pcRebuildCountRef = 0, currentRebuildIdRef = 0
    pcRebuildCountRef.current = 0;
    currentRebuildIdRef.current = 0;

    // ④ triggerPeerConnectionRebuild('peer_recovered', isCallerRef.current ? 'caller' : 'callee')
    if (peerRecoverNoticeRef.current && peerRecoverNoticeRef.current.callId === payload.call_id) {
      peerRecoverNoticeRef.current.rebuildDone = true;
    }
    const role = isCallerRef.current ? 'caller' : 'callee';
    console.log(`[복구 2단계] 랩탑 rebuild 실행 — 역할: ${role}`);
    triggerPeerConnectionRebuildRef.current?.('peer_recovered', role);
  }, [peerRecoveryRemainingSec, clearLegacyRecoveryTimers, stopIceRestartRepeatTimer, stopIceRestartSuccessPoll]);

  /**
   * [결함 ④ 5-3 C안] 진단 막힘 또는 재연결 시간 초과 시 기존 새로고침 안전장치로 전환
   */
  const executeReloadRecoveryFallback = useCallback((reason) => {
    // [결함 ④ 5-3 2단계] 새로고침이 이미 예약되었으면 안전장치 경쟁 생략
    if (reloadScheduledRef.current) {
      console.log('[복구 C안] 새로고침 이미 예약됨 → 안전장치 생략');
      return;
    }

    const callId = currentCallIdRef.current;
    if (!callId) return;

    // 1-7. 오프라인 상태이면 새로고침하지 않고 online 이벤트를 1회 기다려 재시도
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.log(`[복구 C안] 오프라인 상태 → online 이벤트 대기 후 안전장치 재시도 (${reason})`);
      if (networkRecoveryOnlineHandlerRef.current) {
        window.removeEventListener('online', networkRecoveryOnlineHandlerRef.current);
      }
      const onlineHandler = () => {
        networkRecoveryOnlineHandlerRef.current = null;
        console.log('[복구 C안] 온라인 복귀 확인 → 안전장치 재시도');
        executeReloadRecoveryFallback(reason);
      };
      networkRecoveryOnlineHandlerRef.current = onlineHandler;
      window.addEventListener('online', onlineHandler, { once: true });
      return;
    }

    const uses = readCallRecoveryUses(callId);
    if (getAutoReloadCount(uses) >= MAX_AUTO_RELOADS_PER_CALL) {
      console.log('[복구 C안] 자동 새로고침 이미 사용됨 → 실패 종료');
      terminateCall({
        status: 'failed',
        reason: 'reload_recovery_failed',
        toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
        toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
        notifyPeer: true,
      });
      return;
    }

    // [결함 ④ 5-3 2단계 보완 1-3] 먼저 새로고침 예약 시도 후 성공 여부에 따라 타이머 해제 또는 실패 종료
    const scheduled = handleRoadblockConfirmed({ place: `망변경안전장치_${reason}`, round: 1 });
    if (scheduled) {
      if (networkRecoveryDeadlineTimerRef.current) {
        clearTimeout(networkRecoveryDeadlineTimerRef.current);
        networkRecoveryDeadlineTimerRef.current = null;
      }
      if (networkRecoveryOnlineHandlerRef.current) {
        window.removeEventListener('online', networkRecoveryOnlineHandlerRef.current);
        networkRecoveryOnlineHandlerRef.current = null;
      }
    } else {
      console.log(`[복구 C안] 새로고침 예약 실패 → 실패 종료 (사유: ${reason})`);
      terminateCall({
        status: 'failed',
        reason: 'reload_not_scheduled',
        toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
        toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
        notifyPeer: true,
      });
    }
  }, [handleRoadblockConfirmed, terminateCall, t]);

  /**
   * [결함 ④ 5-3 C안] 망 복귀 대기, 진단 및 새로고침 없는 복구 시작
   */
  const startNetworkSwitchRecovery = useCallback(async () => {
    const callId = currentCallIdRef.current;
    if (!callId || callStateRef.current !== 'connected') return;

    // 1-10-1. 복구 회차 고유번호 생성 및 1-4 활성 실행 번호 기록
    const recoverId = `${callId}-${Date.now()}`;
    activeLiveRecoverIdRef.current = recoverId;
    const waitStart = Date.now();
    let channelReady = false;

    // [결함 ④ 5-3 2단계] 통로 복귀 조건 각각의 최초 달성 시각 기록
    // 묶음 3 이후 상대 통로 구독 없음 — httpSend만 사용
    let onLineFirstAt = null;
    let myChannelFirstAt = null;

    // 1-1 & 1-3. 30초 전체 제한 시간까지 통로 복귀 대기 (온라인 복귀 + 내 받는 통로 SUBSCRIBED, 내 통로 미복귀 시 온라인 후 최대 3초 경과 시 송신 시도)
    while (callStateRef.current === 'connected' && isNetworkSwitchRecoveringRef.current && activeLiveRecoverIdRef.current === recoverId) {
      const isOnLine = navigator.onLine === true;
      const isSubscribed = lastUserSignalStatusRef.current === 'SUBSCRIBED';

      if (isOnLine && onLineFirstAt === null) onLineFirstAt = Date.now();
      if (isSubscribed && myChannelFirstAt === null) myChannelFirstAt = Date.now();

      // 조건 A: 온라인 복귀 + 내 받는 통로 SUBSCRIBED
      if (isOnLine && isSubscribed) {
        channelReady = true;
        break;
      }
      // 조건 B: 온라인 복귀 후 3초 경과 시 내 통로 복귀 여부와 무관하게 송신 시도
      if (isOnLine && onLineFirstAt !== null && Date.now() - onLineFirstAt >= 3000) {
        console.log('[복구 C안] 온라인 복귀 3초 경과 — 내 통로 미복귀 상태이나 송신 시도');
        channelReady = true;
        break;
      }

      await new Promise((r) => setTimeout(r, 200));
    }

    const baseAt = networkRecoveryConfirmedAtRef.current || waitStart;
    const onLineDiff = onLineFirstAt !== null ? `+${onLineFirstAt - baseAt}ms` : '미복귀';
    const myChannelDiff = myChannelFirstAt !== null ? `+${myChannelFirstAt - baseAt}ms` : '미복귀';
    console.log(`[복구 C안] 통로 복귀 — onLine ${onLineDiff} / 내통로 ${myChannelDiff} / 보내기방식 httpSend`);

    if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
      const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
      console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
      return;
    }

    if (!channelReady) {
      console.log('[복구 C안] 중단: 사유 통로 미준비');
      return;
    }

    const waitMs = Date.now() - waitStart;
    console.log(`[망 변경] 통로 복귀 — 대기 ${waitMs}ms`);
    if (getAutoReloadCount(readCallRecoveryUses(callId)) >= MAX_AUTO_RELOADS_PER_CALL) {
      console.log('[복구 C안] 자동 새로고침 2회 소진 → 즉시 종료');
      terminateCall({
        status: 'failed',
        reason: 'reload_recovery_failed',
        toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
        toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
        notifyPeer: true,
      });
      return;
    }
    // 1-6 & 1-10-1. call:recover는 1회만 송신
    const recoverSendRes = await sendSignal('call:recover', { call_id: callId, recover_id: recoverId, path: 'live' });
    console.log(`[망 변경] call:recover 전송 — 결과: ${recoverSendRes ?? '완료'}`);

    // 1-4. sendSignal 뒤 중단 여부 확인
    if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
      const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
      console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
      return;
    }

    // [결함 ④ 5-3 방향 전환] 자동 새로고침 미사용 통화는 진단 생략하고 즉시 새로고침 복구로 직행
    const usesNow = readCallRecoveryUses(callId);
    if (getAutoReloadCount(usesNow) < MAX_AUTO_RELOADS_PER_CALL) {
      console.log('[복구 C안] 망 변경 확정 → 진단 생략, 바로 새로고침 복구 (새로고침 없는 복구는 실측 4전 4패)');
      executeReloadRecoveryFallback('망변경확정');
      return;
    }
    console.log('[복구 C안] 자동 새로고침 이미 사용 → 예비 경로(새로고침 없는 복구) 진행');

    // 1-5. [진단] 새로고침 없이 probeIceCandidatesQuick 실행
    const diagRes = await probeIceCandidatesQuick(4000, { requireRelay: true });
    console.log(`[복구 C안] 진단 결과: ${diagRes.status} (종료: ${diagRes.reason}) — 소요 ${diagRes.waitedMs}ms, srflx ${diagRes.counts.srflx} / relay ${diagRes.counts.relay}`);

    // 1-4. 진단 뒤 중단 여부 확인 (새로고침 안전장치 진입 직전 포함)
    if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
      const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
      console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
      return;
    }

    if (diagRes.status !== '정상') {
      // 1-7. 진단 막힘 → 새로고침 안전장치 진입 직전에도 확인
      if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
        const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
        console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
        return;
      }
      console.log('[복구 C안] 새로고침 안전장치 진입 — 사유: 진단막힘');
      executeReloadRecoveryFallback('진단막힘');
      return;
    }

    // [결함 ④ 5-3 2단계 보완 1-1] 수집을 끝까지 마쳤거나 시간초과인데 relay가 0개이면 바로 새로고침
    const isFullGatheredOrTimedOut = ['수집완료', 'null후보', '시간초과'].includes(diagRes.reason);
    if (diagRes.counts.relay === 0 && isFullGatheredOrTimedOut) {
      if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
        const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
        console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
        return;
      }
      console.log(`[복구 C안] 진단 relay 0개(종료: ${diagRes.reason}) → 새로고침 없는 복구 생략, 바로 새로고침`);
      executeReloadRecoveryFallback('진단relay없음');
      return;
    }

    // 1-6 & 1-10-1. [정상 → 새로고침 없는 복구] call:recover-ready 1차 전송
    console.log('[복구 C안] 새로고침 없이 call:recover-ready 전송');
    await sendSignal('call:recover-ready', {
      call_id: callId,
      recover_id: recoverId,
      path: 'live',
      diag: diagRes.counts,
      sent_at: Date.now(),
      video_capable: true,
    });

    // 1-6. 2초 후 call:recover-ready 2차 재송신
    setTimeout(() => {
      // 1-4. 2차 recover-ready 타이머 콜백 안 중단 여부 확인
      if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
        const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
        console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
        return;
      }
      if (!rebuildAnswerCreatedRef.current && !rebuildAnswerAppliedRef.current) {
        console.log(`[복구 C안] call:recover-ready 2차 재송신 — 통화: ${callId.slice(0, 8)}`);
        sendSignal('call:recover-ready', {
          call_id: callId,
          recover_id: recoverId,
          path: 'live',
          diag: diagRes.counts,
          sent_at: Date.now(),
          is_retry: true,
          video_capable: true,
        });
      }
    }, 2000);

    // 1-7. recover-ready 송신 뒤 10000ms 안에 새 연결 성공이 없으면 안전장치 진입
    if (networkRecoverySafetyTimerRef.current) clearTimeout(networkRecoverySafetyTimerRef.current);
    networkRecoverySafetyTimerRef.current = setTimeout(() => {
      networkRecoverySafetyTimerRef.current = null;
      // 1-4. 10초 안전 타이머 콜백 안 중단 여부 확인
      if (callStateRef.current !== 'connected' || !isNetworkSwitchRecoveringRef.current || activeLiveRecoverIdRef.current !== recoverId) {
        const abortReason = callStateRef.current !== 'connected' ? '통화 비연결' : !isNetworkSwitchRecoveringRef.current ? '복구 중 아님' : '실행 번호 불일치';
        console.log(`[복구 C안] 중단: 사유 ${abortReason}`);
        return;
      }
      if (!rebuildAnswerCreatedRef.current && !rebuildAnswerAppliedRef.current) {
        console.log('[복구 C안] 새로고침 안전장치 진입 — 사유: 연결시간초과');
        executeReloadRecoveryFallback('연결시간초과');
      }
    }, 10000);
  }, [sendSignal, executeReloadRecoveryFallback, terminateCall, t]);

  /**
   * [결함 ④ 5-5] 망 변경 확정 공통 처리 함수
   */
  const confirmNetworkSwitch = useCallback((trigger = 'packet_stall') => {
    if (isNetworkSwitchRecoveringRef.current || !isNetworkSwitchSuspectedRef.current) {
      console.log(`[망 변경] 확정 중복 요청 무시 — 계기: ${trigger}`);
      return;
    }

    if (networkSwitchCheckTimerRef.current) {
      clearInterval(networkSwitchCheckTimerRef.current);
      networkSwitchCheckTimerRef.current = null;
    }
    isNetworkSwitchSuspectedRef.current = false;
    isNetworkSwitchRecoveringRef.current = true;
    needsReloadAfterNetworkSwitchRef.current = true;
    console.log(`[망 변경] 새로고침 필요 딱지 부착 — 계기: ${trigger}`);
    networkRecoveryConfirmedAtRef.current = Date.now();
    console.log('[망 변경] 확정 — 내 화면 안내 표시');
    setIsNetworkReconnectingSelf(true);

    // 1-1. 전체 제한 시간 30000ms 타이머 시작
    if (networkRecoveryDeadlineTimerRef.current) clearTimeout(networkRecoveryDeadlineTimerRef.current);
    networkRecoveryDeadlineTimerRef.current = setTimeout(() => {
      networkRecoveryDeadlineTimerRef.current = null;
      if (callStateRef.current === 'connected' && isNetworkSwitchRecoveringRef.current) {
        console.log('[망 변경] 전체 제한 시간(30초) 초과 → 통화 실패 종료');
        terminateCall({
          status: 'failed',
          reason: 'net_switch_timeout',
          toastMessage: t('call.peer_recover_failed', '상대와 연결되지 않았어요. 다시 걸어 주세요.'),
          notifyPeer: false,
        });
      }
    }, 30000);

    // 1-5. 옛 재연결 7개 항목 동일 정리
    clearLegacyRecoveryTimers('망 변경 확정');
    isIceRestartingRef.current = false;
    isRestartRemoteSdpAppliedRef.current = false;
    restartAnswerAppliedRef.current = false;
    isRestartAnswerCreatedRef.current = false;
    restartSawNonConnectedRef.current = false;

    startNetworkSwitchRecovery();
  }, [startNetworkSwitchRecovery, terminateCall, t, clearLegacyRecoveryTimers]);
  confirmNetworkSwitchRef.current = confirmNetworkSwitch;

  /**
   * [결함 ④ 5-3 C안] 망 변경 의심 트리거 및 패킷 감시 후 확정
   */
  const triggerNetworkSwitchSuspect = useCallback(async (trigger) => {
    // 1-1. 통화 상태가 connected일 때만 동작
    if (callStateRef.current !== 'connected') return;
    // 이미 의심·확정·복구 중이면 새 계기는 무시
    if (isNetworkSwitchSuspectedRef.current || isNetworkSwitchRecoveringRef.current) return;
    if (peerRecoveryWaitRef.current?.callId || isRebuildingPcRef.current) {
      const reasonStr = peerRecoveryWaitRef.current?.callId ? '상대복구대기' : '새연결중';
      console.log(`[망 변경] 의심 무시 — 사유: ${reasonStr} (계기: ${trigger})`);
      return;
    }

    isNetworkSwitchSuspectedRef.current = true;
    console.log(`[망 변경] 의심 — 계기: ${trigger}`);
    // [결함 ④ 5-3 2단계] 내 망 변경 의심 시작 시 상대 무음 안내 해제
    peerAudioStagnantCountRef.current = 0;
    if (isPeerAudioUnstableRef.current) {
      isPeerAudioUnstableRef.current = false;
      setIsPeerAudioUnstable(false);
      console.log('[상대 무음 감지] 안내 해제 — 사유: 내 망 변경 의심');
    }

    const pc = pcRef.current;
    if (!pc) {
      isNetworkSwitchSuspectedRef.current = false;
      return;
    }

    let lastPackets = await getAudioPacketsReceived(pc);

    // 1-2. 1초마다 직전 값과 비교하여 2회 연속 미증가 시 확정, 최대 10초 감시
    let secondsElapsed = 0;
    let consecutiveNoIncrease = 0;
    if (networkSwitchCheckTimerRef.current) clearInterval(networkSwitchCheckTimerRef.current);

    networkSwitchCheckTimerRef.current = setInterval(async () => {
      // 1-3 (c). 콜백 첫 줄에서 connected 아니면 타이머 및 표시 해제
      if (callStateRef.current !== 'connected') {
        clearInterval(networkSwitchCheckTimerRef.current);
        networkSwitchCheckTimerRef.current = null;
        isNetworkSwitchSuspectedRef.current = false;
        setIsNetworkReconnectingSelf(false);
        return;
      }

      secondsElapsed += 1;
      const currentPackets = await getAudioPacketsReceived(pcRef.current);

      // 1-3 (d). await 도중 정리된 경우 타이머 진행 방지
      if (!isNetworkSwitchSuspectedRef.current || callStateRef.current !== 'connected' || !networkSwitchCheckTimerRef.current) {
        return;
      }

      const increased = currentPackets > lastPackets;
      lastPackets = currentPackets;

      if (increased) {
        consecutiveNoIncrease = 0;
      } else {
        consecutiveNoIncrease += 1;
      }

      console.log(`[망 변경] 감시 ${secondsElapsed}초 — 패킷 증가 ${increased ? '있음' : '없음'}(연속 ${consecutiveNoIncrease}회)`);

      if (consecutiveNoIncrease >= 2) {
        confirmNetworkSwitch('packet_stall');
        return;
      }

      if (secondsElapsed >= 10) {
        clearInterval(networkSwitchCheckTimerRef.current);
        networkSwitchCheckTimerRef.current = null;
        isNetworkSwitchSuspectedRef.current = false;
        console.log('[망 변경] 오경보 취소 — 수신 패킷 증가 (딱지 안 붙임)');
      }
    }, 1000);
  }, [confirmNetworkSwitch]);
  triggerNetworkSwitchSuspectRef.current = triggerNetworkSwitchSuspect;

  /**
   * [결함 ④ 5-3 C안] CallContext에서 수신 통로(user-signal) 상태 변화 콜백
   */
  const handleUserSignalStatus = useCallback((status) => {
    // 1-4. 통화 상태와 관계없이 마지막 user-signal 상태 기록
    lastUserSignalStatusRef.current = status;
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      recordNetworkDisturbanceRef.current?.();
      if (callStateRef.current !== 'connected') {
        if (!needsReloadPreCallRef.current) {
          needsReloadPreCallRef.current = true;
          console.log(`[통화 전 망 변경] 딱지 부착 — 계기: user-signal(${status}), 상태: ${callStateRef.current}`);
        }
      } else {
        triggerNetworkSwitchSuspectRef.current?.('user-signal');
      }
    }
  }, []);

  // [결함 ④ 5-3 C안] window offline 및 navigator.connection change 감지 리스너
  useEffect(() => {
    if (callState !== 'connected') return;

    const handleOffline = () => {
      triggerNetworkSwitchSuspectRef.current?.('offline');
    };

    window.addEventListener('offline', handleOffline);

    let connectionObj = null;
    const handleConnectionChange = () => {
      triggerNetworkSwitchSuspectRef.current?.('connection');
    };

    if (typeof navigator !== 'undefined' && 'connection' in navigator && navigator.connection) {
      connectionObj = navigator.connection;
      try {
        connectionObj.addEventListener('change', handleConnectionChange);
      } catch {}
    }

    return () => {
      window.removeEventListener('offline', handleOffline);
      if (connectionObj) {
        try {
          connectionObj.removeEventListener('change', handleConnectionChange);
        } catch {}
      }
      if (networkSwitchCheckTimerRef.current) {
        clearInterval(networkSwitchCheckTimerRef.current);
        networkSwitchCheckTimerRef.current = null;
      }
      // 1-3 (b). 감시 타이머 해제 시 의심 상태 복귀
      isNetworkSwitchSuspectedRef.current = false;
    };
  }, [callState]);

  // [결함 ④ 5-9] 통화 외(idle/울림 중) 망 변경 감지 리스너 (마운트 1회 등록)
  useEffect(() => {
    try {
      const l = Number(sessionStorage.getItem(PRE_CALL_RELOAD_STORAGE_KEY) || 0);
      if (l && Date.now() - l <= PRE_CALL_RELOAD_COOLDOWN_MS) console.log(`[통화 전 망 변경] 미리 새로고침 뒤 다시 열림 — ${Date.now() - l}ms 전 실행`);
    } catch {}

    const hasDraft = () => {
      try {
        const a = document.activeElement, t = new Set(['', 'text', 'search', 'email', 'tel', 'url', 'number', 'password']);
        if (a && a.tagName === 'INPUT' && a.type === 'file') return true; // [⑮-다 보완] 커서만 있고 빈 칸이면 미루지 않음 — 글자 검사는 아래 반복문
        for (const el of document.querySelectorAll('textarea, input, [contenteditable="true"]')) {
          if ((el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && t.has(el.type)) || el.getAttribute('contenteditable') === 'true') && (el.value || el.textContent || '').trim()) return true;
          if (el.tagName === 'INPUT' && el.type === 'file' && el.files?.length) return true;
        }
      } catch {}
      return false;
    };

    const checkAndTriggerPreCallReload = () => {
      if (!preCallReloadArmedRef.current || !needsReloadPreCallRef.current) {
        preCallReloadArmedRef.current = false;
        if (preCallReloadTimerRef.current) { clearTimeout(preCallReloadTimerRef.current); preCallReloadTimerRef.current = null; }
        return;
      }
      const now = Date.now();
      let r = (callStateRef.current !== 'idle' || Boolean(currentCallIdRef.current)) ? '통화 진행 중'
        : (navigator.onLine !== true || lastUserSignalStatusRef.current !== 'SUBSCRIBED') ? '망 미복구'
        : (now - lastNetDisturbAtRef.current < PRE_CALL_RELOAD_STABLE_MS) ? '망 안정 시간(10초) 미달'
        : (document.visibilityState !== 'visible' || now - lastVisibleAtRef.current < PRE_CALL_RELOAD_STABLE_MS) ? '화면 가려짐/복귀 직후'
        : (now - lastUserInputAtRef.current < PRE_CALL_RELOAD_QUIET_INPUT_MS) ? '사용자 조작 중(20초 이내)'
        : hasDraft() ? '쓰던 글 또는 선택 파일 있음'
        : (reloadScheduledRef.current === true || Boolean(readValidCallRecoveryInfo())) ? '종료 새로고침 예약 또는 복구 정보 존재'
        : null;
      if (!r) {
        try {
          const l = Number(sessionStorage.getItem(PRE_CALL_RELOAD_STORAGE_KEY) || 0);
          if (l && now - l < PRE_CALL_RELOAD_COOLDOWN_MS) r = '쿨다운(60초) 미달';
        } catch { r = '스토리지 접근 불가'; }
      }
      if (r) {
        if (lastPreCallReloadDeferredReasonRef.current !== r) {
          lastPreCallReloadDeferredReasonRef.current = r;
          console.log(`[통화 전 망 변경] 미리 새로고침 미룸 — 사유: ${r}`);
        }
        if (preCallReloadArmedRef.current && needsReloadPreCallRef.current) preCallReloadTimerRef.current = setTimeout(checkAndTriggerPreCallReload, PRE_CALL_RELOAD_STABLE_MS);
        return;
      }
      try {
        sessionStorage.setItem(PRE_CALL_RELOAD_STORAGE_KEY, String(Date.now()));
        console.log(`[통화 전 망 변경] 미리 새로고침 실행 — 마지막 흔들림 뒤 ${now - lastNetDisturbAtRef.current}ms`);
        preCallReloadArmedRef.current = false;
        needsReloadPreCallRef.current = false;
        window.location.reload();
      } catch (e) {
        console.warn('[통화 전 망 변경] 미리 새로고침 실패 (스토리지 저장 실패):', e);
      }
    };

    const recordDisturbance = () => {
      if (!preCallReloadArmedRef.current) return;
      lastNetDisturbAtRef.current = Date.now();
      if (preCallReloadTimerRef.current) clearTimeout(preCallReloadTimerRef.current);
      preCallReloadTimerRef.current = setTimeout(checkAndTriggerPreCallReload, PRE_CALL_RELOAD_STABLE_MS);
    };
    recordNetworkDisturbanceRef.current = recordDisturbance;

    const markPreCallNetworkSwitch = (trigger) => {
      if (callStateRef.current !== 'connected') {
        if (!needsReloadPreCallRef.current) {
          needsReloadPreCallRef.current = true;
          console.log(`[통화 전 망 변경] 딱지 부착 — 계기: ${trigger}, 상태: ${callStateRef.current}`);
        }
      }
    };

    const handleWindowOffline = () => {
      if (callStateRef.current !== 'connected') preCallReloadArmedRef.current = true;
      markPreCallNetworkSwitch('offline');
      recordDisturbance();
    };
    const handleWindowOnline = () => {
      markPreCallNetworkSwitch('online');
      recordDisturbance();
    };
    const handleConnectionChange = () => {
      markPreCallNetworkSwitch('connection');
      recordDisturbance();
    };

    window.addEventListener('offline', handleWindowOffline);
    window.addEventListener('online', handleWindowOnline);

    let connObj = null;
    if (typeof navigator !== 'undefined' && 'connection' in navigator && navigator.connection) {
      connObj = navigator.connection;
      try {
        connObj.addEventListener('change', handleConnectionChange);
      } catch {}
    }

    const handleUserInput = () => { lastUserInputAtRef.current = Date.now(); };
    window.addEventListener('pointerdown', handleUserInput, { capture: true, passive: true });
    window.addEventListener('keydown', handleUserInput, { capture: true, passive: true });
    window.addEventListener('wheel', handleUserInput, { capture: true, passive: true });
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastVisibleAtRef.current = Date.now();
        if (preCallReloadArmedRef.current && needsReloadPreCallRef.current) {
          if (preCallReloadTimerRef.current) clearTimeout(preCallReloadTimerRef.current);
          preCallReloadTimerRef.current = setTimeout(checkAndTriggerPreCallReload, PRE_CALL_RELOAD_STABLE_MS);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('offline', handleWindowOffline);
      window.removeEventListener('online', handleWindowOnline);
      if (connObj) {
        try {
          connObj.removeEventListener('change', handleConnectionChange);
        } catch {}
      }
      window.removeEventListener('pointerdown', handleUserInput, { capture: true });
      window.removeEventListener('keydown', handleUserInput, { capture: true });
      window.removeEventListener('wheel', handleUserInput, { capture: true });
      document.removeEventListener('visibilitychange', handleVisibility);
      if (preCallReloadTimerRef.current) { clearTimeout(preCallReloadTimerRef.current); preCallReloadTimerRef.current = null; }
      recordNetworkDisturbanceRef.current = null;
    };
  }, []);

  // [결함 ④ 5-3 2단계] 상대 오디오 패킷 수신 정지 감시 (안내 전용, 복구/종료 함수 호출 금지)
  useEffect(() => {
    if (callState !== 'connected') {
      if (peerAudioCheckIntervalRef.current) {
        clearInterval(peerAudioCheckIntervalRef.current);
        peerAudioCheckIntervalRef.current = null;
      }
      peerAudioStagnantCountRef.current = 0;
      lastPeerPacketsReceivedRef.current = null;
      peerPacketsEverReceivedRef.current = false;
      lastMonitoredPcRef.current = null;
      if (isPeerAudioUnstableRef.current) {
        isPeerAudioUnstableRef.current = false;
        setIsPeerAudioUnstable(false);
      }
      return;
    }

    if (peerAudioCheckIntervalRef.current) {
      clearInterval(peerAudioCheckIntervalRef.current);
    }

    let isChecking = false;
    peerAudioCheckIntervalRef.current = setInterval(async () => {
      if (isChecking) return;
      if (callStateRef.current !== 'connected') return;

      const pc = pcRef.current;
      if (!pc || pc.signalingState === 'closed') return;

      // pc가 바뀌면 기준값 초기화
      if (lastMonitoredPcRef.current !== pc) {
        lastMonitoredPcRef.current = pc;
        lastPeerPacketsReceivedRef.current = null;
        peerPacketsEverReceivedRef.current = false;
        peerAudioStagnantCountRef.current = 0;
        if (isPeerAudioUnstableRef.current) {
          isPeerAudioUnstableRef.current = false;
          setIsPeerAudioUnstable(false);
        }
      }

      // 감시 전제 조건 검사 (복구 중 또는 상대 대기 중에는 자체 무음 감지 배제)
      if (
        isRebuildingPcRef.current ||
        peerRecoveryWaitRef.current !== null ||
        isNetworkSwitchSuspectedRef.current ||
        isNetworkSwitchRecoveringRef.current
      ) {
        return;
      }

      isChecking = true;
      try {
        const packets = await getAudioPacketsReceived(pc);
        if (callStateRef.current !== 'connected' || pcRef.current !== pc) {
          isChecking = false;
          return;
        }

        // 통화 시작 후 packetsReceived가 한 번이라도 0보다 커진 적 있는지 확인
        if (!peerPacketsEverReceivedRef.current) {
          if (packets > 0) {
            peerPacketsEverReceivedRef.current = true;
            lastPeerPacketsReceivedRef.current = packets;
            peerAudioStagnantCountRef.current = 0;
          }
          isChecking = false;
          return;
        }

        const lastPackets = lastPeerPacketsReceivedRef.current;
        lastPeerPacketsReceivedRef.current = packets;

        if (lastPackets !== null) {
          if (packets > lastPackets) {
            peerAudioStagnantCountRef.current = 0;
            if (isPeerAudioUnstableRef.current) {
              isPeerAudioUnstableRef.current = false;
              setIsPeerAudioUnstable(false);
              console.log('[상대 무음 감지] 안내 해제');
            }
          } else {
            // 상대가 의도적으로 음소거한 경우 오탐 방지 (0-8 가드 / [결함 ④ 5-3 2단계 보완] 음소거 중 무음 횟수 0 초기화)
            if (!peerMutedRef.current) {
              peerAudioStagnantCountRef.current += 1;
              if (peerAudioStagnantCountRef.current >= 2 && !isPeerAudioUnstableRef.current) {
                isPeerAudioUnstableRef.current = true;
                setIsPeerAudioUnstable(true);
                console.log('[상대 무음 감지] 안내 표시');
              }
            } else {
              peerAudioStagnantCountRef.current = 0;
            }
          }
        }
      } catch {
      } finally {
        isChecking = false;
      }
    }, 1000);

    return () => {
      if (peerAudioCheckIntervalRef.current) {
        clearInterval(peerAudioCheckIntervalRef.current);
        peerAudioCheckIntervalRef.current = null;
      }
    };
  }, [callState]);

  /**
   * 2차 방어 (나): 통화 생존 신호(Heartbeat) 송수신 기동
   */
  const startHeartbeat = useCallback(() => {
    lastHeartbeatAtRef.current = Date.now();
    if (heartbeatSendIntervalRef.current) {
      clearInterval(heartbeatSendIntervalRef.current);
      heartbeatSendIntervalRef.current = null;
    }
    if (heartbeatCheckIntervalRef.current) {
      clearInterval(heartbeatCheckIntervalRef.current);
      heartbeatCheckIntervalRef.current = null;
    }

    // 5초마다 상대방에게 call:ping 신호 송출
    heartbeatSendIntervalRef.current = setInterval(() => {
      if (callStateRef.current === 'connected') {
        sendSignal('call:ping', {});
      }
    }, HEARTBEAT_INTERVAL_MS);

    // 5초마다 상대방 생존 검사 (15초 초과 누락 시 단절 판정)
    heartbeatCheckIntervalRef.current = setInterval(() => {
      if (callStateRef.current !== 'connected') return;
      if (isIceRestartingRef.current || isRebuildingPcRef.current) return; // [결함 ④ 작업 2-A 보강] 재연결 진행 중에는 하트비트 종료 판정 보류
      if (peerRecoveryWaitRef.current?.callId || isNetworkSwitchRecoveringRef.current) return; // [결함 ④ 작업 3 4단계 / 5-3] 상대 복구 대기 또는 망 전환 복구 중에는 하트비트 종료 판정 보류
      const now = Date.now();
      if (now - lastHeartbeatAtRef.current > HEARTBEAT_TIMEOUT_MS) {
        terminateCall({
          status: 'failed',
          reason: 'heartbeat_timeout',
          toastMessage: t('call.connection_lost', '네트워크 연결이 끊겨 통화가 종료되었습니다.'),
          notifyPeer: false,
        });
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [sendSignal, terminateCall, t]);
  startHeartbeatRef.current = startHeartbeat;

  /**
   * 상대방 call:ping 수신 처리
   */
  const handleCallPing = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'connected') return;

    lastHeartbeatAtRef.current = Date.now();
  }, [userId]);

  /**
   * 3차 방어: 내 계정이 당사자인 오래된 유령 통화(ringing 1분 이상) 사전 마감
   */
  const cleanupStaleGhostCalls = useCallback(async () => {
    if (!userId) return;
    try {
      const now = Date.now();
      const ringingThreshold = new Date(now - STALE_RINGING_TIMEOUT_MS).toISOString();

      // 1) 내가 발신자(caller_id = userId)인 통화 중 방치된 ringing 정리
      const { error: callerErr } = await supabase
        .from('calls')
        .update({
          status: 'failed',
          ended_at: new Date().toISOString(),
          ended_by: userId,
        })
        .eq('caller_id', userId)
        .eq('status', 'ringing')
        .lt('started_at', ringingThreshold);
      if (callerErr) console.warn('[유령 정리] 내 발신 정리 실패 — ' + callerErr.message);

      // 2) 내가 참여자로 등록된 통화 중 방치된 ringing 정리
      const { data: staleParts, error: staleErr } = await supabase
        .from('call_participants')
        .select('call_id, calls!inner(status, started_at)')
        .eq('profile_id', userId)
        .eq('calls.status', 'ringing')
        .lt('calls.started_at', ringingThreshold)
        .limit(STALE_GHOST_CLEANUP_LIMIT);
      if (staleErr) { console.warn('[유령 정리] 받는 쪽 대상 조회 실패 — ' + staleErr.message); return; }
      const protectedIds = new Set([currentCallIdRef.current, glareAutoAcceptCallIdRef.current].filter(Boolean));
      const staleIds = [...new Set((staleParts || []).map((p) => p.call_id))].filter((id) => id && !protectedIds.has(id));
      if (staleIds.length === 0) return;
      const { error: updErr } = await supabase
        .from('calls')
        .update({ status: 'failed', ended_at: new Date().toISOString(), ended_by: userId })
        .in('id', staleIds)
        .eq('status', 'ringing')
        .lt('started_at', ringingThreshold);
      if (updErr) console.warn('[유령 정리] 받는 쪽 정리 실패 — ' + updErr.message);
      else console.log('[유령 정리] 받는 쪽 ' + staleIds.length + '건 정리');
    } catch (e) {
      console.warn('[useVoiceCall] 발신 전 유령 세션 정리 실패 (발신 계속 진행):', e);
    }
  }, [userId]);

  /**
   * 수신 통화 거절 처리 (declined)
   */
  const declineCall = useCallback(async () => {
    const callId = currentCallIdRef.current;
    if (callId) {
      closeCallNotificationBanner(callId);

      // 1. 발신자에게 거절 브로드캐스트 전송 (await 없이 즉시 대기열 등록)
      sendSignal('call:decline', { call_id: callId }).catch((err) => {
        console.warn('[useVoiceCall] call:decline 신호 전송 실패:', err);
      });

      // 1-1. 내 계정의 다른 탭에 거절 동기화 (await 없이 백그라운드 발송)
      broadcastToOwnDevices('call:declined_elsewhere', { call_id: callId }).catch((err) => {
        console.warn('[useVoiceCall] call:declined_elsewhere 전송 실패:', err);
      });

      // 2. DB calls 테이블 및 call_participants 상태 갱신은 뒤에서 비동기 처리
      const capturedCallId = callId;
      const capturedIsGroup = groupCallRef.current;
      const dbStart = Date.now();
      void (async () => {
        try {
          if (capturedIsGroup) {
            await declineGroupCall(capturedCallId, userId);
            console.log(`[통화 종료] 서버 기록(뒤에서) 완료 — ${Date.now() - dbStart}ms`);
            return;
          }
          await markCallStatus(capturedCallId, 'declined', userId);
          await supabase
            .from('call_participants')
            .update({
              state: 'declined',
              left_at: new Date().toISOString(),
            })
            .eq('call_id', capturedCallId)
            .eq('profile_id', userId);
          console.log(`[통화 종료] 서버 기록(뒤에서) 완료 — ${Date.now() - dbStart}ms`);
        } catch (e) {
          console.error('[useVoiceCall] declineCall 서버 기록 실패:', e);
        }
      })();
    }

    const decUiStart = Date.now();
    cleanupMedia();
    updateCallState('idle');
    onCallFinished?.();
    console.log(`[통화 종료] 화면 정리 ${Date.now() - decUiStart}ms`);
  }, [sendSignal, broadcastToOwnDevices, userId, markCallStatus, cleanupMedia, onCallFinished]);

  /**
   * 음성 통화 발신 시작
   * @param {Object} options
   * @param {string} options.chatId - 대화방 ID
   * @param {Object} options.partner - 상대방 정보 { id, nickname, avatar }
   * @param {string} [options.conversationType] - 'direct' | 'group'
   */
  const startVoiceCall = async (options = {}) => {
    const targetChatId = options.chatId || currentChatIdRef.current;
    const targetPartner = options.partner || currentPartnerRef.current;
    const targetConvType = options.conversationType || 'direct';
    const targetKind = options.kind === 'video' ? 'video' : 'audio';
    const t0Caller = performance.now();
    let lastLapCaller = t0Caller;
    const lapsCaller = {};
    const recordLapCaller = (name) => {
      const now = performance.now();
      const elapsed = Math.round(now - lastLapCaller);
      lapsCaller[name] = elapsed;
      lastLapCaller = now;
      return elapsed;
    };
    let callerIceType = '캐시';
    let callerIceStart = 0;

    // 0. 사용자 제스처 동기 스택에서 AudioContext 사전 확보/활성화
    callAudioGraph.initOrResume();

    if (callStateRef.current !== 'idle' || isGroupCallBusy()) return;
    if (Date.now() - lastCallEndedAtRef.current < 2000) {
      console.log('[걸기] 종료 직후 2초 — 무시');
      return;
    }
    if (!targetChatId || !userId || !targetPartner?.id) return;
    if (targetConvType === 'group') return;

    const myAttempt = ++startAttemptRef.current;
    const prepStart = Date.now();
    if (import.meta.env.DEV) {
      preparingStartTimeRef.current = prepStart;
      console.log(`[발신 준비] 클릭→preparing 경과: ${Date.now() - prepStart}ms`);
    }

    clearCallRecoveryInfo();
    clearCallRecoveryUses();

    // 즉시 발신 준비 상태로 전이 (연타 차단 + 발신 모달 즉시 노출)
    updateCallState('preparing');
    timerStartedRef.current = false;
    connectedAtRef.current = null;
    peerAnsweredRef.current = false;
    autoRecoverySaveCountRef.current = 0;
    peerRecoveryWaitCountRef.current = 0;
    setElapsedSeconds(0);

    currentChatIdRef.current = targetChatId;
    currentPartnerRef.current = targetPartner;
    setCurrentPartner(targetPartner);
    peerIdRef.current = targetPartner.id;
    try {
      ensurePeerChannel(targetPartner.id);
    } catch {
      // [C단계-6] 거는 쪽 선제 구독 예외는 무시하고 기존 흐름 유지
    }

    // [결함 ④ 작업 2-A] 발신자 주도권 및 재연결 상태 초기화
    isCallerRef.current = true;
    iceRestartCountRef.current = 0;
    isIceRestartingRef.current = false;
    iceRestartIdRef.current = 0;
    lastHandledRestartIdRef.current = 0;
    restartSawNonConnectedRef.current = false;
    iceRestartLoggedErrorCodesRef.current.clear();
    stopIceRestartSuccessPoll();

    // 발신자 모달 표시용 상대방 정보 세팅
    const peerData = {
      id: targetPartner.id,
      name: getDisplayName(targetPartner, (targetPartner.friendNickname || targetPartner.name || targetPartner.nickname || '').trim()),
      avatar: targetPartner.avatar || null,
      conversationId: targetChatId,
    };
    setCallerInfo(peerData);

    let activeCallId = null;
    let localAcquiredStream = null;

    const isAborted = (stepName) => {
      if (startAttemptRef.current !== myAttempt || callStateRef.current !== 'preparing') {
        if (import.meta.env.DEV) {
          console.log(`[발신 준비] 취소 — 단계: ${stepName}`);
        }
        if (localAcquiredStream) {
          try {
            localAcquiredStream.getTracks().forEach((track) => track.stop());
          } catch {}
        }
        if (activeCallId) {
          markCallStatus(activeCallId, 'ended', userId);
        }
        const takenOver = (!!currentCallIdRef.current && currentCallIdRef.current !== activeCallId)
          || (!!glareAutoAcceptCallIdRef.current && glareAutoAcceptCallIdRef.current !== activeCallId);
        if (takenOver) {
          console.log('[발신 준비] 취소 — 다른 통화가 이어받음, 연결 정리 건너뜀');
        } else {
          cleanupMedia();
        }
        if (callStateRef.current === 'preparing') {
          updateCallState('idle');
        }
        return true;
      }
      return false;
    };

    // 3차 방어: 내 계정이 당사자인 오래된 유령 세션(ringing 1분+) 사전 마감
    const ghostT0 = performance.now();
    cleanupStaleGhostCalls()
      .then(() => console.log('[걸기 실측] 유령정리(뒤에서) 완료 — ' + Math.round(performance.now() - ghostT0) + 'ms'))
      .catch((e) => console.warn('[걸기 실측] 유령정리(뒤에서) 실패 — ' + (e?.message || e)));

    // 1. 마이크 권한 요청 전 기존 잔류 스트림 정지 (고아 스트림 방어)
    if (localStreamRef.current) {
      try {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch (e) {}
      localStreamRef.current = null;
    }

    let stream;
    try {
      stream = await acquireCallStartMic();
      localAcquiredStream = stream;
      localStreamRef.current = stream;
      if (isAborted('acquireCallStartMic 직후')) return;
      recordLapCaller('마이크');
    } catch (err) {
      if (isAborted('acquireCallStartMic 실패')) return;
      console.error('[useVoiceCall] 마이크 권한 거부 또는 장치 없음:', err);
      showToast(t('call.mic_permission_denied', '마이크 권한이 필요합니다. 브라우저 설정에서 마이크를 허용해 주세요.'));
      cleanupMedia();
      updateCallState('idle');
      return;
    }

    // 2. DB calls 테이블 생성 (ringing)
    const { data: callData, error: callErr } = await supabase
      .from('calls')
      .insert({
        conversation_id: targetChatId,
        caller_id: userId,
        kind: targetKind,
        status: 'ringing',
      })
      .select('id')
      .single();

    if (callData?.id) {
      activeCallId = callData.id;
      if (targetKind === 'video') {
        wantVideoOnConnectRef.current = { callId: callData.id, done: false };
      }
    }

    if (isAborted('calls insert 직후')) return;
    recordLapCaller('통화생성');

    if (callErr || !callData) {
      if (isAborted('calls insert 오류 판정')) return;
      console.error('[useVoiceCall] calls 레코드 생성 실패:', callErr);
      showToast(t('call.failed', '통화 연결에 실패했습니다.'));
      cleanupMedia();
      updateCallState('idle');
      return;
    }

    const callId = callData.id;
    currentCallIdRef.current = callId;

    // 3. call_participants 생성 (발신자 joined, 수신자 invited)
    try {
      await supabase.from('call_participants').insert([
        {
          call_id: callId,
          profile_id: userId,
          state: 'joined',
          joined_at: new Date().toISOString(),
        },
        {
          call_id: callId,
          profile_id: targetPartner.id,
          state: 'invited',
        },
      ]);
    } catch (partErr) {
      console.error('[useVoiceCall] call_participants 등록 실패:', partErr);
    }
    if (isAborted('call_participants insert 직후')) return;
    recordLapCaller('참가자등록');

    // ★ ICE 서버 목록 비동기 조회 (캐시 우선, 만료 시 갱신, 실패 시 Google STUN 폴백)
    let iceFetchStartAt = 0;
    if (needsReloadPreCallRef.current) {
      if (hasValidTurnCache()) {
        console.log('[통화 전 망 변경] 딱지 있음 — 유효 ICE 자격 보관분 사용(새로 받기 생략) — 계기: 걸기');
      } else {
        iceFetchStartAt = Date.now();
        clearIceServersCache();
        console.log('[통화 전 망 변경] 딱지 확인 → ICE 자격 새로 받기 — 계기: 걸기');
      }
    }
    callerIceStart = performance.now();
    const iceServers = await getIceServers();
    const callerIceElapsed = Math.round(performance.now() - callerIceStart);
    callerIceType = (iceFetchStartAt > 0 || callerIceElapsed >= 50) ? '새로' : '캐시';
    recordLapCaller('ICE자격');
    if (iceFetchStartAt) {
      const resStr = iceServers === DEFAULT_ICE_SERVERS ? '기본값' : '성공';
      console.log(`[통화 전 망 변경] ICE 자격 새로 받음 — 결과: ${resStr}, 소요 ${Date.now() - iceFetchStartAt}ms`);
    }
    if (isAborted('getIceServers 직후')) return;

    // 발신 시작 시 ICE 후보 배열 및 새 연결 상태 초기화
    sentIceCandidatesRef.current = [];
    queuedCandidatesRef.current = [];
    pendingRestartCandidatesRef.current = [];
    staleRemoteUfragsRef.current = new Set();
    isRestartRemoteSdpAppliedRef.current = false;
    isRebuildingPcRef.current = false;
    pcRebuildCountRef.current = 0;
    if (pcRebuildTimeoutRef.current) {
      clearTimeout(pcRebuildTimeoutRef.current);
      pcRebuildTimeoutRef.current = null;
    }
    if (pcRebuildRepeatTimerRef.current) {
      clearInterval(pcRebuildRepeatTimerRef.current);
      pcRebuildRepeatTimerRef.current = null;
    }
    rebuildAnswerAppliedRef.current = false;
    rebuildAnswerCreatedRef.current = false;
    lastRebuildOfferSentAtRef.current = 0;
    pcRebuildStartTimeRef.current = 0;
    currentRebuildIdRef.current = 0;
    isRebuildAnswerCreatingRef.current = false;
    rebuildFirstSrflxAtRef.current = 0;
    rebuildFirstRelayAtRef.current = 0;

    // 4. RTCPeerConnection 생성 및 연결 감시(2차 방어) 등록
    const forceRelay = isForceRelayEnabled();
    const rtcConfig = {
      iceServers,
      ...(forceRelay ? { iceTransportPolicy: 'relay' } : {}),
    };
    if (import.meta.env.DEV) {
      callStartTimeRef.current = Date.now();
      localCandidatesStatsRef.current = { host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } };
      remoteCandidatesStatsRef.current = { host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } };
      localCandidatesLoggedRef.current = false;
      remoteCandidatesLoggedRef.current = false;
      iceDumpCountRef.current = 0;
      if (iceJudgeTimerRef.current) {
        clearTimeout(iceJudgeTimerRef.current);
        iceJudgeTimerRef.current = null;
      }
      iceJudgeStartRef.current = null;
      iceJudgeLoggedRef.current = false;
      iceJudgeDumpCountRef.current = 0;
      excludedLocalHostTcpCountRef.current = 0;
      excludedRemoteHostTcpCountRef.current = 0;

      let turnCount = 0;
      let stunCount = 0;
      if (Array.isArray(iceServers)) {
        iceServers.forEach((item) => {
          const urls = Array.isArray(item?.urls) ? item.urls : [item?.urls];
          urls.forEach((u) => {
            if (typeof u === 'string') {
              if (u.startsWith('turn:') || u.startsWith('turns:')) turnCount += 1;
              else if (u.startsWith('stun:')) stunCount += 1;
            }
          });
        });
      }
      const isFallback = turnCount === 0;
      console.log(
        `[ICE 서버] 모드: ${forceRelay ? 'relay강제' : '자동'} / TURN: ${turnCount} / STUN: ${stunCount} / 폴백: ${isFallback ? '예(Google STUN만)' : '아니오'}`
      );
    }
    if (initialRoadblockTimerRef.current) {
      clearTimeout(initialRoadblockTimerRef.current);
      initialRoadblockTimerRef.current = null;
    }
    initialRoadblockJudgedRef.current = false;
    roadblockConfirmedRef.current = null;
    notifyLocalTrackChanged(stream.getAudioTracks()[0]);
    const pc = new RTCPeerConnection(rtcConfig);
    pcRef.current = pc;

    pc.onconnectionstatechange = () => handlePeerConnectionStateChange(pc.connectionState, pc);
    pc.oniceconnectionstatechange = () => handleIceConnectionStateChange(pc.iceConnectionState, pc);
    pc.onicegatheringstatechange = () => {
      if (import.meta.env.DEV) {
        const elapsed = callStartTimeRef.current ? `${Date.now() - callStartTimeRef.current}ms` : '0ms';
        console.log(`[ICE 전이] iceGatheringState: ${pc.iceGatheringState} (${elapsed})`);
        if (pc.iceGatheringState === 'complete') {
          logLocalCandidatesSummary(localCandidatesStatsRef, localCandidatesLoggedRef, excludedLocalHostTcpCountRef);
        }
      }
    };
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState !== 'complete') return;
      if (initialRoadblockJudgedRef.current) return;
      if (isIceRestartingRef.current || isRebuildingPcRef.current) return;
      if (initialRoadblockTimerRef.current) {
        clearTimeout(initialRoadblockTimerRef.current);
        initialRoadblockTimerRef.current = null;
      }
      initialRoadblockJudgedRef.current = true;
      logIceRoadblockJudgement({
        pc,
        counts: countCandidatesInSdp(pc.localDescription),
        complete: true,
        reason: '수집완료',
        place: '최초 건 쪽',
        role: '건 사람',
        roadblockConfirmedRef,
        onRoadblockConfirmed: handleRoadblockConfirmed,
      });
    });
    pc.onicecandidateerror = (event) => {
      if ((isIceRestartingRef.current || isRebuildingPcRef.current) && event) {
        const code = event.errorCode;
        if (code && !iceRestartLoggedErrorCodesRef.current.has(code)) {
          iceRestartLoggedErrorCodesRef.current.add(code);
          const target = formatIceErrorTarget(event.url);
          console.log(`[재연결] ICE 후보 오류 — 코드: ${code}, 대상: ${target}, 사유: ${event.errorText || 'none'}`);
        }
      }
    };

    if (import.meta.env.DEV) {
      if (iceDumpTimerRef.current) clearTimeout(iceDumpTimerRef.current);
      iceDumpTimerRef.current = setTimeout(() => {
        const currentPc = pcRef.current;
        if (currentPc && currentPc.connectionState !== 'connected' && currentPc.iceConnectionState !== 'connected') {
          dumpCandidatePairs(currentPc, '10초 미연결', iceDumpCountRef);
        }
      }, 10000);
    }

    stream.getTracks().forEach((track) => {
      const sender = pc.addTrack(track, stream);
      if (track.kind === 'audio') {
        audioSenderRef.current = sender;
      }
    });

    const videoTransceiver = pc.addTransceiver('video', { direction: 'sendrecv' });
    videoSenderRef.current = videoTransceiver.sender;

    let iceCandidateCount = 0;
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        if (import.meta.env.DEV) {
          recordCandidateStat(localCandidatesStatsRef, event.candidate);
        }
        // [수정 (나)] host tcp 전송 제외 (발신측)
        const { type, proto } = parseCandidateTypeAndProto(event.candidate);
        if (type === 'host' && proto === 'tcp') {
          if (import.meta.env.DEV) {
            excludedLocalHostTcpCountRef.current += 1;
          }
          return;
        }

        iceCandidateCount += 1;
        const candJson = event.candidate.toJSON();
        if (sentIceCandidatesRef.current.length < ICE_CANDIDATE_BUFFER_LIMIT) {
          sentIceCandidatesRef.current.push(candJson);
        } else if (!iceOverflowReportedRef.current.sent) {
          iceOverflowReportedRef.current.sent = true;
          try {
            reportMessage('ice_candidate_buffer_overflow', {
              level: 'warning',
              extra: { buffer: 'sent', limit: ICE_CANDIDATE_BUFFER_LIMIT },
            });
          } catch (err) {
            console.warn('[useVoiceCall] ICE 넘침 리포트 실패:', err);
          }
        }
        sendSignal('call:ice', {
          candidate: candJson,
          call_id: callId,
        });
      } else {
        if (import.meta.env.DEV) {
          logLocalCandidatesSummary(localCandidatesStatsRef, localCandidatesLoggedRef, excludedLocalHostTcpCountRef);
          console.info('[ICE 실측] 발신 후보 수:', iceCandidateCount);
        }
      }
    };

    pc.ontrack = (event) => {
      if (event.track.kind === 'audio') {
        if (remoteAudioRef.current && event.streams[0]) {
          callAudioGraph.attachRemoteStream(event.streams[0], remoteAudioRef.current);
        }
      } else if (event.track.kind === 'video') {
        remoteVideoTrackRef.current = event.track;
        setRemoteVideoStream(new MediaStream([event.track]));
        framesFlowingRef.current = (event.track.muted === false);
        applyRemoteVideoVisibility();
        event.track.onunmute = () => {
          framesFlowingRef.current = true;
          applyRemoteVideoVisibility();
          if (import.meta.env.DEV) console.log('[화상] 상대 영상 수신 시작');
        };
        event.track.onmute = () => {
          framesFlowingRef.current = false;
          applyRemoteVideoVisibility();
          if (import.meta.env.DEV) console.log('[화상] 상대 영상 멈춤');
        };
      }
    };

    // 5. SDP Offer 생성 및 전송 (발신자 프로필 확실히 확보)
    try {
      const offer = await pc.createOffer();
      if (isAborted('createOffer 직후')) return;

      await pc.setLocalDescription(offer);
      if (isAborted('setLocalDescription 직후')) return;
      recordLapCaller('오퍼생성');

      // [결함 ④ 작업 3 2단계 보완] 처음 걸기 4초 시간 기준 판정 타이머 (setLocalDescription 직후 가동)
      const currentCallPc = pc;
      if (initialRoadblockTimerRef.current) {
        clearTimeout(initialRoadblockTimerRef.current);
      }
      initialRoadblockTimerRef.current = setTimeout(() => {
        initialRoadblockTimerRef.current = null;
        if (initialRoadblockJudgedRef.current) return;
        if (!currentCallPc || currentCallPc.signalingState === 'closed') return;
        if (isIceRestartingRef.current || isRebuildingPcRef.current) {
          initialRoadblockJudgedRef.current = true;
          console.log('[길 막힘 감지] 최초 판정 생략 — 4초 안에 재연결 진입(재연결 쪽 판정이 대신함), 위치: 최초 건 쪽');
          return;
        }
        initialRoadblockJudgedRef.current = true;
        logIceRoadblockJudgement({
          pc: currentCallPc,
          counts: countCandidatesInSdp(currentCallPc.localDescription),
          complete: false,
          reason: '시간초과',
          place: '최초 건 쪽',
          role: '건 사람',
          roadblockConfirmedRef,
          onRoadblockConfirmed: handleRoadblockConfirmed,
        });
      }, 4000);

      let senderProfile = myProfile;

      if (!senderProfile && userId) {
        try {
          const { data: prof, error: profErr } = await supabase
            .from('profiles')
            .select('id, name, nickname, avatar, name_display_choice')
            .eq('id', userId)
            .maybeSingle();
          if (profErr) {
            console.error('[useVoiceCall] 발신자 내 프로필 조회 실패:', profErr);
          }
          if (prof) {
            senderProfile = prof;
          }
        } catch (e) {
          console.error('[useVoiceCall] 발신자 내 프로필 조회 예외:', e);
        }
      }
      if (isAborted('profiles fallback 조회 직후')) return;

      const senderName = getDisplayName(senderProfile, '');
      const senderAvatar = senderProfile?.avatar || null;

      await sendSignal('call:offer', {
        call_id: callId,
        conversation_id: targetChatId,
        caller_id: userId,
        caller_name: senderName,
        caller_avatar: senderAvatar,
        target_user_id: targetPartner.id,
        kind: targetKind,
        sdp: pc.localDescription,
        video_capable: true,
      });
      recordLapCaller('오퍼전송');

      if (startAttemptRef.current !== myAttempt || callStateRef.current !== 'preparing') {
        if (import.meta.env.DEV) {
          console.log('[발신 준비] 취소 — 단계: sendSignal call:offer 직후');
        }
        try {
          console.log(`[call:end 송신] 출처=startCall reason=canceled call=${callId ? callId.slice(0, 8) : 'null'}`);
          await sendSignal('call:end', { call_id: callId, reason: 'canceled' });
        } catch {}
        if (activeCallId) {
          markCallStatus(activeCallId, 'ended', userId);
        }
        const takenOver = (!!currentCallIdRef.current && currentCallIdRef.current !== activeCallId)
          || (!!glareAutoAcceptCallIdRef.current && glareAutoAcceptCallIdRef.current !== activeCallId);
        if (takenOver) {
          console.log('[발신 준비] 취소 — 다른 통화가 이어받음, 연결 정리 건너뜀');
        } else {
          cleanupMedia();
        }
        if (callStateRef.current === 'preparing') {
          updateCallState('idle');
        }
        return;
      }

      updateCallState('calling');
      if (import.meta.env.DEV) {
        console.log(`[발신 준비] preparing→calling 경과: ${Date.now() - prepStart}ms`);
      }
      const callerTotal = Math.round(performance.now() - t0Caller);
      console.log(`[걸기 실측] 마이크 ${lapsCaller['마이크'] ?? 0}ms · 통화생성 ${lapsCaller['통화생성'] ?? 0}ms · 참가자등록 ${lapsCaller['참가자등록'] ?? 0}ms · ICE자격 ${lapsCaller['ICE자격'] ?? 0}ms(${callerIceType}) · 오퍼생성 ${lapsCaller['오퍼생성'] ?? 0}ms · 오퍼전송 ${lapsCaller['오퍼전송'] ?? 0}ms · 합계 ${callerTotal}ms`);

      // [F구역 ⑥ 묶음 3 긴급수리] 오퍼전송 중 도착하여 보관된 조기 answer 즉시 적용 (call_id 일치 시)
      if (pendingEarlyAnswerRef.current && pendingEarlyAnswerRef.current.call_id === callId) {
        const earlyAnswer = pendingEarlyAnswerRef.current;
        pendingEarlyAnswerRef.current = null;
        console.log(`[통화] 보관된 조기 answer 즉시 적용 (call: ${callId.slice(0, 8)})`);
        void handleCallAnswerRef.current?.(earlyAnswer);
      }

      // 6. 발신자 통화 대기음 시작 ("뚜르르")
      startRingbackTone();

      // 7. 30초 무응답 자동 종료 타이머 기동 (발신자 주체)
      missedTimerRef.current = setTimeout(async () => {
        if (callStateRef.current !== 'calling') return;
        const targetCallId = currentCallIdRef.current;
        if (targetCallId) {
          await terminateCall({
            status: 'missed',
            reason: 'missed',
            toastMessage: t('call.no_answer', '응답이 없습니다.'),
            notifyPeer: true,
            endedByUserId: userId,
          });
        }
      }, 30000);

    } catch (offerErr) {
      console.error('[useVoiceCall] Offer 생성 실패:', offerErr);
      showToast(t('call.failed', '통화 연결에 실패했습니다.'));
      endCall({ autoReason: 'offer_failed' });
    }
  };

  /**
   * DB 복구 수신 상태에서 발신자로부터 도착한 재전송 offer/sdp 보강 처리
   * (벨소리, 35초 타이머, 모달 재마운트 등은 일절 재실행하지 않고 sdp 보강 및 받기 버튼 활성화만 수행)
   */
  const applyRecoveredOffer = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload?.call_id || payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'incoming') return;

    if (offerResendTimeoutRef.current) {
      clearTimeout(offerResendTimeoutRef.current);
      offerResendTimeoutRef.current = null;
    }

    console.log('[useVoiceCall] applyRecoveredOffer: SDP offer 수신 확보 완료');
    incomingOfferRef.current = {
      ...incomingOfferRef.current,
      ...payload,
    };
    peerVideoCapableRef.current = payload.video_capable === true;
    setPeerVideoCapable(payload.video_capable === true);
    if (import.meta.env.DEV) console.info('[화상 판별] 상대 video_capable:', peerVideoCapableRef.current, '(resend)');
    if (!peerIdRef.current && (payload.sender_id || payload.caller_id)) {
      peerIdRef.current = payload.sender_id || payload.caller_id;
    }
    setHasRecoveredOfferSdp(true);
  }, []);

  /**
   * 앱 복귀/마운트 시 DB에서 감지된 진행 중 수신 통화 복구 시작
   */
  const startRecoveredIncomingCall = useCallback(async ({ callId, conversationId, callerId, kind = 'audio' }) => {
    if (callStateRef.current !== 'idle' || isGroupCallBusy()) {
      console.log('[useVoiceCall] startRecoveredIncomingCall 무시: idle 상태가 아님', callStateRef.current);
      return;
    }

    // 차단된 통화 시도인지 2차 안전망 확인
    try {
      const { data: blockedRow, error: blockErr } = await supabase
        .from('blocked_call_attempts')
        .select('call_id')
        .eq('call_id', callId)
        .maybeSingle();

      if (!blockErr && blockedRow) {
        console.info('[useVoiceCall] 차단된 수신 통화 복구 차단:', callId);
        return;
      }
    } catch (blockEx) {
      console.warn('[useVoiceCall] 복구 진입 시 차단 통화 확인 예외 (복구 계속 진행):', blockEx);
    }

    if (callStateRef.current !== 'idle' || isGroupCallBusy() || recentlyEndedCallIdsRef.current.has(callId)) {
      return;
    }

    console.log('[useVoiceCall] startRecoveredIncomingCall 복구 진입:', callId, callerId);
    currentCallIdRef.current = callId;
    currentChatIdRef.current = conversationId || null;
    peerIdRef.current = callerId;
    ensurePeerChannel(callerId);
    const recKind = kind === 'video' ? 'video' : 'audio';
    setIncomingKind(recKind);
    incomingKindRef.current = recKind;

    incomingOfferRef.current = {
      call_id: callId,
      conversation_id: conversationId,
      caller_id: callerId,
      sender_id: callerId,
      kind: recKind,
    };
    setHasRecoveredOfferSdp(false);

    // 발신자 프로필 조회
    let callerName = '';
    let callerAvatar = null;
    if (callerId) {
      try {
        const { data: prof, error: profErr } = await supabase
          .from('profiles')
          .select('id, name, nickname, avatar, name_display_choice')
          .eq('id', callerId)
          .maybeSingle();
        if (profErr) {
          console.error('[useVoiceCall] 복구 수신 발신자 프로필 조회 실패:', profErr);
        }
        if (prof) {
          callerName = getDisplayName(prof, '');
          callerAvatar = prof.avatar || null;
        }
      } catch (e) {
        console.error('[useVoiceCall] 복구 수신 발신자 프로필 조회 예외:', e);
      }
    }

    const peerData = {
      id: callerId,
      name: callerName,
      avatar: callerAvatar,
      conversationId: conversationId || null,
    };

    setCallerInfo(peerData);
    setCurrentPartner(peerData);
    currentPartnerRef.current = peerData;
    updateCallState('incoming');

    // 수신 벨소리 및 진동 시작
    startRingtone();

    // 수신측 35초 예비 자동 종료 타이머 기동
    if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
    incomingTimeoutRef.current = setTimeout(() => {
      if (callStateRef.current === 'incoming') {
        cleanupMedia();
        updateCallState('idle');
      }
    }, INCOMING_AUTO_EXPIRE_MS);

    // (2) 즉시 발신자에게 offer 재전송 요청 신호 전송
    await sendSignal('call:offer_resend', {
      call_id: callId,
      sender_id: userId,
    }, callerId);

    // (3) 5초 내 offer 미도착 시 수신 화면 정리 및 안내 (DB 상태는 발신측이 missed 처리하므로 절대 쓰지 않음)
    if (offerResendTimeoutRef.current) clearTimeout(offerResendTimeoutRef.current);
    offerResendTimeoutRef.current = setTimeout(() => {
      if (callStateRef.current === 'incoming' && currentCallIdRef.current === callId && !incomingOfferRef.current?.sdp) {
        console.warn('[useVoiceCall] 5초 내 SDP offer 미도착으로 복구 수신 화면 자동 정리');
        cleanupMedia();
        updateCallState('idle');
        showToast(t('call.recovery_failed', '통화 연결 정보를 가져오지 못했습니다.'));
      }
    }, 5000);
  }, [userId, ensurePeerChannel, sendSignal, cleanupMedia, showToast, t]);

  /**
   * 수신된 call:offer 처리
   */
  const handleCallOffer = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;

    // ★ 재전송 offer(is_resend: true)가 도착했을 때 이미 해당 통화 수신 화면이 떠 있으면 sdp만 보강하고 벨/타이머 재시작 방지
    if (payload.is_resend && callStateRef.current === 'incoming' && payload.call_id === currentCallIdRef.current) {
      applyRecoveredOffer(payload);
      return;
    }

    const isGlare = (callStateRef.current === 'calling' || callStateRef.current === 'preparing')
      && payload.sender_id === currentPartnerRef.current?.id
      && payload.conversation_id === currentChatIdRef.current;
    if (isGlare) {
      const iWin = String(userId).toLowerCase() < String(payload.sender_id).toLowerCase();
      console.log('[동시 발신] 판정 — 나: ' + (iWin ? '이김(상대 신호 무시)' : '짐(내 통화 정리 후 자동 받기)')
        + ', 내 통화: ' + String(currentCallIdRef.current).slice(0, 8) + ', 상대 통화: ' + String(payload.call_id).slice(0, 8));
      if (iWin) return;
      const glareFromState = callStateRef.current;
      startAttemptRef.current = 0; // endCall과 같은 방식: 진행 중인 걸기를 다음 await에서 멈춤
      await terminateCall({ status: 'ended', reason: 'canceled', toastMessage: null, notifyPeer: !!currentCallIdRef.current });
      const glareWaitStart = Date.now();
      await new Promise((resolve) => {
        const tick = () => {
          if ((!isTerminatingRef.current && callStateRef.current === 'idle') || Date.now() - glareWaitStart >= 2000) { resolve(); return; }
          setTimeout(tick, 50);
        };
        tick();
      });
      if (isTerminatingRef.current || callStateRef.current !== 'idle') {
        console.warn('[동시 발신] 정리 미완료 — busy 보내지 않고 중단, 상태: ' + callStateRef.current);
        return;
      }
      glareAutoAcceptCallIdRef.current = payload.call_id;
      console.log('[동시 발신] 내 통화 정리 완료(' + (glareFromState === 'preparing' ? '준비 중' : '거는 중') + ', 대기 ' + (Date.now() - glareWaitStart) + 'ms) → 상대 통화 받을 준비');
    }

    // ★ 통화 중(busy) 응답: 수신자가 이미 다른 통화 중(발신·수신·통화중 어느 상태든)이면 자동 거절
    if (callStateRef.current !== 'idle' || isGroupCallBusy()) {
      if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
      sendSignal('call:busy', {
        call_id: payload.call_id,
        busy_user_id: userId,
      }, payload.sender_id, { oneShot: true });
      return;
    }

    // ★ 유효하지 않은 call_id 또는 최근 종료된 통화 번호는 조용히 무시
    if (!payload.call_id || recentlyEndedCallIdsRef.current.has(payload.call_id)) {
      if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
      return;
    }

    // ★ 중복 offer 처리 방지
    if (processingOfferCallIdsRef.current.has(payload.call_id)) {
      if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
      return;
    }
    processingOfferCallIdsRef.current.add(payload.call_id);

    clearCallRecoveryInfo();
    clearCallRecoveryUses();

    // ★ 벨 전 DB 확인 비동기 창 동안 도착할 call:ice 후보 임시 보관소 등록
    pendingIceRef.current = { callId: payload.call_id, candidates: [] };
    isAcceptingRef.current = false;
    timerStartedRef.current = false;
    connectedAtRef.current = null;
    peerAnsweredRef.current = false;
    autoRecoverySaveCountRef.current = 0;
    peerRecoveryWaitCountRef.current = 0;
    setElapsedSeconds(0);

    // 조기 도착 보관함(earlyArrivalIceRef)에 보관된 후보 병합
    if (earlyArrivalIceRef.current.has(payload.call_id)) {
      const earlyData = earlyArrivalIceRef.current.get(payload.call_id);
      earlyArrivalIceRef.current.delete(payload.call_id);
      if (earlyData && Array.isArray(earlyData.candidates) && earlyData.candidates.length > 0) {
        pendingIceRef.current.candidates.push(...earlyData.candidates);
        if (import.meta.env.DEV) {
          console.log(`[신호 수신 검문] 조기 보관된 후보 ${earlyData.candidates.length}개를 pendingIce에 병합 (call_id: 대기)`);
        }
      }
    }

    try {
      // 1. 벨 전 DB 확인 (calls 및 blocked_call_attempts 조회)
      const fetchCallRecord = () => supabase
        .from('calls')
        .select('id, status, caller_id, conversation_id, kind')
        .eq('id', payload.call_id)
        .eq('status', 'ringing')
        .eq('caller_id', payload.sender_id)
        .maybeSingle();

      const fetchBlockedAttempt = () => supabase
        .from('blocked_call_attempts')
        .select('call_id')
        .eq('call_id', payload.call_id)
        .maybeSingle();

      const [callRes, blockRes] = await Promise.all([
        fetchCallRecord(),
        fetchBlockedAttempt(),
      ]);
      let dbCallRecord = callRes.data;

      // 차단 행 있음 -> 벨·화면 없이 조용히 무시(console.info 한 줄만)
      if (blockRes.data) {
        if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
        if (pendingIceRef.current.callId === payload.call_id) {
          pendingIceRef.current = { callId: null, candidates: [] };
        }
        console.info('[useVoiceCall] 차단된 사용자의 통화 offer 무시:', payload.call_id);
        return;
      }
      if (blockRes.error) {
        console.warn('[useVoiceCall] blocked_call_attempts 벨 전 조회 실패 (차단 아님으로 진행):', blockRes.error);
      }

      // calls 0건 -> 100ms 뒤 1회 재시도, 그래도 0건이면 무시(가짜 또는 이미 끝난 통화)
      // 조회 error(네트워크 등) -> 기존처럼 벨을 울린다(진짜 전화를 놓치지 않는 쪽 우선, console.warn)
      if (!callRes.error && !callRes.data) {
        await new Promise((r) => setTimeout(r, 100));
        const [retryCall, retryBlock] = await Promise.all([
          fetchCallRecord(),
          fetchBlockedAttempt(),
        ]);
        dbCallRecord = retryCall.data;

        if (retryBlock.data) {
          if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
          if (pendingIceRef.current.callId === payload.call_id) {
            pendingIceRef.current = { callId: null, candidates: [] };
          }
          console.info('[useVoiceCall] 차단된 사용자의 통화 offer 무시 (재시도 확인):', payload.call_id);
          return;
        }

        if (retryCall.error) {
          console.warn('[useVoiceCall] calls 재시도 조회 오류 (통화 수신 진행):', retryCall.error);
        } else if (!retryCall.data) {
          if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
          if (pendingIceRef.current.callId === payload.call_id) {
            pendingIceRef.current = { callId: null, candidates: [] };
          }
          console.info('[useVoiceCall] 유효하지 않거나 이미 종료된 통화 offer 무시:', payload.call_id);
          return;
        }
      } else if (callRes.error) {
        console.warn('[useVoiceCall] 벨 전 calls 조회 오류 (통화 수신 진행):', callRes.error);
      }

      // 2. 이름·사진 확정: 확인 통과 시 caller_id로 profiles를 조회해 확정, 실패 시 신호 값 사용
      // ★ 사진 컬럼은 avatar (avatar_url 조회 시 42703 오류 방지, §2-81)
      const senderId = payload.sender_id || payload.caller_id;
      let callerName = '';
      let callerAvatar = null;

      if (senderId) {
        try {
          const { data: prof, error: profErr } = await supabase
            .from('profiles')
            .select('id, name, nickname, avatar, name_display_choice')
            .eq('id', senderId)
            .maybeSingle();
          if (profErr) {
            console.error('[useVoiceCall] 수신측 발신자 프로필 조회 실패:', profErr);
          }
          if (prof) {
            callerName = getDisplayName(prof, '');
            callerAvatar = prof.avatar || null;
          }
        } catch (e) {
          console.error('[useVoiceCall] 수신측 발신자 프로필 조회 예외:', e);
        }
      }

      if (!callerName) {
        callerName = (payload.caller_name || '').trim();
      }
      if (!callerAvatar) {
        callerAvatar = payload.caller_avatar || null;
      }

      // 3. 확인을 마친 뒤 벨 울리기 직전 경합 방지 3대 검사
      const isGlareLate = (callStateRef.current === 'preparing' || callStateRef.current === 'calling')
        && payload.sender_id === currentPartnerRef.current?.id
        && payload.conversation_id === currentChatIdRef.current;
      if (isGlareLate) {
        const iWin = String(userId).toLowerCase() < String(payload.sender_id).toLowerCase();
        if (iWin) {
          console.log('[동시 발신] 늦은 판정 — 나: 이김(상대 신호 무시)');
        } else {
          console.log('[동시 발신] 늦은 판정 — 나: 짐(내 통화 정리 후 자동 받기)');
          const savedIce = pendingIceRef.current.callId === payload.call_id ? [...pendingIceRef.current.candidates] : [];
          pendingIceRef.current = { callId: null, candidates: [] };
          startAttemptRef.current = 0;
          await terminateCall({ status: 'ended', reason: 'canceled', toastMessage: null, notifyPeer: !!currentCallIdRef.current });
          const glareWaitStart = Date.now();
          await new Promise((resolve) => {
            const tick = () => {
              if ((!isTerminatingRef.current && callStateRef.current === 'idle') || Date.now() - glareWaitStart >= 2000) { resolve(); return; }
              setTimeout(tick, 50);
            };
            tick();
          });
          if (isTerminatingRef.current || callStateRef.current !== 'idle') {
            if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
            if (pendingIceRef.current.callId === payload.call_id) {
              pendingIceRef.current = { callId: null, candidates: [] };
            }
            console.warn('[동시 발신] 정리 미완료 — busy 보내지 않고 중단, 상태: ' + callStateRef.current);
            return;
          }
          glareAutoAcceptCallIdRef.current = payload.call_id;
          pendingIceRef.current = { callId: payload.call_id, candidates: savedIce };
          if (earlyArrivalIceRef.current.has(payload.call_id)) {
            const earlyData = earlyArrivalIceRef.current.get(payload.call_id);
            earlyArrivalIceRef.current.delete(payload.call_id);
            if (earlyData && Array.isArray(earlyData.candidates) && earlyData.candidates.length > 0) {
              const space = Math.max(0, ICE_CANDIDATE_BUFFER_LIMIT - pendingIceRef.current.candidates.length);
              if (space > 0) {
                pendingIceRef.current.candidates.push(...earlyData.candidates.slice(0, space));
              }
            }
          }
          console.log('[동시 발신] 늦은 판정 — 보관후보 복원 ' + pendingIceRef.current.candidates.length + '개');
        }
      }

      // ㉮ callStateRef.current가 여전히 'idle'인가
      // ㉯ 그 call_id가 Set에 없는가
      // ㉰ 같은 call_id를 이미 처리 중이 아닌가(중복 offer 방지)
      if (
        callStateRef.current !== 'idle' ||
        recentlyEndedCallIdsRef.current.has(payload.call_id) ||
        currentCallIdRef.current === payload.call_id
      ) {
        if (glareAutoAcceptCallIdRef.current === payload.call_id) glareAutoAcceptCallIdRef.current = null;
        if (pendingIceRef.current.callId === payload.call_id) {
          pendingIceRef.current = { callId: null, candidates: [] };
        }
        console.info('[useVoiceCall] 수신 준비 완료 후 경합 검사로 벨 취소:', {
          state: callStateRef.current,
          isRecentlyEnded: recentlyEndedCallIdsRef.current.has(payload.call_id),
          isCurrentCall: currentCallIdRef.current === payload.call_id,
        });
        return;
      }

      // 4. 상태 세팅 및 벨소리 기동
      currentCallIdRef.current = payload.call_id;
      // ★ 벨 전 보관된 임시 ICE 후보를 queuedCandidatesRef 뒤에 이관 후 초기화
      if (pendingIceRef.current.callId === payload.call_id) {
        const n = pendingIceRef.current.candidates.length;
        if (import.meta.env.DEV) console.info('[ICE 실측] 벨 전 보관 후보 수:', n);
        if (pendingIceRef.current.candidates.length > 0) {
          queuedCandidatesRef.current.push(...pendingIceRef.current.candidates);
        }
        pendingIceRef.current = { callId: null, candidates: [] };
      }
      currentChatIdRef.current = payload.conversation_id || null;
      incomingOfferRef.current = payload;
      const rawKind = dbCallRecord?.kind || payload.kind || 'audio';
      const determinedKind = rawKind === 'video' ? 'video' : 'audio';
      setIncomingKind(determinedKind);
      incomingKindRef.current = determinedKind;
      peerVideoCapableRef.current = payload.video_capable === true;
      setPeerVideoCapable(payload.video_capable === true);
      if (import.meta.env.DEV) console.info('[화상 판별] 상대 video_capable:', peerVideoCapableRef.current, '(offer)');
      peerIdRef.current = payload.sender_id;
      ensurePeerChannel(payload.sender_id);
      setHasRecoveredOfferSdp(true);

      const peerData = {
        id: senderId,
        name: callerName,
        avatar: callerAvatar,
        conversationId: payload.conversation_id || null,
      };

      setCallerInfo(peerData);
      setCurrentPartner(peerData);
      currentPartnerRef.current = peerData;
      updateCallState('incoming');
      try {
        ensurePeerChannel(payload.sender_id);
      } catch {
        // [C단계-6] 받는 쪽 선제 구독 예외는 무시하고 기존 흐름 유지
      }

      // ★ 수신 벨소리 및 진동 시작 ("울림 1초 -> 쉼 2초" 패턴)
      if (glareAutoAcceptCallIdRef.current !== payload.call_id) {
        startRingtone();
      }

      // ★ 수신측 35초 예비 자동 종료 타이머 기동 (안전망: 신호 유실 시 무한 벨 방지)
      if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current);
      incomingTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current === 'incoming') {
          cleanupMedia();
          updateCallState('idle');
        }
      }, INCOMING_AUTO_EXPIRE_MS);
    } catch (err) {
      if (pendingIceRef.current.callId === payload.call_id) {
        pendingIceRef.current = { callId: null, candidates: [] };
      }
      throw err;
    } finally {
      processingOfferCallIdsRef.current.delete(payload.call_id);
    }
  }, [userId, ensurePeerChannel, sendSignal, cleanupMedia, applyRecoveredOffer, terminateCall]);

  /**
   * 다른 기기/탭에서 통화가 수락되었을 때 수신 모달 및 벨소리 종료 (다중 탭 방어)
   */
  const handleCallAcceptedElsewhere = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id !== userId) return;
    if (payload.sender_tab_id === TAB_INSTANCE_ID) {
      console.log('[탭 알림] 자기 탭 알림 무시 — 이벤트=call:accepted_elsewhere');
      return;
    }
    if (isAcceptingRef.current === true && payload.call_id === currentCallIdRef.current) {
      console.log('[탭 알림] 받는 중이라 다른 기기 수락 알림 무시');
      return;
    }
    if (callStateRef.current === 'incoming' && payload.call_id === currentCallIdRef.current) {
      console.log('[useVoiceCall] 다른 기기/탭에서 통화가 수락되어 수신 화면을 정리합니다.');
      cleanupMedia();
      updateCallState('idle');
    }
  }, [userId, cleanupMedia]);

  /**
   * 다른 기기/탭에서 통화가 거절되었을 때 수신 모달 및 벨소리 종료 (다중 탭 방어)
   */
  const handleCallDeclinedElsewhere = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id !== userId) return;
    if (payload.sender_tab_id === TAB_INSTANCE_ID) {
      console.log('[탭 알림] 자기 탭 알림 무시 — 이벤트=call:declined_elsewhere');
      return;
    }
    if (callStateRef.current === 'incoming' && payload.call_id === currentCallIdRef.current) {
      console.log('[useVoiceCall] 다른 기기/탭에서 통화가 거절되어 수신 화면을 정리합니다.');
      cleanupMedia();
      updateCallState('idle');
    }
  }, [userId, cleanupMedia]);

  /**
   * 수신 통화 받기(수락)
   */
  const acceptCall = async (options = {}) => {
    const t0Accept = performance.now();
    let lastLapAccept = t0Accept;
    const lapsAccept = {};
    const recordLapAccept = (name) => {
      const now = performance.now();
      const elapsed = Math.round(now - lastLapAccept);
      lapsAccept[name] = elapsed;
      lastLapAccept = now;
      return elapsed;
    };
    let acceptIceType = '캐시';
    let acceptIceStart = 0;
    let pendingCount = 0;
    let answerSentAt = 0;
    const withVideo = Boolean(options && options.withVideo);
    // 0. 사용자 제스처 동기 스택에서 AudioContext 사전 확보/활성화
    callAudioGraph.initOrResume();

    if (callStateRef.current !== 'incoming' || !incomingOfferRef.current) {
      console.warn('[useVoiceCall] 수신 대기 상태가 아니거나 offer 정보가 없어 수락을 중단합니다. (상태:', callStateRef.current, ')');
      return;
    }
    if (isAcceptingRef.current) {
      console.warn('[useVoiceCall] 이미 수락 처리 중이므로 중복 수락 요청을 무시합니다.');
      return;
    }
    isAcceptingRef.current = true;
    setIsAccepting(true);

    // [결함 ④ 작업 2-A] 수신자 주도권 및 재연결 상태 초기화
    isCallerRef.current = false;
    peerAnsweredRef.current = false;
    autoRecoverySaveCountRef.current = 0;
    peerRecoveryWaitCountRef.current = 0;
    iceRestartCountRef.current = 0;
    isIceRestartingRef.current = false;
    iceRestartIdRef.current = 0;
    lastHandledRestartIdRef.current = 0;
    pendingRestartCandidatesRef.current = [];
    staleRemoteUfragsRef.current = new Set();
    isRestartRemoteSdpAppliedRef.current = false;
    restartSawNonConnectedRef.current = false;
    iceRestartLoggedErrorCodesRef.current.clear();
    stopIceRestartSuccessPoll();

    const acceptStart = Date.now();
    if (import.meta.env.DEV) {
      acceptStartTimeRef.current = acceptStart;
    }

    // 0. 수신 예비 타이머 해제 및 벨소리/진동 즉시 정지 (첫 await 전에 즉시 실행)
    if (incomingTimeoutRef.current) {
      clearTimeout(incomingTimeoutRef.current);
      incomingTimeoutRef.current = null;
    }
    stopAllCallSounds();

    try {
      const offerPayload = incomingOfferRef.current;
      const callId = offerPayload.call_id;
      if (withVideo) {
        wantVideoOnConnectRef.current = { callId, done: false };
      }

    // ★ F-2: sdp 없이 acceptCall에 진입한 경우 즉시 안내 후 화면을 닫음 (기다리지 않고 즉시 실패 처리)
    if (!offerPayload.sdp) {
      console.error('[useVoiceCall] acceptCall 거부: SDP offer 미확보');
      showToast(t('call.recovery_failed', '통화 연결 정보를 가져오지 못했습니다.'));
      cleanupMedia();
      updateCallState('idle');
      return;
    }

    if (offerResendTimeoutRef.current) {
      clearTimeout(offerResendTimeoutRef.current);
      offerResendTimeoutRef.current = null;
    }

    closeCallNotificationBanner(callId);




    // 1. 마이크 권한 요청 전 기존 잔류 스트림 정지 (고아 스트림 방어)
    if (localStreamRef.current) {
      try {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      } catch (e) {}
      localStreamRef.current = null;
    }

    // 2. 마이크 권한 요청
    let stream;
    try {
      stream = await acquireCallStartMic();
      localStreamRef.current = stream;
      recordLapAccept('마이크');
    } catch (err) {
      console.error('[useVoiceCall] 수신 마이크 권한 획득 실패:', err);
      showToast(t('call.mic_permission_denied', '마이크 권한이 필요합니다. 브라우저 설정에서 마이크를 허용해 주세요.'));
      declineCall();
      return;
    }

    // 3. DB 상태 갱신 (calls connected, call_participants joined)
    const nowIso = new Date().toISOString();
    try {
      const { data: updatedCalls, error: updateErr } = await supabase
        .from('calls')
        .update({
          status: 'connected',
          answered_at: nowIso,
        })
        .eq('id', callId)
        .eq('status', 'ringing')
        .select('id');

      // 오류 없이 0건 갱신이면 이미 상대방이 취소/종료한 통화이므로 즉시 수락 중단
      if (!updateErr && (!updatedCalls || updatedCalls.length === 0)) {
        console.warn('[useVoiceCall] 수락 중 통화가 이미 종료되어 수락을 중단합니다.');
        if (localStreamRef.current) {
          try {
            localStreamRef.current.getTracks().forEach((track) => track.stop());
          } catch {}
          localStreamRef.current = null;
        }
        cleanupMedia();
        updateCallState('idle');
        showToast(t('call.already_ended', '이미 종료된 통화입니다.'), 3000);
        onCallFinished?.();
        return;
      }
      recordLapAccept('상태기록');

      const tabT0 = performance.now();
      broadcastToOwnDevices('call:accepted_elsewhere', { call_id: callId })
        .then(() => console.log('[받기 실측] 탭알림(뒤에서) 완료 — ' + Math.round(performance.now() - tabT0) + 'ms'))
        .catch((e) => console.warn('[받기 실측] 탭알림(뒤에서) 실패 — ' + (e?.message || e)));

      // [결함 ④ 작업 3] 수락 성공 확정 (answered_at 기록됨)
      peerAnsweredRef.current = true;
      peerAnsweredAtRef.current = Date.now();
      schedulePreAudioRoadblockRecheckOnAnswer(callId);
      startAudioConnectWatch(callId, 'callee');


    } catch (dbErr) {
      console.error('[useVoiceCall] 수락 상태 DB 갱신 실패:', dbErr);
    }

    // ★ ICE 서버 목록 비동기 조회 (캐시 우선, 만료 시 갱신, 실패 시 Google STUN 폴백)
    let iceFetchStartAt = 0;
    if (needsReloadPreCallRef.current) {
      if (hasValidTurnCache()) {
        console.log('[통화 전 망 변경] 딱지 있음 — 유효 ICE 자격 보관분 사용(새로 받기 생략) — 계기: 받기');
      } else {
        iceFetchStartAt = Date.now();
        clearIceServersCache();
        console.log('[통화 전 망 변경] 딱지 확인 → ICE 자격 새로 받기 — 계기: 받기');
      }
    }
    acceptIceStart = performance.now();
    const iceServers = await getIceServers();
    const acceptIceElapsed = Math.round(performance.now() - acceptIceStart);
    acceptIceType = (iceFetchStartAt > 0 || acceptIceElapsed >= 50) ? '새로' : '캐시';
    recordLapAccept('ICE자격');
    if (iceFetchStartAt) {
      const resStr = iceServers === DEFAULT_ICE_SERVERS ? '기본값' : '성공';
      console.log(`[통화 전 망 변경] ICE 자격 새로 받음 — 결과: ${resStr}, 소요 ${Date.now() - iceFetchStartAt}ms`);
    }

    // ★ 자격증명 조회 중 통화가 취소/종료되었는지 상태 재확인
    if (currentCallIdRef.current !== callId || callStateRef.current === 'idle') {
      console.warn('[useVoiceCall] 수락 진행 중 통화가 취소되어 PeerConnection 생성을 중단합니다.');
      cleanupMedia();
      return;
    }

    // [C단계-5] 경로 B·C 통합 보호: 연결 장치 생성~응답 전송 중 예외는 즉시 정리
    try {
    // 수락 시작 시 새 연결 상태 초기화
    isRebuildingPcRef.current = false;
    pcRebuildCountRef.current = 0;
    if (pcRebuildTimeoutRef.current) {
      clearTimeout(pcRebuildTimeoutRef.current);
      pcRebuildTimeoutRef.current = null;
    }
    if (pcRebuildRepeatTimerRef.current) {
      clearInterval(pcRebuildRepeatTimerRef.current);
      pcRebuildRepeatTimerRef.current = null;
    }
    rebuildAnswerAppliedRef.current = false;
    rebuildAnswerCreatedRef.current = false;
    lastRebuildOfferSentAtRef.current = 0;
    pcRebuildStartTimeRef.current = 0;
    currentRebuildIdRef.current = 0;
    isRebuildAnswerCreatingRef.current = false;
    rebuildFirstSrflxAtRef.current = 0;
    rebuildFirstRelayAtRef.current = 0;

    // 4. RTCPeerConnection 생성 및 연결 감시(2차 방어) 등록
    const forceRelay = isForceRelayEnabled();
    const rtcConfig = {
      iceServers,
      ...(forceRelay ? { iceTransportPolicy: 'relay' } : {}),
    };
    if (import.meta.env.DEV) {
      if (!callStartTimeRef.current) callStartTimeRef.current = Date.now();
      localCandidatesStatsRef.current = { host: { udp: 0, tcp: 0, total: 0 }, srflx: { udp: 0, tcp: 0, total: 0 }, relay: { udp: 0, tcp: 0, total: 0 }, prflx: { udp: 0, tcp: 0, total: 0 } };
      localCandidatesLoggedRef.current = false;
      iceDumpCountRef.current = 0;
      if (iceJudgeTimerRef.current) {
        clearTimeout(iceJudgeTimerRef.current);
        iceJudgeTimerRef.current = null;
      }
      iceJudgeStartRef.current = null;
      iceJudgeLoggedRef.current = false;
      iceJudgeDumpCountRef.current = 0;
      excludedLocalHostTcpCountRef.current = 0;
      excludedRemoteHostTcpCountRef.current = 0;

      let turnCount = 0;
      let stunCount = 0;
      if (Array.isArray(iceServers)) {
        iceServers.forEach((item) => {
          const urls = Array.isArray(item?.urls) ? item.urls : [item?.urls];
          urls.forEach((u) => {
            if (typeof u === 'string') {
              if (u.startsWith('turn:') || u.startsWith('turns:')) turnCount += 1;
              else if (u.startsWith('stun:')) stunCount += 1;
            }
          });
        });
      }
      const isFallback = turnCount === 0;
      console.log(
        `[ICE 서버] 모드: ${forceRelay ? 'relay강제' : '자동'} / TURN: ${turnCount} / STUN: ${stunCount} / 폴백: ${isFallback ? '예(Google STUN만)' : '아니오'}`
      );
    }
    if (initialRoadblockTimerRef.current) {
      clearTimeout(initialRoadblockTimerRef.current);
      initialRoadblockTimerRef.current = null;
    }
    initialRoadblockJudgedRef.current = false;
    roadblockConfirmedRef.current = null;
    notifyLocalTrackChanged(stream.getAudioTracks()[0]);
    const pc = new RTCPeerConnection(rtcConfig);
    pcRef.current = pc;
    const onAcceptConnChange = () => {
      if (pc.connectionState === 'connected') {
        pc.removeEventListener('connectionstatechange', onAcceptConnChange);
        const connNow = performance.now();
        const fromAnswerMs = answerSentAt > 0 ? Math.round(connNow - answerSentAt) : 0;
        const fromAcceptMs = Math.round(connNow - t0Accept);
        console.log(`[받기 실측] 응답 전송 뒤 연결까지 ${fromAnswerMs}ms · 받기부터 연결까지 ${fromAcceptMs}ms`);
      }
    };
    pc.addEventListener('connectionstatechange', onAcceptConnChange);

    pc.onconnectionstatechange = () => handlePeerConnectionStateChange(pc.connectionState, pc);
    pc.oniceconnectionstatechange = () => handleIceConnectionStateChange(pc.iceConnectionState, pc);
    pc.onicegatheringstatechange = () => {
      if (import.meta.env.DEV) {
        const elapsed = callStartTimeRef.current ? `${Date.now() - callStartTimeRef.current}ms` : '0ms';
        console.log(`[ICE 전이] iceGatheringState: ${pc.iceGatheringState} (${elapsed})`);
        if (pc.iceGatheringState === 'complete') {
          logLocalCandidatesSummary(localCandidatesStatsRef, localCandidatesLoggedRef, excludedLocalHostTcpCountRef);
        }
      }
    };
    pc.addEventListener('icegatheringstatechange', () => {
      if (pc.iceGatheringState !== 'complete') return;
      if (initialRoadblockJudgedRef.current) return;
      if (isIceRestartingRef.current || isRebuildingPcRef.current) return;
      if (initialRoadblockTimerRef.current) {
        clearTimeout(initialRoadblockTimerRef.current);
        initialRoadblockTimerRef.current = null;
      }
      initialRoadblockJudgedRef.current = true;
      logIceRoadblockJudgement({
        pc,
        counts: countCandidatesInSdp(pc.localDescription),
        complete: true,
        reason: '수집완료',
        place: '최초 받는 쪽',
        role: '받은 사람',
        roadblockConfirmedRef,
        onRoadblockConfirmed: handleRoadblockConfirmed,
      });
    });
    pc.onicecandidateerror = (event) => {
      if ((isIceRestartingRef.current || isRebuildingPcRef.current) && event) {
        const code = event.errorCode;
        if (code && !iceRestartLoggedErrorCodesRef.current.has(code)) {
          iceRestartLoggedErrorCodesRef.current.add(code);
          const target = formatIceErrorTarget(event.url);
          console.log(`[재연결] ICE 후보 오류 — 코드: ${code}, 대상: ${target}, 사유: ${event.errorText || 'none'}`);
        }
      }
    };

    if (import.meta.env.DEV) {
      if (iceDumpTimerRef.current) clearTimeout(iceDumpTimerRef.current);
      iceDumpTimerRef.current = setTimeout(() => {
        const currentPc = pcRef.current;
        if (currentPc && currentPc.connectionState !== 'connected' && currentPc.iceConnectionState !== 'connected') {
          dumpCandidatePairs(currentPc, '10초 미연결', iceDumpCountRef);
        }
      }, 10000);
    }

    stream.getTracks().forEach((track) => {
      const sender = pc.addTrack(track, stream);
      if (track.kind === 'audio') {
        audioSenderRef.current = sender;
      }
    });

    let iceCandidateCount = 0;
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        if (import.meta.env.DEV) {
          recordCandidateStat(localCandidatesStatsRef, event.candidate);
        }
        // [수정 (나)] host tcp 전송 제외 (수신측)
        const { type, proto } = parseCandidateTypeAndProto(event.candidate);
        if (type === 'host' && proto === 'tcp') {
          if (import.meta.env.DEV) {
            excludedLocalHostTcpCountRef.current += 1;
          }
          return;
        }

        iceCandidateCount += 1;
        sendSignal('call:ice', {
          candidate: event.candidate.toJSON(),
          call_id: callId,
        });
      } else {
        if (import.meta.env.DEV) {
          logLocalCandidatesSummary(localCandidatesStatsRef, localCandidatesLoggedRef, excludedLocalHostTcpCountRef);
          console.info('[ICE 실측] 수신 후보 수:', iceCandidateCount);
        }
      }
    };

    pc.ontrack = (event) => {
      if (event.track.kind === 'audio') {
        if (remoteAudioRef.current && event.streams[0]) {
          callAudioGraph.attachRemoteStream(event.streams[0], remoteAudioRef.current);
        }
      } else if (event.track.kind === 'video') {
        remoteVideoTrackRef.current = event.track;
        setRemoteVideoStream(new MediaStream([event.track]));
        framesFlowingRef.current = (event.track.muted === false);
        applyRemoteVideoVisibility();
        event.track.onunmute = () => {
          framesFlowingRef.current = true;
          applyRemoteVideoVisibility();
          if (import.meta.env.DEV) console.log('[화상] 상대 영상 수신 시작');
        };
        event.track.onmute = () => {
          framesFlowingRef.current = false;
          applyRemoteVideoVisibility();
          if (import.meta.env.DEV) console.log('[화상] 상대 영상 멈춤');
        };
      }
    };

    // 5. Remote Description (Offer SDP) 설정
      await pc.setRemoteDescription(new RTCSessionDescription(offerPayload.sdp));
      recordLapAccept('원격설명');

      // 대기 중인 ICE 후보 추가
      pendingCount = queuedCandidatesRef.current.length;
      if (queuedCandidatesRef.current.length > 0) {
        for (const cand of queuedCandidatesRef.current) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          } catch (e) {
            console.warn('[useVoiceCall] 대기 ICE 후보 적용 실패:', e);
          }
        }
        queuedCandidatesRef.current = [];
      }
      recordLapAccept('보관후보');

      const videoTransceiver = pc.getTransceivers().find(
        (t) => t.receiver?.track?.kind === 'video' && t.direction !== 'stopped'
      );
      if (videoTransceiver) {
        videoTransceiver.direction = 'sendrecv';
        videoSenderRef.current = videoTransceiver.sender;
      }

      // 6. SDP Answer 생성 및 발송
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      recordLapAccept('응답생성');

      // [결함 ④ 작업 3 2단계 보완] 처음 받기 4초 시간 기준 판정 타이머 (setLocalDescription 직후 가동)
      const currentCallPc = pc;
      if (initialRoadblockTimerRef.current) {
        clearTimeout(initialRoadblockTimerRef.current);
      }
      initialRoadblockTimerRef.current = setTimeout(() => {
        initialRoadblockTimerRef.current = null;
        if (initialRoadblockJudgedRef.current) return;
        if (!currentCallPc || currentCallPc.signalingState === 'closed') return;
        if (isIceRestartingRef.current || isRebuildingPcRef.current) {
          initialRoadblockJudgedRef.current = true;
          console.log('[길 막힘 감지] 최초 판정 생략 — 4초 안에 재연결 진입(재연결 쪽 판정이 대신함), 위치: 최초 받는 쪽');
          return;
        }
        initialRoadblockJudgedRef.current = true;
        logIceRoadblockJudgement({
          pc: currentCallPc,
          counts: countCandidatesInSdp(currentCallPc.localDescription),
          complete: false,
          reason: '시간초과',
          place: '최초 받는 쪽',
          role: '받은 사람',
          roadblockConfirmedRef,
          onRoadblockConfirmed: handleRoadblockConfirmed,
        });
      }, 4000);

      if (import.meta.env.DEV) {
        startIceRestartJudgeTimer();
      }

      await sendSignal('call:answer', {
        call_id: callId,
        sdp: pc.localDescription,
        video_capable: true,
      });
      recordLapAccept('응답전송');
      answerSentAt = performance.now();
      const totalToAnswer = Math.round(answerSentAt - t0Accept);
      console.log(`[받기 실측] 마이크 ${lapsAccept['마이크'] ?? 0}ms · 상태기록 ${lapsAccept['상태기록'] ?? 0}ms · ICE자격 ${lapsAccept['ICE자격'] ?? 0}ms(${acceptIceType}) · 원격설명 ${lapsAccept['원격설명'] ?? 0}ms · 보관후보 ${pendingCount}개 ${lapsAccept['보관후보'] ?? 0}ms · 응답생성 ${lapsAccept['응답생성'] ?? 0}ms · 응답전송 ${lapsAccept['응답전송'] ?? 0}ms · 응답까지 합계 ${totalToAnswer}ms`);

      const partT0 = performance.now();
      supabase
        .from('call_participants')
        .update({
          state: 'joined',
          joined_at: nowIso,
        })
        .eq('call_id', callId)
        .eq('profile_id', userId)
        .then(({ error }) => {
          if (error) console.warn('[받기 실측] 참가자기록(뒤에서) 실패 — ' + (error.message || error));
          else console.log('[받기 실측] 참가자기록(뒤에서) 완료 — ' + Math.round(performance.now() - partT0) + 'ms');
        })
        .catch((e) => console.warn('[받기 실측] 참가자기록(뒤에서) 실패 — ' + (e?.message || e)));

      updateCallState('connected');
      if (import.meta.env.DEV) {
        console.log(`[받기] 클릭→connected 경과: ${Date.now() - acceptStartTimeRef.current}ms`);
      }
      startHeartbeat(); // 2차 방어 (나): 생존 신호 기동
      triggerAutoCameraOnConnectRef.current?.(callId);
    } catch (ansErr) {
      if (currentCallIdRef.current !== callId || callStateRef.current === 'idle') {
        console.warn('[받기] 응답 준비 중 오류 — 이미 다른 통화이거나 종료 상태라 끊기 생략', ansErr);
        return;
      }
      console.error('[useVoiceCall] 응답 준비 실패(연결 장치 생성~응답 전송):', ansErr);
      showToast(t('call.failed', '통화 연결에 실패했습니다.'));
      endCall({ autoReason: 'answer_failed' });
    }
  } finally {
    isAcceptingRef.current = false;
    setIsAccepting(false);
  }
};

  const acceptCallRef = useRef(acceptCall);
  acceptCallRef.current = acceptCall;

  // 동시 발신(Glare): 진 쪽에서 상대 통화 수신 완료 시 자동 받기 기동
  useEffect(() => {
    if (callState === 'incoming' && glareAutoAcceptCallIdRef.current && glareAutoAcceptCallIdRef.current === incomingOfferRef.current?.call_id) {
      const targetCallId = glareAutoAcceptCallIdRef.current;
      glareAutoAcceptCallIdRef.current = null;
      console.log('[동시 발신] 자동 받기 시작 — 통화: ' + String(targetCallId).slice(0, 8));
      acceptCallRef.current?.();
    }
  }, [callState]);

  /**
   * 수신된 call:answer 처리 (발신자 측)
   */
  const handleCallAnswer = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    // [F구역 ⑥ 묶음 3 긴급수리] 오퍼전송 await 중 조기 도착한 answer 보관 (버리지 않고 preparing 완료 시 즉시 적용)
    if (callStateRef.current === 'preparing') {
      console.log(`[통화] 조기 도착 answer 보관 (상태: preparing, call: ${payload.call_id.slice(0, 8)})`);
      pendingEarlyAnswerRef.current = payload;
      return;
    }
    if (callStateRef.current !== 'calling') return;

    peerVideoCapableRef.current = payload.video_capable === true;
    setPeerVideoCapable(payload.video_capable === true);
    if (import.meta.env.DEV) console.info('[화상 판별] 상대 video_capable:', peerVideoCapableRef.current, '(answer)');

    // ★ 수락되었으므로 발신 대기음 정지 및 30초 무응답 타이머 해제
    peerAnsweredRef.current = true;
    peerAnsweredAtRef.current = Date.now();
    stopAllCallSounds();
    if (missedTimerRef.current) {
      clearTimeout(missedTimerRef.current);
      missedTimerRef.current = null;
    }

    // [결함 ④ 5-8] 건 쪽 울림 중 막힘 확정 보류 건이 있으면 응답 시점에 6초 재판정 타이머 1회 예약
    schedulePreAudioRoadblockRecheckOnAnswer(payload.call_id);
    startAudioConnectWatch(payload.call_id, 'caller');

    const pc = pcRef.current;
    if (!pc) return;

    try {
      // [결함 ④ 작업 3] 늦게 온 중복 answer는 적용 생략 (연결 정보는 call:ice로 이미 전달됨)
      if (pc.signalingState !== 'have-local-offer') {
        console.log(`[통화] 늦은 answer — 설명 적용 생략 (상태: ${pc.signalingState})`);
        return;
      }
      await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));

      if (import.meta.env.DEV) {
        startIceRestartJudgeTimer();
      }

      // 대기 중인 ICE 후보 추가
      if (queuedCandidatesRef.current.length > 0) {
        for (const cand of queuedCandidatesRef.current) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          } catch (e) {
            console.warn('[useVoiceCall] 대기 ICE 후보 적용 실패:', e);
          }
        }
        queuedCandidatesRef.current = [];
      }

      updateCallState('connected');
      startHeartbeat(); // 2차 방어 (나): 생존 신호 기동
      triggerAutoCameraOnConnectRef.current?.(payload.call_id);
    } catch (err) {
      console.error('[useVoiceCall] Answer 처리 실패:', err);
    }
  }, [userId, startHeartbeat, startIceRestartJudgeTimer, schedulePreAudioRoadblockRecheckOnAnswer, startAudioConnectWatch]);
  handleCallAnswerRef.current = handleCallAnswer;

  /**
   * 수신된 call:ice 처리
   */
  const handleCallIce = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (import.meta.env.DEV && payload.candidate) {
      recordCandidateStat(remoteCandidatesStatsRef, payload.candidate);
    }
    // [수정 (나)] host tcp 수신 제외 (상대 기기 host tcp 방어 — 조기·벨전·대기열·즉시적용 전수 차단)
    if (payload.candidate) {
      const { type, proto } = parseCandidateTypeAndProto(payload.candidate);
      if (type === 'host' && proto === 'tcp') {
        if (import.meta.env.DEV) {
          excludedRemoteHostTcpCountRef.current += 1;
        }
        return;
      }
    }
    if (payload.call_id !== currentCallIdRef.current) {
      if (payload.call_id === pendingIceRef.current.callId) {
        if (pendingIceRef.current.candidates.length < ICE_CANDIDATE_BUFFER_LIMIT) {
          pendingIceRef.current.candidates.push(payload.candidate);
          if (import.meta.env.DEV) {
            console.log(`[신호 수신 검문] pending 후보 보관 (call_id: 대기, 수량: ${pendingIceRef.current.candidates.length})`);
          }
        } else if (!iceOverflowReportedRef.current.pending) {
          iceOverflowReportedRef.current.pending = true;
          try {
            reportMessage('ice_candidate_buffer_overflow', {
              level: 'warning',
              extra: { buffer: 'pending', limit: ICE_CANDIDATE_BUFFER_LIMIT },
            });
          } catch (err) {
            console.warn('[useVoiceCall] ICE 넘침 리포트 실패:', err);
          }
        }
        return;
      }

      // 최근 종료된 통화 목록에 있으면 폐기
      if (recentlyEndedCallIdsRef.current.has(payload.call_id)) {
        if (import.meta.env.DEV) {
          console.log('[신호 수신 검문] 종료된 통화 ICE 후보 무시 (call_id: 종료됨)');
        }
        return;
      }

      // offer보다 먼저 도착한 조기 도착 후보 보관
      if (payload.candidate) {
        const now = Date.now();
        // 만료된 항목(10초 경과) 정리
        for (const [cId, item] of earlyArrivalIceRef.current.entries()) {
          if (now - item.receivedAt > EARLY_ICE_TTL_MS) {
            earlyArrivalIceRef.current.delete(cId);
          }
        }

        let earlyEntry = earlyArrivalIceRef.current.get(payload.call_id);
        if (!earlyEntry) {
          if (earlyArrivalIceRef.current.size >= 10) {
            const oldestKey = earlyArrivalIceRef.current.keys().next().value;
            earlyArrivalIceRef.current.delete(oldestKey);
          }
          earlyEntry = { candidates: [], receivedAt: now };
          earlyArrivalIceRef.current.set(payload.call_id, earlyEntry);
        }

        if (earlyEntry.candidates.length < EARLY_ICE_LIMIT_PER_CALL) {
          earlyEntry.candidates.push(payload.candidate);
          if (import.meta.env.DEV) {
            console.log(`[신호 수신 검문] 조기 도착 후보 보관 (call_id: 보관, 누적: ${earlyEntry.candidates.length})`);
          }
        } else {
          if (import.meta.env.DEV) {
            console.warn(`[신호 수신 검문] 조기 도착 후보 상한 초과 무시 (call_id: 보관, 한도: ${EARLY_ICE_LIMIT_PER_CALL})`);
          }
        }
      }
      return;
    }
    if (!payload.candidate) return;

    const pc = pcRef.current;
    // ① remoteDescription이 없으면 → 기존처럼 queuedCandidatesRef에 보관 (변경 없음)
    if (!pc || !pc.remoteDescription || !pc.remoteDescription.type) {
      queuedCandidatesRef.current.push(payload.candidate);
      if (import.meta.env.DEV) {
        console.log(`[신호 수신 검문] 현재 통화 후보 queuedCandidates 보관 (call_id: 현재, 누적: ${queuedCandidatesRef.current.length})`);
      }
      return;
    }

    const ufrag = getCandidateUfrag(payload.candidate);
    const remoteUfrags = getRemoteUfrags(pc);

    // ② 표식이 있고 staleRemoteUfragsRef에 들어 있으면 → 버리고 '[재연결] 지난 후보 무시(옛 표식)'를 기록한다.
    if (ufrag && staleRemoteUfragsRef.current.has(ufrag)) {
      console.log(`[재연결] 지난 후보 무시(옛 표식: ${ufrag})`);
      return;
    }

    // ③ 표식이 있는데 현재 원격 표식 집합에 없으면 → pendingRestartCandidatesRef에 보관하고 '[재연결] 새 회차 후보 보관'을 기록한다.
    // ④ 표식이 null이고 재연결이 진행 중이며 이번 회차의 새 원격 설명이 아직 적용되지 않았으면 → 보관한다(③과 같은 처리).
    const isNewUnknownUfrag = Boolean(ufrag && !remoteUfrags.has(ufrag));
    const isNullUfragAwaitingRemoteSdp = !ufrag && isIceRestartingRef.current && !isRestartRemoteSdpAppliedRef.current;

    if (isNewUnknownUfrag || isNullUfragAwaitingRemoteSdp) {
      if (pendingRestartCandidatesRef.current.length >= RESTART_CANDIDATE_BUFFER_LIMIT) {
        const dropped = pendingRestartCandidatesRef.current.shift();
        const droppedUfrag = getCandidateUfrag(dropped);
        console.warn(`[재연결] 새 회차 후보 대기열 초과(50개)로 오래된 후보 버림(표식: ${droppedUfrag || 'none'})`);
      }
      pendingRestartCandidatesRef.current.push(payload.candidate);
      const { type } = parseCandidateTypeAndProto(payload.candidate);
      console.log(`[재연결] 새 회차 후보 보관 (표식: ${ufrag || 'none'}, 유형: ${type}, 누적: ${pendingRestartCandidatesRef.current.length})`);
      return;
    }

    // ⑤ 그 밖의 경우 → 기존처럼 addIceCandidate를 한다.
    //    실패하면 통화를 끝내지 않는다. 재연결과 관련 있으면 '[재연결] 후보 적용 실패(표식, 사유)'를, 관련 없으면 기존 경고를 그대로 남긴다.
    try {
      await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
      if (import.meta.env.DEV) {
        console.log('[신호 수신 검문] 현재 통화 후보 즉시 적용 (call_id: 현재)');
      }
    } catch (e) {
      if (isIceRestartingRef.current || iceRestartCountRef.current > 0) {
        console.log(`[재연결] 후보 적용 실패(표식: ${ufrag || 'none'}, 사유: ${e?.message || e})`);
      } else {
        console.warn('[useVoiceCall] addIceCandidate 실패:', e);
      }
    }
  }, [userId]);

  /**
   * 수신된 call:decline 처리 (상대가 거절함)
   */
  const handleCallDecline = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'calling') return;

    cleanupMedia();
    updateCallState('idle');
    showToast(t('call.declined', '상대가 통화를 거절했습니다.'), 3000);
    onCallFinished?.();
  }, [userId, cleanupMedia, showToast, t, onCallFinished]);

  /**
   * 수신된 call:busy 처리 (상대가 통화 중임)
   */
  const handleCallBusy = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (payload.call_id !== currentCallIdRef.current) return;
    if (callStateRef.current !== 'calling') return;

    await terminateCall({
      status: 'busy',
      toastMessage: t('call.busy', '상대가 통화 중입니다.'),
      notifyPeer: false,
      endedByUserId: payload.busy_user_id || payload.sender_id,
    });
  }, [userId, terminateCall, t]);

  /**
   * 마이크 음소거 토글
   */
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const audioTracks = localStreamRef.current.getAudioTracks();
    if (audioTracks.length === 0) return;

    const nextMuted = !isMuted;
    audioTracks.forEach((track) => {
      track.enabled = !nextMuted;
    });
    setIsMuted(nextMuted);

    // 상대방에게 음소거 상태 브로드캐스트 전송
    if (currentCallIdRef.current) {
      sendSignal('call:mute', {
        call_id: currentCallIdRef.current,
        is_muted: nextMuted,
      });
    }
  }, [isMuted, sendSignal]);

  /**
   * 현재 연결된 오디오 입력 장치 핑거프린트 추출 헬퍼 (변경 감지 비교용)
   */
  const getAudioInputFingerprint = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return '';
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((d) => d.kind === 'audioinput')
        .map((d) => `${d.deviceId}:${d.label}`)
        .sort()
        .join('|');
    } catch {
      return '';
    }
  }, []);

  /**
   * 현재 활성 마이크 groupId 조회 헬퍼 (동기)
   */
  const getCurrentMicGroupId = useCallback(() => {
    return localStreamRef.current?.getAudioTracks?.()[0]?.getSettings?.()?.groupId || '';
  }, []);

  /**
   * 현재 활성 마이크 레이블 조회 헬퍼 (표시 전용 — 규칙 22·§8-3)
   * - 실제 물리 장치 이름 반환 (deviceId 'default', 'communications' 제외한 동일 groupId 장치 탐색)
   */
  const getCurrentMicLabel = useCallback(async () => {
    const t = localStreamRef.current?.getAudioTracks?.()[0];
    if (!t || t.readyState !== 'live' || typeof t.label !== 'string') {
      return '';
    }
    const fallbackLabel = t.label.trim();
    const trackDeviceId = t.getSettings?.()?.deviceId;
    const isVirtualOrAlias = trackDeviceId === 'default' || trackDeviceId === 'communications';
    const targetGroupId = t.getSettings?.()?.groupId;
    if (!targetGroupId || typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return isVirtualOrAlias ? '' : fallbackLabel;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const matchedDevice = devices.find(
        (d) =>
          d.kind === 'audioinput' &&
          d.groupId === targetGroupId &&
          d.deviceId !== 'default' &&
          d.deviceId !== 'communications' &&
          d.label
      );
      if (matchedDevice && typeof matchedDevice.label === 'string' && matchedDevice.label.trim()) {
        return matchedDevice.label.trim();
      }
    } catch {}
    return isVirtualOrAlias ? '' : fallbackLabel;
  }, []);

  /**
   * 통화 중 마이크 트랙 핫스왑 (재협상 없이 오디오 소스 교체)
   * 
   * [보완된 실행 순서]
   * ① 동시 실행 잠금 및 통화 상태 1차 확인
   * ② getPreferredMicConstraints()로 조건 결정(exact default 지정, 실패 시 기본 조건 1회 재시도) 후 새 트랙 획득 (실패 시 기존 트랙 유지하고 return false)
   * ③ [종료 경합 검사 1차] await 직후 통화 생존 여부 (pcRef, callState connected, localStreamRef) 확인
   *    -> 하나라도 종료 상태면 newTrack.stop() 후 return false
   * ④ [음소거 사전 동기화] sender 바인딩 "전"에 newTrack.enabled = !isMutedRef.current 적용 (소리 누출 방지)
   * ⑤ 오디오 sender.replaceTrack(새 트랙) 실행
   * ⑥ [종료 경합 검사 2차] replaceTrack await 직후 통화 생존 여부 재확인
   *    -> 통화가 이미 끝났다면 newTrack.stop() 후 return false
   * ⑦ localStreamRef 보관 위치를 새 트랙으로 갱신 (종료 경로 6개에서 새 트랙 stop 보장)
   * ⑧ 마이크 레벨 미터링 스트림 갱신
   * ⑨ 옛 트랙.stop()
   * ⑩ 장치 핑거프린트 캐시 동기화
   * 
   * [새 트랙 누수 방지 & finally 보장]
   * - getUserMedia 이후 예외 발생 시 catch 블록에서 newTrack.stop() 반드시 호출
   * - finally 블록에서 isReplacingAudioRef.current = false 해제 및 1초 유예 타이머 보장
   */
  const replaceAudioTrack = useCallback(async () => {
    // 1. 이미 교체가 진행 중이면 연타 및 중복 이벤트 방지 잠금
    if (isReplacingAudioRef.current) {
      console.warn('[useVoiceCall] replaceAudioTrack 이미 진행 중 — 중복 호출 무시');
      return false;
    }

    // 통화가 연결된 상태가 아니면 시작하지 않음
    if (callStateRef.current !== 'connected' || !pcRef.current || !localStreamRef.current) {
      console.warn('[useVoiceCall] 통화 연결 상태가 아니므로 replaceAudioTrack 건너뜜');
      return false;
    }

    isReplacingAudioRef.current = true;
    isSwappingAudioRef.current = true;
    if (swapGraceTimerRef.current) {
      clearTimeout(swapGraceTimerRef.current);
      swapGraceTimerRef.current = null;
    }

    let newStream = null;
    let newTrack = null;
    let committed = false;

    try {
      // ② getUserMedia로 새 트랙 획득 (선호 default 지정 및 실패 시 1회 재시도)
      try {
        const preferredConstraints = await getPreferredMicConstraints();
        const isDefaultSpecified = preferredConstraints !== CALL_AUDIO_CONSTRAINTS;
        if (import.meta.env.DEV) {
          console.info('[마이크 교체] 요청 조건:', isDefaultSpecified ? 'default 지정' : '기본 조건');
        }
        try {
          newStream = await navigator.mediaDevices.getUserMedia(preferredConstraints);
        } catch (initialErr) {
          if (
            isDefaultSpecified &&
            (initialErr?.name === 'OverconstrainedError' || initialErr?.name === 'NotFoundError')
          ) {
            if (import.meta.env.DEV) {
              console.info('[마이크 교체] 요청 조건: 기본 조건(재시도)');
            }
            newStream = await navigator.mediaDevices.getUserMedia(CALL_AUDIO_CONSTRAINTS);
          } else {
            throw initialErr;
          }
        }
      } catch (err) {
        console.error('[useVoiceCall] 새 마이크 트랙 획득 실패 (기존 통화 유지):', err);
        return false;
      }

      newTrack = newStream?.getAudioTracks()[0];
      if (!newTrack) {
        console.error('[useVoiceCall] 새 스트림에 오디오 트랙 없음');
        return false;
      }

      // ③ [종료 경합 방지 1차]: getUserMedia await 직후 통화 생존 확인 (ref 기반 최신 상태 검사)
      const isCallAliveAfterGUM = Boolean(
        pcRef.current &&
        callStateRef.current === 'connected' &&
        localStreamRef.current
      );
      if (!isCallAliveAfterGUM) {
        console.warn('[useVoiceCall] getUserMedia 도중 통화 종료 감지 — 새 트랙 폐기');
        try { newTrack.stop(); } catch {}
        return false;
      }

      // ④ [음소거 사전 동기화]: replaceTrack 호출 "전"에 음소거 상태 적용 (마이크 소리 누출 차단)
      newTrack.enabled = !isMutedRef.current;

      // ⑤ 오디오 sender replaceTrack (audioSenderRef 단일 경로)
      if (audioSenderRef.current) {
        await audioSenderRef.current.replaceTrack(newTrack);
        committed = true; // ★ WebRTC 송신자 트랙 교체 성공 — 마이크 커밋 완료
      }

      // ⑥ [종료 경합 방지 2차]: replaceTrack await 직후 통화 생존 재확인 (ref 기반 최신 상태 검사)
      const isCallAliveAfterReplace = Boolean(
        pcRef.current &&
        callStateRef.current === 'connected' &&
        localStreamRef.current
      );
      if (!isCallAliveAfterReplace) {
        console.warn('[useVoiceCall] replaceTrack 도중 통화 종료 감지 — 새 트랙 폐기');
        try { newTrack.stop(); } catch {}
        return false;
      }

      // committed 이후 단계(⑦ 보관 위치 갱신, ⑧ 미터링, ⑨ 옛 트랙 정지, ⑩ 핑거프린트)는 내부 try/catch로 보호
      const oldTrack = localStreamRef.current.getAudioTracks()[0];
      try {
        // ⑦ localStreamRef 보관 위치를 새 트랙으로 갱신 (종료 경로 6개에서 새 트랙 stop 보장)
        if (oldTrack && oldTrack !== newTrack) {
          try {
            localStreamRef.current.removeTrack(oldTrack);
          } catch {}
        }
        if (!localStreamRef.current.getAudioTracks().includes(newTrack)) {
          localStreamRef.current.addTrack(newTrack);
        }

        // ⑧ 마이크 레벨 미터링 스트림 갱신
        if (micMeterStopRef.current) {
          try {
            micMeterStopRef.current();
          } catch {}
          micMeterStopRef.current = null;
        }
        if (localStreamRef.current) {
          micMeterStopRef.current = startMicLevelMeter(localStreamRef.current, () => {});
        }

        notifyLocalTrackChanged(newTrack);

        // ⑨ 옛 트랙 정지 (새 트랙이 완전히 안착된 후 정지)
        if (oldTrack && oldTrack !== newTrack) {
          try {
            oldTrack.stop();
          } catch {}
        }

        // ⑩ 장치 핑거프린트 캐시 동기화
        const currentFp = await getAudioInputFingerprint();
        if (currentFp) {
          lastAudioInputFingerprintRef.current = currentFp;
        }
      } catch (postCommitErr) {
        console.warn('[useVoiceCall] replaceAudioTrack 후속 정리 중 경고 (마이크 연결은 유지):', postCommitErr);
        // ⑦ 복구 안전망: localStreamRef에 새 트랙이 들어갔는지 재확인하고 없으면 반드시 등록 (종료 시 stop 보장)
        if (localStreamRef.current && !localStreamRef.current.getAudioTracks().includes(newTrack)) {
          try {
            localStreamRef.current.addTrack(newTrack);
          } catch {}
        }
      }

      console.log('[useVoiceCall] replaceAudioTrack 완료: 마이크 핫스왑 성공');
      return true;
    } catch (error) {
      if (committed) {
        // committed === true 인 경우 이미 상대방에게 새 마이크가 송출 중이므로 새 트랙을 끄지 않고 통화 유지
        console.warn('[useVoiceCall] 마이크 커밋 후 예외 발생 (새 트랙 유지, 성공 간주):', error);
        if (localStreamRef.current && newTrack && !localStreamRef.current.getAudioTracks().includes(newTrack)) {
          try {
            localStreamRef.current.addTrack(newTrack);
          } catch {}
        }
        return true;
      }

      // committed === false 인 경우에만 새 트랙 누수 방지를 위해 newTrack.stop() 실행
      console.error('[useVoiceCall] replaceAudioTrack 커밋 전 예외 발생 — 새 트랙 정리:', error);
      if (newTrack) {
        try {
          newTrack.stop();
        } catch {}
      }
      return false;
    } finally {
      // 잠금 해제 및 1초 유예 타이머 보장
      isReplacingAudioRef.current = false;
      if (swapGraceTimerRef.current) {
        clearTimeout(swapGraceTimerRef.current);
        swapGraceTimerRef.current = null;
      }
      swapGraceTimerRef.current = setTimeout(() => {
        isSwappingAudioRef.current = false;
        swapGraceTimerRef.current = null;
      }, 1000);
    }
  }, [getAudioInputFingerprint, notifyLocalTrackChanged]);
  replaceAudioTrackRef.current = replaceAudioTrack;

  /**
   * 오디오 장치 변경 자동 감지 (devicechange 및 visibilitychange)
   * - 통화 연결(connected) 중에만 리스너 등록
   * - 오디오 입력 장치 목록 변경 시에만 replaceAudioTrack 호출
   * - 통화 종료 시 모든 리스너 완벽 해제
   */
  useEffect(() => {
    if (callState !== 'connected') {
      lastAudioInputFingerprintRef.current = '';
      return;
    }

    // 초기 오디오 입력 장치 목록 캐싱
    getAudioInputFingerprint().then((fp) => {
      lastAudioInputFingerprintRef.current = fp;
    });

    const handleDeviceChange = async () => {
      if (callStateRef.current !== 'connected') return;
      const currentFp = await getAudioInputFingerprint();
      if (lastAudioInputFingerprintRef.current && currentFp !== lastAudioInputFingerprintRef.current) {
        console.log('[useVoiceCall] 오디오 입력 장치 변경 감지 (devicechange) -> replaceAudioTrack 실행');
        lastAudioInputFingerprintRef.current = currentFp;
        const prevGroup = micGroupSnapshotRef.current || getCurrentMicGroupId();
        const ok = await replaceAudioTrack();
        if (ok === true && callStateRef.current === 'connected') {
          const nextGroup = getCurrentMicGroupId();
          if (prevGroup && nextGroup && prevGroup === nextGroup) {
            if (import.meta.env.DEV) {
              console.info('[마이크 안내] 같은 장치 — 생략');
            }
          } else {
            const label = await getCurrentMicLabel();
            if (callStateRef.current === 'connected') {
              if (import.meta.env.DEV) {
                console.info('[마이크 안내] 자동 교체:', label);
              }
              micNoticeSeqRef.current += 1;
              setMicChangeNotice({ id: micNoticeSeqRef.current, label });
            }
          }
        }
      } else if (!lastAudioInputFingerprintRef.current && currentFp) {
        lastAudioInputFingerprintRef.current = currentFp;
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && callStateRef.current === 'connected') {
        const currentFp = await getAudioInputFingerprint();
        if (lastAudioInputFingerprintRef.current && currentFp !== lastAudioInputFingerprintRef.current) {
          console.log('[useVoiceCall] 화면 복귀 시 장치 변경 감지 (visibilitychange) -> replaceAudioTrack 실행');
          lastAudioInputFingerprintRef.current = currentFp;
          const prevGroup = micGroupSnapshotRef.current || getCurrentMicGroupId();
          const ok = await replaceAudioTrack();
          if (ok === true && callStateRef.current === 'connected') {
            const nextGroup = getCurrentMicGroupId();
            if (prevGroup && nextGroup && prevGroup === nextGroup) {
              if (import.meta.env.DEV) {
                console.info('[마이크 안내] 같은 장치 — 생략');
              }
            } else {
              const label = await getCurrentMicLabel();
              if (callStateRef.current === 'connected') {
                if (import.meta.env.DEV) {
                  console.info('[마이크 안내] 자동 교체:', label);
                }
                micNoticeSeqRef.current += 1;
                setMicChangeNotice({ id: micNoticeSeqRef.current, label });
              }
            }
          }
        } else if (!lastAudioInputFingerprintRef.current && currentFp) {
          lastAudioInputFingerprintRef.current = currentFp;
        }
      }
    };

    if (navigator?.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (navigator?.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      lastAudioInputFingerprintRef.current = '';
    };
  }, [callState, getAudioInputFingerprint, replaceAudioTrack, getCurrentMicGroupId, getCurrentMicLabel]);

  /**
   * 수신된 call:mute 처리 (상대방 음소거 상태 갱신)
   */
  const handleCallMute = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (!payload.call_id || payload.call_id !== currentCallIdRef.current) return;

    setPeerMuted(Boolean(payload.is_muted));
  }, [userId]);

  /**
   * [B-2 단위 3 수리] 수신된 call:video_state 처리 (상대방 카메라 켬/끔 신호)
   */
  const handleCallVideoState = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;
    if (!payload.call_id || payload.call_id !== currentCallIdRef.current) return;

    if (payload.on === false) {
      peerVideoStateRef.current = 'off';
      framesFlowingRef.current = false;
      peerVideoOffAtRef.current = Date.now();
      applyRemoteVideoVisibility();
      if (import.meta.env.DEV) console.log('[화상] 상대 카메라 신호: 끔');
    } else if (payload.on === true) {
      peerVideoStateRef.current = 'on';
      applyRemoteVideoVisibility();
      if (import.meta.env.DEV) console.log('[화상] 상대 카메라 신호: 켬');
    }
  }, [userId, applyRemoteVideoVisibility]);

  /**
   * [B-2 단위 3 수리] 받는 쪽 화면 멈춤 감시 (보조 안전장치: inbound-rtp framesDecoded)
   */
  useEffect(() => {
    if (callState !== 'connected') return;

    let isChecking = false;
    let consecutiveIncreasing = 0;
    let consecutiveStagnant = 0;
    let lastFramesDecoded = null;
    let qualityLogTick = 0;
    let prevVideoBytesReceived = null;
    let prevVideoTimestamp = null;

    const intervalId = setInterval(async () => {
      if (isChecking) return;
      if (document.visibilityState !== 'visible') {
        consecutiveIncreasing = 0;
        consecutiveStagnant = 0;
        return;
      }
      const pc = pcRef.current;
      if (!pc || !remoteVideoTrackRef.current) return;

      isChecking = true;
      try {
        const stats = await pc.getStats();
        let currentFramesDecoded = null;
        stats.forEach((report) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            if (typeof report.framesDecoded === 'number') {
              currentFramesDecoded = report.framesDecoded;
            }
          }
        });

        if (currentFramesDecoded !== null) {
          const prevFlowing = framesFlowingRef.current;

          if (lastFramesDecoded !== null) {
            if (currentFramesDecoded > lastFramesDecoded) {
              consecutiveIncreasing += 1;
              consecutiveStagnant = 0;
              framesFlowingRef.current = true;
            } else {
              consecutiveStagnant += 1;
              consecutiveIncreasing = 0;
              if (consecutiveStagnant >= 2) {
                framesFlowingRef.current = false;
              }
            }

            // "켬" 신호 유실 복구: peerVideoStateRef가 'off'이고 연속 증가 2회 이상, 끔 직후 2초 경과
            if (
              (peerVideoStateRef.current === 'off' || peerVideoStateRef.current === 'unknown') &&
              consecutiveIncreasing >= 2 &&
              Date.now() - peerVideoOffAtRef.current >= 2000
            ) {
              peerVideoStateRef.current = 'on';
            }

            if (framesFlowingRef.current !== prevFlowing) {
              if (import.meta.env.DEV) {
                console.log(`[화상] 화면 감시: ${framesFlowingRef.current ? '흐름' : '멈춤'}`);
              }
            }

            applyRemoteVideoVisibility();
          }

          lastFramesDecoded = currentFramesDecoded;
        }

        // [C-11] 5초마다 1회 TURN 중계(relay) 경로 감지 및 송신 화질 자동 낮춤 (CALL_DESIGN §5-2)
        qualityLogTick += 1;
        const isFiveSecondTick = qualityLogTick >= 5;
        if (isFiveSecondTick) {
          qualityLogTick = 0;

          let selectedPair = null;
          stats.forEach((report) => {
            if (report.type === 'transport' && report.selectedCandidatePairId) {
              selectedPair = stats.get(report.selectedCandidatePairId);
            }
            if (!selectedPair && report.type === 'candidate-pair' && (report.selected || report.nominated)) {
              selectedPair = report;
            }
          });

          let isRelayConnection = false;
          if (selectedPair) {
            const localCand = selectedPair.localCandidateId ? stats.get(selectedPair.localCandidateId) : null;
            const remoteCand = selectedPair.remoteCandidateId ? stats.get(selectedPair.remoteCandidateId) : null;
            const localType = localCand?.candidateType || localCand?.type || '';
            const remoteType = remoteCand?.candidateType || remoteCand?.type || '';
            isRelayConnection = localType === 'relay' || remoteType === 'relay';
          }

          const sender = videoSenderRef.current;
          const currentTrack = sender?.track;

          if (
            isRelayConnection &&
            sender &&
            currentTrack &&
            !relayQualityApplyingRef.current &&
            (relayQualityAppliedRef.current.sender !== sender || relayQualityAppliedRef.current.track !== currentTrack)
          ) {
            relayQualityApplyingRef.current = true;
            (async () => {
              try {
                const params = sender.getParameters?.();
                if (params?.encodings && params.encodings.length > 0 && params.encodings[0]) {
                  params.encodings[0].maxBitrate = RELAY_VIDEO_MAX_BITRATE;
                  const trackHeight = currentTrack.getSettings ? currentTrack.getSettings().height : null;
                  if (typeof trackHeight === 'number' && trackHeight > RELAY_VIDEO_TARGET_HEIGHT) {
                    params.encodings[0].scaleResolutionDownBy = trackHeight / RELAY_VIDEO_TARGET_HEIGHT;
                  }
                  await sender.setParameters(params);
                  relayQualityAppliedRef.current = { sender, track: currentTrack };
                  console.log(
                    `[화질] 중계 연결 — 송신 화질 낮춤 적용 (촬영 높이 ${trackHeight ?? '미상'} → 목표 480, 최대 600kbps)`
                  );
                }
              } catch (err) {
                console.warn('[화질] 중계 화질 낮춤 setParameters 실패 (통화 유지):', err);
              } finally {
                relayQualityApplyingRef.current = false;
              }
            })();
          }
        }

        // [B-3 전 시험 준비] 5회 주기(약 5초) 화질 기록 로그 (개발 모드 전용)
        if (import.meta.env.DEV) {
          try {
            if (isFiveSecondTick) {

              let videoInbound = null;
              let audioInbound = null;
              let selectedPair = null;

              stats.forEach((report) => {
                if (report.type === 'inbound-rtp') {
                  if (report.kind === 'video') videoInbound = report;
                  if (report.kind === 'audio') audioInbound = report;
                }
                if (report.type === 'transport' && report.selectedCandidatePairId) {
                  selectedPair = stats.get(report.selectedCandidatePairId);
                }
                if (!selectedPair && report.type === 'candidate-pair' && (report.selected || report.nominated)) {
                  selectedPair = report;
                }
              });

              // 경로 및 프로토콜, RTT
              let localType = '-';
              let remoteType = '-';
              let proto = '-';
              let rttText = '-';

              if (selectedPair) {
                const localCand = selectedPair.localCandidateId ? stats.get(selectedPair.localCandidateId) : null;
                const remoteCand = selectedPair.remoteCandidateId ? stats.get(selectedPair.remoteCandidateId) : null;
                localType = localCand?.candidateType || localCand?.type || '-';
                remoteType = remoteCand?.candidateType || remoteCand?.type || '-';
                proto = (localCand?.protocol || remoteCand?.protocol || localCand?.relayProtocol || remoteCand?.relayProtocol || '-').toLowerCase();
                if (typeof selectedPair.currentRoundTripTime === 'number') {
                  rttText = `${Math.round(selectedPair.currentRoundTripTime * 1000)}ms`;
                }
              }

              // 영상 항목: {frameWidth}x{frameHeight} | {framesPerSecond}fps | {kbps}kbps | 영상 손실 {packetsLost}/{packetsReceived} | 지터 {jitter×1000 반올림}ms
              let resText = '-';
              let fpsText = '-';
              let kbpsText = '-';
              let videoLossText = '-';
              let jitterText = '-';

              if (videoInbound) {
                if (typeof videoInbound.frameWidth === 'number' && typeof videoInbound.frameHeight === 'number') {
                  resText = `${videoInbound.frameWidth}x${videoInbound.frameHeight}`;
                }
                if (typeof videoInbound.framesPerSecond === 'number') {
                  fpsText = `${Math.round(videoInbound.framesPerSecond)}fps`;
                }
                if (typeof videoInbound.bytesReceived === 'number' && typeof videoInbound.timestamp === 'number') {
                  if (
                    prevVideoBytesReceived !== null &&
                    prevVideoTimestamp !== null &&
                    videoInbound.bytesReceived >= prevVideoBytesReceived &&
                    videoInbound.timestamp > prevVideoTimestamp
                  ) {
                    const bytesDiff = videoInbound.bytesReceived - prevVideoBytesReceived;
                    const timeDiff = videoInbound.timestamp - prevVideoTimestamp;
                    kbpsText = `${Math.round((bytesDiff * 8) / timeDiff)}kbps`;
                  }
                  prevVideoBytesReceived = videoInbound.bytesReceived;
                  prevVideoTimestamp = videoInbound.timestamp;
                }
                if (typeof videoInbound.packetsLost === 'number' && typeof videoInbound.packetsReceived === 'number') {
                  videoLossText = `${videoInbound.packetsLost}/${videoInbound.packetsReceived}`;
                }
                if (typeof videoInbound.jitter === 'number') {
                  jitterText = `${Math.round(videoInbound.jitter * 1000)}ms`;
                }
              } else {
                prevVideoBytesReceived = null;
                prevVideoTimestamp = null;
              }

              // 소리 항목: 소리 손실 {audio packetsLost}/{audio packetsReceived}
              let audioLossText = '-';
              if (audioInbound) {
                if (typeof audioInbound.packetsLost === 'number' && typeof audioInbound.packetsReceived === 'number') {
                  audioLossText = `${audioInbound.packetsLost}/${audioInbound.packetsReceived}`;
                }
              }

              console.log(
                `[화질] 경로 ${localType}<->${remoteType}(${proto}) | ${resText} | ${fpsText} | ${kbpsText} | 영상 손실 ${videoLossText} | 소리 손실 ${audioLossText} | 지터 ${jitterText} | RTT ${rttText}`
              );
            }
          } catch (qualErr) {
            if (import.meta.env.DEV) {
              console.warn('[useVoiceCall] 화질 로그 수집 실패:', qualErr);
            }
          }
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[useVoiceCall] 화면 감시 getStats 실패:', err);
        }
      } finally {
        isChecking = false;
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [callState, applyRemoteVideoVisibility]);

  /**
   * 수신측 DB 복구 시 발신측으로 요청된 offer·ICE 재전송 신호 처리
   */
  const handleOfferResendRequest = useCallback(async (eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload?.call_id) return;

    // 응답 조건:
    // · payload.call_id === 현재 통화 ID
    // · 자신이 이 통화의 발신자 (currentCallIdRef.current 일치 & calling 상태)
    // · 통화 상태가 아직 발신·대기 중 (calling 상태)
    // · peerConnection.localDescription 이 존재
    if (payload.call_id !== currentCallIdRef.current) {
      console.log('[useVoiceCall] handleOfferResendRequest 무시: call_id 불일치', payload.call_id, currentCallIdRef.current);
      return;
    }
    if (callStateRef.current !== 'calling') {
      console.log('[useVoiceCall] handleOfferResendRequest 무시: calling 상태가 아님', callStateRef.current);
      return;
    }
    const pc = pcRef.current;
    if (!pc || !pc.localDescription) {
      console.log('[useVoiceCall] handleOfferResendRequest 무시: localDescription 없음');
      return;
    }

    // 쿨다운: 동일 call_id 에 대해 3초 내 재요청은 무시 (폭주 차단)
    const now = Date.now();
    if (now - lastOfferResendAtRef.current < 3000) {
      console.log('[useVoiceCall] handleOfferResendRequest 무시: 3초 쿨다운 적용 중');
      return;
    }
    lastOfferResendAtRef.current = now;

    console.log('[useVoiceCall] handleOfferResendRequest 수신 -> offer 및 ICE 재전송 실행');

    const targetPartner = currentPartnerRef.current;
    const targetChatId = currentChatIdRef.current;
    const callId = currentCallIdRef.current;
    const targetUserId = targetPartner?.id || payload.sender_id;

    let senderProfile = myProfile;
    if (!senderProfile && userId) {
      try {
        const { data: prof, error: profErr } = await supabase
          .from('profiles')
          .select('id, name, nickname, avatar, name_display_choice')
          .eq('id', userId)
          .maybeSingle();
        if (profErr) {
          console.error('[useVoiceCall] 발신자 내 프로필 조회 실패:', profErr);
        }
        if (prof) {
          senderProfile = prof;
        }
      } catch (e) {
        console.error('[useVoiceCall] 발신자 내 프로필 조회 예외:', e);
      }
    }

    const senderName = getDisplayName(senderProfile, '');
    const senderAvatar = senderProfile?.avatar || null;

    // (1) localDescription 을 기존 call:offer 와 완전히 동일한 payload 구조로 재전송 (is_resend: true 플래그 추가)
    await sendSignal('call:offer', {
      call_id: callId,
      conversation_id: targetChatId,
      caller_id: userId,
      caller_name: senderName,
      caller_avatar: senderAvatar,
      target_user_id: targetUserId,
      kind: 'audio',
      sdp: pc.localDescription,
      is_resend: true,
      video_capable: true,
    }, targetUserId);

    // (2) sentIceCandidatesRef 전체를 기존 call:ice 규약으로 순차 재전송
    for (const cand of sentIceCandidatesRef.current) {
      await sendSignal('call:ice', {
        candidate: cand,
        call_id: callId,
      }, targetUserId);
    }
  }, [userId, myProfile, sendSignal]);

  /**
   * 상대방에 의한 call:end 처리 (정상 종료 또는 무응답 종료 또는 발신 취소)
   */
  const handleCallEnd = useCallback((eventPayload) => {
    const payload = eventPayload?.payload || eventPayload;
    if (!payload || payload.sender_id === userId) return;

    const isMatchingCall = payload.call_id && payload.call_id === currentCallIdRef.current;

    if (!isMatchingCall) {
      if (payload.call_id) {
        recordRecentlyEndedCall(payload.call_id);
      }
      return;
    }

    const isWaitingRecovery = Boolean(peerRecoveryWaitRef.current?.callId && peerRecoveryWaitRef.current.callId === payload.call_id);
    console.log(`[call:end 수신] call=${payload.call_id?.slice(0, 8)} reason=${payload.reason ?? 'null'} 대기중=${isWaitingRecovery}`);

    // [결함 ④] 상대 복구 대기 중에는 명시적 사용자 취소/종료('normal_hangup', 'canceled') 및 복구 실패('reload_not_scheduled', 'rebuild_exhausted', 'reload_recovery_failed', 'mic_failed')만 인정하고, 출처 불명(unload, unmount 등) 신호는 무시
    if (
      isWaitingRecovery &&
      payload.reason !== 'normal_hangup' &&
      payload.reason !== 'canceled' &&
      payload.reason !== 'reload_not_scheduled' &&
      payload.reason !== 'rebuild_exhausted' &&
      payload.reason !== 'reload_recovery_failed' &&
      payload.reason !== 'mic_failed'
    ) {
      console.log('[복구 대기] 상대 call:end 무시 reason=' + (payload.reason ?? 'null') + ' → 20초 대기 유지');
      return;
    }

    if (payload.call_id) {
      recordRecentlyEndedCall(payload.call_id);
    }

    const isMissedReason = payload.reason === 'missed';
    const isCanceledReason = payload.reason === 'canceled';
    const isFailedReason = payload.reason === 'offer_failed' || payload.reason === 'answer_failed';

    if (['reload_recovery_failed', 'rebuild_exhausted', 'reload_not_scheduled'].includes(payload.reason)) {
      terminateCall({
        status: 'failed',
        reason: payload.reason,
        toastMessage: t('call.unstable_ended', '연결이 불안정해 통화가 끊겼어요. 다시 걸어 주세요.'),
        toastDurationMs: UNSTABLE_ENDED_TOAST_MS,
        notifyPeer: false,
      });
      return;
    }

    if (payload.reason === 'mic_failed') {
      terminateCall({
        status: 'failed',
        reason: 'mic_failed',
        toastMessage: t('call.peer_recover_failed', '상대와 연결되지 않았어요. 다시 걸어 주세요.'),
        toastDurationMs: 3000,
        notifyPeer: false,
      });
      return;
    }

    terminateCall({
      status: isFailedReason ? 'failed' : ((isMissedReason || isCanceledReason) ? 'missed' : 'ended'),
      toastMessage: isFailedReason
        ? t('call.failed', '통화 연결에 실패했습니다.')
        : ((isMissedReason || isCanceledReason) ? null : t('call.ended', '통화가 종료되었습니다.')),
      notifyPeer: false,
    });
  }, [userId, terminateCall, t, recordRecentlyEndedCall]);

  /**
   * 컴포넌트 unmount 시 미디어 및 세션 정리 안전장치
   */
  useEffect(() => {
    return () => {
      // 새로고침·창 닫기는 handleUnload가 맡는다. 여기는 로그아웃 등 화면이 내려가는 경우라 복구와 무관하게 정상 종료한다.
      const callId = currentCallIdRef.current;
      if (callStateRef.current !== 'idle' && callId) {
        console.log(`[call:end 송신] 출처=unmount reason=unmount call=${callId.slice(0, 8)}`);
        sendSignalRef.current?.('call:end', { call_id: callId, reason: 'unmount' });
        const unmountEverConnected = timerStartedRef.current;
        const unmountPeerAnswered = peerAnsweredRef.current;
        const unmountStatus = resolveFinalCallStatus('ended', unmountPeerAnswered, unmountEverConnected);
        supabase
          .from('calls')
          .update({
            status: unmountStatus,
            ended_at: new Date().toISOString(),
            ended_by: userIdRef.current,
          })
          .eq('id', callId)
          .in('status', ['ringing', 'connected'])
          .then(() => {});
      }
      if (peerChannelIdleCloseTimerRef.current) {
        clearTimeout(peerChannelIdleCloseTimerRef.current);
        peerChannelIdleCloseTimerRef.current = null;
      }
      closePeerChannelRef.current?.('언마운트');
      cleanupMediaRef.current?.();
    };
  }, []);
  // 실시간 마이크 하드웨어 잠김(isMicLocked) 판정 (connected 상태 시에만 동작)
  useEffect(() => {
    if (callState !== 'connected' || !localStreamRef.current) {
      if (micMeterStopRef.current) {
        try {
          micMeterStopRef.current();
        } catch (e) {}
        micMeterStopRef.current = null;
      }
      if (micTrackCheckIntervalRef.current) {
        clearInterval(micTrackCheckIntervalRef.current);
        micTrackCheckIntervalRef.current = null;
      }
      setIsMicLocked(false);
      isFirstMicCheckRef.current = true;
      micLockConsecutiveStartRef.current = 0;
      return;
    }

    const stream = localStreamRef.current;
    isFirstMicCheckRef.current = true;
    micLockConsecutiveStartRef.current = 0;

    const updateTrackStatus = () => {
      // 마이크 트랙 핫스왑 진행 중이거나 교체 직후 1초 유예 기간에는 검사 바이패스 (오탐 방지)
      if (isSwappingAudioRef.current) return;

      const currentTrack = localStreamRef.current?.getAudioTracks()[0];
      if (!currentTrack) return;

      const isTrackMuted = Boolean(currentTrack.muted);
      const trackReadyState = currentTrack.readyState || 'unknown';

      // 하드웨어/OS 마이크 잠김(isMicLocked) 판정
      // - 앱 내부에서 사용자가 직접 음소거한 경우(isMuted === true)는 절대 잠김으로 오탐하지 않음
      // - 오디오 트랙의 muted === true 이거나 readyState !== 'live' 인 경우 잠김 조건 충족
      const isHardwareLocked = !isMutedRef.current && (isTrackMuted || trackReadyState !== 'live');

      if (isHardwareLocked) {
        const now = Date.now();
        if (isFirstMicCheckRef.current) {
          // ★ connected 전환 직후 첫 측정에서 이미 잠겨있다면 지연 없이 즉시 반영 (알림 배너 진입 대응)
          setIsMicLocked(true);
          micLockConsecutiveStartRef.current = now;
        } else {
          // 통화 도중에 잠김으로 바뀐 경우: 약 700ms 연속 유지 시에만 반영 (오탐 방지)
          if (!micLockConsecutiveStartRef.current) {
            micLockConsecutiveStartRef.current = now;
          } else if (now - micLockConsecutiveStartRef.current >= 700) {
            setIsMicLocked(true);
          }
        }
      } else {
        // 해제(잠김 -> 정상)는 항상 즉시 반영
        micLockConsecutiveStartRef.current = 0;
        setIsMicLocked(false);
      }

      isFirstMicCheckRef.current = false;
    };

    updateTrackStatus();
    micTrackCheckIntervalRef.current = setInterval(updateTrackStatus, 100);

    // RMS 레벨 측정기 시작 (오디오 파이프라인 활성 유지)
    micMeterStopRef.current = startMicLevelMeter(stream, () => {});

    return () => {
      if (micMeterStopRef.current) {
        try {
          micMeterStopRef.current();
        } catch (e) {}
        micMeterStopRef.current = null;
      }
      if (micTrackCheckIntervalRef.current) {
        clearInterval(micTrackCheckIntervalRef.current);
        micTrackCheckIntervalRef.current = null;
      }
      setIsMicLocked(false);
      isFirstMicCheckRef.current = true;
      micLockConsecutiveStartRef.current = 0;
    };
  }, [callState]);

  // [F구역-1] 화면 켜짐 유지 (WakeLock) 함수 및 생명주기 효과
  const acquireWakeLock = useCallback((reason) => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator) || typeof navigator.wakeLock?.request !== 'function') {
      console.log('[화면 켜짐] 미지원 — 건너뜀');
      return;
    }
    if (wakeLockRequestingRef.current || (wakeLockRef.current && wakeLockRef.current.released === false)) {
      return;
    }
    wakeLockRequestingRef.current = true;
    navigator.wakeLock
      .request('screen')
      .then((sentinel) => {
        if (callStateRef.current !== 'connected') {
          sentinel.release().catch(() => {});
          return;
        }
        wakeLockRef.current = sentinel;
        console.log('[화면 켜짐] 걸음 — 계기=' + reason);
        sentinel.addEventListener('release', () => {
          console.log('[화면 켜짐] 풀림');
          if (wakeLockRef.current === sentinel) {
            wakeLockRef.current = null;
          }
        });
      })
      .catch((err) => {
        console.warn('[화면 켜짐] 요청 실패(무시):', err?.name);
      })
      .finally(() => {
        wakeLockRequestingRef.current = false;
      });
  }, []);

  const releaseWakeLock = useCallback((reason) => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
      console.log('[화면 켜짐] 해제 — 계기=' + reason);
    }
  }, []);

  useEffect(() => {
    if (callState === 'connected') {
      acquireWakeLock('연결');
    } else {
      releaseWakeLock('상태=' + callState);
    }
  }, [callState, acquireWakeLock, releaseWakeLock]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && callStateRef.current === 'connected') {
        acquireWakeLock('화면 복귀');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock('언마운트');
    };
  }, [acquireWakeLock, releaseWakeLock]);

  // [C-9] callAudioGraph 통화 활성 여부 연동
  useEffect(() => {
    callAudioGraph.setCallActiveChecker(() => callStateRef.current === 'connected');
    return () => {
      callAudioGraph.setCallActiveChecker(null);
    };
  }, []);

  // [C-9] 화면 복귀 시 소리·마이크 회복
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && callStateRef.current === 'connected') {
        // (1) 마이크 트랙 ended 감지 시 다시 잡기
        const curTrack = localStreamRef.current?.getAudioTracks?.()[0];
        if (curTrack && curTrack.readyState === 'ended') {
          console.warn('[마이크 회복] 화면 복귀 시 마이크 끊김 감지 → 다시 잡기');
          replaceAudioTrackRef.current?.().catch((e) => console.warn('[마이크 회복] replaceAudioTrack 예외(무시):', e));
        }

        // (2) 상대 소리 오디오 엘리먼트 멈춤 감지 시 play() 재시도
        if (remoteAudioRef.current) {
          const audioEl = remoteAudioRef.current;
          if (audioEl.paused && audioEl.srcObject) {
            console.log('[소리 회복] 화면 복귀 시 원격 오디오 멈춤 감지 → play() 재시도');
            audioEl.play().catch((e) => {
              console.warn('[소리 회복] 원격 오디오 play() 실패:', e);
              if (e?.name === 'NotAllowedError') {
                callAudioGraph.setAudioBlocked(true);
              }
            });
          }
        }

        // (3) AudioContext 상태 감지 (running 아닐 때 resume)
        const ctx = callAudioGraph.getSharedAudioContext();
        if (ctx && ctx.state !== 'running') {
          console.log(`[소리 회복] 화면 복귀 시 AudioContext ${ctx.state} → resume() 시도`);
          ctx.resume().catch((e) => console.warn('[소리 회복] AudioContext resume() 실패:', e));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // [C-9] 통화 중 당겨서 새로고침 방지 (html, body 인라인 overscroll-behavior: none)
  useEffect(() => {
    if (callState !== 'idle') {
      const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;
      const prevBodyOverscroll = document.body.style.overscrollBehavior;
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overscrollBehavior = 'none';

      return () => {
        document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
        document.body.style.overscrollBehavior = prevBodyOverscroll;
      };
    }
  }, [callState]);

  const getPeerVideoCapable = useCallback(() => peerVideoCapableRef.current === true, []);

  const startCamera = useCallback(async (desiredFacing) => {
    const facing = desiredFacing === 'environment' ? 'environment' : 'user';
    if (callStateRef.current !== 'connected' || !videoSenderRef.current || cameraBusyRef.current) {
      if (import.meta.env.DEV) console.info('[화상] 카메라 실패: not_ready');
      return { ok: false, reason: 'not_ready' };
    }

    if (peerVideoCapableRef.current !== true) {
      if (import.meta.env.DEV) console.info('[화상] 상대 미지원으로 카메라 안 켬');
      return { ok: false, reason: 'peer_unsupported' };
    }

    cameraBusyRef.current = true;
    try {
      if (localVideoTrackRef.current) {
        try {
          localVideoTrackRef.current.stop();
        } catch {}
        localVideoTrackRef.current = null;
      }
      if (localVideoStreamRef.current) {
        try {
          localVideoStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        localVideoStreamRef.current = null;
      }
      stopStreamQuietly(cameraCandidateStreamRef.current);
      cameraCandidateStreamRef.current = null;
      cameraVerifySeqRef.current++;

      let stream;
      try {
        const storedId = isLaptopDevice() ? getStoredCameraDeviceId() : '';
        if (storedId) {
          try {
            if (import.meta.env.DEV) {
              console.info('[화상] 기억 카메라 우선 시도: ' + storedId.slice(0, 8));
            }
            stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: storedId } },
              audio: false,
            });
            if (import.meta.env.DEV) {
              console.info('[화상] 기억 카메라 획득 성공');
            }
          } catch (storedErr) {
            const errName = storedErr?.name || '';
            if (errName === 'OverconstrainedError' || errName === 'NotFoundError') {
              if (import.meta.env.DEV) {
                console.info(`[화상] 기억 카메라 실패(${errName}) → 기억 삭제`);
              }
              clearStoredCameraDeviceId();
            } else if (errName === 'NotReadableError' || errName === 'AbortError') {
              if (import.meta.env.DEV) {
                console.info(`[화상] 기억 카메라 일시 오류(${errName}) → 기억 유지`);
              }
            } else {
              throw storedErr;
            }
          }
        }

        if (!stream) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: facing } },
            audio: false,
          });
        }
      } catch (err) {
        let reason = 'unavailable';
        if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
          reason = 'permission_denied';
        }
        if (import.meta.env.DEV) console.info('[화상] 카메라 실패: ' + reason);
        return { ok: false, reason };
      }

      if (callStateRef.current !== 'connected') {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {}
        if (import.meta.env.DEV) console.info('[화상] 카메라 실패: not_ready (획득 직후)');
        return { ok: false, reason: 'not_ready' };
      }

      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {}
        if (import.meta.env.DEV) console.info('[화상] 카메라 실패: unavailable (트랙 없음)');
        return { ok: false, reason: 'unavailable' };
      }

      // 개발 모드 진단 로그: 장치 라벨 및 상세 설정 확인 (1-a, 1-b)
      if (import.meta.env.DEV) {
        const settings = videoTrack.getSettings ? videoTrack.getSettings() : {};
        console.info(
          '[화상] 카메라 잡음:',
          videoTrack.label,
          '| readyState:',
          videoTrack.readyState,
          '| muted:',
          videoTrack.muted,
          '| width:',
          settings.width,
          '| height:',
          settings.height,
          '| frameRate:',
          settings.frameRate
        );
        videoTrack.onmute = () => console.warn('[화상] 로컬 영상 트랙 음소거됨(onmute)');
        videoTrack.onunmute = () => console.info('[화상] 로컬 영상 트랙 음소거 해제(onunmute)');
        videoTrack.onended = () => console.warn('[화상] 로컬 영상 트랙 종료됨(onended)');
      }

      localVideoTrackRef.current = videoTrack;
      notifyLocalVideoTrackChangedRef.current?.(videoTrack);
      localVideoStreamRef.current = stream;
      setLocalVideoStream(stream);
      const actualFacing = videoTrack.getSettings?.().facingMode || facing;
      currentFacingModeRef.current = actualFacing;
      setCameraFacing(actualFacing);

      try {
        await videoSenderRef.current.replaceTrack(videoTrack);
      } catch (replaceErr) {
        notifyLocalVideoTrackChangedRef.current?.(null);
        try {
          videoTrack.stop();
        } catch {}
        localVideoTrackRef.current = null;
        localVideoStreamRef.current = null;
        if (import.meta.env.DEV) console.info('[화상] 카메라 실패: unavailable (replaceTrack 실패)', replaceErr);
        return { ok: false, reason: 'unavailable' };
      }

      // replaceTrack 완료 직후 통화 생존 재확인 (4-a, 1-a 반영)
      if (callStateRef.current !== 'connected') {
        notifyLocalVideoTrackChangedRef.current?.(null);
        try {
          videoTrack.stop();
        } catch {}
        localVideoTrackRef.current = null;
        localVideoStreamRef.current = null;
        setCameraOn(false);
        if (import.meta.env.DEV) console.info('[화상] 카메라 실패: not_ready (replaceTrack 직후 종료 감지)');
        return { ok: false, reason: 'not_ready' };
      }

      setCameraOn(true);
      if (navigator.mediaDevices?.enumerateDevices) {
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          const count = devices.filter((d) => d.kind === 'videoinput').length;
          setCameraDeviceCount(count);
          if (import.meta.env.DEV) {
            console.info('[화상] 카메라 장치 수: ' + count);
          }
        }).catch((err) => {
          setCameraDeviceCount(0);
          if (import.meta.env.DEV) {
            console.warn('[화상] 카메라 장치 수 조회 실패:', err);
          }
        });
      } else {
        setCameraDeviceCount(0);
      }

      console.info('[화상] 영상 끼우기 완료');
      console.info('[화상] 카메라 켬');

      // 비동기 화면 출력 판정 (B-2 단위 1-A)
      const verifyToken = ++cameraVerifySeqRef.current;
      const isAliveFn = () =>
        cameraVerifySeqRef.current === verifyToken &&
        localVideoTrackRef.current === videoTrack &&
        callStateRef.current === 'connected';

      const runCameraAutoSwitch = async (brokenTrack, token) => {
        const isValid = () =>
          cameraVerifySeqRef.current === token &&
          localVideoTrackRef.current === brokenTrack &&
          callStateRef.current === 'connected' &&
          Boolean(videoSenderRef.current);

        try {
          if (!isValid()) return;

          let devices = [];
          try {
            devices = (await navigator.mediaDevices.enumerateDevices()).filter(
              (d) => d.kind === 'videoinput' && Boolean(d.deviceId)
            );
          } catch {
            devices = [];
          }

          if (!isValid()) return;

          const brokenDeviceId = brokenTrack.getSettings ? brokenTrack.getSettings().deviceId : '';
          const candidates = devices
            .filter((d) => d.deviceId !== brokenDeviceId)
            .slice(0, AUTO_SWITCH_MAX_CANDIDATES);

          if (import.meta.env.DEV) {
            console.info(`[화상] 자동 전환 시작: 후보 ${candidates.length}개`);
          }

          let switchedSuccess = false;

          for (const cand of candidates) {
            if (!isValid()) {
              if (import.meta.env.DEV) console.info('[화상] 자동 전환 중단: 무효화됨');
              break;
            }

            let candStream = null;
            try {
              candStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: cand.deviceId } },
                audio: false,
              });
            } catch (candErr) {
              if (import.meta.env.DEV) {
                console.info(`[화상] 후보 획득 실패: ${candErr?.name || 'unknown'}`);
              }
              continue;
            }

            cameraCandidateStreamRef.current = candStream;

            if (!isValid()) {
              stopStreamQuietly(candStream);
              cameraCandidateStreamRef.current = null;
              if (import.meta.env.DEV) console.info('[화상] 자동 전환 중단: 획득 직후 무효화됨');
              break;
            }

            const candTrack = candStream.getVideoTracks()[0];
            if (!candTrack) {
              stopStreamQuietly(candStream);
              cameraCandidateStreamRef.current = null;
              continue;
            }

            if (candTrack.getCapabilities?.()?.facingMode?.includes('environment')) {
              stopStreamQuietly(candStream);
              cameraCandidateStreamRef.current = null;
              continue;
            }

            const isCandAlive = () => isValid() && candTrack.readyState === 'live';
            const candRes = await inspectVideoTrackLiveness(candTrack, isCandAlive);

            if (import.meta.env.DEV && candRes) {
              console.info(
                `[화상] 후보 판정: 결과 ${candRes.status} / 밝기 ${candRes.brightness.toFixed(1)} / 변화량 ${candRes.diff.toFixed(1)}`
              );
            }

            if (!isValid()) {
              stopStreamQuietly(candStream);
              cameraCandidateStreamRef.current = null;
              if (import.meta.env.DEV) console.info('[화상] 자동 전환 중단: 판정 후 무효화됨');
              break;
            }

            if (candRes && candRes.status === 'aborted') {
              stopStreamQuietly(candStream);
              cameraCandidateStreamRef.current = null;
              if (import.meta.env.DEV) console.info('[화상] 자동 전환 중단: aborted');
              return;
            }

            if (candRes && candRes.status === 'ok') {
              if (!isValid()) {
                stopStreamQuietly(candStream);
                cameraCandidateStreamRef.current = null;
                if (import.meta.env.DEV) console.info('[화상] 자동 전환 중단: 교체 직전 무효화됨');
                return;
              }

              try {
                await videoSenderRef.current.replaceTrack(candTrack);
              } catch (repErr) {
                if (import.meta.env.DEV) console.info('[화상] 후보 replaceTrack 실패:', repErr);
                stopStreamQuietly(candStream);
                cameraCandidateStreamRef.current = null;
                continue;
              }

              if (!isValid()) {
                if (import.meta.env.DEV) console.info('[화상] 자동 전환 중단: replaceTrack 후 상태 변경됨');
                stopStreamQuietly(candStream);
                cameraCandidateStreamRef.current = null;
                return;
              }

              cameraCandidateStreamRef.current = null;
              const oldTrack = localVideoTrackRef.current;
              const oldStream = localVideoStreamRef.current;
              notifyLocalVideoTrackChangedRef.current?.(candTrack);
              localVideoTrackRef.current = candTrack;
              localVideoStreamRef.current = candStream;
              setLocalVideoStream(candStream);

              if (import.meta.env.DEV) {
                if (oldTrack) {
                  oldTrack.onmute = null;
                  oldTrack.onunmute = null;
                  oldTrack.onended = null;
                }
                candTrack.onmute = () => console.warn('[화상] 로컬 영상 트랙 음소거됨(onmute)');
                candTrack.onunmute = () => console.info('[화상] 로컬 영상 트랙 음소거 해제(onunmute)');
                candTrack.onended = () => console.warn('[화상] 로컬 영상 트랙 종료됨(onended)');
              }

              if (oldTrack) {
                try {
                  oldTrack.stop();
                } catch {
                  /* 의도적 무시: 이전 트랙 정지 실패 */
                }
              }
              if (oldStream) {
                stopStreamQuietly(oldStream);
              }

              const newDeviceId = candTrack.getSettings?.().deviceId || cand.deviceId || '';
              if (newDeviceId) {
                setStoredCameraDeviceId(newDeviceId);
                if (import.meta.env.DEV) {
                  console.info('[화상] 정상 카메라 기억 저장: ' + newDeviceId.slice(0, 8));
                }
              }

              const switchedName = candTrack.label || cand.label || '';
              if (!cameraNoticeGivenRef.current) {
                cameraNoticeGivenRef.current = true;
                setCameraNotice({
                  id: ++cameraNoticeSeqRef.current,
                  type: 'switched',
                  name: switchedName,
                });
              }

              if (import.meta.env.DEV) {
                console.info('[화상] 자동 전환 성공: ' + (switchedName || '이름 없음'));
              }

              switchedSuccess = true;
              break;
            } else {
              // 'black' / 'frozen' 등 이상 후보 즉시 정지 후 다음 후보
              stopStreamQuietly(candStream);
              cameraCandidateStreamRef.current = null;
            }
          }

          if (!switchedSuccess && isValid() && !cameraNoticeGivenRef.current) {
            if (import.meta.env.DEV) {
              console.info('[화상] 자동 전환 실패: 후보 전부 이상');
            }
            cameraNoticeGivenRef.current = true;
            setCameraNotice({ id: ++cameraNoticeSeqRef.current, type: 'no_picture' });
          }
        } catch (autoErr) {
          if (import.meta.env.DEV) {
            console.info('[화상] 자동 전환 중단: 예외 발생', autoErr);
          }
          stopStreamQuietly(cameraCandidateStreamRef.current);
          cameraCandidateStreamRef.current = null;
        }
      };

      inspectVideoTrackLiveness(videoTrack, isAliveFn).then((res) => {
        if (!res || res.status === 'aborted') return;

        if (import.meta.env.DEV) {
          console.info(
            `[화상] 화면 판정: 결과 ${res.status} / 밝기 ${res.brightness.toFixed(1)} / 변화량 ${res.diff.toFixed(1)} / 재측정 ${res.retried ? '예' : '아니오'}`
          );
        }

        if (res.status === 'ok') {
          if (isLaptopDevice()) {
            const devId = videoTrack.getSettings ? videoTrack.getSettings().deviceId : '';
            if (devId) {
              setStoredCameraDeviceId(devId);
              if (import.meta.env.DEV) {
                console.info('[화상] 정상 카메라 기억 저장: ' + devId.slice(0, 8));
              }
            }
          }
          return;
        }

        if (res.status === 'black' || res.status === 'frozen') {
          if (!isLaptopDevice()) {
            if (!cameraNoticeGivenRef.current) {
              cameraNoticeGivenRef.current = true;
              setCameraNotice({ id: ++cameraNoticeSeqRef.current, type: 'no_picture' });
            }
            return;
          }

          // 랩탑: 자동 전환 실행 (cameraNoticeGivenRef와 상관없이 실행, 안내 잠금은 안내창에만)
          runCameraAutoSwitch(videoTrack, verifyToken);
        }
      }).catch(() => {});

      return { ok: true };
    } finally {
      cameraBusyRef.current = false;
    }
  }, []);
  startCameraRef.current = startCamera;

  /**
   * [B-3a 단위 1] 첫 connected 확정 시 카메라 자동 켜기 (의도 ref가 있을 때 1회만)
   */
  const triggerAutoCameraOnConnect = useCallback(async (currentCallId) => {
    if (!wantVideoOnConnectRef.current) return;
    const req = wantVideoOnConnectRef.current;
    if (req.done) return;
    if (req.callId && currentCallId && req.callId !== currentCallId) return;
    req.done = true;

    // 1. 망 복구 · ICE 재연결 · 새로고침 복구로 다시 connected가 된 경우 제외 (⑬ 복원 장치에 맡김)
    if (isIceRestartingRef.current || isRebuildingPcRef.current || pendingCameraResumeRef.current) {
      console.info('[화상] 자동 카메라 켜기 생략: 망 복구/재연결 진행 중');
      return;
    }

    // 2. 이미 카메라가 켜져 있는 경우 제외
    if (localVideoTrackRef.current) {
      console.info('[화상] 자동 카메라 켜기 생략: 이미 카메라 켜짐');
      return;
    }

    // 3. 상대가 화상을 지원하지 않는 경우 (peerVideoCapable === false)
    if (peerVideoCapableRef.current !== true) {
      console.info('[화상] 자동 카메라 켜기 생략: 상대 화상 미지원');
      showToast(t('call.peer_video_unsupported'), 8000);
      return;
    }

    if (callStateRef.current !== 'connected') {
      console.info('[화상] 자동 카메라 켜기 생략: connected 상태 아님');
      return;
    }

    // 4. 정상 자동 켜기 시도
    try {
      console.info(`[화상] 처음 connected 확정 → 카메라 자동 켜기 시도 (callId: ${currentCallId ? currentCallId.slice(0, 8) : 'null'})`);
      const res = await startCamera();
      if (res && !res.ok) {
        console.warn('[화상] 자동 카메라 켜기 실패 (음성 통화 유지):', res.reason);
        if (res.reason === 'permission_denied') {
          const isDesktop = isWindows() || isMac();
          showToast(t(isDesktop ? 'call.camera_permission_denied_desktop' : 'call.camera_permission_denied'), 3000);
        } else if (res.reason === 'unavailable') {
          showToast(t('call.camera_unavailable'), 3000);
        }
      }
    } catch (e) {
      console.error('[화상] 자동 카메라 켜기 예외 (음성 유지):', e);
    }
  }, [startCamera, showToast, t]);
  triggerAutoCameraOnConnectRef.current = triggerAutoCameraOnConnect;

  /**
   * [B-2 단위 2] 폰 통화 중 전면↔후면 카메라 전환
   */
  const switchCameraFacing = useCallback(async () => {
    if (
      callStateRef.current !== 'connected' ||
      !cameraOn ||
      !localVideoTrackRef.current ||
      !videoSenderRef.current ||
      cameraBusyRef.current
    ) {
      return { ok: false, reason: cameraBusyRef.current ? 'busy' : 'not_ready' };
    }

    if (isRebuildingPcRef.current || isIceRestartingRef.current) {
      showToast(t('call.camera_switch_reconnecting'), 3000);
      console.info('[화상] 재연결 중 카메라 전환 요청 거부');
      return { ok: false, reason: 'busy' };
    }

    cameraBusyRef.current = true;
    const token = ++cameraVerifySeqRef.current;
    stopStreamQuietly(cameraCandidateStreamRef.current);
    cameraCandidateStreamRef.current = null;

    const isValid = () =>
      cameraVerifySeqRef.current === token &&
      callStateRef.current === 'connected' &&
      Boolean(videoSenderRef.current);

    const handleAbortedCleanup = async (streamToStop) => {
      if (streamToStop) {
        stopStreamQuietly(streamToStop);
        if (cameraCandidateStreamRef.current === streamToStop) {
          cameraCandidateStreamRef.current = null;
        }
      }
      if (callStateRef.current === 'connected') {
        try {
          await videoSenderRef.current?.replaceTrack(null);
        } catch {
          /* 의도적 무시: 취소 끄기 중 replaceTrack 실패 */
        }
        localVideoTrackRef.current = null;
        localVideoStreamRef.current = null;
        setCameraOn(false);
        currentFacingModeRef.current = 'user';
        setCameraFacing('user');
        if (import.meta.env.DEV) console.info('[화상] 전환 중 끄기 → 카메라 끔');
      }
      return { ok: false, reason: 'aborted' };
    };

    try {
      const target = currentFacingModeRef.current === 'user' ? 'environment' : 'user';

      // (나) P13 직전 정리: DEV 감시 핸들러 해제 후 트랙/스트림 참조 비우고 stop
      const oldTrack = localVideoTrackRef.current;
      const oldStream = localVideoStreamRef.current;
      notifyLocalVideoTrackChangedRef.current?.(null);
      if (import.meta.env.DEV && oldTrack) {
        oldTrack.onmute = null;
        oldTrack.onunmute = null;
        oldTrack.onended = null;
      }
      localVideoTrackRef.current = null;
      localVideoStreamRef.current = null;
      if (oldTrack) {
        try {
          oldTrack.stop();
        } catch {
          /* 의도적 무시: 이전 트랙 정지 실패 */
        }
      }
      stopStreamQuietly(oldStream);

      // (가) 새로 얻은 스트림은 getUserMedia 직후 cameraCandidateStreamRef.current에 보관
      let newStream = null;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: target } },
          audio: false,
        });
      } catch (errExact) {
        if (import.meta.env.DEV) console.info('[화상] exact 획득 실패, ideal 재시도:', errExact?.name);
        try {
          newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: target } },
            audio: false,
          });
        } catch (errIdeal) {
          if (import.meta.env.DEV) console.warn('[화상] 전환 카메라 획득 완전 실패:', errIdeal?.name);
        }
      }

      if (newStream) {
        cameraCandidateStreamRef.current = newStream;
      }

      // (다) 취소 처리(P14 확장): 매 await 직후 isValid 검사
      if (!isValid()) {
        return await handleAbortedCleanup(newStream);
      }

      let replaceSuccess = false;
      const newTrack = newStream?.getVideoTracks()?.[0] || null;
      if (newTrack && videoSenderRef.current) {
        try {
          await videoSenderRef.current.replaceTrack(newTrack);
          replaceSuccess = true;
        } catch (repErr) {
          if (import.meta.env.DEV) console.warn('[화상] replaceTrack 실패:', repErr);
        }
      }

      if (!isValid()) {
        return await handleAbortedCleanup(newStream);
      }

      if (replaceSuccess && newTrack && newStream) {
        localVideoTrackRef.current = newTrack;
        notifyLocalVideoTrackChangedRef.current?.(newTrack);
        syncVideoSenderTrack('전환 완료');
        localVideoStreamRef.current = newStream;
        setLocalVideoStream(newStream);
        cameraCandidateStreamRef.current = null; // (가) 성공 직후 비움 (stop하지 않음)

        if (import.meta.env.DEV) {
          newTrack.onmute = () => console.warn('[화상] 로컬 영상 트랙 음소거됨(onmute)');
          newTrack.onunmute = () => console.info('[화상] 로컬 영상 트랙 음소거 해제(onunmute)');
          newTrack.onended = () => console.warn('[화상] 로컬 영상 트랙 종료됨(onended)');
        }

        const actualFacing = newTrack.getSettings?.().facingMode || target;
        currentFacingModeRef.current = actualFacing;
        setCameraFacing(actualFacing);
        setCameraOn(true);

        if (import.meta.env.DEV) {
          console.info(`[화상] 카메라 전환 성공: ${actualFacing} | ${newTrack.label || ''}`);
          inspectVideoTrackLiveness(newTrack, () => isValid() && localVideoTrackRef.current === newTrack)
            .then((res) => {
              if (res && res.status !== 'aborted') {
                console.info(`[화상] 전환 후 화면 판정(DEV 진단): ${res.status}`);
              }
            })
            .catch(() => {});
        }

        return { ok: true, facing: actualFacing };
      }

      // [P15] 획득 실패 또는 replaceTrack 실패: 방금 얻은 트랙 정리 후 원래 방향 복귀
      if (newStream) {
        stopStreamQuietly(newStream);
        if (cameraCandidateStreamRef.current === newStream) {
          cameraCandidateStreamRef.current = null;
        }
      }

      const fallbackTarget = target === 'user' ? 'environment' : 'user';
      let restoreStream = null;
      try {
        restoreStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: fallbackTarget } },
          audio: false,
        });
      } catch (restoreErr) {
        if (import.meta.env.DEV) console.warn('[화상] 원래 방향 복귀 획득 실패:', restoreErr);
      }

      if (restoreStream) {
        cameraCandidateStreamRef.current = restoreStream;
      }

      if (!isValid()) {
        return await handleAbortedCleanup(restoreStream);
      }

      const restoreTrack = restoreStream?.getVideoTracks()?.[0] || null;
      let restoreReplaceSuccess = false;
      if (restoreTrack && videoSenderRef.current) {
        try {
          await videoSenderRef.current.replaceTrack(restoreTrack);
          restoreReplaceSuccess = true;
        } catch (repErr2) {
          if (import.meta.env.DEV) console.warn('[화상] 복귀 replaceTrack 실패:', repErr2);
        }
      }

      if (!isValid()) {
        return await handleAbortedCleanup(restoreStream);
      }

      if (restoreReplaceSuccess && restoreTrack && restoreStream) {
        localVideoTrackRef.current = restoreTrack;
        notifyLocalVideoTrackChangedRef.current?.(restoreTrack);
        localVideoStreamRef.current = restoreStream;
        setLocalVideoStream(restoreStream);
        cameraCandidateStreamRef.current = null;

        if (import.meta.env.DEV) {
          restoreTrack.onmute = () => console.warn('[화상] 로컬 영상 트랙 음소거됨(onmute)');
          restoreTrack.onunmute = () => console.info('[화상] 로컬 영상 트랙 음소거 해제(onunmute)');
          restoreTrack.onended = () => console.warn('[화상] 로컬 영상 트랙 종료됨(onended)');
        }

        const restoredFacing = restoreTrack.getSettings?.().facingMode || fallbackTarget;
        currentFacingModeRef.current = restoredFacing;
        setCameraFacing(restoredFacing);
        setCameraOn(true);
        return { ok: false, reason: 'failed_restored' };
      }

      // [P16] 원래 방향 복귀마저 실패
      if (restoreStream) {
        stopStreamQuietly(restoreStream);
        if (cameraCandidateStreamRef.current === restoreStream) {
          cameraCandidateStreamRef.current = null;
        }
      }
      try {
        await videoSenderRef.current?.replaceTrack(null);
      } catch {
        /* 의도적 무시: 복귀 실패 후 replaceTrack(null) */
      }
      localVideoTrackRef.current = null;
      localVideoStreamRef.current = null;
      setCameraOn(false);
      currentFacingModeRef.current = 'user';
      setCameraFacing('user');
      return { ok: false, reason: 'failed_off' };
    } finally {
      // (라) finally에서 cameraBusyRef.current = false 반드시 실행
      cameraBusyRef.current = false;
    }
  }, [cameraOn, showToast, t, syncVideoSenderTrack]);

  const stopCamera = useCallback(async () => {
    cameraVerifySeqRef.current++;
    stopStreamQuietly(cameraCandidateStreamRef.current);
    cameraCandidateStreamRef.current = null;
    if (cameraBusyRef.current) {
      if (import.meta.env.DEV) console.info('[화상] 카메라 실패: not_ready');
      return { ok: false, reason: 'not_ready' };
    }

    cameraBusyRef.current = true;
    try {
      if (videoSenderRef.current) {
        try {
          await videoSenderRef.current.replaceTrack(null);
        } catch {}
      }
      notifyLocalVideoTrackChangedRef.current?.(null);
      if (localVideoTrackRef.current) {
        try {
          localVideoTrackRef.current.stop();
        } catch {}
        localVideoTrackRef.current = null;
      }
      if (localVideoStreamRef.current) {
        try {
          localVideoStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        localVideoStreamRef.current = null;
      }
      setCameraOn(false);
      console.info('[화상] 카메라 끔');
      return { ok: true };
    } finally {
      cameraBusyRef.current = false;
    }
  }, []);
  stopCameraRef.current = stopCamera;

  return {
    callState,
    incomingKind,
    isAccepting,
    callerInfo,
    partner: currentPartner,
    elapsedSeconds,
    isAudioConnected,
    remoteAudioRef,
    startVoiceCall,
    acceptCall,
    declineCall,
    endCall,
    handleCallOffer,
    handleCallAnswer,
    handleCallIce,
    handleCallEnd,
    handleCallRecover,
    handleCallRecoverReady,
    handleCallDecline,
    handleCallBusy,
    handleCallPing,
    peerRecoveryRemainingSec,
    handleCallAcceptedElsewhere,
    handleCallDeclinedElsewhere,
    handleOfferResendRequest,
    handleIceRestartRequest,
    handleIceRestartOffer,
    handleIceRestartAnswer,
    handlePeerConnectionRebuildRequest,
    handlePeerConnectionRebuildOffer,
    handlePeerConnectionRebuildAnswer,
    hasRecoveredOfferSdp,
    startRecoveredIncomingCall,
    applyRecoveredOffer,
    isMuted,
    peerMuted,
    toggleMute,
    handleCallMute,
    handleCallVideoState,
    isMicLocked,
    replaceAudioTrack,
    getCurrentMicLabel,
    micChangeNotice,
    cameraNotice,
    cameraOn,
    startCamera,
    stopCamera,
    switchCameraFacing,
    cameraDeviceCount,
    peerVideoCapable,
    getPeerVideoCapable,
    localVideoStream,
    remoteVideoStream,
    remoteVideoOn,
    cameraFacing,
    isNetworkReconnectingSelf,
    isNetworkReconnectingPeer,
    isPeerAudioUnstable,
    isReconnectedSuccess,
    handleUserSignalStatus,
  };
}
